// lib/context/NotifiFrontendClientContext.tsx
import {
  instantiateFrontendClient
} from "@notifi-network/notifi-frontend-client";
import React from "react";

// lib/utils/constants.ts
var defaultCopy = {
  connect: {
    title: "Notifications",
    description: "See your activity and gain access to alerts",
    hardwareWalletLabel: "Use Hardware Wallet",
    buttonText: "Next"
  },
  poweredByNotifi: "Powered by",
  inputFields: {
    email: "Email address",
    phoneNumber: "Phone Number",
    telegram: "Telegram ID"
  },
  inputToggles: {
    discord: "Discord DM Bot",
    slack: "Slack",
    wallet: "Wallet Alerts",
    discordUnavailable: "Discord unavailable",
    slackUnavailable: "Slack unavailable",
    walletUnavailable: "Only available for Coinbase Wallet"
  },
  ftu: {
    headerTitles: {
      ftuTargetEdit: "How do you want to be notified?",
      ftuAlertEdit: "Select alerts"
    }
  },
  ftuTargetEdit: {
    description: "Select a minimum of one destination",
    headerTitle: "How do you want to be notified?",
    buttonText: "Next"
  },
  ftuTargetList: {
    headerTitle: "Verify your destinations",
    buttonText: "Next"
  },
  ftuAlertEdit: {
    headerTitle: "Select alerts",
    buttonText: "Done"
  },
  targetList: {
    email: "Email",
    phoneNumber: "SMS",
    telegram: "Telegram",
    discord: "Discord",
    slack: "Slack",
    wallet: "Wallets",
    emailVerifyMessage: "We\u2019ve sent a verification email to:",
    postCtaEmail: "Email sent",
    postCtaEmailDurationInMs: 5e3,
    postCtaTelegram: "Done!",
    postCtaTelegramDurationInMs: 5e3,
    postCtaDiscord: "",
    postCtaDiscordDurationInMs: 0,
    postCtaSlack: "Done!",
    postCtaSlackDurationInMs: 5e3,
    discordVerifiedMessage: "Make sure Discord DMs are enabled",
    discordVerifiedPromptTooltip: "Make sure you have enabled DMs in Discord. Right click on the server after joining, go to Privacy Settings, and enable Direct Messages.",
    walletVerifyMessage: "2-3 signatures required to verify",
    walletVerifyTooltip: "Wallet messages are powered by XMTP and delivered natively into Coinbase Wallet. Download the Coinbase Wallet App.",
    walletVerifiedMessage: "Enable messages in Coinbase Wallet App",
    walletVerifiedTooltip: "Make sure messages are enabled in your Coinbase Wallet Mobile App.",
    walletVerifiedTooltipLink: {
      text: "More info",
      url: "https://docs.notifi.network/docs/target-setup/wallet-integration"
    }
  },
  topicOptions: {
    customInputPlaceholder: "Custom"
  },
  subscriptionValueInput: {
    dropdownPlaceholder: "--- Select one option ---"
  },
  topicStackRowInput: {
    buttonContent: "Save"
  },
  topicStackRow: {
    cta: "+ Add"
  },
  historyRow: {
    legacyTopic: "Legacy: topic is removed"
  },
  inboxHistoryList: {
    headerTitle: "Notifications",
    buttonText: "Load More"
  },
  inboxHistoryDetail: {
    headerTitle: ""
  },
  historyList: {
    inboxEmptyTitle: "Inbox empty",
    inboxEmptyDescription: `You haven\u2019t received any notifications yet. You can manage your
    destinations and alerts under settings.`
  },
  inboxConfigTopic: {
    header: "Manage Notifications",
    title: "Manage the alerts you want to receive:"
  },
  targetStateBanner: {
    verify: {
      title: "Manage alert destinations",
      description: "You have unverified destinations"
    },
    verifyInHistory: {
      title: "Verify your",
      ctaText: "Verify"
    },
    Signup: {
      textInHistory: "Get alerts via",
      ctaInHistory: "Sign Up",
      textInConfig: "Get notifications to the destinations of your choice."
    }
  },
  inboxConfigTargetList: {
    header: "Manage Destinations",
    buttonText: "Edit Destinations"
  },
  inboxConfigTargetEdit: {
    header: "Edit Destinations",
    buttonTextHasTarget: "Save Changes",
    buttonTextNoTarget: "Subscribe"
  },
  errorGlobal: {
    header: "An error occurred",
    title: "Something went wrong, please try again: ",
    detail: "Contact support if the issue persists"
  },
  targetInputField: {
    inValidErrorMessage: (target) => `Please enter a valid ${target} address`
  }
};

// lib/utils/topic.ts
import {
  objectKeys
} from "@notifi-network/notifi-frontend-client";
var validateEventType = (eventType) => {
  return eventType.type === "fusion" && eventType.selectedUIType === "TOGGLE" || eventType.type === "label";
};
var categorizeTopics = (topics, unCategorizedTopicsLabelName) => {
  const categorizedEventTypeItems = [];
  const uncategorizedEventTypeItem = {
    name: unCategorizedTopicsLabelName ?? "General",
    type: "label",
    subTopics: []
  };
  let currentLabel = void 0;
  topics.filter(validateEventType).forEach((row) => {
    if (row.type === "label") {
      currentLabel = {
        ...row,
        subTopics: []
      };
      categorizedEventTypeItems.push(currentLabel);
    } else {
      if (currentLabel) {
        currentLabel.subTopics.push(row);
      } else {
        uncategorizedEventTypeItem.subTopics.push(row);
      }
    }
  });
  return {
    categorizedTopics: categorizedEventTypeItems,
    uncategorizedTopics: uncategorizedEventTypeItem
  };
};
var getUserInputParams = (topic) => {
  const parsedMetadata = JSON.parse(
    topic.fusionEventDescriptor.metadata ?? "{}"
  );
  if (!isFusionEventMetadata(parsedMetadata)) {
    return [];
  }
  const filters = parsedMetadata?.filters?.filter(isAlertFilter) ?? [];
  return filters[0]?.userInputParams ?? [];
};
var getUiConfigOverride = (topic) => {
  const parsedMetadata = JSON.parse(
    topic.fusionEventDescriptor.metadata ?? "{}"
  );
  if (!isFusionEventMetadata(parsedMetadata)) {
    return {};
  }
  return parsedMetadata?.uiConfigOverride ?? {};
};
var getFusionEventMetadata = (topic) => {
  const parsedMetadata = JSON.parse(
    topic.fusionEventDescriptor.metadata ?? "{}"
  );
  if (isFusionEventMetadata(parsedMetadata)) {
    return parsedMetadata;
  }
  return null;
};
var getFusionFilter = (topic) => {
  const parsedMetadata = JSON.parse(
    topic.fusionEventDescriptor.metadata ?? "{}"
  );
  if (isFusionEventMetadata(parsedMetadata)) {
    return parsedMetadata?.filters?.[0] ?? {};
  }
  return null;
};
var isTopicGroupValid = (topics) => {
  const isAllTopicWithoutFilters = topics.every(
    (topic) => getFusionEventMetadata(topic)?.filters.filter(isAlertFilter).length === 0
  );
  if (isAllTopicWithoutFilters) {
    return true;
  }
  const benchmarkTopic = topics[0];
  const benchmarkTopicMetadata = getFusionEventMetadata(benchmarkTopic);
  const benchmarkAlertFilter = benchmarkTopicMetadata?.filters.find(isAlertFilter);
  const benchmarkUserInputParams = benchmarkAlertFilter?.userInputParams;
  if (!benchmarkAlertFilter) {
    return false;
  }
  if (benchmarkUserInputParams && benchmarkUserInputParams.length > 0) {
    const isGroupNameNotMatched = topics.find(
      (topic) => topic.uiConfig.topicGroupName !== benchmarkTopic.uiConfig.topicGroupName
    );
    if (isGroupNameNotMatched) {
      return false;
    }
    const userInputParamsList = topics.map((topic) => {
      return getUserInputParams(topic);
    });
    for (let i = 1; i < userInputParamsList.length; i++) {
      const userInputParams = userInputParamsList[i];
      const userInputParamMap = new Map(
        userInputParams.map((userInputParam) => [
          userInputParam.name,
          userInputParam
        ])
      );
      const benchmarkUserInputParamMap = new Map(
        benchmarkUserInputParams.map((userInputParam) => [
          userInputParam.name,
          userInputParam
        ])
      );
      const benchmarkUserInputParamKeys = Array.from(
        benchmarkUserInputParamMap.keys()
      );
      for (const key of benchmarkUserInputParamKeys) {
        const benchmarkUserInputParam = benchmarkUserInputParamMap.get(key);
        const userInputParam = userInputParamMap.get(key);
        if (!userInputParam || !benchmarkUserInputParam) {
          return false;
        }
        const benchmarkParamOptions = benchmarkUserInputParam.options.join("");
        const userInputParamOptions = userInputParam.options.join("");
        if (benchmarkParamOptions !== userInputParamOptions) {
          return false;
        }
        if (benchmarkUserInputParam.defaultValue !== userInputParam.defaultValue) {
          return false;
        }
      }
    }
  }
  const benchmarkSubscriptionValueOrRef = benchmarkTopicMetadata?.uiConfigOverride?.subscriptionValueOrRef;
  const benchmarkSubscriptionRef = benchmarkSubscriptionValueOrRef?.type === "ref" ? benchmarkSubscriptionValueOrRef.ref : null;
  if (benchmarkTopicMetadata?.uiConfigOverride?.isSubscriptionValueInputable && benchmarkSubscriptionRef) {
    const isValid = topics.every((topic) => {
      const subTopicMetadata = getFusionEventMetadata(topic);
      const subTopicSubscriptionValueOrRef = subTopicMetadata?.uiConfigOverride?.subscriptionValueOrRef;
      const subTopicSubscriptionRef = subTopicSubscriptionValueOrRef?.type === "ref" ? subTopicSubscriptionValueOrRef.ref : null;
      return benchmarkSubscriptionRef === subTopicSubscriptionRef;
    });
    return isValid;
  }
  return true;
};
var isFusionEventMetadata = (metadata) => {
  const metadataToVerify = metadata;
  if (typeof metadataToVerify !== "object" || !metadataToVerify) {
    return false;
  }
  return "filters" in metadataToVerify && Array.isArray(metadataToVerify.filters);
};
var isAlertFilter = (filter) => {
  return "userInputParams" in filter && "type" in filter && filter.type === "AlertFilter";
};
var isFusionFilterOptions = (filterOptions) => {
  const filterOptionsToVerify = filterOptions;
  if (typeof filterOptionsToVerify !== "object" || !filterOptionsToVerify) {
    return false;
  }
  return "version" in filterOptionsToVerify && filterOptionsToVerify.version === 1;
};
var getUpdatedAlertFilterOptions = (filterName, alertFilterOptions, inputParmName, value) => {
  return {
    ...alertFilterOptions,
    input: {
      ...alertFilterOptions.input,
      [filterName]: {
        ...alertFilterOptions.input[filterName],
        [inputParmName]: value
      }
    }
  };
};
var derivePrefixAndSuffixFromValueType = (kind) => {
  switch (kind) {
    case "price":
      return {
        prefix: "$",
        suffix: ""
      };
    case "percentage":
      return {
        prefix: "",
        suffix: "%"
      };
    case "string":
    case "integer":
      return {
        prefix: "",
        suffix: ""
      };
    default:
      return {
        prefix: "",
        suffix: ""
      };
  }
};
var isAlertMetadataForTopicStack = (alertMetadata) => {
  return objectKeys(alertMetadata).length > 1 && "timestamp" in alertMetadata && "subscriptionValue" in alertMetadata && "subscriptionLabel" in alertMetadata && "fusionEventTypeId" in alertMetadata;
};
var resolveAlertName = (alertName) => {
  const resolved = alertName.split(":;:");
  if (resolved.length === 1) {
    return {
      fusionEventTypeId: resolved[0]
    };
  }
  const [fusionEventTypeId, subscriptionValue, subscriptionLabel, timestamp] = resolved;
  return {
    fusionEventTypeId,
    subscriptionValue,
    subscriptionLabel,
    timestamp
  };
};
var resolveTopicStackAlertName = (alertName) => {
  const [fusionEventTypeId, subscriptionValue, subscriptionLabel, timestamp] = alertName.split(":;:");
  return {
    fusionEventTypeId,
    subscriptionValue,
    subscriptionLabel,
    timestamp
  };
};
var composeTopicStackAlertName = (fusionEventTypeId, subscriptionValue, subscriptionLabel) => {
  return `${fusionEventTypeId}:;:${subscriptionValue}:;:${subscriptionLabel}:;:${Date.now()}`;
};
var ConvertOptionDirection = /* @__PURE__ */ ((ConvertOptionDirection2) => {
  ConvertOptionDirection2["FtoB"] = "frontendToBackend";
  ConvertOptionDirection2["BtoF"] = "BackendToFrontend";
  return ConvertOptionDirection2;
})(ConvertOptionDirection || {});
var convertOptionValue = (value, type, direction) => {
  if (value === "") return "";
  direction = direction ?? "frontendToBackend" /* FtoB */;
  if (type === "percentage") {
    return direction === "frontendToBackend" /* FtoB */ ? Number(value) / 100 : Number(value) * 100;
  }
  return value;
};

// lib/utils/target.ts
import { objectKeys as objectKeys2 } from "@notifi-network/notifi-frontend-client";
var formatTelegramForSubscription = (telegramId) => {
  if (telegramId.startsWith("@")) {
    return telegramId.slice(1);
  }
  return telegramId;
};
var reformatSignatureForWalletTarget = (signature) => {
  if (!signature) return "";
  let hexString = "0x";
  Object.values(signature).forEach(
    (v) => hexString += v.toString(16).padStart(2, "0")
  );
  return hexString;
};
var hasTarget = (targetData) => {
  return !objectKeys2(targetData).every((key) => {
    const target = targetData[key];
    if (typeof target === "string") {
      return !target;
    }
    if (typeof target === "object") {
      return !target.data;
    }
  });
};
var getAvailableTargetInputCount = (targetInputs) => {
  return objectKeys2(targetInputs).filter((key) => {
    if (isFormTarget(key)) {
      return targetInputs[key].value;
    }
    return targetInputs[key];
  }).length;
};
var isTargetCta = (targetInfoPrompt) => {
  return targetInfoPrompt.type === "cta";
};
var isTargetVerified = (targetInfoPrompt) => {
  return targetInfoPrompt.type === "message";
};
var isFormTarget = (target) => {
  const supportedFormTargets = ["email", "phoneNumber", "telegram"];
  return supportedFormTargets.includes(target);
};
var isToggleTarget = (target) => {
  const supportedToggleTargets = ["discord", "slack", "wallet"];
  return supportedToggleTargets.includes(target);
};
var getWalletTargetSignMessage = (address, senderAddress, nonce) => `Coinbase Wallet Messaging subscribe
Address: ${address}
Partner Address: ${senderAddress}
Nonce: ${nonce}`;
var getTargetValidateRegex = (target) => {
  switch (target) {
    case "email":
      return new RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$");
    case "telegram":
      return new RegExp(".{5,}");
    default:
      throw new Error("Not supported target type");
  }
};

// lib/utils/frontendClient.ts
var loginViaSolanaHardwareWallet = async (frontendClient, walletWithSignParams) => {
  if (walletWithSignParams.walletBlockchain !== "SOLANA")
    throw new Error("loginViaSolanaHardwareWallet: Only SOLANA is supported");
  if (!walletWithSignParams.hardwareLoginPlugin)
    throw new Error(
      "loginViaSolanaHardwareWallet: loginViaSolanaHardwareWallet: Missing hardwareLoginPlugin"
    );
  const plugin = walletWithSignParams.hardwareLoginPlugin;
  const { nonce } = await frontendClient.beginLoginViaTransaction({
    walletAddress: walletWithSignParams.walletPublicKey,
    walletBlockchain: walletWithSignParams.walletBlockchain
  });
  const transactionSignature = await plugin.sendMessage(nonce);
  const logInResult = await frontendClient.completeLoginViaTransaction({
    walletAddress: walletWithSignParams.walletPublicKey,
    walletBlockchain: walletWithSignParams.walletBlockchain,
    transactionSignature
  });
  if (logInResult?.completeLogInByTransaction === void 0) {
    throw new Error("Log in failed");
  }
  return logInResult;
};

// lib/utils/math.ts
function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null)
    return false;
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }
  return true;
}
function customSort(a, b) {
  if (typeof a === "object" && typeof b === "object") {
    return JSON.stringify(a).localeCompare(JSON.stringify(b));
  }
  return a < b ? -1 : a > b ? 1 : 0;
}
function isEqual(value, other) {
  if (value === other) return true;
  if (typeof value !== typeof other) return false;
  if (Array.isArray(value) && Array.isArray(other)) {
    if (value.length !== other.length) return false;
    const sortedValue = [...value].sort(customSort);
    const sortedOther = [...other].sort(customSort);
    for (let i = 0; i < sortedValue.length; i++) {
      if (!isEqual(sortedValue[i], sortedOther[i])) return false;
    }
    return true;
  }
  if (Array.isArray(value) || Array.isArray(other)) return false;
  return deepEqual(value, other);
}

// lib/context/NotifiFrontendClientContext.tsx
var NotifiFrontendClientContext = React.createContext(
  {}
);
var NotifiFrontendClientContextProvider = ({
  children,
  tenantId,
  env,
  storageOption,
  isEnabledLoginViaTransaction,
  ...walletWithSignParams
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [transactionNonce, setTransactionNonce] = React.useState(
    null
  );
  const [frontendClient, setFrontendClient] = React.useState(null);
  const [frontendClientStatus, setFrontendClientStatus] = React.useState({
    isExpired: false,
    isInitialized: false,
    isAuthenticated: false
  });
  const userId = walletWithSignParams.walletBlockchain === "OFF_CHAIN" ? walletWithSignParams.userAccount : walletWithSignParams.walletPublicKey;
  const isLogInViaTransactionAvailable = walletWithSignParams.walletBlockchain !== "OFF_CHAIN" && isEnabledLoginViaTransaction;
  React.useEffect(() => {
    const frontendClient2 = instantiateFrontendClient(
      tenantId,
      walletWithSignParams,
      env,
      storageOption
    );
    setIsLoading(true);
    frontendClient2.initialize().then(() => {
      setFrontendClientStatus({
        isExpired: frontendClient2.userState?.status === "expired",
        isInitialized: !!frontendClient2,
        isAuthenticated: frontendClient2.userState?.status === "authenticated"
      });
      setFrontendClient(frontendClient2);
      setError(null);
    }).catch((e) => {
      if (e instanceof Error) {
        setError({
          ...e,
          message: `  failed to initialize frontendClient (.initialize): ${e.message}`
        });
      }
      console.error(e);
    }).finally(() => setIsLoading(false));
  }, [userId]);
  React.useEffect(() => {
    if (!frontendClient || !frontendClientStatus.isInitialized || !isLogInViaTransactionAvailable)
      return;
    const getNonce = async () => {
      const nonce = await frontendClient?.beginLoginViaTransaction({
        walletAddress: walletWithSignParams.walletPublicKey,
        walletBlockchain: walletWithSignParams.walletBlockchain
      });
      return nonce.nonce;
    };
    getNonce().then((nonce) => setTransactionNonce(nonce));
    const interval = setInterval(async () => {
      getNonce().then((nonce) => setTransactionNonce(nonce));
    }, 6e5);
    return () => clearInterval(interval);
  }, [frontendClientStatus.isInitialized, isLogInViaTransactionAvailable]);
  const login = async () => {
    if (!frontendClient || !frontendClientStatus.isInitialized) {
      setError(new Error(".login: Frontend client not initialized"));
      return;
    }
    setIsLoading(true);
    try {
      await frontendClient.logIn(walletWithSignParams);
      setFrontendClientStatus({
        isExpired: frontendClient.userState?.status === "expired",
        isInitialized: !!frontendClient,
        isAuthenticated: frontendClient.userState?.status === "authenticated"
      });
      setFrontendClient(frontendClient);
      setError(null);
    } catch (error2) {
      if (error2 instanceof Error) {
        const newError = {
          ...error2,
          message: `login: Failed to login User rejects to sign, or mis-impl the signMessage method: ${JSON.stringify(
            error2.message
          )}`
        };
        setError(newError);
        console.error(newError);
      }
    } finally {
      setIsLoading(false);
    }
    return frontendClient;
  };
  const _loginViaTransaction = React.useCallback(
    async (signatureSignedWithNotifiNonce) => {
      if (!isLogInViaTransactionAvailable) return;
      if (!frontendClient || !frontendClientStatus.isInitialized) {
        setError(
          new Error(".loginViaTransaction: Frontend client not initialized")
        );
        return;
      }
      setIsLoading(true);
      try {
        await frontendClient?.completeLoginViaTransaction({
          walletAddress: walletWithSignParams.walletPublicKey,
          walletBlockchain: walletWithSignParams.walletBlockchain,
          transactionSignature: signatureSignedWithNotifiNonce
        });
        setFrontendClientStatus({
          isExpired: frontendClient.userState?.status === "expired",
          isInitialized: !!frontendClient,
          isAuthenticated: frontendClient.userState?.status === "authenticated"
        });
        setFrontendClient(frontendClient);
        setError(null);
      } catch (error2) {
        if (error2 instanceof Error) {
          const newError = {
            ...error2,
            message: `loginViaTrasaction: failed to call .completeLoginViaTransaction: ${error2.message}`
          };
          setError(newError);
          console.error(newError);
        }
      } finally {
        setIsLoading(false);
      }
      return frontendClient;
    },
    [userId, !!frontendClient, isEnabledLoginViaTransaction]
  );
  const loginViaHardwareWallet = React.useCallback(async () => {
    if (!frontendClient || !frontendClientStatus.isInitialized || walletWithSignParams.walletBlockchain === "OFF_CHAIN") {
      setError(
        new Error(
          ".loginViaHardwareWallet: Frontend client not initialized / or Invalid blockchain"
        )
      );
      return;
    }
    setIsLoading(true);
    try {
      await loginViaSolanaHardwareWallet(frontendClient, walletWithSignParams);
      setFrontendClientStatus({
        isExpired: frontendClient.userState?.status === "expired",
        isInitialized: !!frontendClient,
        isAuthenticated: frontendClient.userState?.status === "authenticated"
      });
      setFrontendClient(frontendClient);
      setError(null);
    } catch (error2) {
      if (error2 instanceof Error) {
        const newError = {
          ...error2,
          message: `loginViaHardwareWallet: User rejects to sign, or mis-impl the signMessage method: ${error2.message}`
        };
        setError(newError);
        console.error(newError);
      }
    } finally {
      setIsLoading(false);
    }
    return frontendClient;
  }, [userId, !!frontendClient]);
  if (!frontendClient) return null;
  return /* @__PURE__ */ React.createElement(
    NotifiFrontendClientContext.Provider,
    {
      value: {
        frontendClient,
        frontendClientStatus,
        isLoading,
        error,
        login,
        loginViaHardwareWallet,
        walletWithSignParams,
        loginViaTransaction: isLogInViaTransactionAvailable && transactionNonce ? {
          nonce: transactionNonce,
          login: _loginViaTransaction
        } : null
      }
    },
    children
  );
};
var useNotifiFrontendClientContext = () => React.useContext(NotifiFrontendClientContext);

// lib/context/NotifiTargetContext.tsx
import {
  objectKeys as objectKeys3
} from "@notifi-network/notifi-frontend-client";

// ../../node_modules/libphonenumber-js/metadata.min.json.js
var metadata_min_json_default = { "version": 4, "country_calling_codes": { "1": ["US", "AG", "AI", "AS", "BB", "BM", "BS", "CA", "DM", "DO", "GD", "GU", "JM", "KN", "KY", "LC", "MP", "MS", "PR", "SX", "TC", "TT", "VC", "VG", "VI"], "7": ["RU", "KZ"], "20": ["EG"], "27": ["ZA"], "30": ["GR"], "31": ["NL"], "32": ["BE"], "33": ["FR"], "34": ["ES"], "36": ["HU"], "39": ["IT", "VA"], "40": ["RO"], "41": ["CH"], "43": ["AT"], "44": ["GB", "GG", "IM", "JE"], "45": ["DK"], "46": ["SE"], "47": ["NO", "SJ"], "48": ["PL"], "49": ["DE"], "51": ["PE"], "52": ["MX"], "53": ["CU"], "54": ["AR"], "55": ["BR"], "56": ["CL"], "57": ["CO"], "58": ["VE"], "60": ["MY"], "61": ["AU", "CC", "CX"], "62": ["ID"], "63": ["PH"], "64": ["NZ"], "65": ["SG"], "66": ["TH"], "81": ["JP"], "82": ["KR"], "84": ["VN"], "86": ["CN"], "90": ["TR"], "91": ["IN"], "92": ["PK"], "93": ["AF"], "94": ["LK"], "95": ["MM"], "98": ["IR"], "211": ["SS"], "212": ["MA", "EH"], "213": ["DZ"], "216": ["TN"], "218": ["LY"], "220": ["GM"], "221": ["SN"], "222": ["MR"], "223": ["ML"], "224": ["GN"], "225": ["CI"], "226": ["BF"], "227": ["NE"], "228": ["TG"], "229": ["BJ"], "230": ["MU"], "231": ["LR"], "232": ["SL"], "233": ["GH"], "234": ["NG"], "235": ["TD"], "236": ["CF"], "237": ["CM"], "238": ["CV"], "239": ["ST"], "240": ["GQ"], "241": ["GA"], "242": ["CG"], "243": ["CD"], "244": ["AO"], "245": ["GW"], "246": ["IO"], "247": ["AC"], "248": ["SC"], "249": ["SD"], "250": ["RW"], "251": ["ET"], "252": ["SO"], "253": ["DJ"], "254": ["KE"], "255": ["TZ"], "256": ["UG"], "257": ["BI"], "258": ["MZ"], "260": ["ZM"], "261": ["MG"], "262": ["RE", "YT"], "263": ["ZW"], "264": ["NA"], "265": ["MW"], "266": ["LS"], "267": ["BW"], "268": ["SZ"], "269": ["KM"], "290": ["SH", "TA"], "291": ["ER"], "297": ["AW"], "298": ["FO"], "299": ["GL"], "350": ["GI"], "351": ["PT"], "352": ["LU"], "353": ["IE"], "354": ["IS"], "355": ["AL"], "356": ["MT"], "357": ["CY"], "358": ["FI", "AX"], "359": ["BG"], "370": ["LT"], "371": ["LV"], "372": ["EE"], "373": ["MD"], "374": ["AM"], "375": ["BY"], "376": ["AD"], "377": ["MC"], "378": ["SM"], "380": ["UA"], "381": ["RS"], "382": ["ME"], "383": ["XK"], "385": ["HR"], "386": ["SI"], "387": ["BA"], "389": ["MK"], "420": ["CZ"], "421": ["SK"], "423": ["LI"], "500": ["FK"], "501": ["BZ"], "502": ["GT"], "503": ["SV"], "504": ["HN"], "505": ["NI"], "506": ["CR"], "507": ["PA"], "508": ["PM"], "509": ["HT"], "590": ["GP", "BL", "MF"], "591": ["BO"], "592": ["GY"], "593": ["EC"], "594": ["GF"], "595": ["PY"], "596": ["MQ"], "597": ["SR"], "598": ["UY"], "599": ["CW", "BQ"], "670": ["TL"], "672": ["NF"], "673": ["BN"], "674": ["NR"], "675": ["PG"], "676": ["TO"], "677": ["SB"], "678": ["VU"], "679": ["FJ"], "680": ["PW"], "681": ["WF"], "682": ["CK"], "683": ["NU"], "685": ["WS"], "686": ["KI"], "687": ["NC"], "688": ["TV"], "689": ["PF"], "690": ["TK"], "691": ["FM"], "692": ["MH"], "850": ["KP"], "852": ["HK"], "853": ["MO"], "855": ["KH"], "856": ["LA"], "880": ["BD"], "886": ["TW"], "960": ["MV"], "961": ["LB"], "962": ["JO"], "963": ["SY"], "964": ["IQ"], "965": ["KW"], "966": ["SA"], "967": ["YE"], "968": ["OM"], "970": ["PS"], "971": ["AE"], "972": ["IL"], "973": ["BH"], "974": ["QA"], "975": ["BT"], "976": ["MN"], "977": ["NP"], "992": ["TJ"], "993": ["TM"], "994": ["AZ"], "995": ["GE"], "996": ["KG"], "998": ["UZ"] }, "countries": { "AC": ["247", "00", "(?:[01589]\\d|[46])\\d{4}", [5, 6]], "AD": ["376", "00", "(?:1|6\\d)\\d{7}|[135-9]\\d{5}", [6, 8, 9], [["(\\d{3})(\\d{3})", "$1 $2", ["[135-9]"]], ["(\\d{4})(\\d{4})", "$1 $2", ["1"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["6"]]]], "AE": ["971", "00", "(?:[4-7]\\d|9[0-689])\\d{7}|800\\d{2,9}|[2-4679]\\d{7}", [5, 6, 7, 8, 9, 10, 11, 12], [["(\\d{3})(\\d{2,9})", "$1 $2", ["60|8"]], ["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["[236]|[479][2-8]"], "0$1"], ["(\\d{3})(\\d)(\\d{5})", "$1 $2 $3", ["[479]"]], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["5"], "0$1"]], "0"], "AF": ["93", "00", "[2-7]\\d{8}", [9], [["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[2-7]"], "0$1"]], "0"], "AG": ["1", "011", "(?:268|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([457]\\d{6})$|1", "268$1", 0, "268"], "AI": ["1", "011", "(?:264|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2457]\\d{6})$|1", "264$1", 0, "264"], "AL": ["355", "00", "(?:700\\d\\d|900)\\d{3}|8\\d{5,7}|(?:[2-5]|6\\d)\\d{7}", [6, 7, 8, 9], [["(\\d{3})(\\d{3,4})", "$1 $2", ["80|9"], "0$1"], ["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["4[2-6]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[2358][2-5]|4"], "0$1"], ["(\\d{3})(\\d{5})", "$1 $2", ["[23578]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["6"], "0$1"]], "0"], "AM": ["374", "00", "(?:[1-489]\\d|55|60|77)\\d{6}", [8], [["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["[89]0"], "0 $1"], ["(\\d{3})(\\d{5})", "$1 $2", ["2|3[12]"], "(0$1)"], ["(\\d{2})(\\d{6})", "$1 $2", ["1|47"], "(0$1)"], ["(\\d{2})(\\d{6})", "$1 $2", ["[3-9]"], "0$1"]], "0"], "AO": ["244", "00", "[29]\\d{8}", [9], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[29]"]]]], "AR": ["54", "00", "(?:11|[89]\\d\\d)\\d{8}|[2368]\\d{9}", [10, 11], [["(\\d{4})(\\d{2})(\\d{4})", "$1 $2-$3", ["2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9])", "2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8]))|2(?:2[24-9]|3[1-59]|47)", "2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5[56][46]|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]", "2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|58|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|54(?:4|5[13-7]|6[89])|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:454|85[56])[46]|3(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]"], "0$1", 1], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2-$3", ["1"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{4})", "$1-$2-$3", ["[68]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2-$3", ["[23]"], "0$1", 1], ["(\\d)(\\d{4})(\\d{2})(\\d{4})", "$2 15-$3-$4", ["9(?:2[2-469]|3[3-578])", "9(?:2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9]))", "9(?:2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8])))|92(?:2[24-9]|3[1-59]|47)", "9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5(?:[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]", "9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|5(?:4(?:4|5[13-7]|6[89])|[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]"], "0$1", 0, "$1 $2 $3-$4"], ["(\\d)(\\d{2})(\\d{4})(\\d{4})", "$2 15-$3-$4", ["91"], "0$1", 0, "$1 $2 $3-$4"], ["(\\d{3})(\\d{3})(\\d{5})", "$1-$2-$3", ["8"], "0$1"], ["(\\d)(\\d{3})(\\d{3})(\\d{4})", "$2 15-$3-$4", ["9"], "0$1", 0, "$1 $2 $3-$4"]], "0", 0, "0?(?:(11|2(?:2(?:02?|[13]|2[13-79]|4[1-6]|5[2457]|6[124-8]|7[1-4]|8[13-6]|9[1267])|3(?:02?|1[467]|2[03-6]|3[13-8]|[49][2-6]|5[2-8]|[67])|4(?:7[3-578]|9)|6(?:[0136]|2[24-6]|4[6-8]?|5[15-8])|80|9(?:0[1-3]|[19]|2\\d|3[1-6]|4[02568]?|5[2-4]|6[2-46]|72?|8[23]?))|3(?:3(?:2[79]|6|8[2578])|4(?:0[0-24-9]|[12]|3[5-8]?|4[24-7]|5[4-68]?|6[02-9]|7[126]|8[2379]?|9[1-36-8])|5(?:1|2[1245]|3[237]?|4[1-46-9]|6[2-4]|7[1-6]|8[2-5]?)|6[24]|7(?:[069]|1[1568]|2[15]|3[145]|4[13]|5[14-8]|7[2-57]|8[126])|8(?:[01]|2[15-7]|3[2578]?|4[13-6]|5[4-8]?|6[1-357-9]|7[36-8]?|8[5-8]?|9[124])))15)?", "9$1"], "AS": ["1", "011", "(?:[58]\\d\\d|684|900)\\d{7}", [10], 0, "1", 0, "([267]\\d{6})$|1", "684$1", 0, "684"], "AT": ["43", "00", "1\\d{3,12}|2\\d{6,12}|43(?:(?:0\\d|5[02-9])\\d{3,9}|2\\d{4,5}|[3467]\\d{4}|8\\d{4,6}|9\\d{4,7})|5\\d{4,12}|8\\d{7,12}|9\\d{8,12}|(?:[367]\\d|4[0-24-9])\\d{4,11}", [4, 5, 6, 7, 8, 9, 10, 11, 12, 13], [["(\\d)(\\d{3,12})", "$1 $2", ["1(?:11|[2-9])"], "0$1"], ["(\\d{3})(\\d{2})", "$1 $2", ["517"], "0$1"], ["(\\d{2})(\\d{3,5})", "$1 $2", ["5[079]"], "0$1"], ["(\\d{3})(\\d{3,10})", "$1 $2", ["(?:31|4)6|51|6(?:5[0-3579]|[6-9])|7(?:20|32|8)|[89]"], "0$1"], ["(\\d{4})(\\d{3,9})", "$1 $2", ["[2-467]|5[2-6]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["5"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4,7})", "$1 $2 $3", ["5"], "0$1"]], "0"], "AU": ["61", "001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011", "1(?:[0-79]\\d{7}(?:\\d(?:\\d{2})?)?|8[0-24-9]\\d{7})|[2-478]\\d{8}|1\\d{4,7}", [5, 6, 7, 8, 9, 10, 12], [["(\\d{2})(\\d{3,4})", "$1 $2", ["16"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2,4})", "$1 $2 $3", ["16"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["14|4"], "0$1"], ["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["[2378]"], "(0$1)"], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["1(?:30|[89])"]]], "0", 0, "(183[12])|0", 0, 0, 0, [["(?:(?:(?:2(?:[0-26-9]\\d|3[0-8]|4[02-9]|5[0135-9])|7(?:[013-57-9]\\d|2[0-8]))\\d|3(?:(?:[0-3589]\\d|6[1-9]|7[0-35-9])\\d|4(?:[0-578]\\d|90)))\\d\\d|8(?:51(?:0(?:0[03-9]|[12479]\\d|3[2-9]|5[0-8]|6[1-9]|8[0-7])|1(?:[0235689]\\d|1[0-69]|4[0-589]|7[0-47-9])|2(?:0[0-79]|[18][13579]|2[14-9]|3[0-46-9]|[4-6]\\d|7[89]|9[0-4])|3\\d\\d)|(?:6[0-8]|[78]\\d)\\d{3}|9(?:[02-9]\\d{3}|1(?:(?:[0-58]\\d|6[0135-9])\\d|7(?:0[0-24-9]|[1-9]\\d)|9(?:[0-46-9]\\d|5[0-79])))))\\d{3}", [9]], ["4(?:79[01]|83[0-389]|94[0-4])\\d{5}|4(?:[0-36]\\d|4[047-9]|5[0-25-9]|7[02-8]|8[0-24-9]|9[0-37-9])\\d{6}", [9]], ["180(?:0\\d{3}|2)\\d{3}", [7, 10]], ["190[0-26]\\d{6}", [10]], 0, 0, 0, ["163\\d{2,6}", [5, 6, 7, 8, 9]], ["14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}", [9]], ["13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}", [6, 8, 10, 12]]], "0011"], "AW": ["297", "00", "(?:[25-79]\\d\\d|800)\\d{4}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[25-9]"]]]], "AX": ["358", "00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))", "2\\d{4,9}|35\\d{4,5}|(?:60\\d\\d|800)\\d{4,6}|7\\d{5,11}|(?:[14]\\d|3[0-46-9]|50)\\d{4,8}", [5, 6, 7, 8, 9, 10, 11, 12], 0, "0", 0, 0, 0, 0, "18", 0, "00"], "AZ": ["994", "00", "365\\d{6}|(?:[124579]\\d|60|88)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["90"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["1[28]|2|365|46", "1[28]|2|365[45]|46", "1[28]|2|365(?:4|5[02])|46"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[13-9]"], "0$1"]], "0"], "BA": ["387", "00", "6\\d{8}|(?:[35689]\\d|49|70)\\d{6}", [8, 9], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["6[1-3]|[7-9]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2-$3", ["[3-5]|6[56]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3 $4", ["6"], "0$1"]], "0"], "BB": ["1", "011", "(?:246|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "246$1", 0, "246"], "BD": ["880", "00", "[1-469]\\d{9}|8[0-79]\\d{7,8}|[2-79]\\d{8}|[2-9]\\d{7}|[3-9]\\d{6}|[57-9]\\d{5}", [6, 7, 8, 9, 10], [["(\\d{2})(\\d{4,6})", "$1-$2", ["31[5-8]|[459]1"], "0$1"], ["(\\d{3})(\\d{3,7})", "$1-$2", ["3(?:[67]|8[013-9])|4(?:6[168]|7|[89][18])|5(?:6[128]|9)|6(?:[15]|28|4[14])|7[2-589]|8(?:0[014-9]|[12])|9[358]|(?:3[2-5]|4[235]|5[2-578]|6[0389]|76|8[3-7]|9[24])1|(?:44|66)[01346-9]"], "0$1"], ["(\\d{4})(\\d{3,6})", "$1-$2", ["[13-9]|2[23]"], "0$1"], ["(\\d)(\\d{7,8})", "$1-$2", ["2"], "0$1"]], "0"], "BE": ["32", "00", "4\\d{8}|[1-9]\\d{7}", [8, 9], [["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["(?:80|9)0"], "0$1"], ["(\\d)(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[239]|4[23]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[15-8]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["4"], "0$1"]], "0"], "BF": ["226", "00", "[025-7]\\d{7}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[025-7]"]]]], "BG": ["359", "00", "00800\\d{7}|[2-7]\\d{6,7}|[89]\\d{6,8}|2\\d{5}", [6, 7, 8, 9, 12], [["(\\d)(\\d)(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["2"], "0$1"], ["(\\d{3})(\\d{4})", "$1 $2", ["43[1-6]|70[1-9]"], "0$1"], ["(\\d)(\\d{3})(\\d{3,4})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2,3})", "$1 $2 $3", ["[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["(?:70|8)0"], "0$1"], ["(\\d{3})(\\d{3})(\\d{2})", "$1 $2 $3", ["43[1-7]|7"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[48]|9[08]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["9"], "0$1"]], "0"], "BH": ["973", "00", "[136-9]\\d{7}", [8], [["(\\d{4})(\\d{4})", "$1 $2", ["[13679]|8[02-4679]"]]]], "BI": ["257", "00", "(?:[267]\\d|31)\\d{6}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2367]"]]]], "BJ": ["229", "00", "[24-689]\\d{7}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[24-689]"]]]], "BL": ["590", "00", "590\\d{6}|(?:69|80|9\\d)\\d{7}", [9], 0, "0", 0, 0, 0, 0, 0, [["590(?:2[7-9]|3[3-7]|5[12]|87)\\d{4}"], ["69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))\\d{4}"], ["80[0-5]\\d{6}"], 0, 0, 0, 0, 0, ["9(?:(?:39[5-7]|76[018])\\d|475[0-5])\\d{4}"]]], "BM": ["1", "011", "(?:441|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "441$1", 0, "441"], "BN": ["673", "00", "[2-578]\\d{6}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[2-578]"]]]], "BO": ["591", "00(?:1\\d)?", "8001\\d{5}|(?:[2-467]\\d|50)\\d{6}", [8, 9], [["(\\d)(\\d{7})", "$1 $2", ["[235]|4[46]"]], ["(\\d{8})", "$1", ["[67]"]], ["(\\d{3})(\\d{2})(\\d{4})", "$1 $2 $3", ["8"]]], "0", 0, "0(1\\d)?"], "BQ": ["599", "00", "(?:[34]1|7\\d)\\d{5}", [7], 0, 0, 0, 0, 0, 0, "[347]"], "BR": ["55", "00(?:1[245]|2[1-35]|31|4[13]|[56]5|99)", "(?:[1-46-9]\\d\\d|5(?:[0-46-9]\\d|5[0-46-9]))\\d{8}|[1-9]\\d{9}|[3589]\\d{8}|[34]\\d{7}", [8, 9, 10, 11], [["(\\d{4})(\\d{4})", "$1-$2", ["300|4(?:0[02]|37)", "4(?:02|37)0|[34]00"]], ["(\\d{3})(\\d{2,3})(\\d{4})", "$1 $2 $3", ["(?:[358]|90)0"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2-$3", ["(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-57]"], "($1)"], ["(\\d{2})(\\d{5})(\\d{4})", "$1 $2-$3", ["[16][1-9]|[2-57-9]"], "($1)"]], "0", 0, "(?:0|90)(?:(1[245]|2[1-35]|31|4[13]|[56]5|99)(\\d{10,11}))?", "$2"], "BS": ["1", "011", "(?:242|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([3-8]\\d{6})$|1", "242$1", 0, "242"], "BT": ["975", "00", "[17]\\d{7}|[2-8]\\d{6}", [7, 8], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["[2-68]|7[246]"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["1[67]|7"]]]], "BW": ["267", "00", "(?:0800|(?:[37]|800)\\d)\\d{6}|(?:[2-6]\\d|90)\\d{5}", [7, 8, 10], [["(\\d{2})(\\d{5})", "$1 $2", ["90"]], ["(\\d{3})(\\d{4})", "$1 $2", ["[24-6]|3[15-9]"]], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[37]"]], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["0"]], ["(\\d{3})(\\d{4})(\\d{3})", "$1 $2 $3", ["8"]]]], "BY": ["375", "810", "(?:[12]\\d|33|44|902)\\d{7}|8(?:0[0-79]\\d{5,7}|[1-7]\\d{9})|8(?:1[0-489]|[5-79]\\d)\\d{7}|8[1-79]\\d{6,7}|8[0-79]\\d{5}|8\\d{5}", [6, 7, 8, 9, 10, 11], [["(\\d{3})(\\d{3})", "$1 $2", ["800"], "8 $1"], ["(\\d{3})(\\d{2})(\\d{2,4})", "$1 $2 $3", ["800"], "8 $1"], ["(\\d{4})(\\d{2})(\\d{3})", "$1 $2-$3", ["1(?:5[169]|6[3-5]|7[179])|2(?:1[35]|2[34]|3[3-5])", "1(?:5[169]|6(?:3[1-3]|4|5[125])|7(?:1[3-9]|7[0-24-6]|9[2-7]))|2(?:1[35]|2[34]|3[3-5])"], "8 0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2-$3-$4", ["1(?:[56]|7[467])|2[1-3]"], "8 0$1"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2-$3-$4", ["[1-4]"], "8 0$1"], ["(\\d{3})(\\d{3,4})(\\d{4})", "$1 $2 $3", ["[89]"], "8 $1"]], "8", 0, "0|80?", 0, 0, 0, 0, "8~10"], "BZ": ["501", "00", "(?:0800\\d|[2-8])\\d{6}", [7, 11], [["(\\d{3})(\\d{4})", "$1-$2", ["[2-8]"]], ["(\\d)(\\d{3})(\\d{4})(\\d{3})", "$1-$2-$3-$4", ["0"]]]], "CA": ["1", "011", "(?:[2-8]\\d|90)\\d{8}|3\\d{6}", [7, 10], 0, "1", 0, 0, 0, 0, 0, [["(?:2(?:04|[23]6|[48]9|50|63)|3(?:06|43|54|6[578]|82)|4(?:03|1[68]|[26]8|3[178]|50|74)|5(?:06|1[49]|48|79|8[147])|6(?:04|[18]3|39|47|72)|7(?:0[59]|42|53|78|8[02])|8(?:[06]7|19|25|7[39])|90[25])[2-9]\\d{6}", [10]], ["", [10]], ["8(?:00|33|44|55|66|77|88)[2-9]\\d{6}", [10]], ["900[2-9]\\d{6}", [10]], ["52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|(?:5(?:00|2[125-9]|33|44|66|77|88)|622)[2-9]\\d{6}", [10]], 0, ["310\\d{4}", [7]], 0, ["600[2-9]\\d{6}", [10]]]], "CC": ["61", "001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011", "1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}", [6, 7, 8, 9, 10, 12], 0, "0", 0, "([59]\\d{7})$|0", "8$1", 0, 0, [["8(?:51(?:0(?:02|31|60|89)|1(?:18|76)|223)|91(?:0(?:1[0-2]|29)|1(?:[28]2|50|79)|2(?:10|64)|3(?:[06]8|22)|4[29]8|62\\d|70[23]|959))\\d{3}", [9]], ["4(?:79[01]|83[0-389]|94[0-4])\\d{5}|4(?:[0-36]\\d|4[047-9]|5[0-25-9]|7[02-8]|8[0-24-9]|9[0-37-9])\\d{6}", [9]], ["180(?:0\\d{3}|2)\\d{3}", [7, 10]], ["190[0-26]\\d{6}", [10]], 0, 0, 0, 0, ["14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}", [9]], ["13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}", [6, 8, 10, 12]]], "0011"], "CD": ["243", "00", "(?:(?:[189]|5\\d)\\d|2)\\d{7}|[1-68]\\d{6}", [7, 8, 9, 10], [["(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3", ["88"], "0$1"], ["(\\d{2})(\\d{5})", "$1 $2", ["[1-6]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{4})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[89]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["5"], "0$1"]], "0"], "CF": ["236", "00", "(?:[27]\\d{3}|8776)\\d{4}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[278]"]]]], "CG": ["242", "00", "222\\d{6}|(?:0\\d|80)\\d{7}", [9], [["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["8"]], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[02]"]]]], "CH": ["41", "00", "8\\d{11}|[2-9]\\d{8}", [9, 12], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["8[047]|90"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2-79]|81"], "0$1"], ["(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4 $5", ["8"], "0$1"]], "0"], "CI": ["225", "00", "[02]\\d{9}", [10], [["(\\d{2})(\\d{2})(\\d)(\\d{5})", "$1 $2 $3 $4", ["2"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{4})", "$1 $2 $3 $4", ["0"]]]], "CK": ["682", "00", "[2-578]\\d{4}", [5], [["(\\d{2})(\\d{3})", "$1 $2", ["[2-578]"]]]], "CL": ["56", "(?:0|1(?:1[0-69]|2[02-5]|5[13-58]|69|7[0167]|8[018]))0", "12300\\d{6}|6\\d{9,10}|[2-9]\\d{8}", [9, 10, 11], [["(\\d{5})(\\d{4})", "$1 $2", ["219", "2196"], "($1)"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["44"]], ["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["2[1-36]"], "($1)"], ["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["9[2-9]"]], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["3[2-5]|[47]|5[1-3578]|6[13-57]|8(?:0[1-9]|[1-9])"], "($1)"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["60|8"]], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"]], ["(\\d{3})(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3 $4", ["60"]]]], "CM": ["237", "00", "[26]\\d{8}|88\\d{6,7}", [8, 9], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["88"]], ["(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4 $5", ["[26]|88"]]]], "CN": ["86", "00|1(?:[12]\\d|79)\\d\\d00", "(?:(?:1[03-689]|2\\d)\\d\\d|6)\\d{8}|1\\d{10}|[126]\\d{6}(?:\\d(?:\\d{2})?)?|86\\d{5,6}|(?:[3-579]\\d|8[0-57-9])\\d{5,9}", [7, 8, 9, 10, 11, 12], [["(\\d{2})(\\d{5,6})", "$1 $2", ["(?:10|2[0-57-9])[19]|3(?:[157]|35|49|9[1-68])|4(?:1[124-9]|2[179]|6[47-9]|7|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:07|1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3|4[13]|5[1-5]|7[0-79]|9[0-35-9])|(?:4[35]|59|85)[1-9]", "(?:10|2[0-57-9])(?:1[02]|9[56])|8078|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))1", "10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|80781|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))12", "10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|807812|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123", "10(?:1(?:0|23)|9[56])|2[0-57-9](?:1(?:00|23)|9[56])|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:1[124-9]|2[179]|[35][1-9]|6[47-9]|7\\d|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:078|1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|3\\d|4[13]|5[1-5]|7[0-79]|9[0-35-9]))123"], "0$1"], ["(\\d{3})(\\d{5,6})", "$1 $2", ["3(?:[157]|35|49|9[1-68])|4(?:[17]|2[179]|6[47-9]|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]|4[13]|5[1-5])|(?:4[35]|59|85)[1-9]", "(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))[19]", "85[23](?:10|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:10|9[56])", "85[23](?:100|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:100|9[56])"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["(?:4|80)0"]], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["10|2(?:[02-57-9]|1[1-9])", "10|2(?:[02-57-9]|1[1-9])", "10[0-79]|2(?:[02-57-9]|1[1-79])|(?:10|21)8(?:0[1-9]|[1-9])"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["3(?:[3-59]|7[02-68])|4(?:[26-8]|3[3-9]|5[2-9])|5(?:3[03-9]|[468]|7[028]|9[2-46-9])|6|7(?:[0-247]|3[04-9]|5[0-4689]|6[2368])|8(?:[1-358]|9[1-7])|9(?:[013479]|5[1-5])|(?:[34]1|55|79|87)[02-9]"], "0$1", 1], ["(\\d{3})(\\d{7,8})", "$1 $2", ["9"]], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["80"], "0$1", 1], ["(\\d{3})(\\d{4})(\\d{4})", "$1 $2 $3", ["[3-578]"], "0$1", 1], ["(\\d{3})(\\d{4})(\\d{4})", "$1 $2 $3", ["1[3-9]"]], ["(\\d{2})(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3 $4", ["[12]"], "0$1", 1]], "0", 0, "(1(?:[12]\\d|79)\\d\\d)|0", 0, 0, 0, 0, "00"], "CO": ["57", "00(?:4(?:[14]4|56)|[579])", "60\\d{8}|(?:1\\d|[39])\\d{9}", [10, 11], [["(\\d{3})(\\d{7})", "$1 $2", ["6|90"], "($1)"], ["(\\d{3})(\\d{7})", "$1 $2", ["3[0-357]|91"]], ["(\\d)(\\d{3})(\\d{7})", "$1-$2-$3", ["1"], "0$1", 0, "$1 $2 $3"]], "0", 0, "0([3579]|4(?:[14]4|56))?"], "CR": ["506", "00", "(?:8\\d|90)\\d{8}|(?:[24-8]\\d{3}|3005)\\d{4}", [8, 10], [["(\\d{4})(\\d{4})", "$1 $2", ["[2-7]|8[3-9]"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1-$2-$3", ["[89]"]]], 0, 0, "(19(?:0[0-2468]|1[09]|20|66|77|99))"], "CU": ["53", "119", "(?:[2-7]|8\\d\\d)\\d{7}|[2-47]\\d{6}|[34]\\d{5}", [6, 7, 8, 10], [["(\\d{2})(\\d{4,6})", "$1 $2", ["2[1-4]|[34]"], "(0$1)"], ["(\\d)(\\d{6,7})", "$1 $2", ["7"], "(0$1)"], ["(\\d)(\\d{7})", "$1 $2", ["[56]"], "0$1"], ["(\\d{3})(\\d{7})", "$1 $2", ["8"], "0$1"]], "0"], "CV": ["238", "0", "(?:[2-59]\\d\\d|800)\\d{4}", [7], [["(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3", ["[2-589]"]]]], "CW": ["599", "00", "(?:[34]1|60|(?:7|9\\d)\\d)\\d{5}", [7, 8], [["(\\d{3})(\\d{4})", "$1 $2", ["[3467]"]], ["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["9[4-8]"]]], 0, 0, 0, 0, 0, "[69]"], "CX": ["61", "001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011", "1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}", [6, 7, 8, 9, 10, 12], 0, "0", 0, "([59]\\d{7})$|0", "8$1", 0, 0, [["8(?:51(?:0(?:01|30|59|88)|1(?:17|46|75)|2(?:22|35))|91(?:00[6-9]|1(?:[28]1|49|78)|2(?:09|63)|3(?:12|26|75)|4(?:56|97)|64\\d|7(?:0[01]|1[0-2])|958))\\d{3}", [9]], ["4(?:79[01]|83[0-389]|94[0-4])\\d{5}|4(?:[0-36]\\d|4[047-9]|5[0-25-9]|7[02-8]|8[0-24-9]|9[0-37-9])\\d{6}", [9]], ["180(?:0\\d{3}|2)\\d{3}", [7, 10]], ["190[0-26]\\d{6}", [10]], 0, 0, 0, 0, ["14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}", [9]], ["13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}", [6, 8, 10, 12]]], "0011"], "CY": ["357", "00", "(?:[279]\\d|[58]0)\\d{6}", [8], [["(\\d{2})(\\d{6})", "$1 $2", ["[257-9]"]]]], "CZ": ["420", "00", "(?:[2-578]\\d|60)\\d{7}|9\\d{8,11}", [9, 10, 11, 12], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[2-8]|9[015-7]"]], ["(\\d{2})(\\d{3})(\\d{3})(\\d{2})", "$1 $2 $3 $4", ["96"]], ["(\\d{2})(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["9"]], ["(\\d{3})(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["9"]]]], "DE": ["49", "00", "[2579]\\d{5,14}|49(?:[34]0|69|8\\d)\\d\\d?|49(?:37|49|60|7[089]|9\\d)\\d{1,3}|49(?:2[024-9]|3[2-689]|7[1-7])\\d{1,8}|(?:1|[368]\\d|4[0-8])\\d{3,13}|49(?:[015]\\d|2[13]|31|[46][1-8])\\d{1,9}", [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], [["(\\d{2})(\\d{3,13})", "$1 $2", ["3[02]|40|[68]9"], "0$1"], ["(\\d{3})(\\d{3,12})", "$1 $2", ["2(?:0[1-389]|1[124]|2[18]|3[14])|3(?:[35-9][15]|4[015])|906|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1", "2(?:0[1-389]|12[0-8])|3(?:[35-9][15]|4[015])|906|2(?:[13][14]|2[18])|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1"], "0$1"], ["(\\d{4})(\\d{2,11})", "$1 $2", ["[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]", "[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|4[13578]|9[1346])|5(?:0[14]|2[1-3589]|6[1-4]|7[13468]|8[13568])|6(?:2[1-489]|3[124-6]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|6|7[1467]|8[136])|9(?:0[12479]|2[1358]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]|3[68]4[1347]|3(?:47|60)[1356]|3(?:3[46]|46|5[49])[1246]|3[4579]3[1357]"], "0$1"], ["(\\d{3})(\\d{4})", "$1 $2", ["138"], "0$1"], ["(\\d{5})(\\d{2,10})", "$1 $2", ["3"], "0$1"], ["(\\d{3})(\\d{5,11})", "$1 $2", ["181"], "0$1"], ["(\\d{3})(\\d)(\\d{4,10})", "$1 $2 $3", ["1(?:3|80)|9"], "0$1"], ["(\\d{3})(\\d{7,8})", "$1 $2", ["1[67]"], "0$1"], ["(\\d{3})(\\d{7,12})", "$1 $2", ["8"], "0$1"], ["(\\d{5})(\\d{6})", "$1 $2", ["185", "1850", "18500"], "0$1"], ["(\\d{3})(\\d{4})(\\d{4})", "$1 $2 $3", ["7"], "0$1"], ["(\\d{4})(\\d{7})", "$1 $2", ["18[68]"], "0$1"], ["(\\d{4})(\\d{7})", "$1 $2", ["15[1279]"], "0$1"], ["(\\d{5})(\\d{6})", "$1 $2", ["15[03568]", "15(?:[0568]|31)"], "0$1"], ["(\\d{3})(\\d{8})", "$1 $2", ["18"], "0$1"], ["(\\d{3})(\\d{2})(\\d{7,8})", "$1 $2 $3", ["1(?:6[023]|7)"], "0$1"], ["(\\d{4})(\\d{2})(\\d{7})", "$1 $2 $3", ["15[279]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{8})", "$1 $2 $3", ["15"], "0$1"]], "0"], "DJ": ["253", "00", "(?:2\\d|77)\\d{6}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[27]"]]]], "DK": ["45", "00", "[2-9]\\d{7}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2-9]"]]]], "DM": ["1", "011", "(?:[58]\\d\\d|767|900)\\d{7}", [10], 0, "1", 0, "([2-7]\\d{6})$|1", "767$1", 0, "767"], "DO": ["1", "011", "(?:[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, 0, 0, 0, "8001|8[024]9"], "DZ": ["213", "00", "(?:[1-4]|[5-79]\\d|80)\\d{7}", [8, 9], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[1-4]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["9"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[5-8]"], "0$1"]], "0"], "EC": ["593", "00", "1\\d{9,10}|(?:[2-7]|9\\d)\\d{7}", [8, 9, 10, 11], [["(\\d)(\\d{3})(\\d{4})", "$1 $2-$3", ["[2-7]"], "(0$1)", 0, "$1-$2-$3"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["9"], "0$1"], ["(\\d{4})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["1"]]], "0"], "EE": ["372", "00", "8\\d{9}|[4578]\\d{7}|(?:[3-8]\\d|90)\\d{5}", [7, 8, 10], [["(\\d{3})(\\d{4})", "$1 $2", ["[369]|4[3-8]|5(?:[0-2]|5[0-478]|6[45])|7[1-9]|88", "[369]|4[3-8]|5(?:[02]|1(?:[0-8]|95)|5[0-478]|6(?:4[0-4]|5[1-589]))|7[1-9]|88"]], ["(\\d{4})(\\d{3,4})", "$1 $2", ["[45]|8(?:00|[1-49])", "[45]|8(?:00[1-9]|[1-49])"]], ["(\\d{2})(\\d{2})(\\d{4})", "$1 $2 $3", ["7"]], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["8"]]]], "EG": ["20", "00", "[189]\\d{8,9}|[24-6]\\d{8}|[135]\\d{7}", [8, 9, 10], [["(\\d)(\\d{7,8})", "$1 $2", ["[23]"], "0$1"], ["(\\d{2})(\\d{6,7})", "$1 $2", ["1[35]|[4-6]|8[2468]|9[235-7]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["[89]"], "0$1"], ["(\\d{2})(\\d{8})", "$1 $2", ["1"], "0$1"]], "0"], "EH": ["212", "00", "[5-8]\\d{8}", [9], 0, "0", 0, 0, 0, 0, "528[89]"], "ER": ["291", "00", "[178]\\d{6}", [7], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["[178]"], "0$1"]], "0"], "ES": ["34", "00", "[5-9]\\d{8}", [9], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[89]00"]], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[5-9]"]]]], "ET": ["251", "00", "(?:11|[2-579]\\d)\\d{7}", [9], [["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[1-579]"], "0$1"]], "0"], "FI": ["358", "00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))", "[1-35689]\\d{4}|7\\d{10,11}|(?:[124-7]\\d|3[0-46-9])\\d{8}|[1-9]\\d{5,8}", [5, 6, 7, 8, 9, 10, 11, 12], [["(\\d{5})", "$1", ["20[2-59]"], "0$1"], ["(\\d{3})(\\d{3,7})", "$1 $2", ["(?:[1-3]0|[68])0|70[07-9]"], "0$1"], ["(\\d{2})(\\d{4,8})", "$1 $2", ["[14]|2[09]|50|7[135]"], "0$1"], ["(\\d{2})(\\d{6,10})", "$1 $2", ["7"], "0$1"], ["(\\d)(\\d{4,9})", "$1 $2", ["(?:1[49]|[2568])[1-8]|3(?:0[1-9]|[1-9])|9"], "0$1"]], "0", 0, 0, 0, 0, "1[03-79]|[2-9]", 0, "00"], "FJ": ["679", "0(?:0|52)", "45\\d{5}|(?:0800\\d|[235-9])\\d{6}", [7, 11], [["(\\d{3})(\\d{4})", "$1 $2", ["[235-9]|45"]], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["0"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "FK": ["500", "00", "[2-7]\\d{4}", [5]], "FM": ["691", "00", "(?:[39]\\d\\d|820)\\d{4}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[389]"]]]], "FO": ["298", "00", "[2-9]\\d{5}", [6], [["(\\d{6})", "$1", ["[2-9]"]]], 0, 0, "(10(?:01|[12]0|88))"], "FR": ["33", "00", "[1-9]\\d{8}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"], "0 $1"], ["(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4 $5", ["[1-79]"], "0$1"]], "0"], "GA": ["241", "00", "(?:[067]\\d|11)\\d{6}|[2-7]\\d{6}", [7, 8], [["(\\d)(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2-7]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["0"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["11|[67]"], "0$1"]], 0, 0, "0(11\\d{6}|60\\d{6}|61\\d{6}|6[256]\\d{6}|7[467]\\d{6})", "$1"], "GB": ["44", "00", "[1-357-9]\\d{9}|[18]\\d{8}|8\\d{6}", [7, 9, 10], [["(\\d{3})(\\d{4})", "$1 $2", ["800", "8001", "80011", "800111", "8001111"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3", ["845", "8454", "84546", "845464"], "0$1"], ["(\\d{3})(\\d{6})", "$1 $2", ["800"], "0$1"], ["(\\d{5})(\\d{4,5})", "$1 $2", ["1(?:38|5[23]|69|76|94)", "1(?:(?:38|69)7|5(?:24|39)|768|946)", "1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"], "0$1"], ["(\\d{4})(\\d{5,6})", "$1 $2", ["1(?:[2-69][02-9]|[78])"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["[25]|7(?:0|6[02-9])", "[25]|7(?:0|6(?:[03-9]|2[356]))"], "0$1"], ["(\\d{4})(\\d{6})", "$1 $2", ["7"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["[1389]"], "0$1"]], "0", 0, 0, 0, 0, 0, [["(?:1(?:1(?:3(?:[0-58]\\d\\d|73[0-35])|4(?:(?:[0-5]\\d|70)\\d|69[7-9])|(?:(?:5[0-26-9]|[78][0-49])\\d|6(?:[0-4]\\d|50))\\d)|(?:2(?:(?:0[024-9]|2[3-9]|3[3-79]|4[1-689]|[58][02-9]|6[0-47-9]|7[013-9]|9\\d)\\d|1(?:[0-7]\\d|8[0-3]))|(?:3(?:0\\d|1[0-8]|[25][02-9]|3[02-579]|[468][0-46-9]|7[1-35-79]|9[2-578])|4(?:0[03-9]|[137]\\d|[28][02-57-9]|4[02-69]|5[0-8]|[69][0-79])|5(?:0[1-35-9]|[16]\\d|2[024-9]|3[015689]|4[02-9]|5[03-9]|7[0-35-9]|8[0-468]|9[0-57-9])|6(?:0[034689]|1\\d|2[0-35689]|[38][013-9]|4[1-467]|5[0-69]|6[13-9]|7[0-8]|9[0-24578])|7(?:0[0246-9]|2\\d|3[0236-8]|4[03-9]|5[0-46-9]|6[013-9]|7[0-35-9]|8[024-9]|9[02-9])|8(?:0[35-9]|2[1-57-9]|3[02-578]|4[0-578]|5[124-9]|6[2-69]|7\\d|8[02-9]|9[02569])|9(?:0[02-589]|[18]\\d|2[02-689]|3[1-57-9]|4[2-9]|5[0-579]|6[2-47-9]|7[0-24578]|9[2-57]))\\d)\\d)|2(?:0[013478]|3[0189]|4[017]|8[0-46-9]|9[0-2])\\d{3})\\d{4}|1(?:2(?:0(?:46[1-4]|87[2-9])|545[1-79]|76(?:2\\d|3[1-8]|6[1-6])|9(?:7(?:2[0-4]|3[2-5])|8(?:2[2-8]|7[0-47-9]|8[3-5])))|3(?:6(?:38[2-5]|47[23])|8(?:47[04-9]|64[0157-9]))|4(?:044[1-7]|20(?:2[23]|8\\d)|6(?:0(?:30|5[2-57]|6[1-8]|7[2-8])|140)|8(?:052|87[1-3]))|5(?:2(?:4(?:3[2-79]|6\\d)|76\\d)|6(?:26[06-9]|686))|6(?:06(?:4\\d|7[4-79])|295[5-7]|35[34]\\d|47(?:24|61)|59(?:5[08]|6[67]|74)|9(?:55[0-4]|77[23]))|7(?:26(?:6[13-9]|7[0-7])|(?:442|688)\\d|50(?:2[0-3]|[3-68]2|76))|8(?:27[56]\\d|37(?:5[2-5]|8[239])|843[2-58])|9(?:0(?:0(?:6[1-8]|85)|52\\d)|3583|4(?:66[1-8]|9(?:2[01]|81))|63(?:23|3[1-4])|9561))\\d{3}", [9, 10]], ["7(?:457[0-57-9]|700[01]|911[028])\\d{5}|7(?:[1-3]\\d\\d|4(?:[0-46-9]\\d|5[0-689])|5(?:0[0-8]|[13-9]\\d|2[0-35-9])|7(?:0[1-9]|[1-7]\\d|8[02-9]|9[0-689])|8(?:[014-9]\\d|[23][0-8])|9(?:[024-9]\\d|1[02-9]|3[0-689]))\\d{6}", [10]], ["80[08]\\d{7}|800\\d{6}|8001111"], ["(?:8(?:4[2-5]|7[0-3])|9(?:[01]\\d|8[2-49]))\\d{7}|845464\\d", [7, 10]], ["70\\d{8}", [10]], 0, ["(?:3[0347]|55)\\d{8}", [10]], ["76(?:464|652)\\d{5}|76(?:0[0-28]|2[356]|34|4[01347]|5[49]|6[0-369]|77|8[14]|9[139])\\d{6}", [10]], ["56\\d{8}", [10]]], 0, " x"], "GD": ["1", "011", "(?:473|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "473$1", 0, "473"], "GE": ["995", "00", "(?:[3-57]\\d\\d|800)\\d{6}", [9], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["70"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["32"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[57]"]], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[348]"], "0$1"]], "0"], "GF": ["594", "00", "[56]94\\d{6}|(?:80|9\\d)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[56]|9[47]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[89]"], "0$1"]], "0"], "GG": ["44", "00", "(?:1481|[357-9]\\d{3})\\d{6}|8\\d{6}(?:\\d{2})?", [7, 9, 10], 0, "0", 0, "([25-9]\\d{5})$|0", "1481$1", 0, 0, [["1481[25-9]\\d{5}", [10]], ["7(?:(?:781|839)\\d|911[17])\\d{5}", [10]], ["80[08]\\d{7}|800\\d{6}|8001111"], ["(?:8(?:4[2-5]|7[0-3])|9(?:[01]\\d|8[0-3]))\\d{7}|845464\\d", [7, 10]], ["70\\d{8}", [10]], 0, ["(?:3[0347]|55)\\d{8}", [10]], ["76(?:464|652)\\d{5}|76(?:0[0-28]|2[356]|34|4[01347]|5[49]|6[0-369]|77|8[14]|9[139])\\d{6}", [10]], ["56\\d{8}", [10]]]], "GH": ["233", "00", "(?:[235]\\d{3}|800)\\d{5}", [8, 9], [["(\\d{3})(\\d{5})", "$1 $2", ["8"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[235]"], "0$1"]], "0"], "GI": ["350", "00", "(?:[25]\\d|60)\\d{6}", [8], [["(\\d{3})(\\d{5})", "$1 $2", ["2"]]]], "GL": ["299", "00", "(?:19|[2-689]\\d|70)\\d{4}", [6], [["(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3", ["19|[2-9]"]]]], "GM": ["220", "00", "[2-9]\\d{6}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[2-9]"]]]], "GN": ["224", "00", "722\\d{6}|(?:3|6\\d)\\d{7}", [8, 9], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["3"]], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[67]"]]]], "GP": ["590", "00", "590\\d{6}|(?:69|80|9\\d)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[569]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"], "0$1"]], "0", 0, 0, 0, 0, 0, [["590(?:0[1-68]|[14][0-24-9]|2[0-68]|3[1-9]|5[3-579]|[68][0-689]|7[08]|9\\d)\\d{4}"], ["69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))\\d{4}"], ["80[0-5]\\d{6}"], 0, 0, 0, 0, 0, ["9(?:(?:39[5-7]|76[018])\\d|475[0-5])\\d{4}"]]], "GQ": ["240", "00", "222\\d{6}|(?:3\\d|55|[89]0)\\d{7}", [9], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[235]"]], ["(\\d{3})(\\d{6})", "$1 $2", ["[89]"]]]], "GR": ["30", "00", "5005000\\d{3}|8\\d{9,11}|(?:[269]\\d|70)\\d{8}", [10, 11, 12], [["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["21|7"]], ["(\\d{4})(\\d{6})", "$1 $2", ["2(?:2|3[2-57-9]|4[2-469]|5[2-59]|6[2-9]|7[2-69]|8[2-49])|5"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["[2689]"]], ["(\\d{3})(\\d{3,4})(\\d{5})", "$1 $2 $3", ["8"]]]], "GT": ["502", "00", "80\\d{6}|(?:1\\d{3}|[2-7])\\d{7}", [8, 11], [["(\\d{4})(\\d{4})", "$1 $2", ["[2-8]"]], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"]]]], "GU": ["1", "011", "(?:[58]\\d\\d|671|900)\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "671$1", 0, "671"], "GW": ["245", "00", "[49]\\d{8}|4\\d{6}", [7, 9], [["(\\d{3})(\\d{4})", "$1 $2", ["40"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[49]"]]]], "GY": ["592", "001", "(?:[2-8]\\d{3}|9008)\\d{3}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[2-9]"]]]], "HK": ["852", "00(?:30|5[09]|[126-9]?)", "8[0-46-9]\\d{6,7}|9\\d{4,7}|(?:[2-7]|9\\d{3})\\d{7}", [5, 6, 7, 8, 9, 11], [["(\\d{3})(\\d{2,5})", "$1 $2", ["900", "9003"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[2-7]|8[1-4]|9(?:0[1-9]|[1-8])"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["8"]], ["(\\d{3})(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["9"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "HN": ["504", "00", "8\\d{10}|[237-9]\\d{7}", [8, 11], [["(\\d{4})(\\d{4})", "$1-$2", ["[237-9]"]]]], "HR": ["385", "00", "(?:[24-69]\\d|3[0-79])\\d{7}|80\\d{5,7}|[1-79]\\d{7}|6\\d{5,6}", [6, 7, 8, 9], [["(\\d{2})(\\d{2})(\\d{2,3})", "$1 $2 $3", ["6[01]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2,3})", "$1 $2 $3", ["8"], "0$1"], ["(\\d)(\\d{4})(\\d{3})", "$1 $2 $3", ["1"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["6|7[245]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["9"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[2-57]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["8"], "0$1"]], "0"], "HT": ["509", "00", "(?:[2-489]\\d|55)\\d{6}", [8], [["(\\d{2})(\\d{2})(\\d{4})", "$1 $2 $3", ["[2-589]"]]]], "HU": ["36", "00", "[235-7]\\d{8}|[1-9]\\d{7}", [8, 9], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["1"], "(06 $1)"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6|8[2-57-9]|9[2-69]"], "(06 $1)"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[2-9]"], "06 $1"]], "06"], "ID": ["62", "00[89]", "00[1-9]\\d{9,14}|(?:[1-36]|8\\d{5})\\d{6}|00\\d{9}|[1-9]\\d{8,10}|[2-9]\\d{7}", [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["15"]], ["(\\d{2})(\\d{5,9})", "$1 $2", ["2[124]|[36]1"], "(0$1)"], ["(\\d{3})(\\d{5,7})", "$1 $2", ["800"], "0$1"], ["(\\d{3})(\\d{5,8})", "$1 $2", ["[2-79]"], "(0$1)"], ["(\\d{3})(\\d{3,4})(\\d{3})", "$1-$2-$3", ["8[1-35-9]"], "0$1"], ["(\\d{3})(\\d{6,8})", "$1 $2", ["1"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["804"], "0$1"], ["(\\d{3})(\\d)(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["80"], "0$1"], ["(\\d{3})(\\d{4})(\\d{4,5})", "$1-$2-$3", ["8"], "0$1"]], "0"], "IE": ["353", "00", "(?:1\\d|[2569])\\d{6,8}|4\\d{6,9}|7\\d{8}|8\\d{8,9}", [7, 8, 9, 10], [["(\\d{2})(\\d{5})", "$1 $2", ["2[24-9]|47|58|6[237-9]|9[35-9]"], "(0$1)"], ["(\\d{3})(\\d{5})", "$1 $2", ["[45]0"], "(0$1)"], ["(\\d)(\\d{3,4})(\\d{4})", "$1 $2 $3", ["1"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[2569]|4[1-69]|7[14]"], "(0$1)"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["70"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["81"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[78]"], "0$1"], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["1"]], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["4"], "(0$1)"], ["(\\d{2})(\\d)(\\d{3})(\\d{4})", "$1 $2 $3 $4", ["8"], "0$1"]], "0"], "IL": ["972", "0(?:0|1[2-9])", "1\\d{6}(?:\\d{3,5})?|[57]\\d{8}|[1-489]\\d{7}", [7, 8, 9, 10, 11, 12], [["(\\d{4})(\\d{3})", "$1-$2", ["125"]], ["(\\d{4})(\\d{2})(\\d{2})", "$1-$2-$3", ["121"]], ["(\\d)(\\d{3})(\\d{4})", "$1-$2-$3", ["[2-489]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1-$2-$3", ["[57]"], "0$1"], ["(\\d{4})(\\d{3})(\\d{3})", "$1-$2-$3", ["12"]], ["(\\d{4})(\\d{6})", "$1-$2", ["159"]], ["(\\d)(\\d{3})(\\d{3})(\\d{3})", "$1-$2-$3-$4", ["1[7-9]"]], ["(\\d{3})(\\d{1,2})(\\d{3})(\\d{4})", "$1-$2 $3-$4", ["15"]]], "0"], "IM": ["44", "00", "1624\\d{6}|(?:[3578]\\d|90)\\d{8}", [10], 0, "0", 0, "([25-8]\\d{5})$|0", "1624$1", 0, "74576|(?:16|7[56])24"], "IN": ["91", "00", "(?:000800|[2-9]\\d\\d)\\d{7}|1\\d{7,12}", [8, 9, 10, 11, 12, 13], [["(\\d{8})", "$1", ["5(?:0|2[23]|3[03]|[67]1|88)", "5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|888)", "5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|8888)"], 0, 1], ["(\\d{4})(\\d{4,5})", "$1 $2", ["180", "1800"], 0, 1], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["140"], 0, 1], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["11|2[02]|33|4[04]|79[1-7]|80[2-46]", "11|2[02]|33|4[04]|79(?:[1-6]|7[19])|80(?:[2-4]|6[0-589])", "11|2[02]|33|4[04]|79(?:[124-6]|3(?:[02-9]|1[0-24-9])|7(?:1|9[1-6]))|80(?:[2-4]|6[0-589])"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["1(?:2[0-249]|3[0-25]|4[145]|[68]|7[1257])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|5[12]|[78]1)|6(?:12|[2-4]1|5[17]|6[13]|80)|7(?:12|3[134]|4[47]|61|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91)|(?:43|59|75)[15]|(?:1[59]|29|67|72)[14]", "1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|674|7(?:(?:2[14]|3[34]|5[15])[2-6]|61[346]|88[0-8])|8(?:70[2-6]|84[235-7]|91[3-7])|(?:1(?:29|60|8[06])|261|552|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))[2-7]", "1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12(?:[2-6]|7[0-8])|74[2-7])|7(?:(?:2[14]|5[15])[2-6]|3171|61[346]|88(?:[2-7]|82))|8(?:70[2-6]|84(?:[2356]|7[19])|91(?:[3-6]|7[19]))|73[134][2-6]|(?:74[47]|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[2-6]|7[19])|(?:1(?:29|60|8[06])|261|552|6(?:[2-4]1|5[17]|6[13]|7(?:1|4[0189])|80)|7(?:12|88[01]))[2-7]"], "0$1", 1], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2[2457-9]|3[2-5]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1[013-9]|28|3[129]|4[1-35689]|5[29]|6[02-5]|70)|807", "1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2(?:[2457]|84|95)|3(?:[2-4]|55)|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1(?:[013-8]|9[6-9])|28[6-8]|3(?:17|2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4|5[0-367])|70[13-7])|807[19]", "1(?:[2-479]|5(?:[0236-9]|5[013-9]))|[2-5]|6(?:2(?:84|95)|355|83)|73179|807(?:1|9[1-3])|(?:1552|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])\\d|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]))[2-7]"], "0$1", 1], ["(\\d{5})(\\d{5})", "$1 $2", ["[6-9]"], "0$1", 1], ["(\\d{4})(\\d{2,4})(\\d{4})", "$1 $2 $3", ["1(?:6|8[06])", "1(?:6|8[06]0)"], 0, 1], ["(\\d{4})(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["18"], 0, 1]], "0"], "IO": ["246", "00", "3\\d{6}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["3"]]]], "IQ": ["964", "00", "(?:1|7\\d\\d)\\d{7}|[2-6]\\d{7,8}", [8, 9, 10], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["1"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[2-6]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["7"], "0$1"]], "0"], "IR": ["98", "00", "[1-9]\\d{9}|(?:[1-8]\\d\\d|9)\\d{3,4}", [4, 5, 6, 7, 10], [["(\\d{4,5})", "$1", ["96"], "0$1"], ["(\\d{2})(\\d{4,5})", "$1 $2", ["(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])[12689]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["9"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["[1-8]"], "0$1"]], "0"], "IS": ["354", "00|1(?:0(?:01|[12]0)|100)", "(?:38\\d|[4-9])\\d{6}", [7, 9], [["(\\d{3})(\\d{4})", "$1 $2", ["[4-9]"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["3"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "IT": ["39", "00", "0\\d{5,10}|1\\d{8,10}|3(?:[0-8]\\d{7,10}|9\\d{7,8})|(?:43|55|70)\\d{8}|8\\d{5}(?:\\d{2,4})?", [6, 7, 8, 9, 10, 11, 12], [["(\\d{2})(\\d{4,6})", "$1 $2", ["0[26]"]], ["(\\d{3})(\\d{3,6})", "$1 $2", ["0[13-57-9][0159]|8(?:03|4[17]|9[2-5])", "0[13-57-9][0159]|8(?:03|4[17]|9(?:2|3[04]|[45][0-4]))"]], ["(\\d{4})(\\d{2,6})", "$1 $2", ["0(?:[13-579][2-46-8]|8[236-8])"]], ["(\\d{4})(\\d{4})", "$1 $2", ["894"]], ["(\\d{2})(\\d{3,4})(\\d{4})", "$1 $2 $3", ["0[26]|5"]], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["1(?:44|[679])|[378]|43"]], ["(\\d{3})(\\d{3,4})(\\d{4})", "$1 $2 $3", ["0[13-57-9][0159]|14"]], ["(\\d{2})(\\d{4})(\\d{5})", "$1 $2 $3", ["0[26]"]], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["0"]], ["(\\d{3})(\\d{4})(\\d{4,5})", "$1 $2 $3", ["3"]]], 0, 0, 0, 0, 0, 0, [["0669[0-79]\\d{1,6}|0(?:1(?:[0159]\\d|[27][1-5]|31|4[1-4]|6[1356]|8[2-57])|2\\d\\d|3(?:[0159]\\d|2[1-4]|3[12]|[48][1-6]|6[2-59]|7[1-7])|4(?:[0159]\\d|[23][1-9]|4[245]|6[1-5]|7[1-4]|81)|5(?:[0159]\\d|2[1-5]|3[2-6]|4[1-79]|6[4-6]|7[1-578]|8[3-8])|6(?:[0-57-9]\\d|6[0-8])|7(?:[0159]\\d|2[12]|3[1-7]|4[2-46]|6[13569]|7[13-6]|8[1-59])|8(?:[0159]\\d|2[3-578]|3[1-356]|[6-8][1-5])|9(?:[0159]\\d|[238][1-5]|4[12]|6[1-8]|7[1-6]))\\d{2,7}", [6, 7, 8, 9, 10, 11]], ["3[2-9]\\d{7,8}|(?:31|43)\\d{8}", [9, 10]], ["80(?:0\\d{3}|3)\\d{3}", [6, 9]], ["(?:0878\\d{3}|89(?:2\\d|3[04]|4(?:[0-4]|[5-9]\\d\\d)|5[0-4]))\\d\\d|(?:1(?:44|6[346])|89(?:38|5[5-9]|9))\\d{6}", [6, 8, 9, 10]], ["1(?:78\\d|99)\\d{6}", [9, 10]], ["3[2-8]\\d{9,10}", [11, 12]], 0, 0, ["55\\d{8}", [10]], ["84(?:[08]\\d{3}|[17])\\d{3}", [6, 9]]]], "JE": ["44", "00", "1534\\d{6}|(?:[3578]\\d|90)\\d{8}", [10], 0, "0", 0, "([0-24-8]\\d{5})$|0", "1534$1", 0, 0, [["1534[0-24-8]\\d{5}"], ["7(?:(?:(?:50|82)9|937)\\d|7(?:00[378]|97\\d))\\d{5}"], ["80(?:07(?:35|81)|8901)\\d{4}"], ["(?:8(?:4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))|90(?:066[59]|1810|71(?:07|55)))\\d{4}"], ["701511\\d{4}"], 0, ["(?:3(?:0(?:07(?:35|81)|8901)|3\\d{4}|4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))|55\\d{4})\\d{4}"], ["76(?:464|652)\\d{5}|76(?:0[0-28]|2[356]|34|4[01347]|5[49]|6[0-369]|77|8[14]|9[139])\\d{6}"], ["56\\d{8}"]]], "JM": ["1", "011", "(?:[58]\\d\\d|658|900)\\d{7}", [10], 0, "1", 0, 0, 0, 0, "658|876"], "JO": ["962", "00", "(?:(?:[2689]|7\\d)\\d|32|53)\\d{6}", [8, 9], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["[2356]|87"], "(0$1)"], ["(\\d{3})(\\d{5,6})", "$1 $2", ["[89]"], "0$1"], ["(\\d{2})(\\d{7})", "$1 $2", ["70"], "0$1"], ["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["7"], "0$1"]], "0"], "JP": ["81", "010", "00[1-9]\\d{6,14}|[257-9]\\d{9}|(?:00|[1-9]\\d\\d)\\d{6}", [8, 9, 10, 11, 12, 13, 14, 15, 16, 17], [["(\\d{3})(\\d{3})(\\d{3})", "$1-$2-$3", ["(?:12|57|99)0"], "0$1"], ["(\\d{4})(\\d)(\\d{4})", "$1-$2-$3", ["1(?:26|3[79]|4[56]|5[4-68]|6[3-5])|499|5(?:76|97)|746|8(?:3[89]|47|51)|9(?:80|9[16])", "1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:76|97)9|7468|8(?:3(?:8[7-9]|96)|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]", "1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:769|979[2-69])|7468|8(?:3(?:8[7-9]|96[2457-9])|477|51[2-9])|9(?:802|9(?:1[23]|69))|1(?:45|58)[67]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1-$2-$3", ["60"], "0$1"], ["(\\d)(\\d{4})(\\d{4})", "$1-$2-$3", ["[36]|4(?:2[09]|7[01])", "[36]|4(?:2(?:0|9[02-69])|7(?:0[019]|1))"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1-$2-$3", ["1(?:1|5[45]|77|88|9[69])|2(?:2[1-37]|3[0-269]|4[59]|5|6[24]|7[1-358]|8[1369]|9[0-38])|4(?:[28][1-9]|3[0-57]|[45]|6[248]|7[2-579]|9[29])|5(?:2|3[0459]|4[0-369]|5[29]|8[02389]|9[0-389])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9[2-6])|8(?:2[124589]|3[26-9]|49|51|6|7[0-468]|8[68]|9[019])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9[1-489])", "1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2(?:[127]|3[014-9])|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9[19])|62|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|8[1-9]|9[29])|5(?:2|3(?:[045]|9[0-8])|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0-2469])|3(?:[29]|60)|49|51|6(?:[0-24]|36|5[0-3589]|7[23]|9[01459])|7[0-468]|8[68])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9(?:[1289]|3[34]|4[0178]))|(?:264|837)[016-9]|2(?:57|93)[015-9]|(?:25[0468]|422|838)[01]|(?:47[59]|59[89]|8(?:6[68]|9))[019]", "1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2[127]|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9(?:17|99))|6(?:2|4[016-9])|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|9[29])|5(?:2|3(?:[045]|9(?:[0-58]|6[4-9]|7[0-35689]))|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0169])|3(?:[29]|60|7(?:[017-9]|6[6-8]))|49|51|6(?:[0-24]|36[2-57-9]|5(?:[0-389]|5[23])|6(?:[01]|9[178])|7(?:2[2-468]|3[78])|9[0145])|7[0-468]|8[68])|9(?:4[15]|5[138]|7[156]|8[189]|9(?:[1289]|3(?:31|4[357])|4[0178]))|(?:8294|96)[1-3]|2(?:57|93)[015-9]|(?:223|8699)[014-9]|(?:25[0468]|422|838)[01]|(?:48|8292|9[23])[1-9]|(?:47[59]|59[89]|8(?:68|9))[019]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{4})", "$1-$2-$3", ["[14]|[289][2-9]|5[3-9]|7[2-4679]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1-$2-$3", ["800"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4})", "$1-$2-$3", ["[257-9]"], "0$1"]], "0", 0, "(000[259]\\d{6})$|(?:(?:003768)0?)|0", "$1"], "KE": ["254", "000", "(?:[17]\\d\\d|900)\\d{6}|(?:2|80)0\\d{6,7}|[4-6]\\d{6,8}", [7, 8, 9, 10], [["(\\d{2})(\\d{5,7})", "$1 $2", ["[24-6]"], "0$1"], ["(\\d{3})(\\d{6})", "$1 $2", ["[17]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[89]"], "0$1"]], "0"], "KG": ["996", "00", "8\\d{9}|[235-9]\\d{8}", [9, 10], [["(\\d{4})(\\d{5})", "$1 $2", ["3(?:1[346]|[24-79])"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[235-79]|88"], "0$1"], ["(\\d{3})(\\d{3})(\\d)(\\d{2,3})", "$1 $2 $3 $4", ["8"], "0$1"]], "0"], "KH": ["855", "00[14-9]", "1\\d{9}|[1-9]\\d{7,8}", [8, 9, 10], [["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[1-9]"], "0$1"], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["1"]]], "0"], "KI": ["686", "00", "(?:[37]\\d|6[0-79])\\d{6}|(?:[2-48]\\d|50)\\d{3}", [5, 8], 0, "0"], "KM": ["269", "00", "[3478]\\d{6}", [7], [["(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3", ["[3478]"]]]], "KN": ["1", "011", "(?:[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-7]\\d{6})$|1", "869$1", 0, "869"], "KP": ["850", "00|99", "85\\d{6}|(?:19\\d|[2-7])\\d{7}", [8, 10], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["8"], "0$1"], ["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["[2-7]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"], "0$1"]], "0"], "KR": ["82", "00(?:[125689]|3(?:[46]5|91)|7(?:00|27|3|55|6[126]))", "00[1-9]\\d{8,11}|(?:[12]|5\\d{3})\\d{7}|[13-6]\\d{9}|(?:[1-6]\\d|80)\\d{7}|[3-6]\\d{4,5}|(?:00|7)0\\d{8}", [5, 6, 8, 9, 10, 11, 12, 13, 14], [["(\\d{2})(\\d{3,4})", "$1-$2", ["(?:3[1-3]|[46][1-4]|5[1-5])1"], "0$1"], ["(\\d{4})(\\d{4})", "$1-$2", ["1"]], ["(\\d)(\\d{3,4})(\\d{4})", "$1-$2-$3", ["2"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1-$2-$3", ["[36]0|8"], "0$1"], ["(\\d{2})(\\d{3,4})(\\d{4})", "$1-$2-$3", ["[1346]|5[1-5]"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4})", "$1-$2-$3", ["[57]"], "0$1"], ["(\\d{2})(\\d{5})(\\d{4})", "$1-$2-$3", ["5"], "0$1"]], "0", 0, "0(8(?:[1-46-8]|5\\d\\d))?"], "KW": ["965", "00", "18\\d{5}|(?:[2569]\\d|41)\\d{6}", [7, 8], [["(\\d{4})(\\d{3,4})", "$1 $2", ["[169]|2(?:[235]|4[1-35-9])|52"]], ["(\\d{3})(\\d{5})", "$1 $2", ["[245]"]]]], "KY": ["1", "011", "(?:345|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "345$1", 0, "345"], "KZ": ["7", "810", "(?:33622|8\\d{8})\\d{5}|[78]\\d{9}", [10, 14], 0, "8", 0, 0, 0, 0, "33|7", 0, "8~10"], "LA": ["856", "00", "[23]\\d{9}|3\\d{8}|(?:[235-8]\\d|41)\\d{6}", [8, 9, 10], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["2[13]|3[14]|[4-8]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3 $4", ["30[0135-9]"], "0$1"], ["(\\d{2})(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["[23]"], "0$1"]], "0"], "LB": ["961", "00", "[27-9]\\d{7}|[13-9]\\d{6}", [7, 8], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["[13-69]|7(?:[2-57]|62|8[0-7]|9[04-9])|8[02-9]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[27-9]"]]], "0"], "LC": ["1", "011", "(?:[58]\\d\\d|758|900)\\d{7}", [10], 0, "1", 0, "([2-8]\\d{6})$|1", "758$1", 0, "758"], "LI": ["423", "00", "[68]\\d{8}|(?:[2378]\\d|90)\\d{5}", [7, 9], [["(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3", ["[2379]|8(?:0[09]|7)", "[2379]|8(?:0(?:02|9)|7)"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["8"]], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["69"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["6"]]], "0", 0, "(1001)|0"], "LK": ["94", "00", "[1-9]\\d{8}", [9], [["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["7"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[1-689]"], "0$1"]], "0"], "LR": ["231", "00", "(?:[245]\\d|33|77|88)\\d{7}|(?:2\\d|[4-6])\\d{6}", [7, 8, 9], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["4[67]|[56]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[2-578]"], "0$1"]], "0"], "LS": ["266", "00", "(?:[256]\\d\\d|800)\\d{5}", [8], [["(\\d{4})(\\d{4})", "$1 $2", ["[2568]"]]]], "LT": ["370", "00", "(?:[3469]\\d|52|[78]0)\\d{6}", [8], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["52[0-7]"], "(0-$1)", 1], ["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["[7-9]"], "0 $1", 1], ["(\\d{2})(\\d{6})", "$1 $2", ["37|4(?:[15]|6[1-8])"], "(0-$1)", 1], ["(\\d{3})(\\d{5})", "$1 $2", ["[3-6]"], "(0-$1)", 1]], "0", 0, "[08]"], "LU": ["352", "00", "35[013-9]\\d{4,8}|6\\d{8}|35\\d{2,4}|(?:[2457-9]\\d|3[0-46-9])\\d{2,9}", [4, 5, 6, 7, 8, 9, 10, 11], [["(\\d{2})(\\d{3})", "$1 $2", ["2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"]], ["(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3", ["2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"]], ["(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3", ["20[2-689]"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})", "$1 $2 $3 $4", ["2(?:[0367]|4[3-8])"]], ["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["80[01]|90[015]"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3 $4", ["20"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["6"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})", "$1 $2 $3 $4 $5", ["2(?:[0367]|4[3-8])"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{1,5})", "$1 $2 $3 $4", ["[3-57]|8[13-9]|9(?:0[89]|[2-579])|(?:2|80)[2-9]"]]], 0, 0, "(15(?:0[06]|1[12]|[35]5|4[04]|6[26]|77|88|99)\\d)"], "LV": ["371", "00", "(?:[268]\\d|90)\\d{6}", [8], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[269]|8[01]"]]]], "LY": ["218", "00", "[2-9]\\d{8}", [9], [["(\\d{2})(\\d{7})", "$1-$2", ["[2-9]"], "0$1"]], "0"], "MA": ["212", "00", "[5-8]\\d{8}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["5[45]"], "0$1"], ["(\\d{4})(\\d{5})", "$1-$2", ["5(?:2[2-46-9]|3[3-9]|9)|8(?:0[89]|92)"], "0$1"], ["(\\d{2})(\\d{7})", "$1-$2", ["8"], "0$1"], ["(\\d{3})(\\d{6})", "$1-$2", ["[5-7]"], "0$1"]], "0", 0, 0, 0, 0, 0, [["5(?:2(?:[0-25-79]\\d|3[1-578]|4[02-46-8]|8[0235-7])|3(?:[0-47]\\d|5[02-9]|6[02-8]|8[014-9]|9[3-9])|(?:4[067]|5[03])\\d)\\d{5}"], ["(?:6(?:[0-79]\\d|8[0-247-9])|7(?:[0167]\\d|2[0-4]|5[01]|8[0-3]))\\d{6}"], ["80[0-7]\\d{6}"], ["89\\d{7}"], 0, 0, 0, 0, ["(?:592(?:4[0-2]|93)|80[89]\\d\\d)\\d{4}"]]], "MC": ["377", "00", "(?:[3489]|6\\d)\\d{7}", [8, 9], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["4"], "0$1"], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[389]"]], ["(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4 $5", ["6"], "0$1"]], "0"], "MD": ["373", "00", "(?:[235-7]\\d|[89]0)\\d{6}", [8], [["(\\d{3})(\\d{5})", "$1 $2", ["[89]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["22|3"], "0$1"], ["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["[25-7]"], "0$1"]], "0"], "ME": ["382", "00", "(?:20|[3-79]\\d)\\d{6}|80\\d{6,7}", [8, 9], [["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[2-9]"], "0$1"]], "0"], "MF": ["590", "00", "590\\d{6}|(?:69|80|9\\d)\\d{7}", [9], 0, "0", 0, 0, 0, 0, 0, [["590(?:0[079]|[14]3|[27][79]|3[03-7]|5[0-268]|87)\\d{4}"], ["69(?:0\\d\\d|1(?:2[2-9]|3[0-5])|4(?:0[89]|1[2-6]|9\\d)|6(?:1[016-9]|5[0-4]|[67]\\d))\\d{4}"], ["80[0-5]\\d{6}"], 0, 0, 0, 0, 0, ["9(?:(?:39[5-7]|76[018])\\d|475[0-5])\\d{4}"]]], "MG": ["261", "00", "[23]\\d{8}", [9], [["(\\d{2})(\\d{2})(\\d{3})(\\d{2})", "$1 $2 $3 $4", ["[23]"], "0$1"]], "0", 0, "([24-9]\\d{6})$|0", "20$1"], "MH": ["692", "011", "329\\d{4}|(?:[256]\\d|45)\\d{5}", [7], [["(\\d{3})(\\d{4})", "$1-$2", ["[2-6]"]]], "1"], "MK": ["389", "00", "[2-578]\\d{7}", [8], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["2|34[47]|4(?:[37]7|5[47]|64)"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[347]"], "0$1"], ["(\\d{3})(\\d)(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[58]"], "0$1"]], "0"], "ML": ["223", "00", "[24-9]\\d{7}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[24-9]"]]]], "MM": ["95", "00", "1\\d{5,7}|95\\d{6}|(?:[4-7]|9[0-46-9])\\d{6,8}|(?:2|8\\d)\\d{5,8}", [6, 7, 8, 9, 10], [["(\\d)(\\d{2})(\\d{3})", "$1 $2 $3", ["16|2"], "0$1"], ["(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3", ["4(?:[2-46]|5[3-5])|5|6(?:[1-689]|7[235-7])|7(?:[0-4]|5[2-7])|8[1-5]|(?:60|86)[23]"], "0$1"], ["(\\d)(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[12]|452|678|86", "[12]|452|6788|86"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[4-7]|8[1-35]"], "0$1"], ["(\\d)(\\d{3})(\\d{4,6})", "$1 $2 $3", ["9(?:2[0-4]|[35-9]|4[137-9])"], "0$1"], ["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["8"], "0$1"], ["(\\d)(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["92"], "0$1"], ["(\\d)(\\d{5})(\\d{4})", "$1 $2 $3", ["9"], "0$1"]], "0"], "MN": ["976", "001", "[12]\\d{7,9}|[5-9]\\d{7}", [8, 9, 10], [["(\\d{2})(\\d{2})(\\d{4})", "$1 $2 $3", ["[12]1"], "0$1"], ["(\\d{4})(\\d{4})", "$1 $2", ["[5-9]"]], ["(\\d{3})(\\d{5,6})", "$1 $2", ["[12]2[1-3]"], "0$1"], ["(\\d{4})(\\d{5,6})", "$1 $2", ["[12](?:27|3[2-8]|4[2-68]|5[1-4689])", "[12](?:27|3[2-8]|4[2-68]|5[1-4689])[0-3]"], "0$1"], ["(\\d{5})(\\d{4,5})", "$1 $2", ["[12]"], "0$1"]], "0"], "MO": ["853", "00", "0800\\d{3}|(?:28|[68]\\d)\\d{6}", [7, 8], [["(\\d{4})(\\d{3})", "$1 $2", ["0"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[268]"]]]], "MP": ["1", "011", "[58]\\d{9}|(?:67|90)0\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "670$1", 0, "670"], "MQ": ["596", "00", "596\\d{6}|(?:69|80|9\\d)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[569]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"], "0$1"]], "0"], "MR": ["222", "00", "(?:[2-4]\\d\\d|800)\\d{5}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2-48]"]]]], "MS": ["1", "011", "(?:[58]\\d\\d|664|900)\\d{7}", [10], 0, "1", 0, "([34]\\d{6})$|1", "664$1", 0, "664"], "MT": ["356", "00", "3550\\d{4}|(?:[2579]\\d\\d|800)\\d{5}", [8], [["(\\d{4})(\\d{4})", "$1 $2", ["[2357-9]"]]]], "MU": ["230", "0(?:0|[24-7]0|3[03])", "(?:[57]|8\\d\\d)\\d{7}|[2-468]\\d{6}", [7, 8, 10], [["(\\d{3})(\\d{4})", "$1 $2", ["[2-46]|8[013]"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[57]"]], ["(\\d{5})(\\d{5})", "$1 $2", ["8"]]], 0, 0, 0, 0, 0, 0, 0, "020"], "MV": ["960", "0(?:0|19)", "(?:800|9[0-57-9]\\d)\\d{7}|[34679]\\d{6}", [7, 10], [["(\\d{3})(\\d{4})", "$1-$2", ["[34679]"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["[89]"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "MW": ["265", "00", "(?:[1289]\\d|31|77)\\d{7}|1\\d{6}", [7, 9], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["1[2-9]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[137-9]"], "0$1"]], "0"], "MX": ["52", "0[09]", "[2-9]\\d{9}", [10], [["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["33|5[56]|81"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["[2-9]"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "MY": ["60", "00", "1\\d{8,9}|(?:3\\d|[4-9])\\d{7}", [8, 9, 10], [["(\\d)(\\d{3})(\\d{4})", "$1-$2 $3", ["[4-79]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1-$2 $3", ["1(?:[02469]|[378][1-9]|53)|8", "1(?:[02469]|[37][1-9]|53|8(?:[1-46-9]|5[7-9]))|8"], "0$1"], ["(\\d)(\\d{4})(\\d{4})", "$1-$2 $3", ["3"], "0$1"], ["(\\d)(\\d{3})(\\d{2})(\\d{4})", "$1-$2-$3-$4", ["1(?:[367]|80)"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1-$2 $3", ["15"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4})", "$1-$2 $3", ["1"], "0$1"]], "0"], "MZ": ["258", "00", "(?:2|8\\d)\\d{7}", [8, 9], [["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["2|8[2-79]"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["8"]]]], "NA": ["264", "00", "[68]\\d{7,8}", [8, 9], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["88"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["6"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["87"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["8"], "0$1"]], "0"], "NC": ["687", "00", "(?:050|[2-57-9]\\d\\d)\\d{3}", [6], [["(\\d{2})(\\d{2})(\\d{2})", "$1.$2.$3", ["[02-57-9]"]]]], "NE": ["227", "00", "[027-9]\\d{7}", [8], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["08"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[089]|2[013]|7[0467]"]]]], "NF": ["672", "00", "[13]\\d{5}", [6], [["(\\d{2})(\\d{4})", "$1 $2", ["1[0-3]"]], ["(\\d)(\\d{5})", "$1 $2", ["[13]"]]], 0, 0, "([0-258]\\d{4})$", "3$1"], "NG": ["234", "009", "38\\d{6}|[78]\\d{9,13}|(?:20|9\\d)\\d{8}", [8, 10, 11, 12, 13, 14], [["(\\d{2})(\\d{3})(\\d{2,3})", "$1 $2 $3", ["3"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[7-9]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["20[129]"], "0$1"], ["(\\d{4})(\\d{2})(\\d{4})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{3})(\\d{4})(\\d{4,5})", "$1 $2 $3", ["[78]"], "0$1"], ["(\\d{3})(\\d{5})(\\d{5,6})", "$1 $2 $3", ["[78]"], "0$1"]], "0"], "NI": ["505", "00", "(?:1800|[25-8]\\d{3})\\d{4}", [8], [["(\\d{4})(\\d{4})", "$1 $2", ["[125-8]"]]]], "NL": ["31", "00", "(?:[124-7]\\d\\d|3(?:[02-9]\\d|1[0-8]))\\d{6}|8\\d{6,9}|9\\d{6,10}|1\\d{4,5}", [5, 6, 7, 8, 9, 10, 11], [["(\\d{3})(\\d{4,7})", "$1 $2", ["[89]0"], "0$1"], ["(\\d{2})(\\d{7})", "$1 $2", ["66"], "0$1"], ["(\\d)(\\d{8})", "$1 $2", ["6"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["1[16-8]|2[259]|3[124]|4[17-9]|5[124679]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[1-578]|91"], "0$1"], ["(\\d{3})(\\d{3})(\\d{5})", "$1 $2 $3", ["9"], "0$1"]], "0"], "NO": ["47", "00", "(?:0|[2-9]\\d{3})\\d{4}", [5, 8], [["(\\d{3})(\\d{2})(\\d{3})", "$1 $2 $3", ["8"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2-79]"]]], 0, 0, 0, 0, 0, "[02-689]|7[0-8]"], "NP": ["977", "00", "(?:1\\d|9)\\d{9}|[1-9]\\d{7}", [8, 10, 11], [["(\\d)(\\d{7})", "$1-$2", ["1[2-6]"], "0$1"], ["(\\d{2})(\\d{6})", "$1-$2", ["1[01]|[2-8]|9(?:[1-59]|[67][2-6])"], "0$1"], ["(\\d{3})(\\d{7})", "$1-$2", ["9"]]], "0"], "NR": ["674", "00", "(?:444|(?:55|8\\d)\\d|666)\\d{4}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[4-68]"]]]], "NU": ["683", "00", "(?:[4-7]|888\\d)\\d{3}", [4, 7], [["(\\d{3})(\\d{4})", "$1 $2", ["8"]]]], "NZ": ["64", "0(?:0|161)", "[1289]\\d{9}|50\\d{5}(?:\\d{2,3})?|[27-9]\\d{7,8}|(?:[34]\\d|6[0-35-9])\\d{6}|8\\d{4,6}", [5, 6, 7, 8, 9, 10], [["(\\d{2})(\\d{3,8})", "$1 $2", ["8[1-79]"], "0$1"], ["(\\d{3})(\\d{2})(\\d{2,3})", "$1 $2 $3", ["50[036-8]|8|90", "50(?:[0367]|88)|8|90"], "0$1"], ["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["24|[346]|7[2-57-9]|9[2-9]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["2(?:10|74)|[589]"], "0$1"], ["(\\d{2})(\\d{3,4})(\\d{4})", "$1 $2 $3", ["1|2[028]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,5})", "$1 $2 $3", ["2(?:[169]|7[0-35-9])|7"], "0$1"]], "0", 0, 0, 0, 0, 0, 0, "00"], "OM": ["968", "00", "(?:1505|[279]\\d{3}|500)\\d{4}|800\\d{5,6}", [7, 8, 9], [["(\\d{3})(\\d{4,6})", "$1 $2", ["[58]"]], ["(\\d{2})(\\d{6})", "$1 $2", ["2"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[179]"]]]], "PA": ["507", "00", "(?:00800|8\\d{3})\\d{6}|[68]\\d{7}|[1-57-9]\\d{6}", [7, 8, 10, 11], [["(\\d{3})(\\d{4})", "$1-$2", ["[1-57-9]"]], ["(\\d{4})(\\d{4})", "$1-$2", ["[68]"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["8"]]]], "PE": ["51", "00|19(?:1[124]|77|90)00", "(?:[14-8]|9\\d)\\d{7}", [8, 9], [["(\\d{3})(\\d{5})", "$1 $2", ["80"], "(0$1)"], ["(\\d)(\\d{7})", "$1 $2", ["1"], "(0$1)"], ["(\\d{2})(\\d{6})", "$1 $2", ["[4-8]"], "(0$1)"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["9"]]], "0", 0, 0, 0, 0, 0, 0, "00", " Anexo "], "PF": ["689", "00", "4\\d{5}(?:\\d{2})?|8\\d{7,8}", [6, 8, 9], [["(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3", ["44"]], ["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["4|8[7-9]"]], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"]]]], "PG": ["675", "00|140[1-3]", "(?:180|[78]\\d{3})\\d{4}|(?:[2-589]\\d|64)\\d{5}", [7, 8], [["(\\d{3})(\\d{4})", "$1 $2", ["18|[2-69]|85"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[78]"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "PH": ["63", "00", "(?:[2-7]|9\\d)\\d{8}|2\\d{5}|(?:1800|8)\\d{7,9}", [6, 8, 9, 10, 11, 12, 13], [["(\\d)(\\d{5})", "$1 $2", ["2"], "(0$1)"], ["(\\d{4})(\\d{4,6})", "$1 $2", ["3(?:23|39|46)|4(?:2[3-6]|[35]9|4[26]|76)|544|88[245]|(?:52|64|86)2", "3(?:230|397|461)|4(?:2(?:35|[46]4|51)|396|4(?:22|63)|59[347]|76[15])|5(?:221|446)|642[23]|8(?:622|8(?:[24]2|5[13]))"], "(0$1)"], ["(\\d{5})(\\d{4})", "$1 $2", ["346|4(?:27|9[35])|883", "3469|4(?:279|9(?:30|56))|8834"], "(0$1)"], ["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["2"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[3-7]|8[2-8]"], "(0$1)"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["[89]"], "0$1"], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"]], ["(\\d{4})(\\d{1,2})(\\d{3})(\\d{4})", "$1 $2 $3 $4", ["1"]]], "0"], "PK": ["92", "00", "122\\d{6}|[24-8]\\d{10,11}|9(?:[013-9]\\d{8,10}|2(?:[01]\\d\\d|2(?:[06-8]\\d|1[01]))\\d{7})|(?:[2-8]\\d{3}|92(?:[0-7]\\d|8[1-9]))\\d{6}|[24-9]\\d{8}|[89]\\d{7}", [8, 9, 10, 11, 12], [["(\\d{3})(\\d{3})(\\d{2,7})", "$1 $2 $3", ["[89]0"], "0$1"], ["(\\d{4})(\\d{5})", "$1 $2", ["1"]], ["(\\d{3})(\\d{6,7})", "$1 $2", ["2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8])", "9(?:2[3-8]|98)|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:22|3[27-9]|4[2-6]|6[3569]|9[25-7]))[2-9]"], "(0$1)"], ["(\\d{2})(\\d{7,8})", "$1 $2", ["(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]"], "(0$1)"], ["(\\d{5})(\\d{5})", "$1 $2", ["58"], "(0$1)"], ["(\\d{3})(\\d{7})", "$1 $2", ["3"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91"], "(0$1)"], ["(\\d{3})(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["[24-9]"], "(0$1)"]], "0"], "PL": ["48", "00", "(?:6|8\\d\\d)\\d{7}|[1-9]\\d{6}(?:\\d{2})?|[26]\\d{5}", [6, 7, 8, 9, 10], [["(\\d{5})", "$1", ["19"]], ["(\\d{3})(\\d{3})", "$1 $2", ["11|20|64"]], ["(\\d{2})(\\d{2})(\\d{3})", "$1 $2 $3", ["(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])1", "(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])19"]], ["(\\d{3})(\\d{2})(\\d{2,3})", "$1 $2 $3", ["64"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["21|39|45|5[0137]|6[0469]|7[02389]|8(?:0[14]|8)"]], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["1[2-8]|[2-7]|8[1-79]|9[145]"]], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["8"]]]], "PM": ["508", "00", "[45]\\d{5}|(?:708|80\\d)\\d{6}", [6, 9], [["(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3", ["[45]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["7"]], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"], "0$1"]], "0"], "PR": ["1", "011", "(?:[589]\\d\\d|787)\\d{7}", [10], 0, "1", 0, 0, 0, 0, "787|939"], "PS": ["970", "00", "[2489]2\\d{6}|(?:1\\d|5)\\d{8}", [8, 9, 10], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["[2489]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["5"], "0$1"], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["1"]]], "0"], "PT": ["351", "00", "1693\\d{5}|(?:[26-9]\\d|30)\\d{7}", [9], [["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["2[12]"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["16|[236-9]"]]]], "PW": ["680", "01[12]", "(?:[24-8]\\d\\d|345|900)\\d{4}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[2-9]"]]]], "PY": ["595", "00", "59\\d{4,6}|9\\d{5,10}|(?:[2-46-8]\\d|5[0-8])\\d{4,7}", [6, 7, 8, 9, 10, 11], [["(\\d{3})(\\d{3,6})", "$1 $2", ["[2-9]0"], "0$1"], ["(\\d{2})(\\d{5})", "$1 $2", ["[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36]"], "(0$1)"], ["(\\d{3})(\\d{4,5})", "$1 $2", ["2[279]|3[13-5]|4[359]|5|6(?:[34]|7[1-46-8])|7[46-8]|85"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["2[14-68]|3[26-9]|4[1246-8]|6(?:1|75)|7[1-35]|8[1-36]"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["87"]], ["(\\d{3})(\\d{6})", "$1 $2", ["9(?:[5-79]|8[1-7])"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[2-8]"], "0$1"], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["9"]]], "0"], "QA": ["974", "00", "800\\d{4}|(?:2|800)\\d{6}|(?:0080|[3-7])\\d{7}", [7, 8, 9, 11], [["(\\d{3})(\\d{4})", "$1 $2", ["2[16]|8"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[3-7]"]]]], "RE": ["262", "00", "(?:26|[689]\\d)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[2689]"], "0$1"]], "0", 0, 0, 0, 0, 0, [["26(?:2\\d\\d|3(?:0\\d|1[0-6]))\\d{4}"], ["69(?:2\\d\\d|3(?:[06][0-6]|1[013]|2[0-2]|3[0-39]|4\\d|5[0-5]|7[0-37]|8[0-8]|9[0-479]))\\d{4}"], ["80\\d{7}"], ["89[1-37-9]\\d{6}"], 0, 0, 0, 0, ["9(?:399[0-3]|479[0-5]|76(?:2[278]|3[0-37]))\\d{4}"], ["8(?:1[019]|2[0156]|84|90)\\d{6}"]]], "RO": ["40", "00", "(?:[236-8]\\d|90)\\d{7}|[23]\\d{5}", [6, 9], [["(\\d{3})(\\d{3})", "$1 $2", ["2[3-6]", "2[3-6]\\d9"], "0$1"], ["(\\d{2})(\\d{4})", "$1 $2", ["219|31"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[23]1"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[236-9]"], "0$1"]], "0", 0, 0, 0, 0, 0, 0, 0, " int "], "RS": ["381", "00", "38[02-9]\\d{6,9}|6\\d{7,9}|90\\d{4,8}|38\\d{5,6}|(?:7\\d\\d|800)\\d{3,9}|(?:[12]\\d|3[0-79])\\d{5,10}", [6, 7, 8, 9, 10, 11, 12], [["(\\d{3})(\\d{3,9})", "$1 $2", ["(?:2[389]|39)0|[7-9]"], "0$1"], ["(\\d{2})(\\d{5,10})", "$1 $2", ["[1-36]"], "0$1"]], "0"], "RU": ["7", "810", "8\\d{13}|[347-9]\\d{9}", [10, 14], [["(\\d{4})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["7(?:1[0-8]|2[1-9])", "7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:1[23]|[2-9]2))", "7(?:1(?:[0-356]2|4[29]|7|8[27])|2(?:13[03-69]|62[013-9]))|72[1-57-9]2"], "8 ($1)", 1], ["(\\d{5})(\\d)(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["7(?:1[0-68]|2[1-9])", "7(?:1(?:[06][3-6]|[18]|2[35]|[3-5][3-5])|2(?:[13][3-5]|[24-689]|7[457]))", "7(?:1(?:0(?:[356]|4[023])|[18]|2(?:3[013-9]|5)|3[45]|43[013-79]|5(?:3[1-8]|4[1-7]|5)|6(?:3[0-35-9]|[4-6]))|2(?:1(?:3[178]|[45])|[24-689]|3[35]|7[457]))|7(?:14|23)4[0-8]|71(?:33|45)[1-79]"], "8 ($1)", 1], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["7"], "8 ($1)", 1], ["(\\d{3})(\\d{3})(\\d{2})(\\d{2})", "$1 $2-$3-$4", ["[349]|8(?:[02-7]|1[1-8])"], "8 ($1)", 1], ["(\\d{4})(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["8"], "8 ($1)"]], "8", 0, 0, 0, 0, "3[04-689]|[489]", 0, "8~10"], "RW": ["250", "00", "(?:06|[27]\\d\\d|[89]00)\\d{6}", [8, 9], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["0"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["2"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[7-9]"], "0$1"]], "0"], "SA": ["966", "00", "92\\d{7}|(?:[15]|8\\d)\\d{8}", [9, 10], [["(\\d{4})(\\d{5})", "$1 $2", ["9"]], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["5"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["81"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["8"]]], "0"], "SB": ["677", "0[01]", "[6-9]\\d{6}|[1-6]\\d{4}", [5, 7], [["(\\d{2})(\\d{5})", "$1 $2", ["6[89]|7|8[4-9]|9(?:[1-8]|9[0-8])"]]]], "SC": ["248", "010|0[0-2]", "(?:[2489]\\d|64)\\d{5}", [7], [["(\\d)(\\d{3})(\\d{3})", "$1 $2 $3", ["[246]|9[57]"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "SD": ["249", "00", "[19]\\d{8}", [9], [["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[19]"], "0$1"]], "0"], "SE": ["46", "00", "(?:[26]\\d\\d|9)\\d{9}|[1-9]\\d{8}|[1-689]\\d{7}|[1-4689]\\d{6}|2\\d{5}", [6, 7, 8, 9, 10, 12], [["(\\d{2})(\\d{2,3})(\\d{2})", "$1-$2 $3", ["20"], "0$1", 0, "$1 $2 $3"], ["(\\d{3})(\\d{4})", "$1-$2", ["9(?:00|39|44|9)"], "0$1", 0, "$1 $2"], ["(\\d{2})(\\d{3})(\\d{2})", "$1-$2 $3", ["[12][136]|3[356]|4[0246]|6[03]|90[1-9]"], "0$1", 0, "$1 $2 $3"], ["(\\d)(\\d{2,3})(\\d{2})(\\d{2})", "$1-$2 $3 $4", ["8"], "0$1", 0, "$1 $2 $3 $4"], ["(\\d{3})(\\d{2,3})(\\d{2})", "$1-$2 $3", ["1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[125689]|4[02-57]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"], "0$1", 0, "$1 $2 $3"], ["(\\d{3})(\\d{2,3})(\\d{3})", "$1-$2 $3", ["9(?:00|39|44)"], "0$1", 0, "$1 $2 $3"], ["(\\d{2})(\\d{2,3})(\\d{2})(\\d{2})", "$1-$2 $3 $4", ["1[13689]|2[0136]|3[1356]|4[0246]|54|6[03]|90[1-9]"], "0$1", 0, "$1 $2 $3 $4"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1-$2 $3 $4", ["10|7"], "0$1", 0, "$1 $2 $3 $4"], ["(\\d)(\\d{3})(\\d{3})(\\d{2})", "$1-$2 $3 $4", ["8"], "0$1", 0, "$1 $2 $3 $4"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1-$2 $3 $4", ["[13-5]|2(?:[247-9]|5[0138])|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"], "0$1", 0, "$1 $2 $3 $4"], ["(\\d{3})(\\d{2})(\\d{2})(\\d{3})", "$1-$2 $3 $4", ["9"], "0$1", 0, "$1 $2 $3 $4"], ["(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1-$2 $3 $4 $5", ["[26]"], "0$1", 0, "$1 $2 $3 $4 $5"]], "0"], "SG": ["65", "0[0-3]\\d", "(?:(?:1\\d|8)\\d\\d|7000)\\d{7}|[3689]\\d{7}", [8, 10, 11], [["(\\d{4})(\\d{4})", "$1 $2", ["[369]|8(?:0[1-9]|[1-9])"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["8"]], ["(\\d{4})(\\d{4})(\\d{3})", "$1 $2 $3", ["7"]], ["(\\d{4})(\\d{3})(\\d{4})", "$1 $2 $3", ["1"]]]], "SH": ["290", "00", "(?:[256]\\d|8)\\d{3}", [4, 5], 0, 0, 0, 0, 0, 0, "[256]"], "SI": ["386", "00|10(?:22|66|88|99)", "[1-7]\\d{7}|8\\d{4,7}|90\\d{4,6}", [5, 6, 7, 8], [["(\\d{2})(\\d{3,6})", "$1 $2", ["8[09]|9"], "0$1"], ["(\\d{3})(\\d{5})", "$1 $2", ["59|8"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[37][01]|4[0139]|51|6"], "0$1"], ["(\\d)(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[1-57]"], "(0$1)"]], "0", 0, 0, 0, 0, 0, 0, "00"], "SJ": ["47", "00", "0\\d{4}|(?:[489]\\d|79)\\d{6}", [5, 8], 0, 0, 0, 0, 0, 0, "79"], "SK": ["421", "00", "[2-689]\\d{8}|[2-59]\\d{6}|[2-5]\\d{5}", [6, 7, 9], [["(\\d)(\\d{2})(\\d{3,4})", "$1 $2 $3", ["21"], "0$1"], ["(\\d{2})(\\d{2})(\\d{2,3})", "$1 $2 $3", ["[3-5][1-8]1", "[3-5][1-8]1[67]"], "0$1"], ["(\\d)(\\d{3})(\\d{3})(\\d{2})", "$1/$2 $3 $4", ["2"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[689]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1/$2 $3 $4", ["[3-5]"], "0$1"]], "0"], "SL": ["232", "00", "(?:[237-9]\\d|66)\\d{6}", [8], [["(\\d{2})(\\d{6})", "$1 $2", ["[236-9]"], "(0$1)"]], "0"], "SM": ["378", "00", "(?:0549|[5-7]\\d)\\d{6}", [8, 10], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[5-7]"]], ["(\\d{4})(\\d{6})", "$1 $2", ["0"]]], 0, 0, "([89]\\d{5})$", "0549$1"], "SN": ["221", "00", "(?:[378]\\d|93)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"]], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[379]"]]]], "SO": ["252", "00", "[346-9]\\d{8}|[12679]\\d{7}|[1-5]\\d{6}|[1348]\\d{5}", [6, 7, 8, 9], [["(\\d{2})(\\d{4})", "$1 $2", ["8[125]"]], ["(\\d{6})", "$1", ["[134]"]], ["(\\d)(\\d{6})", "$1 $2", ["[15]|2[0-79]|3[0-46-8]|4[0-7]"]], ["(\\d)(\\d{7})", "$1 $2", ["(?:2|90)4|[67]"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[348]|64|79|90"]], ["(\\d{2})(\\d{5,7})", "$1 $2", ["1|28|6[0-35-9]|77|9[2-9]"]]], "0"], "SR": ["597", "00", "(?:[2-5]|68|[78]\\d)\\d{5}", [6, 7], [["(\\d{2})(\\d{2})(\\d{2})", "$1-$2-$3", ["56"]], ["(\\d{3})(\\d{3})", "$1-$2", ["[2-5]"]], ["(\\d{3})(\\d{4})", "$1-$2", ["[6-8]"]]]], "SS": ["211", "00", "[19]\\d{8}", [9], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[19]"], "0$1"]], "0"], "ST": ["239", "00", "(?:22|9\\d)\\d{5}", [7], [["(\\d{3})(\\d{4})", "$1 $2", ["[29]"]]]], "SV": ["503", "00", "[267]\\d{7}|(?:80\\d|900)\\d{4}(?:\\d{4})?", [7, 8, 11], [["(\\d{3})(\\d{4})", "$1 $2", ["[89]"]], ["(\\d{4})(\\d{4})", "$1 $2", ["[267]"]], ["(\\d{3})(\\d{4})(\\d{4})", "$1 $2 $3", ["[89]"]]]], "SX": ["1", "011", "7215\\d{6}|(?:[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "(5\\d{6})$|1", "721$1", 0, "721"], "SY": ["963", "00", "[1-39]\\d{8}|[1-5]\\d{7}", [8, 9], [["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[1-5]"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["9"], "0$1", 1]], "0"], "SZ": ["268", "00", "0800\\d{4}|(?:[237]\\d|900)\\d{6}", [8, 9], [["(\\d{4})(\\d{4})", "$1 $2", ["[0237]"]], ["(\\d{5})(\\d{4})", "$1 $2", ["9"]]]], "TA": ["290", "00", "8\\d{3}", [4], 0, 0, 0, 0, 0, 0, "8"], "TC": ["1", "011", "(?:[58]\\d\\d|649|900)\\d{7}", [10], 0, "1", 0, "([2-479]\\d{6})$|1", "649$1", 0, "649"], "TD": ["235", "00|16", "(?:22|[689]\\d|77)\\d{6}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[26-9]"]]], 0, 0, 0, 0, 0, 0, 0, "00"], "TG": ["228", "00", "[279]\\d{7}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[279]"]]]], "TH": ["66", "00[1-9]", "(?:001800|[2-57]|[689]\\d)\\d{7}|1\\d{7,9}", [8, 9, 10, 13], [["(\\d)(\\d{3})(\\d{4})", "$1 $2 $3", ["2"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[13-9]"], "0$1"], ["(\\d{4})(\\d{3})(\\d{3})", "$1 $2 $3", ["1"]]], "0"], "TJ": ["992", "810", "[0-57-9]\\d{8}", [9], [["(\\d{6})(\\d)(\\d{2})", "$1 $2 $3", ["331", "3317"]], ["(\\d{3})(\\d{2})(\\d{4})", "$1 $2 $3", ["44[02-479]|[34]7"]], ["(\\d{4})(\\d)(\\d{4})", "$1 $2 $3", ["3(?:[1245]|3[12])"]], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[0-57-9]"]]], 0, 0, 0, 0, 0, 0, 0, "8~10"], "TK": ["690", "00", "[2-47]\\d{3,6}", [4, 5, 6, 7]], "TL": ["670", "00", "7\\d{7}|(?:[2-47]\\d|[89]0)\\d{5}", [7, 8], [["(\\d{3})(\\d{4})", "$1 $2", ["[2-489]|70"]], ["(\\d{4})(\\d{4})", "$1 $2", ["7"]]]], "TM": ["993", "810", "(?:[1-6]\\d|71)\\d{6}", [8], [["(\\d{2})(\\d{2})(\\d{2})(\\d{2})", "$1 $2-$3-$4", ["12"], "(8 $1)"], ["(\\d{3})(\\d)(\\d{2})(\\d{2})", "$1 $2-$3-$4", ["[1-5]"], "(8 $1)"], ["(\\d{2})(\\d{6})", "$1 $2", ["[67]"], "8 $1"]], "8", 0, 0, 0, 0, 0, 0, "8~10"], "TN": ["216", "00", "[2-57-9]\\d{7}", [8], [["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[2-57-9]"]]]], "TO": ["676", "00", "(?:0800|(?:[5-8]\\d\\d|999)\\d)\\d{3}|[2-8]\\d{4}", [5, 7], [["(\\d{2})(\\d{3})", "$1-$2", ["[2-4]|50|6[09]|7[0-24-69]|8[05]"]], ["(\\d{4})(\\d{3})", "$1 $2", ["0"]], ["(\\d{3})(\\d{4})", "$1 $2", ["[5-9]"]]]], "TR": ["90", "00", "4\\d{6}|8\\d{11,12}|(?:[2-58]\\d\\d|900)\\d{7}", [7, 10, 12, 13], [["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["512|8[01589]|90"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["5(?:[0-59]|61)", "5(?:[0-59]|61[06])", "5(?:[0-59]|61[06]1)"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[24][1-8]|3[1-9]"], "(0$1)", 1], ["(\\d{3})(\\d{3})(\\d{6,7})", "$1 $2 $3", ["80"], "0$1", 1]], "0"], "TT": ["1", "011", "(?:[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-46-8]\\d{6})$|1", "868$1", 0, "868"], "TV": ["688", "00", "(?:2|7\\d\\d|90)\\d{4}", [5, 6, 7], [["(\\d{2})(\\d{3})", "$1 $2", ["2"]], ["(\\d{2})(\\d{4})", "$1 $2", ["90"]], ["(\\d{2})(\\d{5})", "$1 $2", ["7"]]]], "TW": ["886", "0(?:0[25-79]|19)", "[2-689]\\d{8}|7\\d{9,10}|[2-8]\\d{7}|2\\d{6}", [7, 8, 9, 10, 11], [["(\\d{2})(\\d)(\\d{4})", "$1 $2 $3", ["202"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[258]0"], "0$1"], ["(\\d)(\\d{3,4})(\\d{4})", "$1 $2 $3", ["[23568]|4(?:0[02-48]|[1-47-9])|7[1-9]", "[23568]|4(?:0[2-48]|[1-47-9])|(?:400|7)[1-9]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[49]"], "0$1"], ["(\\d{2})(\\d{4})(\\d{4,5})", "$1 $2 $3", ["7"], "0$1"]], "0", 0, 0, 0, 0, 0, 0, 0, "#"], "TZ": ["255", "00[056]", "(?:[25-8]\\d|41|90)\\d{7}", [9], [["(\\d{3})(\\d{2})(\\d{4})", "$1 $2 $3", ["[89]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[24]"], "0$1"], ["(\\d{2})(\\d{7})", "$1 $2", ["5"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[67]"], "0$1"]], "0"], "UA": ["380", "00", "[89]\\d{9}|[3-9]\\d{8}", [9, 10], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["6[12][29]|(?:3[1-8]|4[136-8]|5[12457]|6[49])2|(?:56|65)[24]", "6[12][29]|(?:35|4[1378]|5[12457]|6[49])2|(?:56|65)[24]|(?:3[1-46-8]|46)2[013-9]"], "0$1"], ["(\\d{4})(\\d{5})", "$1 $2", ["3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6[0135689]|7[4-6])|6(?:[12][3-7]|[459])", "3[1-8]|4(?:[1367]|[45][6-9]|8[4-6])|5(?:[1-5]|6(?:[015689]|3[02389])|7[4-6])|6(?:[12][3-7]|[459])"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[3-7]|89|9[1-9]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[89]"], "0$1"]], "0", 0, 0, 0, 0, 0, 0, "0~0"], "UG": ["256", "00[057]", "800\\d{6}|(?:[29]0|[347]\\d)\\d{7}", [9], [["(\\d{4})(\\d{5})", "$1 $2", ["202", "2024"], "0$1"], ["(\\d{3})(\\d{6})", "$1 $2", ["[27-9]|4(?:6[45]|[7-9])"], "0$1"], ["(\\d{2})(\\d{7})", "$1 $2", ["[34]"], "0$1"]], "0"], "US": ["1", "011", "[2-9]\\d{9}|3\\d{6}", [10], [["(\\d{3})(\\d{4})", "$1-$2", ["310"], 0, 1], ["(\\d{3})(\\d{3})(\\d{4})", "($1) $2-$3", ["[2-9]"], 0, 1, "$1-$2-$3"]], "1", 0, 0, 0, 0, 0, [["(?:3052(?:0[0-8]|[1-9]\\d)|5056(?:[0-35-9]\\d|4[468])|7302[0-4]\\d)\\d{4}|(?:305[3-9]|472[24]|505[2-57-9]|7306|983[2-47-9])\\d{6}|(?:2(?:0[1-35-9]|1[02-9]|2[03-57-9]|3[1459]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-47-9]|1[02-9]|2[013569]|3[0-24679]|4[167]|5[0-2]|6[01349]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[023578]|58|6[349]|7[0589]|8[04])|5(?:0[1-47-9]|1[0235-8]|20|3[0149]|4[01]|5[179]|6[1-47]|7[0-5]|8[0256])|6(?:0[1-35-9]|1[024-9]|2[03689]|3[016]|4[0156]|5[01679]|6[0-279]|78|8[0-29])|7(?:0[1-46-8]|1[2-9]|2[04-8]|3[1247]|4[037]|5[47]|6[02359]|7[0-59]|8[156])|8(?:0[1-68]|1[02-8]|2[068]|3[0-2589]|4[03578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[0146-8]|4[01357-9]|5[12469]|7[0-389]|8[04-69]))[2-9]\\d{6}"], [""], ["8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"], ["900[2-9]\\d{6}"], ["52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[125-9]|33|44|66|77|88)[2-9]\\d{6}"], 0, 0, 0, ["305209\\d{4}"]]], "UY": ["598", "0(?:0|1[3-9]\\d)", "0004\\d{2,9}|[1249]\\d{7}|(?:[49]\\d|80)\\d{5}", [6, 7, 8, 9, 10, 11, 12, 13], [["(\\d{3})(\\d{3,4})", "$1 $2", ["0"]], ["(\\d{3})(\\d{4})", "$1 $2", ["[49]0|8"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["9"], "0$1"], ["(\\d{4})(\\d{4})", "$1 $2", ["[124]"]], ["(\\d{3})(\\d{3})(\\d{2,4})", "$1 $2 $3", ["0"]], ["(\\d{3})(\\d{3})(\\d{3})(\\d{2,4})", "$1 $2 $3 $4", ["0"]]], "0", 0, 0, 0, 0, 0, 0, "00", " int. "], "UZ": ["998", "00", "(?:20|33|[5-79]\\d|88)\\d{7}", [9], [["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["[235-9]"]]]], "VA": ["39", "00", "0\\d{5,10}|3[0-8]\\d{7,10}|55\\d{8}|8\\d{5}(?:\\d{2,4})?|(?:1\\d|39)\\d{7,8}", [6, 7, 8, 9, 10, 11, 12], 0, 0, 0, 0, 0, 0, "06698"], "VC": ["1", "011", "(?:[58]\\d\\d|784|900)\\d{7}", [10], 0, "1", 0, "([2-7]\\d{6})$|1", "784$1", 0, "784"], "VE": ["58", "00", "[68]00\\d{7}|(?:[24]\\d|[59]0)\\d{8}", [10], [["(\\d{3})(\\d{7})", "$1-$2", ["[24-689]"], "0$1"]], "0"], "VG": ["1", "011", "(?:284|[58]\\d\\d|900)\\d{7}", [10], 0, "1", 0, "([2-578]\\d{6})$|1", "284$1", 0, "284"], "VI": ["1", "011", "[58]\\d{9}|(?:34|90)0\\d{7}", [10], 0, "1", 0, "([2-9]\\d{6})$|1", "340$1", 0, "340"], "VN": ["84", "00", "[12]\\d{9}|[135-9]\\d{8}|[16]\\d{7}|[16-8]\\d{6}", [7, 8, 9, 10], [["(\\d{2})(\\d{5})", "$1 $2", ["80"], "0$1", 1], ["(\\d{4})(\\d{4,6})", "$1 $2", ["1"], 0, 1], ["(\\d{2})(\\d{3})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["6"], "0$1", 1], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[357-9]"], "0$1", 1], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["2[48]"], "0$1", 1], ["(\\d{3})(\\d{4})(\\d{3})", "$1 $2 $3", ["2"], "0$1", 1]], "0"], "VU": ["678", "00", "[57-9]\\d{6}|(?:[238]\\d|48)\\d{3}", [5, 7], [["(\\d{3})(\\d{4})", "$1 $2", ["[57-9]"]]]], "WF": ["681", "00", "(?:40|72)\\d{4}|8\\d{5}(?:\\d{3})?", [6, 9], [["(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3", ["[478]"]], ["(\\d{3})(\\d{2})(\\d{2})(\\d{2})", "$1 $2 $3 $4", ["8"]]]], "WS": ["685", "0", "(?:[2-6]|8\\d{5})\\d{4}|[78]\\d{6}|[68]\\d{5}", [5, 6, 7, 10], [["(\\d{5})", "$1", ["[2-5]|6[1-9]"]], ["(\\d{3})(\\d{3,7})", "$1 $2", ["[68]"]], ["(\\d{2})(\\d{5})", "$1 $2", ["7"]]]], "XK": ["383", "00", "2\\d{7,8}|3\\d{7,11}|(?:4\\d\\d|[89]00)\\d{5}", [8, 9, 10, 11, 12], [["(\\d{3})(\\d{5})", "$1 $2", ["[89]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3})", "$1 $2 $3", ["[2-4]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["2|39"], "0$1"], ["(\\d{2})(\\d{7,10})", "$1 $2", ["3"], "0$1"]], "0"], "YE": ["967", "00", "(?:1|7\\d)\\d{7}|[1-7]\\d{6}", [7, 8, 9], [["(\\d)(\\d{3})(\\d{3,4})", "$1 $2 $3", ["[1-6]|7(?:[24-6]|8[0-7])"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["7"], "0$1"]], "0"], "YT": ["262", "00", "(?:80|9\\d)\\d{7}|(?:26|63)9\\d{6}", [9], 0, "0", 0, 0, 0, 0, 0, [["269(?:0[0-467]|15|5[0-4]|6\\d|[78]0)\\d{4}"], ["639(?:0[0-79]|1[019]|[267]\\d|3[09]|40|5[05-9]|9[04-79])\\d{4}"], ["80\\d{7}"], 0, 0, 0, 0, 0, ["9(?:(?:39|47)8[01]|769\\d)\\d{4}"]]], "ZA": ["27", "00", "[1-79]\\d{8}|8\\d{4,9}", [5, 6, 7, 8, 9, 10], [["(\\d{2})(\\d{3,4})", "$1 $2", ["8[1-4]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{2,3})", "$1 $2 $3", ["8[1-4]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["860"], "0$1"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["[1-9]"], "0$1"], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["8"], "0$1"]], "0"], "ZM": ["260", "00", "800\\d{6}|(?:21|63|[79]\\d)\\d{7}", [9], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[28]"], "0$1"], ["(\\d{2})(\\d{7})", "$1 $2", ["[79]"], "0$1"]], "0"], "ZW": ["263", "00", "2(?:[0-57-9]\\d{6,8}|6[0-24-9]\\d{6,7})|[38]\\d{9}|[35-8]\\d{8}|[3-6]\\d{7}|[1-689]\\d{6}|[1-3569]\\d{5}|[1356]\\d{4}", [5, 6, 7, 8, 9, 10], [["(\\d{3})(\\d{3,5})", "$1 $2", ["2(?:0[45]|2[278]|[49]8)|3(?:[09]8|17)|6(?:[29]8|37|75)|[23][78]|(?:33|5[15]|6[68])[78]"], "0$1"], ["(\\d)(\\d{3})(\\d{2,4})", "$1 $2 $3", ["[49]"], "0$1"], ["(\\d{3})(\\d{4})", "$1 $2", ["80"], "0$1"], ["(\\d{2})(\\d{7})", "$1 $2", ["24|8[13-59]|(?:2[05-79]|39|5[45]|6[15-8])2", "2(?:02[014]|4|[56]20|[79]2)|392|5(?:42|525)|6(?:[16-8]21|52[013])|8[13-59]"], "(0$1)"], ["(\\d{2})(\\d{3})(\\d{4})", "$1 $2 $3", ["7"], "0$1"], ["(\\d{3})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["2(?:1[39]|2[0157]|[378]|[56][14])|3(?:12|29)", "2(?:1[39]|2[0157]|[378]|[56][14])|3(?:123|29)"], "0$1"], ["(\\d{4})(\\d{6})", "$1 $2", ["8"], "0$1"], ["(\\d{2})(\\d{3,5})", "$1 $2", ["1|2(?:0[0-36-9]|12|29|[56])|3(?:1[0-689]|[24-6])|5(?:[0236-9]|1[2-4])|6(?:[013-59]|7[0-46-9])|(?:33|55|6[68])[0-69]|(?:29|3[09]|62)[0-79]"], "0$1"], ["(\\d{2})(\\d{3})(\\d{3,4})", "$1 $2 $3", ["29[013-9]|39|54"], "0$1"], ["(\\d{4})(\\d{3,5})", "$1 $2", ["(?:25|54)8", "258|5483"], "0$1"]], "0"] }, "nonGeographic": { "800": ["800", 0, "(?:00|[1-9]\\d)\\d{6}", [8], [["(\\d{4})(\\d{4})", "$1 $2", ["\\d"]]], 0, 0, 0, 0, 0, 0, [0, 0, ["(?:00|[1-9]\\d)\\d{6}"]]], "808": ["808", 0, "[1-9]\\d{7}", [8], [["(\\d{4})(\\d{4})", "$1 $2", ["[1-9]"]]], 0, 0, 0, 0, 0, 0, [0, 0, 0, 0, 0, 0, 0, 0, 0, ["[1-9]\\d{7}"]]], "870": ["870", 0, "7\\d{11}|[35-7]\\d{8}", [9, 12], [["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["[35-7]"]]], 0, 0, 0, 0, 0, 0, [0, ["(?:[356]|774[45])\\d{8}|7[6-8]\\d{7}"]]], "878": ["878", 0, "10\\d{10}", [12], [["(\\d{2})(\\d{5})(\\d{5})", "$1 $2 $3", ["1"]]], 0, 0, 0, 0, 0, 0, [0, 0, 0, 0, 0, 0, 0, 0, ["10\\d{10}"]]], "881": ["881", 0, "6\\d{9}|[0-36-9]\\d{8}", [9, 10], [["(\\d)(\\d{3})(\\d{5})", "$1 $2 $3", ["[0-37-9]"]], ["(\\d)(\\d{3})(\\d{5,6})", "$1 $2 $3", ["6"]]], 0, 0, 0, 0, 0, 0, [0, ["6\\d{9}|[0-36-9]\\d{8}"]]], "882": ["882", 0, "[13]\\d{6}(?:\\d{2,5})?|[19]\\d{7}|(?:[25]\\d\\d|4)\\d{7}(?:\\d{2})?", [7, 8, 9, 10, 11, 12], [["(\\d{2})(\\d{5})", "$1 $2", ["16|342"]], ["(\\d{2})(\\d{6})", "$1 $2", ["49"]], ["(\\d{2})(\\d{2})(\\d{4})", "$1 $2 $3", ["1[36]|9"]], ["(\\d{2})(\\d{4})(\\d{3})", "$1 $2 $3", ["3[23]"]], ["(\\d{2})(\\d{3,4})(\\d{4})", "$1 $2 $3", ["16"]], ["(\\d{2})(\\d{4})(\\d{4})", "$1 $2 $3", ["10|23|3(?:[15]|4[57])|4|51"]], ["(\\d{3})(\\d{4})(\\d{4})", "$1 $2 $3", ["34"]], ["(\\d{2})(\\d{4,5})(\\d{5})", "$1 $2 $3", ["[1-35]"]]], 0, 0, 0, 0, 0, 0, [0, ["342\\d{4}|(?:337|49)\\d{6}|(?:3(?:2|47|7\\d{3})|50\\d{3})\\d{7}", [7, 8, 9, 10, 12]], 0, 0, 0, ["348[57]\\d{7}", [11]], 0, 0, ["1(?:3(?:0[0347]|[13][0139]|2[035]|4[013568]|6[0459]|7[06]|8[15-8]|9[0689])\\d{4}|6\\d{5,10})|(?:345\\d|9[89])\\d{6}|(?:10|2(?:3|85\\d)|3(?:[15]|[69]\\d\\d)|4[15-8]|51)\\d{8}"]]], "883": ["883", 0, "(?:[1-4]\\d|51)\\d{6,10}", [8, 9, 10, 11, 12], [["(\\d{3})(\\d{3})(\\d{2,8})", "$1 $2 $3", ["[14]|2[24-689]|3[02-689]|51[24-9]"]], ["(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3", ["510"]], ["(\\d{3})(\\d{3})(\\d{4})", "$1 $2 $3", ["21"]], ["(\\d{4})(\\d{4})(\\d{4})", "$1 $2 $3", ["51[13]"]], ["(\\d{3})(\\d{3})(\\d{3})(\\d{3})", "$1 $2 $3 $4", ["[235]"]]], 0, 0, 0, 0, 0, 0, [0, 0, 0, 0, 0, 0, 0, 0, ["(?:2(?:00\\d\\d|10)|(?:370[1-9]|51\\d0)\\d)\\d{7}|51(?:00\\d{5}|[24-9]0\\d{4,7})|(?:1[0-79]|2[24-689]|3[02-689]|4[0-4])0\\d{5,9}"]]], "888": ["888", 0, "\\d{11}", [11], [["(\\d{3})(\\d{3})(\\d{5})", "$1 $2 $3"]], 0, 0, 0, 0, 0, 0, [0, 0, 0, 0, 0, 0, ["\\d{11}"]]], "979": ["979", 0, "[1359]\\d{8}", [9], [["(\\d)(\\d{4})(\\d{4})", "$1 $2 $3", ["[1359]"]]], 0, 0, 0, 0, 0, 0, [0, 0, 0, ["[1359]\\d{8}"]]] } };

// ../../node_modules/libphonenumber-js/min/exports/withMetadataArgument.js
function withMetadataArgument(func, _arguments) {
  var args = Array.prototype.slice.call(_arguments);
  args.push(metadata_min_json_default);
  return func.apply(this, args);
}

// ../../node_modules/libphonenumber-js/es6/ParseError.js
function _typeof(obj) {
  "@babel/helpers - typeof";
  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(obj2) {
    return typeof obj2;
  } : function(obj2) {
    return obj2 && "function" == typeof Symbol && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
  }, _typeof(obj);
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", { writable: false });
  return Constructor;
}
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } });
  Object.defineProperty(subClass, "prototype", { writable: false });
  if (superClass) _setPrototypeOf(subClass, superClass);
}
function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();
  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived), result2;
    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;
      result2 = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result2 = Super.apply(this, arguments);
    }
    return _possibleConstructorReturn(this, result2);
  };
}
function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }
  return _assertThisInitialized(self);
}
function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}
function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? /* @__PURE__ */ new Map() : void 0;
  _wrapNativeSuper = function _wrapNativeSuper2(Class2) {
    if (Class2 === null || !_isNativeFunction(Class2)) return Class2;
    if (typeof Class2 !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }
    if (typeof _cache !== "undefined") {
      if (_cache.has(Class2)) return _cache.get(Class2);
      _cache.set(Class2, Wrapper);
    }
    function Wrapper() {
      return _construct(Class2, arguments, _getPrototypeOf(this).constructor);
    }
    Wrapper.prototype = Object.create(Class2.prototype, { constructor: { value: Wrapper, enumerable: false, writable: true, configurable: true } });
    return _setPrototypeOf(Wrapper, Class2);
  };
  return _wrapNativeSuper(Class);
}
function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct2(Parent2, args2, Class2) {
      var a = [null];
      a.push.apply(a, args2);
      var Constructor = Function.bind.apply(Parent2, a);
      var instance = new Constructor();
      if (Class2) _setPrototypeOf(instance, Class2.prototype);
      return instance;
    };
  }
  return _construct.apply(null, arguments);
}
function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;
  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {
    }));
    return true;
  } catch (e) {
    return false;
  }
}
function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}
function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf2(o2, p2) {
    o2.__proto__ = p2;
    return o2;
  };
  return _setPrototypeOf(o, p);
}
function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf2(o2) {
    return o2.__proto__ || Object.getPrototypeOf(o2);
  };
  return _getPrototypeOf(o);
}
var ParseError = /* @__PURE__ */ function(_Error) {
  _inherits(ParseError2, _Error);
  var _super = _createSuper(ParseError2);
  function ParseError2(code) {
    var _this;
    _classCallCheck(this, ParseError2);
    _this = _super.call(this, code);
    Object.setPrototypeOf(_assertThisInitialized(_this), ParseError2.prototype);
    _this.name = _this.constructor.name;
    return _this;
  }
  return _createClass(ParseError2);
}(/* @__PURE__ */ _wrapNativeSuper(Error));

// ../../node_modules/libphonenumber-js/es6/constants.js
var MIN_LENGTH_FOR_NSN = 2;
var MAX_LENGTH_FOR_NSN = 17;
var MAX_LENGTH_COUNTRY_CODE = 3;
var VALID_DIGITS = "0-9\uFF10-\uFF19\u0660-\u0669\u06F0-\u06F9";
var DASHES = "-\u2010-\u2015\u2212\u30FC\uFF0D";
var SLASHES = "\uFF0F/";
var DOTS = "\uFF0E.";
var WHITESPACE = " \xA0\xAD\u200B\u2060\u3000";
var BRACKETS = "()\uFF08\uFF09\uFF3B\uFF3D\\[\\]";
var TILDES = "~\u2053\u223C\uFF5E";
var VALID_PUNCTUATION = "".concat(DASHES).concat(SLASHES).concat(DOTS).concat(WHITESPACE).concat(BRACKETS).concat(TILDES);
var PLUS_CHARS = "+\uFF0B";

// ../../node_modules/libphonenumber-js/es6/tools/semver-compare.js
function semver_compare_default(a, b) {
  a = a.split("-");
  b = b.split("-");
  var pa = a[0].split(".");
  var pb = b[0].split(".");
  for (var i = 0; i < 3; i++) {
    var na = Number(pa[i]);
    var nb = Number(pb[i]);
    if (na > nb) return 1;
    if (nb > na) return -1;
    if (!isNaN(na) && isNaN(nb)) return 1;
    if (isNaN(na) && !isNaN(nb)) return -1;
  }
  if (a[1] && b[1]) {
    return a[1] > b[1] ? 1 : a[1] < b[1] ? -1 : 0;
  }
  return !a[1] && b[1] ? 1 : a[1] && !b[1] ? -1 : 0;
}

// ../../node_modules/libphonenumber-js/es6/helpers/isObject.js
var objectConstructor = {}.constructor;
function isObject(object) {
  return object !== void 0 && object !== null && object.constructor === objectConstructor;
}

// ../../node_modules/libphonenumber-js/es6/metadata.js
function _typeof2(obj) {
  "@babel/helpers - typeof";
  return _typeof2 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(obj2) {
    return typeof obj2;
  } : function(obj2) {
    return obj2 && "function" == typeof Symbol && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
  }, _typeof2(obj);
}
function _classCallCheck2(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties2(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass2(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties2(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties2(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", { writable: false });
  return Constructor;
}
var V3 = "1.2.0";
var V4 = "1.7.35";
var DEFAULT_EXT_PREFIX = " ext. ";
var CALLING_CODE_REG_EXP = /^\d+$/;
var Metadata = /* @__PURE__ */ function() {
  function Metadata2(metadata) {
    _classCallCheck2(this, Metadata2);
    validateMetadata(metadata);
    this.metadata = metadata;
    setVersion.call(this, metadata);
  }
  _createClass2(Metadata2, [{
    key: "getCountries",
    value: function getCountries() {
      return Object.keys(this.metadata.countries).filter(function(_) {
        return _ !== "001";
      });
    }
  }, {
    key: "getCountryMetadata",
    value: function getCountryMetadata(countryCode) {
      return this.metadata.countries[countryCode];
    }
  }, {
    key: "nonGeographic",
    value: function nonGeographic() {
      if (this.v1 || this.v2 || this.v3) return;
      return this.metadata.nonGeographic || this.metadata.nonGeographical;
    }
  }, {
    key: "hasCountry",
    value: function hasCountry(country) {
      return this.getCountryMetadata(country) !== void 0;
    }
  }, {
    key: "hasCallingCode",
    value: function hasCallingCode(callingCode) {
      if (this.getCountryCodesForCallingCode(callingCode)) {
        return true;
      }
      if (this.nonGeographic()) {
        if (this.nonGeographic()[callingCode]) {
          return true;
        }
      } else {
        var countryCodes = this.countryCallingCodes()[callingCode];
        if (countryCodes && countryCodes.length === 1 && countryCodes[0] === "001") {
          return true;
        }
      }
    }
  }, {
    key: "isNonGeographicCallingCode",
    value: function isNonGeographicCallingCode(callingCode) {
      if (this.nonGeographic()) {
        return this.nonGeographic()[callingCode] ? true : false;
      } else {
        return this.getCountryCodesForCallingCode(callingCode) ? false : true;
      }
    }
    // Deprecated.
  }, {
    key: "country",
    value: function country(countryCode) {
      return this.selectNumberingPlan(countryCode);
    }
  }, {
    key: "selectNumberingPlan",
    value: function selectNumberingPlan(countryCode, callingCode) {
      if (countryCode && CALLING_CODE_REG_EXP.test(countryCode)) {
        callingCode = countryCode;
        countryCode = null;
      }
      if (countryCode && countryCode !== "001") {
        if (!this.hasCountry(countryCode)) {
          throw new Error("Unknown country: ".concat(countryCode));
        }
        this.numberingPlan = new NumberingPlan(this.getCountryMetadata(countryCode), this);
      } else if (callingCode) {
        if (!this.hasCallingCode(callingCode)) {
          throw new Error("Unknown calling code: ".concat(callingCode));
        }
        this.numberingPlan = new NumberingPlan(this.getNumberingPlanMetadata(callingCode), this);
      } else {
        this.numberingPlan = void 0;
      }
      return this;
    }
  }, {
    key: "getCountryCodesForCallingCode",
    value: function getCountryCodesForCallingCode(callingCode) {
      var countryCodes = this.countryCallingCodes()[callingCode];
      if (countryCodes) {
        if (countryCodes.length === 1 && countryCodes[0].length === 3) {
          return;
        }
        return countryCodes;
      }
    }
  }, {
    key: "getCountryCodeForCallingCode",
    value: function getCountryCodeForCallingCode(callingCode) {
      var countryCodes = this.getCountryCodesForCallingCode(callingCode);
      if (countryCodes) {
        return countryCodes[0];
      }
    }
  }, {
    key: "getNumberingPlanMetadata",
    value: function getNumberingPlanMetadata(callingCode) {
      var countryCode = this.getCountryCodeForCallingCode(callingCode);
      if (countryCode) {
        return this.getCountryMetadata(countryCode);
      }
      if (this.nonGeographic()) {
        var metadata = this.nonGeographic()[callingCode];
        if (metadata) {
          return metadata;
        }
      } else {
        var countryCodes = this.countryCallingCodes()[callingCode];
        if (countryCodes && countryCodes.length === 1 && countryCodes[0] === "001") {
          return this.metadata.countries["001"];
        }
      }
    }
    // Deprecated.
  }, {
    key: "countryCallingCode",
    value: function countryCallingCode() {
      return this.numberingPlan.callingCode();
    }
    // Deprecated.
  }, {
    key: "IDDPrefix",
    value: function IDDPrefix() {
      return this.numberingPlan.IDDPrefix();
    }
    // Deprecated.
  }, {
    key: "defaultIDDPrefix",
    value: function defaultIDDPrefix() {
      return this.numberingPlan.defaultIDDPrefix();
    }
    // Deprecated.
  }, {
    key: "nationalNumberPattern",
    value: function nationalNumberPattern() {
      return this.numberingPlan.nationalNumberPattern();
    }
    // Deprecated.
  }, {
    key: "possibleLengths",
    value: function possibleLengths() {
      return this.numberingPlan.possibleLengths();
    }
    // Deprecated.
  }, {
    key: "formats",
    value: function formats() {
      return this.numberingPlan.formats();
    }
    // Deprecated.
  }, {
    key: "nationalPrefixForParsing",
    value: function nationalPrefixForParsing() {
      return this.numberingPlan.nationalPrefixForParsing();
    }
    // Deprecated.
  }, {
    key: "nationalPrefixTransformRule",
    value: function nationalPrefixTransformRule() {
      return this.numberingPlan.nationalPrefixTransformRule();
    }
    // Deprecated.
  }, {
    key: "leadingDigits",
    value: function leadingDigits() {
      return this.numberingPlan.leadingDigits();
    }
    // Deprecated.
  }, {
    key: "hasTypes",
    value: function hasTypes() {
      return this.numberingPlan.hasTypes();
    }
    // Deprecated.
  }, {
    key: "type",
    value: function type(_type) {
      return this.numberingPlan.type(_type);
    }
    // Deprecated.
  }, {
    key: "ext",
    value: function ext() {
      return this.numberingPlan.ext();
    }
  }, {
    key: "countryCallingCodes",
    value: function countryCallingCodes() {
      if (this.v1) return this.metadata.country_phone_code_to_countries;
      return this.metadata.country_calling_codes;
    }
    // Deprecated.
  }, {
    key: "chooseCountryByCountryCallingCode",
    value: function chooseCountryByCountryCallingCode(callingCode) {
      return this.selectNumberingPlan(callingCode);
    }
  }, {
    key: "hasSelectedNumberingPlan",
    value: function hasSelectedNumberingPlan() {
      return this.numberingPlan !== void 0;
    }
  }]);
  return Metadata2;
}();
var NumberingPlan = /* @__PURE__ */ function() {
  function NumberingPlan2(metadata, globalMetadataObject) {
    _classCallCheck2(this, NumberingPlan2);
    this.globalMetadataObject = globalMetadataObject;
    this.metadata = metadata;
    setVersion.call(this, globalMetadataObject.metadata);
  }
  _createClass2(NumberingPlan2, [{
    key: "callingCode",
    value: function callingCode() {
      return this.metadata[0];
    }
    // Formatting information for regions which share
    // a country calling code is contained by only one region
    // for performance reasons. For example, for NANPA region
    // ("North American Numbering Plan Administration",
    //  which includes USA, Canada, Cayman Islands, Bahamas, etc)
    // it will be contained in the metadata for `US`.
  }, {
    key: "getDefaultCountryMetadataForRegion",
    value: function getDefaultCountryMetadataForRegion() {
      return this.globalMetadataObject.getNumberingPlanMetadata(this.callingCode());
    }
    // Is always present.
  }, {
    key: "IDDPrefix",
    value: function IDDPrefix() {
      if (this.v1 || this.v2) return;
      return this.metadata[1];
    }
    // Is only present when a country supports multiple IDD prefixes.
  }, {
    key: "defaultIDDPrefix",
    value: function defaultIDDPrefix() {
      if (this.v1 || this.v2) return;
      return this.metadata[12];
    }
  }, {
    key: "nationalNumberPattern",
    value: function nationalNumberPattern() {
      if (this.v1 || this.v2) return this.metadata[1];
      return this.metadata[2];
    }
    // "possible length" data is always present in Google's metadata.
  }, {
    key: "possibleLengths",
    value: function possibleLengths() {
      if (this.v1) return;
      return this.metadata[this.v2 ? 2 : 3];
    }
  }, {
    key: "_getFormats",
    value: function _getFormats(metadata) {
      return metadata[this.v1 ? 2 : this.v2 ? 3 : 4];
    }
    // For countries of the same region (e.g. NANPA)
    // formats are all stored in the "main" country for that region.
    // E.g. "RU" and "KZ", "US" and "CA".
  }, {
    key: "formats",
    value: function formats() {
      var _this = this;
      var formats2 = this._getFormats(this.metadata) || this._getFormats(this.getDefaultCountryMetadataForRegion()) || [];
      return formats2.map(function(_) {
        return new Format(_, _this);
      });
    }
  }, {
    key: "nationalPrefix",
    value: function nationalPrefix() {
      return this.metadata[this.v1 ? 3 : this.v2 ? 4 : 5];
    }
  }, {
    key: "_getNationalPrefixFormattingRule",
    value: function _getNationalPrefixFormattingRule(metadata) {
      return metadata[this.v1 ? 4 : this.v2 ? 5 : 6];
    }
    // For countries of the same region (e.g. NANPA)
    // national prefix formatting rule is stored in the "main" country for that region.
    // E.g. "RU" and "KZ", "US" and "CA".
  }, {
    key: "nationalPrefixFormattingRule",
    value: function nationalPrefixFormattingRule() {
      return this._getNationalPrefixFormattingRule(this.metadata) || this._getNationalPrefixFormattingRule(this.getDefaultCountryMetadataForRegion());
    }
  }, {
    key: "_nationalPrefixForParsing",
    value: function _nationalPrefixForParsing() {
      return this.metadata[this.v1 ? 5 : this.v2 ? 6 : 7];
    }
  }, {
    key: "nationalPrefixForParsing",
    value: function nationalPrefixForParsing() {
      return this._nationalPrefixForParsing() || this.nationalPrefix();
    }
  }, {
    key: "nationalPrefixTransformRule",
    value: function nationalPrefixTransformRule() {
      return this.metadata[this.v1 ? 6 : this.v2 ? 7 : 8];
    }
  }, {
    key: "_getNationalPrefixIsOptionalWhenFormatting",
    value: function _getNationalPrefixIsOptionalWhenFormatting() {
      return !!this.metadata[this.v1 ? 7 : this.v2 ? 8 : 9];
    }
    // For countries of the same region (e.g. NANPA)
    // "national prefix is optional when formatting" flag is
    // stored in the "main" country for that region.
    // E.g. "RU" and "KZ", "US" and "CA".
  }, {
    key: "nationalPrefixIsOptionalWhenFormattingInNationalFormat",
    value: function nationalPrefixIsOptionalWhenFormattingInNationalFormat() {
      return this._getNationalPrefixIsOptionalWhenFormatting(this.metadata) || this._getNationalPrefixIsOptionalWhenFormatting(this.getDefaultCountryMetadataForRegion());
    }
  }, {
    key: "leadingDigits",
    value: function leadingDigits() {
      return this.metadata[this.v1 ? 8 : this.v2 ? 9 : 10];
    }
  }, {
    key: "types",
    value: function types() {
      return this.metadata[this.v1 ? 9 : this.v2 ? 10 : 11];
    }
  }, {
    key: "hasTypes",
    value: function hasTypes() {
      if (this.types() && this.types().length === 0) {
        return false;
      }
      return !!this.types();
    }
  }, {
    key: "type",
    value: function type(_type2) {
      if (this.hasTypes() && getType(this.types(), _type2)) {
        return new Type(getType(this.types(), _type2), this);
      }
    }
  }, {
    key: "ext",
    value: function ext() {
      if (this.v1 || this.v2) return DEFAULT_EXT_PREFIX;
      return this.metadata[13] || DEFAULT_EXT_PREFIX;
    }
  }]);
  return NumberingPlan2;
}();
var Format = /* @__PURE__ */ function() {
  function Format2(format3, metadata) {
    _classCallCheck2(this, Format2);
    this._format = format3;
    this.metadata = metadata;
  }
  _createClass2(Format2, [{
    key: "pattern",
    value: function pattern() {
      return this._format[0];
    }
  }, {
    key: "format",
    value: function format3() {
      return this._format[1];
    }
  }, {
    key: "leadingDigitsPatterns",
    value: function leadingDigitsPatterns() {
      return this._format[2] || [];
    }
  }, {
    key: "nationalPrefixFormattingRule",
    value: function nationalPrefixFormattingRule() {
      return this._format[3] || this.metadata.nationalPrefixFormattingRule();
    }
  }, {
    key: "nationalPrefixIsOptionalWhenFormattingInNationalFormat",
    value: function nationalPrefixIsOptionalWhenFormattingInNationalFormat() {
      return !!this._format[4] || this.metadata.nationalPrefixIsOptionalWhenFormattingInNationalFormat();
    }
  }, {
    key: "nationalPrefixIsMandatoryWhenFormattingInNationalFormat",
    value: function nationalPrefixIsMandatoryWhenFormattingInNationalFormat() {
      return this.usesNationalPrefix() && !this.nationalPrefixIsOptionalWhenFormattingInNationalFormat();
    }
    // Checks whether national prefix formatting rule contains national prefix.
  }, {
    key: "usesNationalPrefix",
    value: function usesNationalPrefix() {
      return this.nationalPrefixFormattingRule() && // Check that national prefix formatting rule is not a "dummy" one.
      !FIRST_GROUP_ONLY_PREFIX_PATTERN.test(this.nationalPrefixFormattingRule()) ? true : false;
    }
  }, {
    key: "internationalFormat",
    value: function internationalFormat() {
      return this._format[5] || this.format();
    }
  }]);
  return Format2;
}();
var FIRST_GROUP_ONLY_PREFIX_PATTERN = /^\(?\$1\)?$/;
var Type = /* @__PURE__ */ function() {
  function Type2(type, metadata) {
    _classCallCheck2(this, Type2);
    this.type = type;
    this.metadata = metadata;
  }
  _createClass2(Type2, [{
    key: "pattern",
    value: function pattern() {
      if (this.metadata.v1) return this.type;
      return this.type[0];
    }
  }, {
    key: "possibleLengths",
    value: function possibleLengths() {
      if (this.metadata.v1) return;
      return this.type[1] || this.metadata.possibleLengths();
    }
  }]);
  return Type2;
}();
function getType(types, type) {
  switch (type) {
    case "FIXED_LINE":
      return types[0];
    case "MOBILE":
      return types[1];
    case "TOLL_FREE":
      return types[2];
    case "PREMIUM_RATE":
      return types[3];
    case "PERSONAL_NUMBER":
      return types[4];
    case "VOICEMAIL":
      return types[5];
    case "UAN":
      return types[6];
    case "PAGER":
      return types[7];
    case "VOIP":
      return types[8];
    case "SHARED_COST":
      return types[9];
  }
}
function validateMetadata(metadata) {
  if (!metadata) {
    throw new Error("[libphonenumber-js] `metadata` argument not passed. Check your arguments.");
  }
  if (!isObject(metadata) || !isObject(metadata.countries)) {
    throw new Error("[libphonenumber-js] `metadata` argument was passed but it's not a valid metadata. Must be an object having `.countries` child object property. Got ".concat(isObject(metadata) ? "an object of shape: { " + Object.keys(metadata).join(", ") + " }" : "a " + typeOf(metadata) + ": " + metadata, "."));
  }
}
var typeOf = function typeOf2(_) {
  return _typeof2(_);
};
function getCountryCallingCode(country, metadata) {
  metadata = new Metadata(metadata);
  if (metadata.hasCountry(country)) {
    return metadata.country(country).countryCallingCode();
  }
  throw new Error("Unknown country: ".concat(country));
}
function isSupportedCountry(country, metadata) {
  return metadata.countries.hasOwnProperty(country);
}
function setVersion(metadata) {
  var version = metadata.version;
  if (typeof version === "number") {
    this.v1 = version === 1;
    this.v2 = version === 2;
    this.v3 = version === 3;
    this.v4 = version === 4;
  } else {
    if (!version) {
      this.v1 = true;
    } else if (semver_compare_default(version, V3) === -1) {
      this.v2 = true;
    } else if (semver_compare_default(version, V4) === -1) {
      this.v3 = true;
    } else {
      this.v4 = true;
    }
  }
}

// ../../node_modules/libphonenumber-js/es6/helpers/extension/createExtensionPattern.js
var RFC3966_EXTN_PREFIX = ";ext=";
var getExtensionDigitsPattern = function getExtensionDigitsPattern2(maxLength) {
  return "([".concat(VALID_DIGITS, "]{1,").concat(maxLength, "})");
};
function createExtensionPattern(purpose) {
  var extLimitAfterExplicitLabel = "20";
  var extLimitAfterLikelyLabel = "15";
  var extLimitAfterAmbiguousChar = "9";
  var extLimitWhenNotSure = "6";
  var possibleSeparatorsBetweenNumberAndExtLabel = "[ \xA0\\t,]*";
  var possibleCharsAfterExtLabel = "[:\\.\uFF0E]?[ \xA0\\t,-]*";
  var optionalExtnSuffix = "#?";
  var explicitExtLabels = "(?:e?xt(?:ensi(?:o\u0301?|\xF3))?n?|\uFF45?\uFF58\uFF54\uFF4E?|\u0434\u043E\u0431|anexo)";
  var ambiguousExtLabels = "(?:[x\uFF58#\uFF03~\uFF5E]|int|\uFF49\uFF4E\uFF54)";
  var ambiguousSeparator = "[- ]+";
  var possibleSeparatorsNumberExtLabelNoComma = "[ \xA0\\t]*";
  var autoDiallingAndExtLabelsFound = "(?:,{2}|;)";
  var rfcExtn = RFC3966_EXTN_PREFIX + getExtensionDigitsPattern(extLimitAfterExplicitLabel);
  var explicitExtn = possibleSeparatorsBetweenNumberAndExtLabel + explicitExtLabels + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterExplicitLabel) + optionalExtnSuffix;
  var ambiguousExtn = possibleSeparatorsBetweenNumberAndExtLabel + ambiguousExtLabels + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterAmbiguousChar) + optionalExtnSuffix;
  var americanStyleExtnWithSuffix = ambiguousSeparator + getExtensionDigitsPattern(extLimitWhenNotSure) + "#";
  var autoDiallingExtn = possibleSeparatorsNumberExtLabelNoComma + autoDiallingAndExtLabelsFound + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterLikelyLabel) + optionalExtnSuffix;
  var onlyCommasExtn = possibleSeparatorsNumberExtLabelNoComma + "(?:,)+" + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterAmbiguousChar) + optionalExtnSuffix;
  return rfcExtn + "|" + explicitExtn + "|" + ambiguousExtn + "|" + americanStyleExtnWithSuffix + "|" + autoDiallingExtn + "|" + onlyCommasExtn;
}

// ../../node_modules/libphonenumber-js/es6/helpers/isViablePhoneNumber.js
var MIN_LENGTH_PHONE_NUMBER_PATTERN = "[" + VALID_DIGITS + "]{" + MIN_LENGTH_FOR_NSN + "}";
var VALID_PHONE_NUMBER = "[" + PLUS_CHARS + "]{0,1}(?:[" + VALID_PUNCTUATION + "]*[" + VALID_DIGITS + "]){3,}[" + VALID_PUNCTUATION + VALID_DIGITS + "]*";
var VALID_PHONE_NUMBER_START_REG_EXP = new RegExp("^[" + PLUS_CHARS + "]{0,1}(?:[" + VALID_PUNCTUATION + "]*[" + VALID_DIGITS + "]){1,2}$", "i");
var VALID_PHONE_NUMBER_WITH_EXTENSION = VALID_PHONE_NUMBER + // Phone number extensions
"(?:" + createExtensionPattern() + ")?";
var VALID_PHONE_NUMBER_PATTERN = new RegExp(
  // Either a short two-digit-only phone number
  "^" + MIN_LENGTH_PHONE_NUMBER_PATTERN + "$|^" + VALID_PHONE_NUMBER_WITH_EXTENSION + "$",
  "i"
);
function isViablePhoneNumber(number) {
  return number.length >= MIN_LENGTH_FOR_NSN && VALID_PHONE_NUMBER_PATTERN.test(number);
}
function isViablePhoneNumberStart(number) {
  return VALID_PHONE_NUMBER_START_REG_EXP.test(number);
}

// ../../node_modules/libphonenumber-js/es6/helpers/extension/extractExtension.js
var EXTN_PATTERN = new RegExp("(?:" + createExtensionPattern() + ")$", "i");
function extractExtension(number) {
  var start = number.search(EXTN_PATTERN);
  if (start < 0) {
    return {};
  }
  var numberWithoutExtension = number.slice(0, start);
  var matches = number.match(EXTN_PATTERN);
  var i = 1;
  while (i < matches.length) {
    if (matches[i]) {
      return {
        number: numberWithoutExtension,
        ext: matches[i]
      };
    }
    i++;
  }
}

// ../../node_modules/libphonenumber-js/es6/helpers/parseDigits.js
var DIGITS = {
  "0": "0",
  "1": "1",
  "2": "2",
  "3": "3",
  "4": "4",
  "5": "5",
  "6": "6",
  "7": "7",
  "8": "8",
  "9": "9",
  "\uFF10": "0",
  // Fullwidth digit 0
  "\uFF11": "1",
  // Fullwidth digit 1
  "\uFF12": "2",
  // Fullwidth digit 2
  "\uFF13": "3",
  // Fullwidth digit 3
  "\uFF14": "4",
  // Fullwidth digit 4
  "\uFF15": "5",
  // Fullwidth digit 5
  "\uFF16": "6",
  // Fullwidth digit 6
  "\uFF17": "7",
  // Fullwidth digit 7
  "\uFF18": "8",
  // Fullwidth digit 8
  "\uFF19": "9",
  // Fullwidth digit 9
  "\u0660": "0",
  // Arabic-indic digit 0
  "\u0661": "1",
  // Arabic-indic digit 1
  "\u0662": "2",
  // Arabic-indic digit 2
  "\u0663": "3",
  // Arabic-indic digit 3
  "\u0664": "4",
  // Arabic-indic digit 4
  "\u0665": "5",
  // Arabic-indic digit 5
  "\u0666": "6",
  // Arabic-indic digit 6
  "\u0667": "7",
  // Arabic-indic digit 7
  "\u0668": "8",
  // Arabic-indic digit 8
  "\u0669": "9",
  // Arabic-indic digit 9
  "\u06F0": "0",
  // Eastern-Arabic digit 0
  "\u06F1": "1",
  // Eastern-Arabic digit 1
  "\u06F2": "2",
  // Eastern-Arabic digit 2
  "\u06F3": "3",
  // Eastern-Arabic digit 3
  "\u06F4": "4",
  // Eastern-Arabic digit 4
  "\u06F5": "5",
  // Eastern-Arabic digit 5
  "\u06F6": "6",
  // Eastern-Arabic digit 6
  "\u06F7": "7",
  // Eastern-Arabic digit 7
  "\u06F8": "8",
  // Eastern-Arabic digit 8
  "\u06F9": "9"
  // Eastern-Arabic digit 9
};
function parseDigit(character) {
  return DIGITS[character];
}

// ../../node_modules/libphonenumber-js/es6/parseIncompletePhoneNumber.js
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function() {
      if (i >= o.length) return { done: true };
      return { done: false, value: o[i++] };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function parseIncompletePhoneNumber(string) {
  var result2 = "";
  for (var _iterator = _createForOfIteratorHelperLoose(string.split("")), _step; !(_step = _iterator()).done; ) {
    var character = _step.value;
    result2 += parsePhoneNumberCharacter(character, result2) || "";
  }
  return result2;
}
function parsePhoneNumberCharacter(character, prevParsedCharacters, emitEvent) {
  if (character === "+") {
    if (prevParsedCharacters) {
      if (typeof emitEvent === "function") {
        emitEvent("end");
      }
      return;
    }
    return "+";
  }
  return parseDigit(character);
}

// ../../node_modules/libphonenumber-js/es6/helpers/mergeArrays.js
function _createForOfIteratorHelperLoose2(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray2(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function() {
      if (i >= o.length) return { done: true };
      return { done: false, value: o[i++] };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray2(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray2(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray2(o, minLen);
}
function _arrayLikeToArray2(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function mergeArrays(a, b) {
  var merged = a.slice();
  for (var _iterator = _createForOfIteratorHelperLoose2(b), _step; !(_step = _iterator()).done; ) {
    var element = _step.value;
    if (a.indexOf(element) < 0) {
      merged.push(element);
    }
  }
  return merged.sort(function(a2, b2) {
    return a2 - b2;
  });
}

// ../../node_modules/libphonenumber-js/es6/helpers/checkNumberLength.js
function checkNumberLength(nationalNumber, metadata) {
  return checkNumberLengthForType(nationalNumber, void 0, metadata);
}
function checkNumberLengthForType(nationalNumber, type, metadata) {
  var type_info = metadata.type(type);
  var possible_lengths = type_info && type_info.possibleLengths() || metadata.possibleLengths();
  if (!possible_lengths) {
    return "IS_POSSIBLE";
  }
  if (type === "FIXED_LINE_OR_MOBILE") {
    if (!metadata.type("FIXED_LINE")) {
      return checkNumberLengthForType(nationalNumber, "MOBILE", metadata);
    }
    var mobile_type = metadata.type("MOBILE");
    if (mobile_type) {
      possible_lengths = mergeArrays(possible_lengths, mobile_type.possibleLengths());
    }
  } else if (type && !type_info) {
    return "INVALID_LENGTH";
  }
  var actual_length = nationalNumber.length;
  var minimum_length = possible_lengths[0];
  if (minimum_length === actual_length) {
    return "IS_POSSIBLE";
  }
  if (minimum_length > actual_length) {
    return "TOO_SHORT";
  }
  if (possible_lengths[possible_lengths.length - 1] < actual_length) {
    return "TOO_LONG";
  }
  return possible_lengths.indexOf(actual_length, 1) >= 0 ? "IS_POSSIBLE" : "INVALID_LENGTH";
}

// ../../node_modules/libphonenumber-js/es6/isPossible.js
function isPossiblePhoneNumber(input, options, metadata) {
  if (options === void 0) {
    options = {};
  }
  metadata = new Metadata(metadata);
  if (options.v2) {
    if (!input.countryCallingCode) {
      throw new Error("Invalid phone number object passed");
    }
    metadata.selectNumberingPlan(input.countryCallingCode);
  } else {
    if (!input.phone) {
      return false;
    }
    if (input.country) {
      if (!metadata.hasCountry(input.country)) {
        throw new Error("Unknown country: ".concat(input.country));
      }
      metadata.country(input.country);
    } else {
      if (!input.countryCallingCode) {
        throw new Error("Invalid phone number object passed");
      }
      metadata.selectNumberingPlan(input.countryCallingCode);
    }
  }
  if (metadata.possibleLengths()) {
    return isPossibleNumber(input.phone || input.nationalNumber, metadata);
  } else {
    if (input.countryCallingCode && metadata.isNonGeographicCallingCode(input.countryCallingCode)) {
      return true;
    } else {
      throw new Error('Missing "possibleLengths" in metadata. Perhaps the metadata has been generated before v1.0.18.');
    }
  }
}
function isPossibleNumber(nationalNumber, metadata) {
  switch (checkNumberLength(nationalNumber, metadata)) {
    case "IS_POSSIBLE":
      return true;
    // This library ignores "local-only" phone numbers (for simplicity).
    // See the readme for more info on what are "local-only" phone numbers.
    // case 'IS_POSSIBLE_LOCAL_ONLY':
    // 	return !isInternational
    default:
      return false;
  }
}

// ../../node_modules/libphonenumber-js/es6/helpers/matchesEntirely.js
function matchesEntirely(text, regular_expression) {
  text = text || "";
  return new RegExp("^(?:" + regular_expression + ")$").test(text);
}

// ../../node_modules/libphonenumber-js/es6/helpers/getNumberType.js
function _createForOfIteratorHelperLoose3(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray3(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function() {
      if (i >= o.length) return { done: true };
      return { done: false, value: o[i++] };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray3(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray3(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray3(o, minLen);
}
function _arrayLikeToArray3(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
var NON_FIXED_LINE_PHONE_TYPES = ["MOBILE", "PREMIUM_RATE", "TOLL_FREE", "SHARED_COST", "VOIP", "PERSONAL_NUMBER", "PAGER", "UAN", "VOICEMAIL"];
function getNumberType(input, options, metadata) {
  options = options || {};
  if (!input.country && !input.countryCallingCode) {
    return;
  }
  metadata = new Metadata(metadata);
  metadata.selectNumberingPlan(input.country, input.countryCallingCode);
  var nationalNumber = options.v2 ? input.nationalNumber : input.phone;
  if (!matchesEntirely(nationalNumber, metadata.nationalNumberPattern())) {
    return;
  }
  if (isNumberTypeEqualTo(nationalNumber, "FIXED_LINE", metadata)) {
    if (metadata.type("MOBILE") && metadata.type("MOBILE").pattern() === "") {
      return "FIXED_LINE_OR_MOBILE";
    }
    if (!metadata.type("MOBILE")) {
      return "FIXED_LINE_OR_MOBILE";
    }
    if (isNumberTypeEqualTo(nationalNumber, "MOBILE", metadata)) {
      return "FIXED_LINE_OR_MOBILE";
    }
    return "FIXED_LINE";
  }
  for (var _iterator = _createForOfIteratorHelperLoose3(NON_FIXED_LINE_PHONE_TYPES), _step; !(_step = _iterator()).done; ) {
    var type = _step.value;
    if (isNumberTypeEqualTo(nationalNumber, type, metadata)) {
      return type;
    }
  }
}
function isNumberTypeEqualTo(nationalNumber, type, metadata) {
  type = metadata.type(type);
  if (!type || !type.pattern()) {
    return false;
  }
  if (type.possibleLengths() && type.possibleLengths().indexOf(nationalNumber.length) < 0) {
    return false;
  }
  return matchesEntirely(nationalNumber, type.pattern());
}

// ../../node_modules/libphonenumber-js/es6/isValid.js
function isValidNumber(input, options, metadata) {
  options = options || {};
  metadata = new Metadata(metadata);
  metadata.selectNumberingPlan(input.country, input.countryCallingCode);
  if (metadata.hasTypes()) {
    return getNumberType(input, options, metadata.metadata) !== void 0;
  }
  var nationalNumber = options.v2 ? input.nationalNumber : input.phone;
  return matchesEntirely(nationalNumber, metadata.nationalNumberPattern());
}

// ../../node_modules/libphonenumber-js/es6/helpers/getPossibleCountriesForNumber.js
function getPossibleCountriesForNumber(callingCode, nationalNumber, metadata) {
  var _metadata = new Metadata(metadata);
  var possibleCountries = _metadata.getCountryCodesForCallingCode(callingCode);
  if (!possibleCountries) {
    return [];
  }
  return possibleCountries.filter(function(country) {
    return couldNationalNumberBelongToCountry(nationalNumber, country, metadata);
  });
}
function couldNationalNumberBelongToCountry(nationalNumber, country, metadata) {
  var _metadata = new Metadata(metadata);
  _metadata.selectNumberingPlan(country);
  if (_metadata.numberingPlan.possibleLengths().indexOf(nationalNumber.length) >= 0) {
    return true;
  }
  return false;
}

// ../../node_modules/libphonenumber-js/es6/helpers/applyInternationalSeparatorStyle.js
function applyInternationalSeparatorStyle(formattedNumber) {
  return formattedNumber.replace(new RegExp("[".concat(VALID_PUNCTUATION, "]+"), "g"), " ").trim();
}

// ../../node_modules/libphonenumber-js/es6/helpers/formatNationalNumberUsingFormat.js
var FIRST_GROUP_PATTERN = /(\$\d)/;
function formatNationalNumberUsingFormat(number, format3, _ref) {
  var useInternationalFormat = _ref.useInternationalFormat, withNationalPrefix = _ref.withNationalPrefix, carrierCode = _ref.carrierCode, metadata = _ref.metadata;
  var formattedNumber = number.replace(new RegExp(format3.pattern()), useInternationalFormat ? format3.internationalFormat() : (
    // This library doesn't use `domestic_carrier_code_formatting_rule`,
    // because that one is only used when formatting phone numbers
    // for dialing from a mobile phone, and this is not a dialing library.
    // carrierCode && format.domesticCarrierCodeFormattingRule()
    // 	// First, replace the $CC in the formatting rule with the desired carrier code.
    // 	// Then, replace the $FG in the formatting rule with the first group
    // 	// and the carrier code combined in the appropriate way.
    // 	? format.format().replace(FIRST_GROUP_PATTERN, format.domesticCarrierCodeFormattingRule().replace('$CC', carrierCode))
    // 	: (
    // 		withNationalPrefix && format.nationalPrefixFormattingRule()
    // 			? format.format().replace(FIRST_GROUP_PATTERN, format.nationalPrefixFormattingRule())
    // 			: format.format()
    // 	)
    withNationalPrefix && format3.nationalPrefixFormattingRule() ? format3.format().replace(FIRST_GROUP_PATTERN, format3.nationalPrefixFormattingRule()) : format3.format()
  ));
  if (useInternationalFormat) {
    return applyInternationalSeparatorStyle(formattedNumber);
  }
  return formattedNumber;
}

// ../../node_modules/libphonenumber-js/es6/helpers/getIddPrefix.js
var SINGLE_IDD_PREFIX_REG_EXP = /^[\d]+(?:[~\u2053\u223C\uFF5E][\d]+)?$/;
function getIddPrefix(country, callingCode, metadata) {
  var countryMetadata = new Metadata(metadata);
  countryMetadata.selectNumberingPlan(country, callingCode);
  if (countryMetadata.defaultIDDPrefix()) {
    return countryMetadata.defaultIDDPrefix();
  }
  if (SINGLE_IDD_PREFIX_REG_EXP.test(countryMetadata.IDDPrefix())) {
    return countryMetadata.IDDPrefix();
  }
}

// ../../node_modules/libphonenumber-js/es6/helpers/RFC3966.js
function formatRFC3966(_ref) {
  var number = _ref.number, ext = _ref.ext;
  if (!number) {
    return "";
  }
  if (number[0] !== "+") {
    throw new Error('"formatRFC3966()" expects "number" to be in E.164 format.');
  }
  return "tel:".concat(number).concat(ext ? ";ext=" + ext : "");
}

// ../../node_modules/libphonenumber-js/es6/format.js
function _createForOfIteratorHelperLoose4(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray4(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function() {
      if (i >= o.length) return { done: true };
      return { done: false, value: o[i++] };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray4(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray4(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray4(o, minLen);
}
function _arrayLikeToArray4(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), true).forEach(function(key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
var DEFAULT_OPTIONS = {
  formatExtension: function formatExtension(formattedNumber, extension, metadata) {
    return "".concat(formattedNumber).concat(metadata.ext()).concat(extension);
  }
};
function formatNumber(input, format3, options, metadata) {
  if (options) {
    options = _objectSpread(_objectSpread({}, DEFAULT_OPTIONS), options);
  } else {
    options = DEFAULT_OPTIONS;
  }
  metadata = new Metadata(metadata);
  if (input.country && input.country !== "001") {
    if (!metadata.hasCountry(input.country)) {
      throw new Error("Unknown country: ".concat(input.country));
    }
    metadata.country(input.country);
  } else if (input.countryCallingCode) {
    metadata.selectNumberingPlan(input.countryCallingCode);
  } else return input.phone || "";
  var countryCallingCode = metadata.countryCallingCode();
  var nationalNumber = options.v2 ? input.nationalNumber : input.phone;
  var number;
  switch (format3) {
    case "NATIONAL":
      if (!nationalNumber) {
        return "";
      }
      number = formatNationalNumber(nationalNumber, input.carrierCode, "NATIONAL", metadata, options);
      return addExtension(number, input.ext, metadata, options.formatExtension);
    case "INTERNATIONAL":
      if (!nationalNumber) {
        return "+".concat(countryCallingCode);
      }
      number = formatNationalNumber(nationalNumber, null, "INTERNATIONAL", metadata, options);
      number = "+".concat(countryCallingCode, " ").concat(number);
      return addExtension(number, input.ext, metadata, options.formatExtension);
    case "E.164":
      return "+".concat(countryCallingCode).concat(nationalNumber);
    case "RFC3966":
      return formatRFC3966({
        number: "+".concat(countryCallingCode).concat(nationalNumber),
        ext: input.ext
      });
    // For reference, here's Google's IDD formatter:
    // https://github.com/google/libphonenumber/blob/32719cf74e68796788d1ca45abc85dcdc63ba5b9/java/libphonenumber/src/com/google/i18n/phonenumbers/PhoneNumberUtil.java#L1546
    // Not saying that this IDD formatter replicates it 1:1, but it seems to work.
    // Who would even need to format phone numbers in IDD format anyway?
    case "IDD":
      if (!options.fromCountry) {
        return;
      }
      var formattedNumber = formatIDD(nationalNumber, input.carrierCode, countryCallingCode, options.fromCountry, metadata);
      return addExtension(formattedNumber, input.ext, metadata, options.formatExtension);
    default:
      throw new Error('Unknown "format" argument passed to "formatNumber()": "'.concat(format3, '"'));
  }
}
function formatNationalNumber(number, carrierCode, formatAs, metadata, options) {
  var format3 = chooseFormatForNumber(metadata.formats(), number);
  if (!format3) {
    return number;
  }
  return formatNationalNumberUsingFormat(number, format3, {
    useInternationalFormat: formatAs === "INTERNATIONAL",
    withNationalPrefix: format3.nationalPrefixIsOptionalWhenFormattingInNationalFormat() && options && options.nationalPrefix === false ? false : true,
    carrierCode,
    metadata
  });
}
function chooseFormatForNumber(availableFormats, nationalNnumber) {
  for (var _iterator = _createForOfIteratorHelperLoose4(availableFormats), _step; !(_step = _iterator()).done; ) {
    var format3 = _step.value;
    if (format3.leadingDigitsPatterns().length > 0) {
      var lastLeadingDigitsPattern = format3.leadingDigitsPatterns()[format3.leadingDigitsPatterns().length - 1];
      if (nationalNnumber.search(lastLeadingDigitsPattern) !== 0) {
        continue;
      }
    }
    if (matchesEntirely(nationalNnumber, format3.pattern())) {
      return format3;
    }
  }
}
function addExtension(formattedNumber, ext, metadata, formatExtension2) {
  return ext ? formatExtension2(formattedNumber, ext, metadata) : formattedNumber;
}
function formatIDD(nationalNumber, carrierCode, countryCallingCode, fromCountry, metadata) {
  var fromCountryCallingCode = getCountryCallingCode(fromCountry, metadata.metadata);
  if (fromCountryCallingCode === countryCallingCode) {
    var formattedNumber = formatNationalNumber(nationalNumber, carrierCode, "NATIONAL", metadata);
    if (countryCallingCode === "1") {
      return countryCallingCode + " " + formattedNumber;
    }
    return formattedNumber;
  }
  var iddPrefix = getIddPrefix(fromCountry, void 0, metadata.metadata);
  if (iddPrefix) {
    return "".concat(iddPrefix, " ").concat(countryCallingCode, " ").concat(formatNationalNumber(nationalNumber, null, "INTERNATIONAL", metadata));
  }
}

// ../../node_modules/libphonenumber-js/es6/PhoneNumber.js
function ownKeys2(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys2(Object(source), true).forEach(function(key) {
      _defineProperty2(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys2(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty2(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _classCallCheck3(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties3(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass3(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties3(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties3(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", { writable: false });
  return Constructor;
}
var USE_NON_GEOGRAPHIC_COUNTRY_CODE = false;
var PhoneNumber = /* @__PURE__ */ function() {
  function PhoneNumber2(countryOrCountryCallingCode, nationalNumber, metadata) {
    _classCallCheck3(this, PhoneNumber2);
    if (!countryOrCountryCallingCode) {
      throw new TypeError("`country` or `countryCallingCode` not passed");
    }
    if (!nationalNumber) {
      throw new TypeError("`nationalNumber` not passed");
    }
    if (!metadata) {
      throw new TypeError("`metadata` not passed");
    }
    var _getCountryAndCountry = getCountryAndCountryCallingCode(countryOrCountryCallingCode, metadata), country = _getCountryAndCountry.country, countryCallingCode = _getCountryAndCountry.countryCallingCode;
    this.country = country;
    this.countryCallingCode = countryCallingCode;
    this.nationalNumber = nationalNumber;
    this.number = "+" + this.countryCallingCode + this.nationalNumber;
    this.getMetadata = function() {
      return metadata;
    };
  }
  _createClass3(PhoneNumber2, [{
    key: "setExt",
    value: function setExt(ext) {
      this.ext = ext;
    }
  }, {
    key: "getPossibleCountries",
    value: function getPossibleCountries() {
      if (this.country) {
        return [this.country];
      }
      return getPossibleCountriesForNumber(this.countryCallingCode, this.nationalNumber, this.getMetadata());
    }
  }, {
    key: "isPossible",
    value: function isPossible() {
      return isPossiblePhoneNumber(this, {
        v2: true
      }, this.getMetadata());
    }
  }, {
    key: "isValid",
    value: function isValid() {
      return isValidNumber(this, {
        v2: true
      }, this.getMetadata());
    }
  }, {
    key: "isNonGeographic",
    value: function isNonGeographic() {
      var metadata = new Metadata(this.getMetadata());
      return metadata.isNonGeographicCallingCode(this.countryCallingCode);
    }
  }, {
    key: "isEqual",
    value: function isEqual2(phoneNumber) {
      return this.number === phoneNumber.number && this.ext === phoneNumber.ext;
    }
    // This function was originally meant to be an equivalent for `validatePhoneNumberLength()`,
    // but later it was found out that it doesn't include the possible `TOO_SHORT` result
    // returned from `parsePhoneNumberWithError()` in the original `validatePhoneNumberLength()`,
    // so eventually I simply commented out this method from the `PhoneNumber` class
    // and just left the `validatePhoneNumberLength()` function, even though that one would require
    // and additional step to also validate the actual country / calling code of the phone number.
    // validateLength() {
    // 	const metadata = new Metadata(this.getMetadata())
    // 	metadata.selectNumberingPlan(this.countryCallingCode)
    // 	const result = checkNumberLength(this.nationalNumber, metadata)
    // 	if (result !== 'IS_POSSIBLE') {
    // 		return result
    // 	}
    // }
  }, {
    key: "getType",
    value: function getType2() {
      return getNumberType(this, {
        v2: true
      }, this.getMetadata());
    }
  }, {
    key: "format",
    value: function format3(_format, options) {
      return formatNumber(this, _format, options ? _objectSpread2(_objectSpread2({}, options), {}, {
        v2: true
      }) : {
        v2: true
      }, this.getMetadata());
    }
  }, {
    key: "formatNational",
    value: function formatNational(options) {
      return this.format("NATIONAL", options);
    }
  }, {
    key: "formatInternational",
    value: function formatInternational(options) {
      return this.format("INTERNATIONAL", options);
    }
  }, {
    key: "getURI",
    value: function getURI(options) {
      return this.format("RFC3966", options);
    }
  }]);
  return PhoneNumber2;
}();
var isCountryCode = function isCountryCode2(value) {
  return /^[A-Z]{2}$/.test(value);
};
function getCountryAndCountryCallingCode(countryOrCountryCallingCode, metadataJson) {
  var country;
  var countryCallingCode;
  var metadata = new Metadata(metadataJson);
  if (isCountryCode(countryOrCountryCallingCode)) {
    country = countryOrCountryCallingCode;
    metadata.selectNumberingPlan(country);
    countryCallingCode = metadata.countryCallingCode();
  } else {
    countryCallingCode = countryOrCountryCallingCode;
    if (USE_NON_GEOGRAPHIC_COUNTRY_CODE) {
      if (metadata.isNonGeographicCallingCode(countryCallingCode)) {
        country = "001";
      }
    }
  }
  return {
    country,
    countryCallingCode
  };
}

// ../../node_modules/libphonenumber-js/es6/helpers/stripIddPrefix.js
var CAPTURING_DIGIT_PATTERN = new RegExp("([" + VALID_DIGITS + "])");
function stripIddPrefix(number, country, callingCode, metadata) {
  if (!country) {
    return;
  }
  var countryMetadata = new Metadata(metadata);
  countryMetadata.selectNumberingPlan(country, callingCode);
  var IDDPrefixPattern = new RegExp(countryMetadata.IDDPrefix());
  if (number.search(IDDPrefixPattern) !== 0) {
    return;
  }
  number = number.slice(number.match(IDDPrefixPattern)[0].length);
  var matchedGroups = number.match(CAPTURING_DIGIT_PATTERN);
  if (matchedGroups && matchedGroups[1] != null && matchedGroups[1].length > 0) {
    if (matchedGroups[1] === "0") {
      return;
    }
  }
  return number;
}

// ../../node_modules/libphonenumber-js/es6/helpers/extractNationalNumberFromPossiblyIncompleteNumber.js
function extractNationalNumberFromPossiblyIncompleteNumber(number, metadata) {
  if (number && metadata.numberingPlan.nationalPrefixForParsing()) {
    var prefixPattern = new RegExp("^(?:" + metadata.numberingPlan.nationalPrefixForParsing() + ")");
    var prefixMatch = prefixPattern.exec(number);
    if (prefixMatch) {
      var nationalNumber;
      var carrierCode;
      var capturedGroupsCount = prefixMatch.length - 1;
      var hasCapturedGroups = capturedGroupsCount > 0 && prefixMatch[capturedGroupsCount];
      if (metadata.nationalPrefixTransformRule() && hasCapturedGroups) {
        nationalNumber = number.replace(prefixPattern, metadata.nationalPrefixTransformRule());
        if (capturedGroupsCount > 1) {
          carrierCode = prefixMatch[1];
        }
      } else {
        var prefixBeforeNationalNumber = prefixMatch[0];
        nationalNumber = number.slice(prefixBeforeNationalNumber.length);
        if (hasCapturedGroups) {
          carrierCode = prefixMatch[1];
        }
      }
      var nationalPrefix;
      if (hasCapturedGroups) {
        var possiblePositionOfTheFirstCapturedGroup = number.indexOf(prefixMatch[1]);
        var possibleNationalPrefix = number.slice(0, possiblePositionOfTheFirstCapturedGroup);
        if (possibleNationalPrefix === metadata.numberingPlan.nationalPrefix()) {
          nationalPrefix = metadata.numberingPlan.nationalPrefix();
        }
      } else {
        nationalPrefix = prefixMatch[0];
      }
      return {
        nationalNumber,
        nationalPrefix,
        carrierCode
      };
    }
  }
  return {
    nationalNumber: number
  };
}

// ../../node_modules/libphonenumber-js/es6/helpers/extractNationalNumber.js
function extractNationalNumber(number, metadata) {
  var _extractNationalNumbe = extractNationalNumberFromPossiblyIncompleteNumber(number, metadata), carrierCode = _extractNationalNumbe.carrierCode, nationalNumber = _extractNationalNumbe.nationalNumber;
  if (nationalNumber !== number) {
    if (!shouldHaveExtractedNationalPrefix(number, nationalNumber, metadata)) {
      return {
        nationalNumber: number
      };
    }
    if (metadata.possibleLengths()) {
      if (!isPossibleIncompleteNationalNumber(nationalNumber, metadata)) {
        return {
          nationalNumber: number
        };
      }
    }
  }
  return {
    nationalNumber,
    carrierCode
  };
}
function shouldHaveExtractedNationalPrefix(nationalNumberBefore, nationalNumberAfter, metadata) {
  if (matchesEntirely(nationalNumberBefore, metadata.nationalNumberPattern()) && !matchesEntirely(nationalNumberAfter, metadata.nationalNumberPattern())) {
    return false;
  }
  return true;
}
function isPossibleIncompleteNationalNumber(nationalNumber, metadata) {
  switch (checkNumberLength(nationalNumber, metadata)) {
    case "TOO_SHORT":
    case "INVALID_LENGTH":
      return false;
    default:
      return true;
  }
}

// ../../node_modules/libphonenumber-js/es6/helpers/extractCountryCallingCodeFromInternationalNumberWithoutPlusSign.js
function extractCountryCallingCodeFromInternationalNumberWithoutPlusSign(number, country, callingCode, metadata) {
  var countryCallingCode = country ? getCountryCallingCode(country, metadata) : callingCode;
  if (number.indexOf(countryCallingCode) === 0) {
    metadata = new Metadata(metadata);
    metadata.selectNumberingPlan(country, callingCode);
    var possibleShorterNumber = number.slice(countryCallingCode.length);
    var _extractNationalNumbe = extractNationalNumber(possibleShorterNumber, metadata), possibleShorterNationalNumber = _extractNationalNumbe.nationalNumber;
    var _extractNationalNumbe2 = extractNationalNumber(number, metadata), nationalNumber = _extractNationalNumbe2.nationalNumber;
    if (!matchesEntirely(nationalNumber, metadata.nationalNumberPattern()) && matchesEntirely(possibleShorterNationalNumber, metadata.nationalNumberPattern()) || checkNumberLength(nationalNumber, metadata) === "TOO_LONG") {
      return {
        countryCallingCode,
        number: possibleShorterNumber
      };
    }
  }
  return {
    number
  };
}

// ../../node_modules/libphonenumber-js/es6/helpers/extractCountryCallingCode.js
function extractCountryCallingCode(number, country, callingCode, metadata) {
  if (!number) {
    return {};
  }
  var isNumberWithIddPrefix;
  if (number[0] !== "+") {
    var numberWithoutIDD = stripIddPrefix(number, country, callingCode, metadata);
    if (numberWithoutIDD && numberWithoutIDD !== number) {
      isNumberWithIddPrefix = true;
      number = "+" + numberWithoutIDD;
    } else {
      if (country || callingCode) {
        var _extractCountryCallin = extractCountryCallingCodeFromInternationalNumberWithoutPlusSign(number, country, callingCode, metadata), countryCallingCode = _extractCountryCallin.countryCallingCode, shorterNumber = _extractCountryCallin.number;
        if (countryCallingCode) {
          return {
            countryCallingCodeSource: "FROM_NUMBER_WITHOUT_PLUS_SIGN",
            countryCallingCode,
            number: shorterNumber
          };
        }
      }
      return {
        // No need to set it to `UNSPECIFIED`. It can be just `undefined`.
        // countryCallingCodeSource: 'UNSPECIFIED',
        number
      };
    }
  }
  if (number[1] === "0") {
    return {};
  }
  metadata = new Metadata(metadata);
  var i = 2;
  while (i - 1 <= MAX_LENGTH_COUNTRY_CODE && i <= number.length) {
    var _countryCallingCode = number.slice(1, i);
    if (metadata.hasCallingCode(_countryCallingCode)) {
      metadata.selectNumberingPlan(_countryCallingCode);
      return {
        countryCallingCodeSource: isNumberWithIddPrefix ? "FROM_NUMBER_WITH_IDD" : "FROM_NUMBER_WITH_PLUS_SIGN",
        countryCallingCode: _countryCallingCode,
        number: number.slice(i)
      };
    }
    i++;
  }
  return {};
}

// ../../node_modules/libphonenumber-js/es6/helpers/getCountryByNationalNumber.js
function _createForOfIteratorHelperLoose5(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray5(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function() {
      if (i >= o.length) return { done: true };
      return { done: false, value: o[i++] };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray5(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray5(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray5(o, minLen);
}
function _arrayLikeToArray5(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function getCountryByNationalNumber(nationalPhoneNumber, _ref) {
  var countries = _ref.countries, defaultCountry = _ref.defaultCountry, metadata = _ref.metadata;
  metadata = new Metadata(metadata);
  for (var _iterator = _createForOfIteratorHelperLoose5(countries), _step; !(_step = _iterator()).done; ) {
    var country = _step.value;
    metadata.country(country);
    if (metadata.leadingDigits()) {
      if (nationalPhoneNumber && nationalPhoneNumber.search(metadata.leadingDigits()) === 0) {
        return country;
      }
    } else if (getNumberType({
      phone: nationalPhoneNumber,
      country
    }, void 0, metadata.metadata)) {
      return country;
    }
  }
}

// ../../node_modules/libphonenumber-js/es6/helpers/getCountryByCallingCode.js
var USE_NON_GEOGRAPHIC_COUNTRY_CODE2 = false;
function getCountryByCallingCode(callingCode, _ref) {
  var nationalPhoneNumber = _ref.nationalNumber, defaultCountry = _ref.defaultCountry, metadata = _ref.metadata;
  if (USE_NON_GEOGRAPHIC_COUNTRY_CODE2) {
    if (metadata.isNonGeographicCallingCode(callingCode)) {
      return "001";
    }
  }
  var possibleCountries = metadata.getCountryCodesForCallingCode(callingCode);
  if (!possibleCountries) {
    return;
  }
  if (possibleCountries.length === 1) {
    return possibleCountries[0];
  }
  return getCountryByNationalNumber(nationalPhoneNumber, {
    countries: possibleCountries,
    defaultCountry,
    metadata: metadata.metadata
  });
}

// ../../node_modules/libphonenumber-js/es6/helpers/extractPhoneContext.js
var PLUS_SIGN = "+";
var RFC3966_VISUAL_SEPARATOR_ = "[\\-\\.\\(\\)]?";
var RFC3966_PHONE_DIGIT_ = "([" + VALID_DIGITS + "]|" + RFC3966_VISUAL_SEPARATOR_ + ")";
var RFC3966_GLOBAL_NUMBER_DIGITS_ = "^\\" + PLUS_SIGN + RFC3966_PHONE_DIGIT_ + "*[" + VALID_DIGITS + "]" + RFC3966_PHONE_DIGIT_ + "*$";
var RFC3966_GLOBAL_NUMBER_DIGITS_PATTERN_ = new RegExp(RFC3966_GLOBAL_NUMBER_DIGITS_, "g");
var ALPHANUM_ = VALID_DIGITS;
var RFC3966_DOMAINLABEL_ = "[" + ALPHANUM_ + "]+((\\-)*[" + ALPHANUM_ + "])*";
var VALID_ALPHA_ = "a-zA-Z";
var RFC3966_TOPLABEL_ = "[" + VALID_ALPHA_ + "]+((\\-)*[" + ALPHANUM_ + "])*";
var RFC3966_DOMAINNAME_ = "^(" + RFC3966_DOMAINLABEL_ + "\\.)*" + RFC3966_TOPLABEL_ + "\\.?$";
var RFC3966_DOMAINNAME_PATTERN_ = new RegExp(RFC3966_DOMAINNAME_, "g");
var RFC3966_PREFIX_ = "tel:";
var RFC3966_PHONE_CONTEXT_ = ";phone-context=";
var RFC3966_ISDN_SUBADDRESS_ = ";isub=";
function extractPhoneContext(numberToExtractFrom) {
  var indexOfPhoneContext = numberToExtractFrom.indexOf(RFC3966_PHONE_CONTEXT_);
  if (indexOfPhoneContext < 0) {
    return null;
  }
  var phoneContextStart = indexOfPhoneContext + RFC3966_PHONE_CONTEXT_.length;
  if (phoneContextStart >= numberToExtractFrom.length) {
    return "";
  }
  var phoneContextEnd = numberToExtractFrom.indexOf(";", phoneContextStart);
  if (phoneContextEnd >= 0) {
    return numberToExtractFrom.substring(phoneContextStart, phoneContextEnd);
  } else {
    return numberToExtractFrom.substring(phoneContextStart);
  }
}
function isPhoneContextValid(phoneContext) {
  if (phoneContext === null) {
    return true;
  }
  if (phoneContext.length === 0) {
    return false;
  }
  return RFC3966_GLOBAL_NUMBER_DIGITS_PATTERN_.test(phoneContext) || RFC3966_DOMAINNAME_PATTERN_.test(phoneContext);
}

// ../../node_modules/libphonenumber-js/es6/helpers/extractFormattedPhoneNumberFromPossibleRfc3966NumberUri.js
function extractFormattedPhoneNumberFromPossibleRfc3966NumberUri(numberToParse, _ref) {
  var extractFormattedPhoneNumber = _ref.extractFormattedPhoneNumber;
  var phoneContext = extractPhoneContext(numberToParse);
  if (!isPhoneContextValid(phoneContext)) {
    throw new ParseError("NOT_A_NUMBER");
  }
  var phoneNumberString;
  if (phoneContext === null) {
    phoneNumberString = extractFormattedPhoneNumber(numberToParse) || "";
  } else {
    phoneNumberString = "";
    if (phoneContext.charAt(0) === PLUS_SIGN) {
      phoneNumberString += phoneContext;
    }
    var indexOfRfc3966Prefix = numberToParse.indexOf(RFC3966_PREFIX_);
    var indexOfNationalNumber;
    if (indexOfRfc3966Prefix >= 0) {
      indexOfNationalNumber = indexOfRfc3966Prefix + RFC3966_PREFIX_.length;
    } else {
      indexOfNationalNumber = 0;
    }
    var indexOfPhoneContext = numberToParse.indexOf(RFC3966_PHONE_CONTEXT_);
    phoneNumberString += numberToParse.substring(indexOfNationalNumber, indexOfPhoneContext);
  }
  var indexOfIsdn = phoneNumberString.indexOf(RFC3966_ISDN_SUBADDRESS_);
  if (indexOfIsdn > 0) {
    phoneNumberString = phoneNumberString.substring(0, indexOfIsdn);
  }
  if (phoneNumberString !== "") {
    return phoneNumberString;
  }
}

// ../../node_modules/libphonenumber-js/es6/parse.js
var MAX_INPUT_STRING_LENGTH = 250;
var PHONE_NUMBER_START_PATTERN = new RegExp("[" + PLUS_CHARS + VALID_DIGITS + "]");
var AFTER_PHONE_NUMBER_END_PATTERN = new RegExp("[^" + VALID_DIGITS + "#]+$");
var USE_NON_GEOGRAPHIC_COUNTRY_CODE3 = false;
function parse(text, options, metadata) {
  options = options || {};
  metadata = new Metadata(metadata);
  if (options.defaultCountry && !metadata.hasCountry(options.defaultCountry)) {
    if (options.v2) {
      throw new ParseError("INVALID_COUNTRY");
    }
    throw new Error("Unknown country: ".concat(options.defaultCountry));
  }
  var _parseInput = parseInput(text, options.v2, options.extract), formattedPhoneNumber = _parseInput.number, ext = _parseInput.ext, error = _parseInput.error;
  if (!formattedPhoneNumber) {
    if (options.v2) {
      if (error === "TOO_SHORT") {
        throw new ParseError("TOO_SHORT");
      }
      throw new ParseError("NOT_A_NUMBER");
    }
    return {};
  }
  var _parsePhoneNumber = parsePhoneNumber(formattedPhoneNumber, options.defaultCountry, options.defaultCallingCode, metadata), country = _parsePhoneNumber.country, nationalNumber = _parsePhoneNumber.nationalNumber, countryCallingCode = _parsePhoneNumber.countryCallingCode, countryCallingCodeSource = _parsePhoneNumber.countryCallingCodeSource, carrierCode = _parsePhoneNumber.carrierCode;
  if (!metadata.hasSelectedNumberingPlan()) {
    if (options.v2) {
      throw new ParseError("INVALID_COUNTRY");
    }
    return {};
  }
  if (!nationalNumber || nationalNumber.length < MIN_LENGTH_FOR_NSN) {
    if (options.v2) {
      throw new ParseError("TOO_SHORT");
    }
    return {};
  }
  if (nationalNumber.length > MAX_LENGTH_FOR_NSN) {
    if (options.v2) {
      throw new ParseError("TOO_LONG");
    }
    return {};
  }
  if (options.v2) {
    var phoneNumber = new PhoneNumber(countryCallingCode, nationalNumber, metadata.metadata);
    if (country) {
      phoneNumber.country = country;
    }
    if (carrierCode) {
      phoneNumber.carrierCode = carrierCode;
    }
    if (ext) {
      phoneNumber.ext = ext;
    }
    phoneNumber.__countryCallingCodeSource = countryCallingCodeSource;
    return phoneNumber;
  }
  var valid = (options.extended ? metadata.hasSelectedNumberingPlan() : country) ? matchesEntirely(nationalNumber, metadata.nationalNumberPattern()) : false;
  if (!options.extended) {
    return valid ? result(country, nationalNumber, ext) : {};
  }
  return {
    country,
    countryCallingCode,
    carrierCode,
    valid,
    possible: valid ? true : options.extended === true && metadata.possibleLengths() && isPossibleNumber(nationalNumber, metadata) ? true : false,
    phone: nationalNumber,
    ext
  };
}
function _extractFormattedPhoneNumber(text, extract, throwOnError) {
  if (!text) {
    return;
  }
  if (text.length > MAX_INPUT_STRING_LENGTH) {
    if (throwOnError) {
      throw new ParseError("TOO_LONG");
    }
    return;
  }
  if (extract === false) {
    return text;
  }
  var startsAt = text.search(PHONE_NUMBER_START_PATTERN);
  if (startsAt < 0) {
    return;
  }
  return text.slice(startsAt).replace(AFTER_PHONE_NUMBER_END_PATTERN, "");
}
function parseInput(text, v2, extract) {
  var number = extractFormattedPhoneNumberFromPossibleRfc3966NumberUri(text, {
    extractFormattedPhoneNumber: function extractFormattedPhoneNumber(text2) {
      return _extractFormattedPhoneNumber(text2, extract, v2);
    }
  });
  if (!number) {
    return {};
  }
  if (!isViablePhoneNumber(number)) {
    if (isViablePhoneNumberStart(number)) {
      return {
        error: "TOO_SHORT"
      };
    }
    return {};
  }
  var withExtensionStripped = extractExtension(number);
  if (withExtensionStripped.ext) {
    return withExtensionStripped;
  }
  return {
    number
  };
}
function result(country, nationalNumber, ext) {
  var result2 = {
    country,
    phone: nationalNumber
  };
  if (ext) {
    result2.ext = ext;
  }
  return result2;
}
function parsePhoneNumber(formattedPhoneNumber, defaultCountry, defaultCallingCode, metadata) {
  var _extractCountryCallin = extractCountryCallingCode(parseIncompletePhoneNumber(formattedPhoneNumber), defaultCountry, defaultCallingCode, metadata.metadata), countryCallingCodeSource = _extractCountryCallin.countryCallingCodeSource, countryCallingCode = _extractCountryCallin.countryCallingCode, number = _extractCountryCallin.number;
  var country;
  if (countryCallingCode) {
    metadata.selectNumberingPlan(countryCallingCode);
  } else if (number && (defaultCountry || defaultCallingCode)) {
    metadata.selectNumberingPlan(defaultCountry, defaultCallingCode);
    if (defaultCountry) {
      country = defaultCountry;
    } else {
      if (USE_NON_GEOGRAPHIC_COUNTRY_CODE3) {
        if (metadata.isNonGeographicCallingCode(defaultCallingCode)) {
          country = "001";
        }
      }
    }
    countryCallingCode = defaultCallingCode || getCountryCallingCode(defaultCountry, metadata.metadata);
  } else return {};
  if (!number) {
    return {
      countryCallingCodeSource,
      countryCallingCode
    };
  }
  var _extractNationalNumbe = extractNationalNumber(parseIncompletePhoneNumber(number), metadata), nationalNumber = _extractNationalNumbe.nationalNumber, carrierCode = _extractNationalNumbe.carrierCode;
  var exactCountry = getCountryByCallingCode(countryCallingCode, {
    nationalNumber,
    defaultCountry,
    metadata
  });
  if (exactCountry) {
    country = exactCountry;
    if (exactCountry === "001") {
    } else {
      metadata.country(country);
    }
  }
  return {
    country,
    countryCallingCode,
    countryCallingCodeSource,
    nationalNumber,
    carrierCode
  };
}

// ../../node_modules/libphonenumber-js/es6/parsePhoneNumberWithError_.js
function ownKeys3(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread3(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys3(Object(source), true).forEach(function(key) {
      _defineProperty3(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys3(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty3(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function parsePhoneNumberWithError(text, options, metadata) {
  return parse(text, _objectSpread3(_objectSpread3({}, options), {}, {
    v2: true
  }), metadata);
}

// ../../node_modules/libphonenumber-js/es6/normalizeArguments.js
function ownKeys4(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread4(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys4(Object(source), true).forEach(function(key) {
      _defineProperty4(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys4(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty4(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray6(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray6(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray6(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray6(o, minLen);
}
function _arrayLikeToArray6(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function normalizeArguments(args) {
  var _Array$prototype$slic = Array.prototype.slice.call(args), _Array$prototype$slic2 = _slicedToArray(_Array$prototype$slic, 4), arg_1 = _Array$prototype$slic2[0], arg_2 = _Array$prototype$slic2[1], arg_3 = _Array$prototype$slic2[2], arg_4 = _Array$prototype$slic2[3];
  var text;
  var options;
  var metadata;
  if (typeof arg_1 === "string") {
    text = arg_1;
  } else throw new TypeError("A text for parsing must be a string.");
  if (!arg_2 || typeof arg_2 === "string") {
    if (arg_4) {
      options = arg_3;
      metadata = arg_4;
    } else {
      options = void 0;
      metadata = arg_3;
    }
    if (arg_2) {
      options = _objectSpread4({
        defaultCountry: arg_2
      }, options);
    }
  } else if (isObject(arg_2)) {
    if (arg_3) {
      options = arg_2;
      metadata = arg_3;
    } else {
      metadata = arg_2;
    }
  } else throw new Error("Invalid second argument: ".concat(arg_2));
  return {
    text,
    options,
    metadata
  };
}

// ../../node_modules/libphonenumber-js/es6/parsePhoneNumber_.js
function ownKeys5(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread5(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys5(Object(source), true).forEach(function(key) {
      _defineProperty5(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys5(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty5(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function parsePhoneNumber2(text, options, metadata) {
  if (options && options.defaultCountry && !isSupportedCountry(options.defaultCountry, metadata)) {
    options = _objectSpread5(_objectSpread5({}, options), {}, {
      defaultCountry: void 0
    });
  }
  try {
    return parsePhoneNumberWithError(text, options, metadata);
  } catch (error) {
    if (error instanceof ParseError) {
    } else {
      throw error;
    }
  }
}

// ../../node_modules/libphonenumber-js/es6/isValidPhoneNumber.js
function ownKeys6(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function(sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread6(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys6(Object(source), true).forEach(function(key) {
      _defineProperty6(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys6(Object(source)).forEach(function(key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty6(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
  } else {
    obj[key] = value;
  }
  return obj;
}
function isValidPhoneNumber() {
  var _normalizeArguments = normalizeArguments(arguments), text = _normalizeArguments.text, options = _normalizeArguments.options, metadata = _normalizeArguments.metadata;
  options = _objectSpread6(_objectSpread6({}, options), {}, {
    extract: false
  });
  var phoneNumber = parsePhoneNumber2(text, options, metadata);
  return phoneNumber && phoneNumber.isValid() || false;
}

// ../../node_modules/libphonenumber-js/min/exports/isValidPhoneNumber.js
function isValidPhoneNumber2() {
  return withMetadataArgument(isValidPhoneNumber, arguments);
}

// lib/context/NotifiTargetContext.tsx
import React3, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

// lib/hooks/useTargetWallet.tsx
import { useClient } from "@xmtp/react-sdk";
import React2 from "react";

// lib/utils/xmtp.ts
var coinbaseEndpoint = "https://broadcast.coinbase.com/api/rpc";
var createCoinbaseNonce = async () => {
  const { result: result2 } = await fetch(`${coinbaseEndpoint}/createNonce`, {
    method: "POST"
  }).then((v) => v.json());
  return result2?.nonce;
};
var subscribeCoinbaseMessaging = async (payload) => {
  const response = await fetch(`${coinbaseEndpoint}/messaging/subscribe`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "text/plain"
    }
  });
  return await response.json();
};

// lib/hooks/useTargetWallet.tsx
var useTargetWallet = () => {
  const [isLoading, setIsLoading] = React2.useState(false);
  const [error, setError] = React2.useState(null);
  const { frontendClient, walletWithSignParams } = useNotifiFrontendClientContext();
  const xmtp = useClient();
  const getSignature = React2.useCallback(
    async (message) => {
      let signature = "";
      if (typeof message === "string") {
        const encoder = new TextEncoder();
        message = encoder.encode(message);
      }
      switch (walletWithSignParams.walletBlockchain) {
        case "AVALANCHE":
        case "ETHEREUM":
        case "POLYGON":
        case "ARBITRUM":
        case "BINANCE":
        case "ELYS":
        case "NEUTRON":
        case "ARCHWAY":
        case "AXELAR":
        case "BERACHAIN":
        case "OPTIMISM":
        case "ZKSYNC":
        case "INJECTIVE":
        case "BASE":
        case "BLAST":
        case "CELO":
        case "MANTLE":
        case "LINEA":
        case "SCROLL":
        case "MANTA":
        case "EVMOS":
        case "MONAD":
        case "AGORIC":
        case "ORAI":
        case "KAVA":
        case "CELESTIA":
        case "COSMOS":
        case "DYMENSION":
        case "DYDX":
        case "XION":
        case "NEAR":
        case "SUI":
          signature = await walletWithSignParams.signMessage(message);
          break;
        default: {
          setError(Error("This chain is not supported"));
          throw Error("This chain is not supported");
        }
      }
      return reformatSignatureForWalletTarget(signature);
    },
    [walletWithSignParams.signMessage]
  );
  const xmtpXip42Impl = React2.useCallback(
    async (senderAddress) => {
      const options = {
        persistConversations: false,
        env: "production"
      };
      const address = walletWithSignParams.walletPublicKey;
      const signer = {
        getAddress: () => {
          return new Promise((resolve) => {
            resolve(address);
          });
        },
        signMessage: async (message) => {
          return getSignature(message);
        }
      };
      const client = await xmtp.initialize({ options, signer });
      if (!client) {
        throw Error(
          "xmtpXip42Impl: XMTP client is uninitialized. Please try again."
        );
      }
      const conversation = await client.conversations.newConversation(senderAddress);
      await client.contacts.allow([senderAddress]);
      return conversation.topic.split("/")[3];
    },
    [walletWithSignParams.walletPublicKey, xmtp, getSignature]
  );
  const signCoinbaseSignature = React2.useCallback(
    async (web3TargetId, senderAddress) => {
      setIsLoading(true);
      try {
        const conversationTopic = await xmtpXip42Impl(senderAddress);
        if (!conversationTopic)
          throw Error("Unable to get the conversation topic");
        const address = walletWithSignParams.walletPublicKey;
        const nonce = await createCoinbaseNonce();
        const message = getWalletTargetSignMessage(
          address,
          senderAddress,
          nonce
        );
        const signature = await getSignature(message);
        if (!signature)
          throw Error("Unable to sign the wallet. Please try again.");
        const payload = {
          address,
          nonce,
          signature,
          isActivatedViaCb: true,
          partnerAddress: senderAddress,
          conversationTopic
        };
        await subscribeCoinbaseMessaging(payload);
        const walletVerifyResult = await frontendClient.verifyXmtpTargetViaXip42({
          input: {
            web3TargetId,
            accountId: address,
            conversationTopic
          }
        });
        return walletVerifyResult.verifyXmtpTargetViaXip42.web3Target;
      } catch (e) {
        console.error("error in signCoinbaseSignature: ", e);
        setError(
          e instanceof Error ? {
            ...e,
            message: "useTargetWallet - signCoinbaseSignature: " + e.message
          } : Error("signCoinbaseSignature: Error occurred, please try again.")
        );
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [
      walletWithSignParams.walletPublicKey,
      frontendClient,
      xmtpXip42Impl,
      getSignature
    ]
  );
  return {
    isLoading,
    error,
    signCoinbaseSignature
  };
};

// lib/context/NotifiTargetContext.tsx
var isCtaInfo = (info) => "onClick" in info;
var isFormTargetRenewArgs = (args) => {
  return args.target === "email" || args.target === "telegram" || args.target === "phoneNumber";
};
var isToggleTargetRenewArgs = (args) => {
  return args.target === "slack" || args.target === "wallet" || args.target === "discord";
};
var NotifiTargetContext = createContext(
  {}
  // intentionally empty as initial value
);
var NotifiTargetContextProvider = ({ children, toggleTargetAvailability }) => {
  const { frontendClient, frontendClientStatus } = useNotifiFrontendClientContext();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isInitialLoaded = React3.useRef(false);
  const [targetGroupId, setTargetGroupId] = useState(null);
  const [targetInputs, setTargetInputs] = useState({
    email: { value: "", error: "" },
    phoneNumber: { value: "", error: "" },
    telegram: { value: "", error: "" },
    discord: false,
    slack: false,
    wallet: false
  });
  const [isChangingTargets, setIsChangingTargets] = useState({
    email: false,
    phoneNumber: false,
    telegram: false,
    slack: false,
    discord: false,
    wallet: false
  });
  const [targetData, setTargetData] = useState({
    email: "",
    phoneNumber: "",
    telegram: "",
    discord: {
      useDiscord: false,
      isAvailable: toggleTargetAvailability?.discord ?? true
    },
    slack: {
      useSlack: false,
      isAvailable: toggleTargetAvailability?.slack ?? true
    },
    wallet: {
      useWallet: false,
      isAvailable: toggleTargetAvailability?.wallet ?? false
    }
  });
  const {
    signCoinbaseSignature,
    isLoading: isLoadingWallet,
    error: errorWallet
  } = useTargetWallet();
  const [targetInfoPrompts, setTargetInfoPrompts] = useState({
    email: void 0,
    phoneNumber: void 0,
    telegram: void 0,
    discord: void 0,
    slack: void 0,
    wallet: void 0
  });
  const targetGroupToBeSaved = {
    name: "Default",
    emailAddress: targetInputs.email.value === "" ? void 0 : targetInputs.email.value,
    phoneNumber: isValidPhoneNumber2(targetInputs.phoneNumber.value) ? targetInputs.phoneNumber.value : void 0,
    telegramId: targetInputs.telegram.value === "" ? void 0 : formatTelegramForSubscription(targetInputs.telegram.value),
    discordId: targetInputs.discord ? "Default" : void 0,
    slackId: targetInputs.slack ? "Default" : void 0,
    walletId: targetInputs.wallet ? "Default" : void 0
  };
  const currentSubscription = React3.useRef();
  useEffect(() => {
    if (frontendClientStatus.isAuthenticated && !isInitialLoaded.current) {
      if (isLoading && isInitialLoaded.current) return;
      isInitialLoaded.current = true;
      setIsLoading(true);
      frontendClient.fetchFusionData().then((data) => {
        refreshTargetDocument(data);
      }).catch((e) => {
        isInitialLoaded.current = false;
        if (e instanceof Error) {
          setError({
            ...e,
            message: ` Initial load target data error (fetchFusionData): ${e.message}`
          });
        }
        console.error(e);
      }).finally(() => setIsLoading(false));
    }
    if (frontendClientStatus.isAuthenticated) {
      const targetChangedHandler = (evt) => {
        if (evt.__typename === "TargetStateChangedEvent") {
          frontendClient.fetchFusionData().then(refreshTargetDocument);
        }
      };
      currentSubscription.current = frontendClient.addEventListener(
        "stateChanged",
        targetChangedHandler,
        (error2) => {
          if (error2 instanceof Error) {
            setError({
              ...error2,
              message: `NotifiTargetContext - stateChanged: ${error2.message}`
            });
          }
          console.error("NotifiTargetContext - stateChanged:", error2);
        }
      );
      return () => {
        const id = currentSubscription.current;
        if (!id) return;
        frontendClient.removeEventListener("stateChanged", id);
        currentSubscription.current = void 0;
        setError(null);
      };
    }
  }, [frontendClientStatus.isAuthenticated]);
  useEffect(() => {
    if (targetData.email !== targetInputs.email.value) {
      setIsChangingTargets((prev) => ({ ...prev, email: true }));
    } else {
      setIsChangingTargets((prev) => ({ ...prev, email: false }));
    }
    if (targetData.telegram !== targetInputs.telegram.value) {
      setIsChangingTargets((prev) => ({ ...prev, telegram: true }));
    } else {
      setIsChangingTargets((prev) => ({ ...prev, telegram: false }));
    }
    if (targetData.phoneNumber !== targetInputs.phoneNumber.value) {
      setIsChangingTargets((prev) => ({ ...prev, phoneNumber: true }));
    } else {
      setIsChangingTargets((prev) => ({ ...prev, phoneNumber: false }));
    }
    if (targetData.discord.useDiscord !== targetInputs.discord) {
      setIsChangingTargets((prev) => ({ ...prev, discord: true }));
    } else {
      setIsChangingTargets((prev) => ({ ...prev, discord: false }));
    }
    if (targetData.slack.useSlack !== targetInputs.slack) {
      setIsChangingTargets((prev) => ({ ...prev, slack: true }));
    } else {
      setIsChangingTargets((prev) => ({ ...prev, slack: false }));
    }
    if (targetData.wallet.useWallet !== targetInputs.wallet) {
      setIsChangingTargets((prev) => ({ ...prev, wallet: true }));
    } else {
      setIsChangingTargets((prev) => ({ ...prev, wallet: false }));
    }
  }, [targetInputs]);
  useEffect(() => {
    setTargetData((prev) => ({
      ...prev,
      discord: {
        ...prev.discord,
        isAvailable: toggleTargetAvailability?.discord ?? true
      },
      slack: {
        ...prev.slack,
        isAvailable: toggleTargetAvailability?.slack ?? true
      },
      wallet: {
        ...prev.wallet,
        isAvailable: toggleTargetAvailability?.wallet ?? false
      }
    }));
  }, [toggleTargetAvailability]);
  const unVerifiedTargets = useMemo(() => {
    const {
      email: emailInfoPrompt,
      phoneNumber: phoneNumberInfoPrompt,
      telegram: telegramInfoPrompt,
      discord: discordInfoPrompt,
      wallet: walletInfoPrompt
    } = targetInfoPrompts;
    const unConfirmedTargets = {
      email: emailInfoPrompt?.infoPrompt.type === "cta",
      phoneNumber: phoneNumberInfoPrompt?.infoPrompt.type === "cta",
      telegram: telegramInfoPrompt?.infoPrompt.type === "cta",
      slack: targetData.slack.useSlack && discordInfoPrompt?.infoPrompt.type === "cta" && discordInfoPrompt?.infoPrompt.message === "Enable Bot",
      wallet: targetData.wallet.useWallet && walletInfoPrompt?.infoPrompt.type === "cta" && walletInfoPrompt?.infoPrompt.message === "Sign Wallet",
      discord: targetData.discord.useDiscord && discordInfoPrompt?.infoPrompt.type === "cta" && discordInfoPrompt?.infoPrompt.message === "Enable Bot"
    };
    return objectKeys3(unConfirmedTargets).map((key) => {
      if (unConfirmedTargets[key]) {
        return key;
      }
    }).filter((item) => !!item);
  }, [targetInfoPrompts, targetData]);
  const updateTargetInputs = useCallback(
    (target, value) => {
      if (target in targetInputs) {
        setTargetInputs((prev) => ({
          ...prev,
          [target]: value
        }));
      }
    },
    []
  );
  const renewTargetGroup = useCallback(
    async (singleTargetRenewArgs) => {
      let data = { ...targetGroupToBeSaved };
      if (singleTargetRenewArgs) {
        data = {
          name: "Default",
          emailAddress: !targetData.email ? void 0 : targetData.email,
          phoneNumber: !targetData.phoneNumber ? void 0 : targetData.phoneNumber,
          telegramId: !targetData.telegram ? void 0 : targetData.telegram,
          discordId: targetData.discord.useDiscord ? "Default" : void 0,
          slackId: targetData.slack.useSlack ? "Default" : void 0,
          walletId: targetData.wallet.useWallet ? "Default" : void 0
        };
        const { target, value } = singleTargetRenewArgs;
        if (isFormTargetRenewArgs(singleTargetRenewArgs)) {
          let formTarget = "";
          let formValue = "";
          if (target === "email") {
            formTarget = "emailAddress";
            formValue = value === "" ? void 0 : value;
          }
          if (target === "telegram") {
            formTarget = "telegramId";
            formValue = value === "" ? void 0 : formatTelegramForSubscription(value);
          }
          if (target === "phoneNumber") {
            formTarget = target;
            formValue = isValidPhoneNumber2(value) ? value : void 0;
          }
          data = {
            ...data,
            [formTarget]: formValue
          };
        } else if (isToggleTargetRenewArgs(singleTargetRenewArgs)) {
          data = {
            ...data,
            [`${target}Id`]: value ? "Default" : void 0
          };
        }
      }
      setIsLoading(true);
      return frontendClient.ensureTargetGroup(data).then((_result) => {
        frontendClient.fetchFusionData().then((data2) => {
          refreshTargetDocument(data2);
          setError(null);
        }).catch((e) => setError(e)).finally(() => setIsLoading(false));
      }).catch((e) => setError(e)).finally(() => setIsLoading(false));
    },
    [frontendClient, targetGroupToBeSaved, targetData]
  );
  const updateTargetInfoPrompt = useCallback(
    (type, infoPrompt) => {
      if (!infoPrompt) {
        setTargetInfoPrompts((prev) => ({
          ...prev,
          [type]: void 0
        }));
        return;
      }
      setTargetInfoPrompts((prev) => ({
        ...prev,
        [type]: {
          target: type,
          infoPrompt
        }
      }));
    },
    []
  );
  const refreshTargetDocument = useCallback(
    (newData) => {
      const targetGroup = newData.targetGroup?.find(
        (tg) => tg?.name === "Default"
      );
      setTargetGroupId(targetGroup?.id ?? null);
      setTargetInputs((prev) => ({
        ...prev,
        email: {
          value: targetGroup?.emailTargets?.[0]?.emailAddress ?? prev.email.value
        },
        phoneNumber: {
          value: targetGroup?.smsTargets?.[0]?.phoneNumber ?? prev.phoneNumber.value
        },
        telegram: {
          value: targetGroup?.telegramTargets?.[0]?.telegramId ?? prev.telegram.value
        },
        discord: !!targetGroup?.discordTargets?.find(
          (it) => it?.name === "Default"
        ),
        slack: !!targetGroup?.slackChannelTargets?.find(
          (it) => it?.name === "Default"
        ),
        wallet: !!targetGroup?.web3Targets?.find(
          (it) => it?.name === "Default"
        )
      }));
      const emailTarget = targetGroup?.emailTargets?.[0];
      refreshEmailTarget(emailTarget);
      const smsTarget = targetGroup?.smsTargets?.[0];
      refreshSmsTarget(smsTarget);
      const telegramTarget = targetGroup?.telegramTargets?.[0];
      refreshTelegramTarget(telegramTarget);
      const discordTarget = targetGroup?.discordTargets?.find(
        (it) => it?.name === "Default"
      );
      refreshDiscordTarget(discordTarget);
      const slackTarget = targetGroup?.slackChannelTargets?.find(
        (it) => it?.name === "Default"
      );
      refreshSlackTarget(slackTarget);
      const web3Target = targetGroup?.web3Targets?.find(
        (it) => it?.name === "Default"
      );
      refreshWeb3Target(web3Target);
    },
    []
  );
  const refreshEmailTarget = useCallback(
    async (emailTarget) => {
      setTargetData((prev) => ({
        ...prev,
        email: emailTarget?.emailAddress ?? ""
      }));
      if (emailTarget) {
        switch (emailTarget.isConfirmed) {
          case true:
            updateTargetInfoPrompt("email", {
              type: "message",
              message: "Verified"
            });
            break;
          case false:
            updateTargetInfoPrompt("email", {
              type: "cta",
              message: "Resend Link",
              onClick: () => frontendClient.sendEmailTargetVerification({
                targetId: emailTarget.id
              })
            });
            break;
          default:
            updateTargetInfoPrompt("email", {
              type: "error",
              message: "ERROR: Unexpected email state"
            });
        }
      } else {
        updateTargetInfoPrompt("email", {
          type: "message",
          message: "Verified"
        });
      }
    },
    [frontendClient]
  );
  const refreshSmsTarget = useCallback(
    async (smsTarget) => {
      setTargetData((prev) => ({
        ...prev,
        phoneNumber: smsTarget?.phoneNumber ?? ""
      }));
      if (smsTarget?.phoneNumber) {
        switch (smsTarget.isConfirmed) {
          case true:
            updateTargetInfoPrompt("phoneNumber", {
              type: "message",
              message: "Verified"
            });
            break;
          case false:
            updateTargetInfoPrompt("phoneNumber", {
              type: "error",
              message: "Messages stopped"
            });
            break;
          default:
            updateTargetInfoPrompt("phoneNumber", {
              type: "error",
              message: "ERROR: Unexpected sms state"
            });
        }
      } else {
        updateTargetInfoPrompt("phoneNumber", null);
      }
    },
    [frontendClient]
  );
  const refreshTelegramTarget = useCallback(
    async (telegramTarget) => {
      setTargetData((prev) => ({
        ...prev,
        telegram: telegramTarget?.telegramId ?? ""
      }));
      if (telegramTarget) {
        switch (telegramTarget.isConfirmed) {
          case true:
            updateTargetInfoPrompt("telegram", {
              type: "message",
              message: "Verified"
            });
            break;
          case false:
            updateTargetInfoPrompt("telegram", {
              type: "cta",
              message: "Verify ID",
              onClick: () => {
                if (!telegramTarget?.confirmationUrl) {
                  return;
                }
                window.open(telegramTarget?.confirmationUrl);
              }
            });
            break;
          default:
            updateTargetInfoPrompt("telegram", {
              type: "error",
              message: "ERROR: Unexpected telegram state"
            });
        }
      } else {
        updateTargetInfoPrompt("telegram", null);
      }
    },
    []
  );
  const refreshDiscordTarget = useCallback(
    async (discordTarget) => {
      if (!!discordTarget && !discordTarget.isConfirmed) {
        updateTargetInfoPrompt("discord", {
          type: "cta",
          message: "Enable Bot",
          onClick: () => window.open(discordTarget.verificationLink, "_blank")
        });
        setTargetData((prev) => ({
          ...prev,
          discord: {
            useDiscord: true,
            data: discordTarget,
            isAvailable: toggleTargetAvailability?.discord ?? true
          }
        }));
      } else if (!!discordTarget && discordTarget.isConfirmed) {
        switch (discordTarget.userStatus) {
          case "DISCORD_SERVER_NOT_JOINED":
            updateTargetInfoPrompt("discord", {
              type: "cta",
              message: "Join Server",
              onClick: () => window.open(discordTarget.discordServerInviteLink, "_blank")
            });
            break;
          case "COMPLETE":
            updateTargetInfoPrompt("discord", {
              type: "message",
              message: "Verified"
            });
            break;
          default:
            updateTargetInfoPrompt("discord", {
              type: "error",
              message: "ERROR: Unexpected discord state"
            });
        }
        setTargetData((prev) => ({
          ...prev,
          discord: {
            useDiscord: true,
            data: discordTarget,
            isAvailable: toggleTargetAvailability?.discord ?? true
          }
        }));
      } else {
        setTargetData((prev) => ({
          ...prev,
          discord: {
            useDiscord: false,
            isAvailable: toggleTargetAvailability?.discord ?? true
          }
        }));
      }
    },
    []
  );
  const refreshSlackTarget = useCallback(
    async (slackTarget) => {
      if (slackTarget) {
        switch (slackTarget.verificationStatus) {
          case "UNVERIFIED":
          case "MISSING_PERMISSIONS":
          case "MISSING_CHANNEL":
            updateTargetInfoPrompt("slack", {
              type: "cta",
              message: "Enable Bot",
              onClick: () => window.open(slackTarget.verificationLink, "_blank")
            });
            break;
          case "VERIFIED":
            updateTargetInfoPrompt("slack", {
              type: "message",
              message: "Verified"
            });
            break;
          default:
            updateTargetInfoPrompt("slack", {
              type: "error",
              message: "ERROR: Unexpected slack state"
            });
        }
        setTargetData((prev) => ({
          ...prev,
          slack: {
            useSlack: true,
            data: slackTarget,
            isAvailable: toggleTargetAvailability?.slack ?? true
          }
        }));
      } else {
        setTargetData((prev) => ({
          ...prev,
          slack: {
            useSlack: false,
            isAvailable: toggleTargetAvailability?.slack ?? true
          }
        }));
      }
    },
    []
  );
  const refreshWeb3Target = useCallback(
    async (web3Target) => {
      if (web3Target) {
        switch (web3Target.isConfirmed) {
          case false:
            updateTargetInfoPrompt("wallet", {
              type: "cta",
              message: "Sign Wallet",
              onClick: async () => {
                if (!web3Target.senderAddress) {
                  setError(
                    new Error(
                      "ERROR: Missing sender address, please try again"
                    )
                  );
                  return;
                }
                const updatedWeb3Target = await signCoinbaseSignature(
                  web3Target.id,
                  web3Target.senderAddress
                );
                if (!updatedWeb3Target) return;
                refreshWeb3Target(updatedWeb3Target);
              }
            });
            break;
          case true:
            updateTargetInfoPrompt("wallet", {
              type: "message",
              message: "Verified"
            });
            break;
          default:
            updateTargetInfoPrompt("wallet", {
              type: "error",
              message: "ERROR: Unexpected wallet state"
            });
        }
        setTargetData((prev) => ({
          ...prev,
          wallet: {
            useWallet: true,
            data: web3Target,
            isAvailable: toggleTargetAvailability?.wallet ?? false
          }
        }));
      } else {
        setTargetData((prev) => ({
          ...prev,
          wallet: {
            useWallet: false,
            isAvailable: toggleTargetAvailability?.wallet ?? false
          }
        }));
      }
    },
    [toggleTargetAvailability, signCoinbaseSignature]
  );
  return /* @__PURE__ */ React3.createElement(
    NotifiTargetContext.Provider,
    {
      value: {
        error,
        errorWallet,
        isLoading,
        isLoadingWallet,
        renewTargetGroup,
        unVerifiedTargets,
        isChangingTargets,
        targetDocument: {
          targetGroupId,
          targetInputs,
          targetData,
          targetInfoPrompts
        },
        updateTargetInputs
      }
    },
    children
  );
};
var useNotifiTargetContext = () => useContext(NotifiTargetContext);

// lib/context/NotifiTenantConfigContext.tsx
import React4, {
  createContext as createContext2,
  useContext as useContext2,
  useEffect as useEffect2,
  useState as useState2
} from "react";
var NotifiTenantConfigContext = createContext2(
  {}
);
var NotifiTenantConfigContextProvider = ({ inputs = {}, children, cardId }) => {
  const [isLoading, setIsLoading] = useState2(true);
  const [error, setError] = useState2(null);
  const { frontendClient, frontendClientStatus } = useNotifiFrontendClientContext();
  const isInitialLoaded = React4.useRef(false);
  const [cardConfig, setCardConfig] = useState2(null);
  const [fusionEventTopics, setFusionEventTopics] = useState2([]);
  useEffect2(() => {
    if (!frontendClientStatus.isInitialized || isInitialLoaded.current) return;
    isInitialLoaded.current = true;
    setIsLoading(true);
    frontendClient.fetchTenantConfig({ id: cardId, type: "SUBSCRIPTION_CARD" }).then(({ cardConfig: cardConfig2, fusionEventTopics: fusionEventTopics2 }) => {
      setCardConfig(cardConfig2);
      setFusionEventTopics(fusionEventTopics2);
      setError(null);
    }).catch((e) => {
      isInitialLoaded.current = false;
      if (e instanceof Error) {
        setError({
          ...e,
          message: `Failed to fetch tenant config (.fetchTenantConfig): ${e.message}`
        });
      }
      console.error(e);
    }).finally(() => setIsLoading(false));
  }, [frontendClientStatus]);
  if (!cardConfig) {
    return null;
  }
  const getFusionTopic = (fusionEventId) => {
    return fusionEventTopics.find(
      (topic) => topic.fusionEventDescriptor.id === fusionEventId
    );
  };
  return /* @__PURE__ */ React4.createElement(
    NotifiTenantConfigContext.Provider,
    {
      value: {
        cardConfig,
        inputs,
        isLoading,
        error,
        fusionEventTopics,
        getFusionTopic
      }
    },
    children
  );
};
var useNotifiTenantConfigContext = () => useContext2(NotifiTenantConfigContext);

// lib/context/NotifiTopicContext.tsx
import {
  resolveObjectArrayRef,
  resolveStringRef
} from "@notifi-network/notifi-frontend-client";
import React5, {
  createContext as createContext3,
  useCallback as useCallback2,
  useContext as useContext3,
  useEffect as useEffect3,
  useState as useState3
} from "react";
var NotifiTopicContext = createContext3(
  {}
  // intentionally empty as initial value
);
var NotifiTopicContextProvider = ({
  children
}) => {
  const { inputs } = useNotifiTenantConfigContext();
  const [error, setError] = useState3(null);
  const { frontendClient, frontendClientStatus } = useNotifiFrontendClientContext();
  const [isLoading, setIsLoading] = useState3(true);
  const [alerts, setAlerts] = useState3({});
  const isInitialLoaded = React5.useRef(false);
  useEffect3(() => {
    if (!frontendClientStatus.isAuthenticated || isInitialLoaded.current)
      return;
    isInitialLoaded.current = true;
    frontendClient.fetchFusionData().then(refreshAlerts).catch(() => isInitialLoaded.current = false).finally(() => setIsLoading(false));
  }, [frontendClientStatus.isAuthenticated]);
  const unsubscribeAlert = useCallback2(
    (alertName) => {
      setIsLoading(true);
      const alert = alerts[alertName];
      if (!alert) return;
      frontendClient.deleteAlert({ id: alert.id }).then(() => {
        frontendClient.fetchFusionData().then(refreshAlerts);
        setError(null);
      }).catch((e) => {
        setError(e);
      }).finally(() => setIsLoading(false));
    },
    [alerts, frontendClient]
  );
  const unsubscribeAlerts = useCallback2(
    async (alertNames) => {
      setIsLoading(true);
      const alertIds = alertNames.map((alertName) => alerts[alertName]?.id);
      try {
        await frontendClient.deleteAlerts({ ids: alertIds });
        const data = await frontendClient.fetchFusionData();
        refreshAlerts(data);
        setError(null);
      } catch (e) {
        if (e instanceof Error) setError(e);
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [alerts, frontendClient]
  );
  const isAlertSubscribed = useCallback2(
    (alertName) => {
      return Object.keys(alerts).includes(alertName);
    },
    [alerts]
  );
  const subscribeAlertsWithFilterOptions = async (topicWithFilterOptionsList, targetGroupId) => {
    const createAlertInputs = [];
    const alertNamesToCreate = [];
    topicWithFilterOptionsList.forEach(
      ({ topic, filterOptions, customAlertName, subscriptionValue }) => {
        const fusionEventId = topic.fusionEventDescriptor.id;
        const fusionEventMetadata = getFusionEventMetadata(topic);
        if (!fusionEventMetadata || !fusionEventId) return;
        try {
          if (!subscriptionValue) {
            const subscriptionValueOrRef = getFusionEventMetadata(topic)?.uiConfigOverride?.subscriptionValueOrRef;
            const subscriptionValueObject = subscriptionValueOrRef ? [...resolveObjectArrayRef("", subscriptionValueOrRef, inputs)] : null;
            subscriptionValue = subscriptionValueObject?.[0]?.value;
          }
          const legacySubscriptionValue = resolveStringRef(
            topic.uiConfig.name,
            topic.uiConfig.sourceAddress,
            inputs
          );
          const alertName = customAlertName ?? fusionEventId;
          alertNamesToCreate.push(alertName);
          createAlertInputs.push({
            fusionEventId,
            name: alertName,
            targetGroupId,
            subscriptionValue: subscriptionValue ?? legacySubscriptionValue,
            filterOptions: JSON.stringify(filterOptions)
          });
        } catch (e) {
          console.error(e);
          if (e instanceof Error) {
            const error2 = {
              ...e,
              message: `Cannot resolve subscription value. Make sure "inputs" argument is in correct format. ${e.message}`
            };
            setError(error2);
          }
        }
      }
    );
    setIsLoading(true);
    const alertIds = Object.keys(alerts).filter((alertName) => alertNamesToCreate.includes(alertName)).map((alertName) => alerts[alertName].id);
    if (alertIds.length > 0) {
      try {
        await frontendClient.deleteAlerts({ ids: alertIds });
      } catch (e) {
        if (e instanceof Error)
          setError({ ...e, message: `Failed to del. existing. ${e.message}` });
        console.error(e);
        return setIsLoading(false);
      }
    }
    try {
      await frontendClient.ensureFusionAlerts({ alerts: createAlertInputs });
      const data = await frontendClient.fetchFusionData();
      refreshAlerts(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };
  const subscribeAlertsDefault = async (topics, targetGroupId) => {
    const createAlertInputs = [];
    const alertNamesToCreate = [];
    topics.forEach((topic) => {
      const fusionEventDescriptor = topic.fusionEventDescriptor;
      const fusionEventMetadataJson = fusionEventDescriptor.metadata;
      const fusionEventId = fusionEventDescriptor.id;
      if (fusionEventMetadataJson && fusionEventId) {
        const fusionEventMetadata = getFusionEventMetadata(topic);
        if (fusionEventMetadata?.uiConfigOverride?.isSubscriptionValueInputable)
          return;
        const filters = fusionEventMetadata?.filters?.filter(isAlertFilter);
        const fusionFilterOptionsInput = {};
        if (filters && filters.length > 0) {
          const userInputParams = filters[0].userInputParams;
          const userInputOptions = {};
          userInputParams.forEach((userInputParm) => {
            userInputOptions[userInputParm.name] = convertOptionValue(
              userInputParm.defaultValue,
              userInputParm.kind
            );
          });
          fusionFilterOptionsInput[filters[0].name] = userInputOptions;
        }
        const filterOptions = {
          version: 1,
          input: fusionFilterOptionsInput
        };
        try {
          const subscriptionValueOrRef = getFusionEventMetadata(topic)?.uiConfigOverride?.subscriptionValueOrRef;
          const subscriptionValue = subscriptionValueOrRef ? resolveObjectArrayRef("", subscriptionValueOrRef, inputs) : null;
          const legacySubscriptionValue = resolveStringRef(
            topic.uiConfig.name,
            topic.uiConfig.sourceAddress,
            // NOTE: Deprecated, always the hardcoded '*'
            inputs
          );
          const alertName = fusionEventId;
          alertNamesToCreate.push(alertName);
          createAlertInputs.push({
            fusionEventId,
            name: alertName,
            targetGroupId,
            subscriptionValue: subscriptionValue?.[0]?.value ?? legacySubscriptionValue,
            filterOptions: JSON.stringify(filterOptions)
          });
        } catch (e) {
          console.error(e);
          if (e instanceof Error) {
            const error2 = {
              ...e,
              message: `Cannot resolve subscription value. Make sure "inputs" argument is in correct format. ${e.message}`
            };
            setError(error2);
          }
        }
      }
    });
    setIsLoading(true);
    for (const alertName of Object.keys(alerts)) {
      if (alertNamesToCreate.includes(alertName)) {
        const id = alerts[alertName].id;
        try {
          await frontendClient.deleteAlert({ id });
        } catch (e) {
        }
      }
    }
    try {
      await frontendClient.ensureFusionAlerts({ alerts: createAlertInputs });
      const data = await frontendClient.fetchFusionData();
      refreshAlerts(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };
  const getAlertFilterOptions = useCallback2(
    (alertName) => {
      const parsedFilterOptions = JSON.parse(
        alerts[alertName]?.filterOptions ?? "{}"
      );
      return isFusionFilterOptions(parsedFilterOptions) ? parsedFilterOptions : null;
    },
    [alerts]
  );
  const refreshAlerts = (newData) => {
    const alerts2 = {};
    newData.alert?.forEach((alert) => {
      if (alert?.name) {
        alerts2[alert.name] = alert;
      }
    });
    setAlerts(alerts2);
  };
  const getTopicStackAlertsFromTopicName = (topicName) => {
    return Object.keys(alerts).map((alertName) => {
      const resolved = resolveTopicStackAlertName(alertName);
      if (resolved.fusionEventTypeId === topicName) {
        return {
          alertName,
          id: alerts[alertName].id,
          subscriptionValueInfo: {
            label: resolved.subscriptionLabel,
            value: resolved.subscriptionValue
          },
          filterOptions: getAlertFilterOptions(alertName)
        };
      }
    }).filter(
      (data) => data !== void 0 && "id" in data && "alertName" in data && "subscriptionValueInfo" in data
    );
  };
  const getTopicStackAlerts = (fusionEventTypeId) => {
    return Object.keys(alerts).map((alertName) => {
      const resolved = resolveAlertName(alertName);
      if (isAlertMetadataForTopicStack(resolved) && resolved.fusionEventTypeId === fusionEventTypeId) {
        return {
          alertName,
          id: alerts[alertName].id,
          subscriptionValueInfo: {
            label: resolved.subscriptionLabel,
            value: resolved.subscriptionValue
          },
          filterOptions: getAlertFilterOptions(alertName)
        };
      }
    }).filter(
      (data) => data !== void 0 && "id" in data && "alertName" in data && "subscriptionValueInfo" in data && "filterOptions" in data
    );
  };
  return /* @__PURE__ */ React5.createElement(
    NotifiTopicContext.Provider,
    {
      value: {
        isLoading,
        error,
        subscribeAlertsDefault,
        isAlertSubscribed,
        subscribeAlertsWithFilterOptions,
        getAlertFilterOptions,
        getTopicStackAlerts,
        unsubscribeAlerts,
        unsubscribeAlert,
        getTopicStackAlertsFromTopicName
      }
    },
    children
  );
};
var useNotifiTopicContext = () => useContext3(NotifiTopicContext);

// lib/context/NotifiHistoryContext.tsx
import React6, {
  createContext as createContext4,
  useEffect as useEffect4,
  useState as useState4
} from "react";
var NotifiHistoryContext = createContext4(
  {}
);
var NotifiHistoryContextProvider = ({
  children,
  notificationCountPerPage = 20,
  unreadCountScope = "card"
}) => {
  const [isLoading, setIsLoading] = useState4(true);
  const [error, setError] = useState4(null);
  const { frontendClient, frontendClientStatus } = useNotifiFrontendClientContext();
  const [cursorInfo, setCursorInfo] = useState4({
    hasNextPage: false,
    endCursor: void 0
  });
  const [unreadCount, setUnreadCount] = useState4(null);
  const [isIncludeRead, setIsIncludeRead] = useState4(true);
  const [historyItems, setHistoryItems] = useState4([]);
  const { cardConfig, fusionEventTopics } = useNotifiTenantConfigContext();
  const isInitialLoaded = React6.useRef(false);
  const currentSubscription = React6.useRef();
  useEffect4(() => {
    if (frontendClientStatus.isAuthenticated) {
      const fusionEventIds = new Set(
        fusionEventTopics.map((topic) => topic.fusionEventDescriptor.id ?? "")
      );
      const historyUpdateHandler = (evt) => {
        if (evt.__typename !== "NotificationHistoryStateChangedEvent") return;
        frontendClient.getFusionNotificationHistory({
          first: notificationCountPerPage,
          includeHidden: false,
          includeRead: isIncludeRead
        }).then((res) => {
          const existingItemIds = new Set(
            historyItems.map((item) => item.id)
          );
          const newItems = res?.nodes?.map(parseHistoryItem).filter(
            (item) => !existingItemIds.has(item.id) && fusionEventIds.has(item.fusionEventId)
          );
          if (newItems?.length && newItems.length > 0) {
            setHistoryItems((existing) => [...newItems, ...existing]);
            setUnreadCount(
              (prev) => prev !== null ? prev + newItems.length : null
            );
          }
        });
      };
      currentSubscription.current = frontendClient.addEventListener(
        "stateChanged",
        historyUpdateHandler,
        (error2) => {
          if (error2 instanceof Error) {
            setError({
              ...error2,
              message: `NotifiHistoryContext - stateChanged: ${error2.message}`
            });
          }
          console.error("NotifiHistoryContext - stateChanged:", error2);
        }
      );
      return () => {
        const id = currentSubscription.current;
        if (!id) return;
        frontendClient.removeEventListener("stateChanged", id);
        currentSubscription.current = void 0;
        setError(null);
      };
    }
  }, [frontendClientStatus, historyItems]);
  const getHistoryItems = React6.useCallback(
    async (initialLoad) => {
      if (!frontendClientStatus.isAuthenticated) return;
      const cardEventTypeNames = new Set(
        cardConfig?.eventTypes?.map((event) => event.name) ?? []
      );
      if (cardEventTypeNames.size === 0) {
        return;
      }
      if (!initialLoad && !cursorInfo.hasNextPage) {
        setError(new Error("No more notification history to fetch"));
        return;
      }
      if (isLoading && isInitialLoaded.current) {
        return;
      }
      isInitialLoaded.current = true;
      setIsLoading(true);
      try {
        const result2 = await frontendClient.getFusionNotificationHistory({
          first: notificationCountPerPage,
          after: initialLoad ? void 0 : cursorInfo.endCursor,
          includeHidden: false,
          includeRead: isIncludeRead
        });
        if (!result2 || result2.nodes?.length === 0 || !result2.nodes) {
          return;
        }
        const validHistoryItems = result2.nodes.filter((node) => {
          if (!node.detail || !validateEventDetails(node.detail)) return false;
          return cardEventTypeNames.has(node.detail.sourceName);
        });
        setHistoryItems(
          (existing) => initialLoad ? validHistoryItems.map(parseHistoryItem) ?? [] : [...existing, ...validHistoryItems.map(parseHistoryItem) ?? []]
        );
        setCursorInfo(result2.pageInfo);
        setError(null);
      } catch (e) {
        setError(
          new Error(
            "ERROR: Failed to fetch notification history, please try again."
          )
        );
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [cardConfig, cursorInfo, frontendClientStatus, isIncludeRead]
  );
  useEffect4(() => {
    if (frontendClientStatus.isAuthenticated && !isInitialLoaded.current && cardConfig) {
      getHistoryItems(true);
      let getUnreadCountInput = void 0;
      switch (unreadCountScope) {
        case "card":
          if (!cardConfig.id)
            return setError(
              new Error(
                "Card ID is missing, fetch tenant unread count instead."
              )
            );
          getUnreadCountInput = cardConfig.id;
          break;
        default:
      }
      frontendClient.getUnreadNotificationHistoryCount(getUnreadCountInput).then(({ count }) => {
        setUnreadCount(count);
      });
    }
  }, [frontendClientStatus, getHistoryItems, cardConfig]);
  useEffect4(() => {
    if (frontendClientStatus.isAuthenticated) {
      setHistoryItems([]);
      getHistoryItems(true);
    }
  }, [isIncludeRead]);
  const markAsRead = async (ids) => {
    const isIdsEmpty = ids?.length === 0 || !ids;
    setIsLoading(true);
    switch (isIdsEmpty) {
      case true:
        try {
          const res = await frontendClient.getFusionNotificationHistory({
            first: 1,
            includeHidden: false
          });
          if (!res) return;
          ids = res.nodes?.map((node) => node.id) ?? [];
          await frontendClient.markFusionNotificationHistoryAsRead({
            ids: [],
            beforeId: res.nodes?.[0]?.id
          });
        } catch (e) {
          setError(
            new Error(
              "ERROR: Failed to mark all notification history as read, please try again."
            )
          );
          console.error(e);
        } finally {
          setIsLoading(false);
        }
        setHistoryItems(
          (items) => items.map((item) => ({ ...item, read: true }))
        );
        setUnreadCount(0);
        break;
      case false:
        try {
          await frontendClient.markFusionNotificationHistoryAsRead({
            ids
          });
          setError(null);
        } catch (e) {
          setError(
            new Error(
              "ERROR: Failed to mark selected notification history as read, please try again."
            )
          );
          console.error(e);
        } finally {
          setIsLoading(false);
        }
        setHistoryItems((items) => {
          const updatedItems = items.map((item) => {
            if (ids.includes(item.id)) {
              return { ...item, read: true };
            }
            return item;
          });
          return updatedItems;
        });
        setUnreadCount((prev) => prev ? prev - ids.length : null);
    }
  };
  const parseHistoryItem = React6.useCallback(
    (history) => {
      const eventDetails = history.detail;
      const fusionEventVariables = history.fusionEventVariables;
      if (!eventDetails || eventDetails.__typename !== "GenericEventDetails") {
        return {
          id: "",
          timestamp: "",
          topic: "Unsupported Notification Type",
          icon: "",
          customIconUrl: "",
          subject: "Unsupported Notification Type",
          message: "Invalid notification history detail: only support GenericEventDetails",
          read: true,
          fusionEventId: ""
        };
      }
      const fusionEventTopic = fusionEventTopics.find(
        (fusionEventTopic2) => (
          // TODO: Use FusionEventId to find the topic (Blocker: MVP-5101)
          // fusionEventTopic.uiConfig.fusionEventId ===
          //   eventDetails.fusionEventId ||
          fusionEventTopic2.uiConfig.name === eventDetails.sourceName
        )
      );
      const fusionEventMetadata = fusionEventTopic ? getFusionEventMetadata(fusionEventTopic) : null;
      const historyTopic = fusionEventMetadata?.uiConfigOverride?.historyDisplayName || // display topic name if it has a custom display name
      fusionEventTopic?.uiConfig.topicGroupName || // display topic group name if it belongs to a certain topic group
      fusionEventTopic?.fusionEventDescriptor.name || // display topic name if it has no custom display name
      eventDetails.sourceName;
      return {
        id: history.id,
        timestamp: history.createdDate,
        topic: historyTopic,
        subject: eventDetails.notificationTypeName,
        message: eventDetails.genericMessageHtml ?? eventDetails.genericMessage,
        icon: eventDetails.icon,
        customIconUrl: eventDetails.customIconUrl ?? "",
        read: history.read,
        fusionEventId: parseHistoryFusionVariablesJson(fusionEventVariables)?.NotifiData.EventTypeId ?? ""
      };
    },
    [fusionEventTopics]
  );
  return /* @__PURE__ */ React6.createElement(
    NotifiHistoryContext.Provider,
    {
      value: {
        isLoading,
        error,
        getHistoryItems,
        markAsRead,
        historyItems,
        unreadCount: unreadCount ?? 0,
        hasNextPage: cursorInfo.hasNextPage,
        isIncludeRead,
        setIsIncludeRead
      }
    },
    children
  );
};
var useNotifiHistoryContext = () => React6.useContext(NotifiHistoryContext);
var validateEventDetails = (details) => {
  return details.__typename === "GenericEventDetails";
};
var parseHistoryFusionVariablesJson = (variablesJson) => {
  try {
    const variables = JSON.parse(variablesJson);
    if (typeof variables === "object" && variables.NotifiData && "EventTypeId" in variables.NotifiData) {
      return variables;
    }
    return null;
  } catch (e) {
    console.warn(
      "NotifiHistoryContext: Found invalid fusionVariablesJson: ",
      variablesJson
    );
    return null;
  }
};

// lib/context/NotifiContextProvider.tsx
import React9 from "react";

// lib/context/GlobalStateContext.tsx
import React7, {
  createContext as createContext5,
  useContext as useContext4,
  useState as useState5
} from "react";
var GlobalStateContext = createContext5({
  globalLoading: { isLoading: false },
  setGlobalLoading: () => void 0,
  globalError: null,
  setGlobalError: () => void 0,
  globalCtas: null,
  setGlobalCtas: () => void 0
});
var GlobalStateContextProvider = ({
  children
}) => {
  const [globalLoading, setGlobalLoading] = useState5({
    isLoading: false
  });
  const [globalError, setGlobalError] = useState5(null);
  const [globalCtas, setGlobalCtas] = useState5(null);
  return /* @__PURE__ */ React7.createElement(
    GlobalStateContext.Provider,
    {
      value: {
        globalLoading,
        setGlobalLoading,
        globalError,
        setGlobalError,
        globalCtas,
        setGlobalCtas
      }
    },
    children
  );
};
var useGlobalStateContext = () => useContext4(GlobalStateContext);

// lib/context/NotifiUserSettingContext.tsx
import React8, {
  createContext as createContext6,
  useCallback as useCallback3,
  useContext as useContext5,
  useEffect as useEffect5,
  useState as useState6
} from "react";
var FtuStage = /* @__PURE__ */ ((FtuStage2) => {
  FtuStage2[FtuStage2["Destination"] = 3] = "Destination";
  FtuStage2[FtuStage2["Alerts"] = 2] = "Alerts";
  FtuStage2[FtuStage2["Done"] = 1] = "Done";
  return FtuStage2;
})(FtuStage || {});
var NotifiUserSettingContext = createContext6(
  {}
);
var NotifiUserSettingContextProvider = ({
  children
}) => {
  const { frontendClientStatus, frontendClient } = useNotifiFrontendClientContext();
  const [ftuStage, setFtuStage] = useState6(null);
  const [isLoading, setIsLoading] = useState6(true);
  const [error, setError] = useState6(null);
  const isInitialLoaded = React8.useRef(false);
  useEffect5(() => {
    if (frontendClientStatus.isAuthenticated && !isInitialLoaded.current) {
      if (isLoading && isInitialLoaded.current) return;
      isInitialLoaded.current = true;
      setIsLoading(true);
      frontendClient.getUserSettings().then((userSettings) => {
        setFtuStage(userSettings?.ftuStage ?? null);
      }).catch((e) => {
        if (e instanceof Error) {
          setError({
            ...e,
            message: `Failed to fetch user settings (.getUserSettings): ${e.message}`
          });
        }
        console.error(e);
      }).finally(() => setIsLoading(false));
    }
  }, [frontendClientStatus.isAuthenticated]);
  const updateFtuStage = useCallback3(
    async (ftuConfigStep) => {
      setIsLoading(true);
      try {
        await frontendClient.updateUserSettings({
          input: { ftuStage: ftuConfigStep }
        });
        setFtuStage(ftuConfigStep);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [frontendClient?.userState?.status]
  );
  return /* @__PURE__ */ React8.createElement(
    NotifiUserSettingContext.Provider,
    {
      value: { ftuStage, updateFtuStage, isLoading, error }
    },
    children
  );
};
var useNotifiUserSettingContext = () => useContext5(NotifiUserSettingContext);

// lib/context/NotifiContextProvider.tsx
var NotifiContextProvider = ({ children, ...params }) => {
  return /* @__PURE__ */ React9.createElement(NotifiFrontendClientContextProvider, { ...params }, /* @__PURE__ */ React9.createElement(
    NotifiTenantConfigContextProvider,
    {
      cardId: params.cardId,
      inputs: params.inputs
    },
    /* @__PURE__ */ React9.createElement(
      NotifiTargetContextProvider,
      {
        toggleTargetAvailability: params.toggleTargetAvailability
      },
      /* @__PURE__ */ React9.createElement(NotifiTopicContextProvider, null, /* @__PURE__ */ React9.createElement(
        NotifiHistoryContextProvider,
        {
          notificationCountPerPage: params.notificationCountPerPage,
          unreadCountScope: params.unreadCountScope
        },
        /* @__PURE__ */ React9.createElement(NotifiUserSettingContextProvider, null, /* @__PURE__ */ React9.createElement(GlobalStateContextProvider, null, children))
      ))
    )
  ));
};

// lib/components/NotifiCardModal.tsx
import clsx34 from "clsx";
import React51, { useEffect as useEffect6, useState as useState7 } from "react";

// lib/components/Connect.tsx
import clsx4 from "clsx";
import React16 from "react";

// lib/assets/Icons.tsx
import React10 from "react";
var Icon = (props) => {
  switch (props.type) {
    case "connect":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement("g", { clipPath: "url(#clip0_1821_6938)" }, /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M3.9 12C3.9 10.29 5.29 8.9 7 8.9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15.1H7C5.29 15.1 3.9 13.71 3.9 12ZM8 13H16V11H8V13ZM17 7H13V8.9H17C18.71 8.9 20.1 10.29 20.1 12C20.1 13.71 18.71 15.1 17 15.1H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7Z",
            fill: "currentColor"
          }
        )),
        /* @__PURE__ */ React10.createElement("defs", null, /* @__PURE__ */ React10.createElement("clipPath", { id: "clip0_1821_6938" }, /* @__PURE__ */ React10.createElement("rect", { width: "24", height: "24", fill: "white" })))
      );
    case "check":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M6 12.5L9.84 16L18 8",
            stroke: "currentColor",
            strokeLinecap: "round"
          }
        )
      );
    case "email":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "13",
          height: "11",
          viewBox: "0 0 13 11",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M11.5 0.499996H1.5C0.8125 0.499996 0.25625 1.0625 0.25625 1.75L0.25 9.25C0.25 9.9375 0.8125 10.5 1.5 10.5H11.5C12.1875 10.5 12.75 9.9375 12.75 9.25V1.75C12.75 1.0625 12.1875 0.499996 11.5 0.499996ZM11.5 3L6.5 6.125L1.5 3V1.75L6.5 4.875L11.5 1.75V3Z",
            fill: "currentColor"
          }
        )
      );
    case "sms":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "16",
          height: "16",
          viewBox: "0 0 16 16",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M8.33335 0.708328H2.66669C1.49085 0.708328 0.541687 1.65749 0.541687 2.83333V14.1667C0.541687 15.3425 1.49085 16.2917 2.66669 16.2917H8.33335C9.50919 16.2917 10.4584 15.3425 10.4584 14.1667V2.83333C10.4584 1.65749 9.50919 0.708328 8.33335 0.708328ZM6.91669 14.875H4.08335V14.1667H6.91669V14.875ZM9.21877 12.75H1.78127V2.83333H9.21877V12.75Z",
            fill: "currentColor"
          }
        )
      );
    case "telegram":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "16",
          height: "16",
          viewBox: "0 0 16 16",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M8.63846 15.4458L16.4253 0.189566L0.00576619 5.06668L3.35716 9.10781L12.9656 3.06713L5.27554 11.4143L8.63846 15.4458Z",
            fill: "currentColor"
          }
        )
      );
    case "arrow-back":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z",
            fill: "currentColor"
          }
        )
      );
    case "discord":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "17",
          height: "13",
          viewBox: "0 0 17 13",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M14.0728 1.05036C13.0133 0.564196 11.8771 0.206009 10.6891 0.000860571C10.6675 -0.00309873 10.6459 0.00679578 10.6347 0.0265853C10.4886 0.286483 10.3267 0.62554 10.2134 0.892038C8.93563 0.700746 7.66444 0.700746 6.4129 0.892038C6.29953 0.619617 6.13179 0.286483 5.98501 0.0265853C5.97386 0.00745605 5.95225 -0.00243846 5.93062 0.000860571C4.74329 0.205353 3.60709 0.56354 2.54688 1.05036C2.5377 1.05432 2.52983 1.06092 2.52461 1.06949C0.369461 4.28924 -0.220925 7.42985 0.0686984 10.5315C0.0700089 10.5467 0.0785271 10.5612 0.0903219 10.5704C1.51223 11.6146 2.88959 12.2486 4.24137 12.6688C4.263 12.6754 4.28593 12.6675 4.29969 12.6496C4.61946 12.213 4.9045 11.7525 5.14889 11.2683C5.16332 11.24 5.14955 11.2063 5.12007 11.1951C4.66795 11.0236 4.23744 10.8145 3.82331 10.577C3.79056 10.5579 3.78793 10.5111 3.81807 10.4886C3.90522 10.4233 3.99239 10.3554 4.0756 10.2868C4.09065 10.2742 4.11163 10.2716 4.12933 10.2795C6.84994 11.5217 9.79533 11.5217 12.4838 10.2795C12.5015 10.2709 12.5225 10.2736 12.5382 10.2861C12.6215 10.3547 12.7086 10.4233 12.7964 10.4886C12.8265 10.5111 12.8246 10.5579 12.7918 10.577C12.3777 10.8191 11.9472 11.0236 11.4944 11.1945C11.4649 11.2057 11.4518 11.24 11.4662 11.2683C11.7159 11.7519 12.0009 12.2123 12.3148 12.649C12.3279 12.6675 12.3515 12.6754 12.3731 12.6688C13.7314 12.2486 15.1088 11.6146 16.5307 10.5704C16.5432 10.5612 16.551 10.5473 16.5523 10.5322C16.899 6.9463 15.9718 3.83145 14.0945 1.07015C14.0899 1.06092 14.082 1.05432 14.0728 1.05036ZM5.55517 8.64293C4.73608 8.64293 4.06118 7.89094 4.06118 6.96742C4.06118 6.04391 4.72299 5.29192 5.55517 5.29192C6.39389 5.29192 7.06226 6.05051 7.04915 6.96742C7.04915 7.89094 6.38733 8.64293 5.55517 8.64293ZM11.079 8.64293C10.2599 8.64293 9.585 7.89094 9.585 6.96742C9.585 6.04391 10.2468 5.29192 11.079 5.29192C11.9177 5.29192 12.5861 6.05051 12.573 6.96742C12.573 7.89094 11.9177 8.64293 11.079 8.64293Z",
            fill: "currentColor"
          }
        )
      );
    case "slack":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 16 16",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M3.94125 9.84688C3.941 10.1377 3.85454 10.4219 3.6928 10.6636C3.53105 10.9052 3.30129 11.0935 3.03254 11.2047C2.7638 11.3158 2.46815 11.3447 2.18295 11.2878C1.89776 11.231 1.63584 11.0908 1.4303 10.8851C1.22475 10.6794 1.08481 10.4173 1.02817 10.1321C0.971524 9.84683 1.00072 9.5512 1.11206 9.28255C1.22341 9.0139 1.4119 8.7843 1.65372 8.62276C1.89554 8.46122 2.17982 8.375 2.47063 8.375H3.94125V9.84688Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M4.68262 9.84709C4.68262 9.45706 4.83756 9.083 5.11335 8.8072C5.38915 8.5314 5.76321 8.37646 6.15324 8.37646C6.54328 8.37646 6.91734 8.5314 7.19313 8.8072C7.46893 9.083 7.62387 9.45706 7.62387 9.84709V13.5296C7.62387 13.9196 7.46893 14.2937 7.19313 14.5695C6.91734 14.8453 6.54328 15.0002 6.15324 15.0002C5.76321 15.0002 5.38915 14.8453 5.11335 14.5695C4.83756 14.2937 4.68262 13.9196 4.68262 13.5296V9.84709Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M6.15297 3.94125C5.86216 3.941 5.57795 3.85454 5.33628 3.6928C5.0946 3.53105 4.9063 3.30129 4.79518 3.03254C4.68406 2.7638 4.65512 2.46815 4.712 2.18295C4.76889 1.89776 4.90905 1.63584 5.11477 1.4303C5.32049 1.22475 5.58254 1.08481 5.86777 1.02817C6.15301 0.971524 6.44864 1.00072 6.71729 1.11206C6.98594 1.22341 7.21555 1.4119 7.37709 1.65372C7.53863 1.89554 7.62484 2.17982 7.62484 2.47063V3.94125H6.15297Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M6.15312 4.68262C6.54316 4.68262 6.91722 4.83756 7.19301 5.11335C7.46881 5.38915 7.62375 5.76321 7.62375 6.15324C7.62375 6.54328 7.46881 6.91734 7.19301 7.19313C6.91722 7.46893 6.54316 7.62387 6.15312 7.62387H2.47062C2.08059 7.62387 1.70653 7.46893 1.43074 7.19313C1.15494 6.91734 1 6.54328 1 6.15324C1 5.76321 1.15494 5.38915 1.43074 5.11335C1.70653 4.83756 2.08059 4.68262 2.47062 4.68262H6.15312Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M12.0586 6.15297C12.0588 5.86216 12.1453 5.57795 12.307 5.33628C12.4688 5.0946 12.6986 4.9063 12.9673 4.79518C13.236 4.68406 13.5317 4.65512 13.8169 4.712C14.1021 4.76889 14.364 4.90905 14.5695 5.11477C14.7751 5.32049 14.915 5.58254 14.9717 5.86777C15.0283 6.15301 14.9991 6.44864 14.8878 6.71729C14.7764 6.98594 14.5879 7.21555 14.3461 7.37709C14.1043 7.53863 13.82 7.62484 13.5292 7.62484H12.0586V6.15297Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M11.3177 6.15312C11.3177 6.54316 11.1628 6.91722 10.887 7.19301C10.6112 7.46881 10.2371 7.62375 9.84709 7.62375C9.45706 7.62375 9.083 7.46881 8.8072 7.19301C8.5314 6.91722 8.37646 6.54316 8.37646 6.15312V2.47062C8.37646 2.08059 8.5314 1.70653 8.8072 1.43074C9.083 1.15494 9.45706 1 9.84709 1C10.2371 1 10.6112 1.15494 10.887 1.43074C11.1628 1.70653 11.3177 2.08059 11.3177 2.47062V6.15312Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M9.84688 12.0586C10.1377 12.0588 10.4219 12.1453 10.6636 12.307C10.9052 12.4688 11.0935 12.6986 11.2047 12.9673C11.3158 13.236 11.3447 13.5317 11.2878 13.8169C11.231 14.1021 11.0908 14.364 10.8851 14.5695C10.6794 14.7751 10.4173 14.915 10.1321 14.9717C9.84683 15.0283 9.5512 14.9991 9.28255 14.8878C9.0139 14.7764 8.7843 14.5879 8.62276 14.3461C8.46122 14.1043 8.375 13.82 8.375 13.5292V12.0586H9.84688Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M9.84709 11.3177C9.45706 11.3177 9.083 11.1628 8.8072 10.887C8.5314 10.6112 8.37646 10.2371 8.37646 9.84709C8.37646 9.45706 8.5314 9.083 8.8072 8.8072C9.083 8.5314 9.45706 8.37646 9.84709 8.37646H13.5296C13.9196 8.37646 14.2937 8.5314 14.5695 8.8072C14.8453 9.083 15.0002 9.45706 15.0002 9.84709C15.0002 10.2371 14.8453 10.6112 14.5695 10.887C14.2937 11.1628 13.9196 11.3177 13.5296 11.3177H9.84709Z",
            fill: "currentColor"
          }
        )
      );
    case "info":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M11.3335 8.66671H12.6668V10H11.3335V8.66671ZM11.3335 11.3334H12.6668V15.3334H11.3335V11.3334ZM12.0002 5.33337C8.32016 5.33337 5.3335 8.32004 5.3335 12C5.3335 15.68 8.32016 18.6667 12.0002 18.6667C15.6802 18.6667 18.6668 15.68 18.6668 12C18.6668 8.32004 15.6802 5.33337 12.0002 5.33337ZM12.0002 17.3334C9.06016 17.3334 6.66683 14.94 6.66683 12C6.66683 9.06004 9.06016 6.66671 12.0002 6.66671C14.9402 6.66671 17.3335 9.06004 17.3335 12C17.3335 14.94 14.9402 17.3334 12.0002 17.3334Z",
            fill: "currentColor"
          }
        )
      );
    case "bin":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement("g", { clipPath: "url(#clip0_5611_1341)" }, /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M7.5 17.4444C7.5 18.3 8.175 19 9 19H15C15.825 19 16.5 18.3 16.5 17.4444V8.11111H7.5V17.4444ZM17.25 5.77778H14.625L13.875 5H10.125L9.375 5.77778H6.75V7.33333H17.25V5.77778Z",
            fill: "currentColor"
          }
        )),
        /* @__PURE__ */ React10.createElement("defs", null, /* @__PURE__ */ React10.createElement("clipPath", { id: "clip0_5611_1341" }, /* @__PURE__ */ React10.createElement("rect", { width: "24", height: "24", fill: "white" })))
      );
    case "gear":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M21.2938 13.9001L20.8498 13.6441C21.0497 12.5566 21.0497 11.4417 20.8498 10.3541L21.2938 10.0981C21.6352 9.90114 21.9345 9.63883 22.1746 9.32618C22.4147 9.01353 22.5908 8.65666 22.6929 8.27594C22.7951 7.89522 22.8212 7.49811 22.7699 7.10728C22.7186 6.71645 22.5908 6.33956 22.3938 5.99812C22.1968 5.65669 21.9345 5.35739 21.6219 5.11733C21.3092 4.87726 20.9523 4.70113 20.5716 4.59898C20.1909 4.49684 19.7938 4.47068 19.403 4.52201C19.0121 4.57334 18.6352 4.70114 18.2938 4.89812L17.8488 5.15512C17.0083 4.43704 16.0425 3.88037 14.9998 3.51312V3.00012C14.9998 2.20447 14.6837 1.44141 14.1211 0.878802C13.5585 0.316193 12.7954 0.00012207 11.9998 0.00012207C11.2041 0.00012207 10.4411 0.316193 9.87848 0.878802C9.31587 1.44141 8.9998 2.20447 8.9998 3.00012V3.51312C7.95719 3.88169 6.99165 4.43972 6.1518 5.15912L5.7048 4.90012C5.01524 4.5023 4.19588 4.39469 3.42698 4.60098C2.65808 4.80727 2.00262 5.31056 1.6048 6.00012C1.20697 6.68968 1.09937 7.50904 1.30566 8.27794C1.51195 9.04684 2.01524 9.7023 2.7048 10.1001L3.1488 10.3561C2.94891 11.4437 2.94891 12.5586 3.1488 13.6461L2.7048 13.9021C2.01524 14.2999 1.51195 14.9554 1.30566 15.7243C1.09937 16.4932 1.20697 17.3126 1.6048 18.0021C2.00262 18.6917 2.65808 19.195 3.42698 19.4013C4.19588 19.6075 5.01524 19.4999 5.7048 19.1021L6.1498 18.8451C6.99056 19.5633 7.95678 20.12 8.9998 20.4871V21.0001C8.9998 21.7958 9.31587 22.5588 9.87848 23.1214C10.4411 23.6841 11.2041 24.0001 11.9998 24.0001C12.7954 24.0001 13.5585 23.6841 14.1211 23.1214C14.6837 22.5588 14.9998 21.7958 14.9998 21.0001V20.4871C16.0424 20.1186 17.008 19.5605 17.8478 18.8411L18.2948 19.0991C18.9844 19.4969 19.8037 19.6045 20.5726 19.3983C21.3415 19.192 21.997 18.6887 22.3948 17.9991C22.7926 17.3096 22.9002 16.4902 22.6939 15.7213C22.4876 14.9524 21.9844 14.2969 21.2948 13.8991L21.2938 13.9001ZM18.7458 10.1241C19.0844 11.3512 19.0844 12.6471 18.7458 13.8741C18.6867 14.0877 18.7002 14.3149 18.7841 14.5199C18.8681 14.725 19.0178 14.8964 19.2098 15.0071L20.2938 15.6331C20.5236 15.7657 20.6913 15.9842 20.7601 16.2405C20.8288 16.4967 20.7929 16.7698 20.6603 16.9996C20.5277 17.2294 20.3092 17.3972 20.053 17.4659C19.7967 17.5346 19.5236 17.4987 19.2938 17.3661L18.2078 16.7381C18.0157 16.6269 17.792 16.5827 17.572 16.6126C17.3521 16.6424 17.1483 16.7447 16.9928 16.9031C16.1027 17.8118 14.9814 18.4601 13.7498 18.7781C13.5348 18.8334 13.3444 18.9586 13.2084 19.134C13.0724 19.3095 12.9987 19.5252 12.9988 19.7471V21.0001C12.9988 21.2653 12.8934 21.5197 12.7059 21.7072C12.5184 21.8948 12.264 22.0001 11.9988 22.0001C11.7336 22.0001 11.4792 21.8948 11.2917 21.7072C11.1042 21.5197 10.9988 21.2653 10.9988 21.0001V19.7481C10.9989 19.5262 10.9252 19.3105 10.7892 19.135C10.6532 18.9596 10.4628 18.8344 10.2478 18.7791C9.01615 18.4598 7.89513 17.8101 7.0058 16.9001C6.85032 16.7417 6.64654 16.6394 6.42655 16.6096C6.20657 16.5797 5.9829 16.6239 5.7908 16.7351L4.7068 17.3621C4.59303 17.4288 4.46719 17.4724 4.33652 17.4902C4.20586 17.5081 4.07295 17.4999 3.94545 17.4662C3.81795 17.4325 3.69838 17.3739 3.59362 17.2938C3.48886 17.2137 3.40098 17.1137 3.33504 16.9995C3.26909 16.8852 3.22639 16.7591 3.2094 16.6283C3.1924 16.4976 3.20144 16.3647 3.23599 16.2374C3.27054 16.1102 3.32993 15.991 3.41073 15.8867C3.49153 15.7825 3.59215 15.6953 3.7068 15.6301L4.7908 15.0041C4.98275 14.8934 5.13247 14.722 5.21646 14.5169C5.30044 14.3119 5.31393 14.0847 5.2548 13.8711C4.91616 12.6441 4.91616 11.3482 5.2548 10.1211C5.31286 9.908 5.29873 9.68166 5.21461 9.47741C5.13049 9.27317 4.98111 9.10253 4.7898 8.99212L3.7058 8.36612C3.47599 8.23351 3.30827 8.01505 3.23954 7.75878C3.17081 7.50251 3.20669 7.22943 3.3393 6.99962C3.47191 6.76981 3.69038 6.60209 3.94664 6.53336C4.20291 6.46463 4.47599 6.50051 4.7058 6.63312L5.7918 7.26112C5.98338 7.37264 6.20658 7.41733 6.42633 7.38819C6.64607 7.35905 6.84991 7.25772 7.0058 7.10012C7.89589 6.19147 9.01722 5.54314 10.2488 5.22512C10.4644 5.16969 10.6554 5.04387 10.7914 4.86762C10.9275 4.69137 11.0008 4.47476 10.9998 4.25212V3.00012C10.9998 2.73491 11.1052 2.48055 11.2927 2.29302C11.4802 2.10548 11.7346 2.00012 11.9998 2.00012C12.265 2.00012 12.5194 2.10548 12.7069 2.29302C12.8944 2.48055 12.9998 2.73491 12.9998 3.00012V4.25212C12.9997 4.47408 13.0734 4.68977 13.2094 4.8652C13.3454 5.04064 13.5358 5.16585 13.7508 5.22112C14.9828 5.54027 16.1042 6.19001 16.9938 7.10012C17.1493 7.25859 17.3531 7.36081 17.573 7.39069C17.793 7.42056 18.0167 7.37639 18.2088 7.26512L19.2928 6.63812C19.4066 6.57142 19.5324 6.52789 19.6631 6.51002C19.7937 6.49216 19.9266 6.50031 20.0541 6.53402C20.1816 6.56773 20.3012 6.62633 20.406 6.70644C20.5107 6.78654 20.5986 6.88658 20.6646 7.00079C20.7305 7.11501 20.7732 7.24113 20.7902 7.37191C20.8072 7.50269 20.7982 7.63554 20.7636 7.76281C20.7291 7.89009 20.6697 8.00927 20.5889 8.1135C20.5081 8.21772 20.4074 8.30494 20.2928 8.37012L19.2088 8.99612C19.0178 9.10683 18.8689 9.2776 18.7851 9.48182C18.7014 9.68603 18.6876 9.91222 18.7458 10.1251V10.1241Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M12 8.00073C11.2089 8.00073 10.4355 8.23533 9.77772 8.67485C9.11993 9.11438 8.60723 9.73909 8.30448 10.47C8.00173 11.2009 7.92252 12.0052 8.07686 12.7811C8.2312 13.557 8.61216 14.2697 9.17157 14.8292C9.73098 15.3886 10.4437 15.7695 11.2196 15.9239C11.9956 16.0782 12.7998 15.999 13.5307 15.6963C14.2616 15.3935 14.8864 14.8808 15.3259 14.223C15.7654 13.5652 16 12.7919 16 12.0007C16 10.9399 15.5786 9.92245 14.8284 9.17231C14.0783 8.42216 13.0609 8.00073 12 8.00073ZM12 14.0007C11.6044 14.0007 11.2178 13.8834 10.8889 13.6637C10.56 13.4439 10.3036 13.1316 10.1522 12.7661C10.0009 12.4006 9.96126 11.9985 10.0384 11.6106C10.1156 11.2226 10.3061 10.8662 10.5858 10.5865C10.8655 10.3068 11.2219 10.1163 11.6098 10.0392C11.9978 9.96199 12.3999 10.0016 12.7654 10.153C13.1308 10.3043 13.4432 10.5607 13.6629 10.8896C13.8827 11.2185 14 11.6052 14 12.0007C14 12.5312 13.7893 13.0399 13.4142 13.4149C13.0391 13.79 12.5304 14.0007 12 14.0007Z",
            fill: "currentColor"
          }
        )
      );
    case "gear-fill":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "22",
          height: "24",
          viewBox: "0 0 22 24",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M0.606821 18C1.43398 19.4356 3.26834 19.9288 4.70393 19.1017C4.70491 19.1011 4.70585 19.1005 4.70684 19.1L5.15182 18.843C5.99182 19.5616 6.95735 20.119 7.9998 20.487V21C7.9998 22.6568 9.34296 24 10.9998 24C12.6566 24 13.9998 22.6568 13.9998 21V20.487C15.0424 20.1184 16.008 19.5604 16.8478 18.841L17.2948 19.099C18.7307 19.9274 20.5664 19.4349 21.3948 17.999C22.2232 16.563 21.7308 14.7274 20.2948 13.899L19.8508 13.643C20.0507 12.5554 20.0507 11.4405 19.8508 10.353L20.2948 10.097C21.7307 9.26855 22.2232 7.43292 21.3948 5.99695C20.5664 4.56103 18.7308 4.06852 17.2948 4.89694L16.8498 5.15395C16.009 4.43616 15.0428 3.87984 13.9998 3.513V3C13.9998 1.34316 12.6566 0 10.9998 0C9.34296 0 7.9998 1.34316 7.9998 3V3.513C6.95721 3.88158 5.99163 4.43958 5.15182 5.15902L4.70482 4.90003C3.26885 4.07156 1.43323 4.56408 0.604805 6C-0.223617 7.43592 0.268852 9.27159 1.70482 10.1L2.14882 10.356C1.94895 11.4435 1.94895 12.5584 2.14882 13.646L1.70482 13.902C0.272836 14.7326 -0.218039 16.5647 0.606821 18ZM10.9998 8.00002C13.2089 8.00002 14.9998 9.79088 14.9998 12C14.9998 14.2091 13.2089 16 10.9998 16C8.79068 16 6.99982 14.2091 6.99982 12C6.99982 9.79088 8.79068 8.00002 10.9998 8.00002Z",
            fill: "currentColor"
          }
        )
      );
    case "bell":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "24",
          height: "25",
          viewBox: "0 0 24 25",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M5.94375 9.04573C5.94375 5.70104 8.65517 2.98962 11.9999 2.98962C15.3446 2.98962 18.056 5.70104 18.056 9.04573V11.9069V11.9579L18.0612 12.0087C18.139 12.769 18.3525 13.5107 18.6928 14.1981L20.4004 17.6469C20.6641 18.1795 20.2766 18.8037 19.6824 18.8037L12.0334 18.8037L12.0288 18.8037L11.9999 18.8038L11.9709 18.8037H11.9663H4.3175C3.72325 18.8037 3.33579 18.1795 3.59946 17.6469L5.27892 14.2549C5.71172 13.3808 5.93905 12.4195 5.94374 11.4443H5.94375V11.4395V9.04573ZM11.9999 0.989624C7.5506 0.989624 3.94375 4.59646 3.94375 9.04573V11.437C3.94019 12.1068 3.78387 12.7671 3.48658 13.3675L1.80712 16.7595C0.88527 18.6214 2.2399 20.8037 4.3175 20.8037H11.9663L11.9999 20.8038L12.0334 20.8037H19.6824C21.76 20.8037 23.1146 18.6214 22.1927 16.7595L20.4851 13.3107C20.2583 12.8525 20.1134 12.3592 20.056 11.8532V9.04573C20.056 4.59647 16.4491 0.989624 11.9999 0.989624ZM11.9974 24.9996C10.1648 24.9996 8.64864 23.8374 8.39645 22.3257H15.5984C15.3462 23.8374 13.8301 24.9996 11.9974 24.9996ZM11.9974 18.7604L11.9796 18.7604H12.0153L11.9974 18.7604ZM8.35961 21.88L8.35965 21.8643V21.8956L8.35961 21.88Z",
            fill: "currentColor"
          }
        )
      );
    case "bell-fill":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M4.94394 8.04573C4.94394 4.14875 8.10307 0.989624 12.0001 0.989624C15.897 0.989624 19.0562 4.14875 19.0562 8.04573V10.9069C19.1218 11.5485 19.302 12.1743 19.5892 12.7544L21.2968 16.2032C21.8895 17.4004 21.0185 18.8037 19.6826 18.8037H12.0336L12.0001 18.8038L11.9665 18.8037H4.31768C2.98176 18.8037 2.11072 17.4004 2.70348 16.2032L4.38294 12.8112C4.74815 12.0736 4.93998 11.2624 4.94394 10.4395V8.04573ZM11.9976 23.9996C10.165 23.9996 8.64883 22.8374 8.39664 21.3257H15.5986C15.3464 22.8374 13.8302 23.9996 11.9976 23.9996ZM8.35979 20.88L8.35984 20.8643V20.8956L8.35979 20.88Z",
            fill: "currentColor"
          }
        )
      );
    case "CHART":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M17.0556 5.5H6.94444C6.15 5.5 5.5 6.15 5.5 6.94444V17.0556C5.5 17.85 6.15 18.5 6.94444 18.5H17.0556C17.85 18.5 18.5 17.85 18.5 17.0556V6.94444C18.5 6.15 17.85 5.5 17.0556 5.5ZM9.83333 15.6111H8.38889V10.5556H9.83333V15.6111ZM12.7222 15.6111H11.2778V8.38889H12.7222V15.6111ZM15.6111 15.6111H14.1667V12.7222H15.6111V15.6111Z",
            fill: "currentColor"
          }
        )
      );
    case "CHECKMARK":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          ...props,
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M6 12.5L9.84 16L18 8",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round"
          }
        )
      );
    case "CLOCK":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18ZM12.75 8C12.75 7.58579 12.4142 7.25 12 7.25C11.5858 7.25 11.25 7.58579 11.25 8V12.5V12.8107L11.4697 13.0303L13.9697 15.5303C14.2626 15.8232 14.7374 15.8232 15.0303 15.5303C15.3232 15.2374 15.3232 14.7626 15.0303 14.4697L12.75 12.1893V8Z",
            fill: "currentColor"
          }
        )
      );
    case "DAO":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M17.0623 7.92265L12.0002 5L6.93799 7.92265L12.0002 10.8453L17.0623 7.92265ZM12.0002 12L5.93799 8.5V15.5L12.0002 19L18.0623 15.5V8.5L12.0002 12Z",
            fill: "currentColor"
          }
        )
      );
    case "DOWN_ARROW":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M8 13.4705L12 16.9999M12 16.9999L16 13.4705M12 16.9999V7",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      );
    case "FLAG":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M14.1882 7.52941L13.8824 6H7V19H8.52941V13.6471H12.8118L13.1176 15.1765H18.4706V7.52941H14.1882Z",
            fill: "currentColor"
          }
        )
      );
    case "GRAPH":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M7 16.5L10.5 11L13 14L17 8",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round"
          }
        )
      );
    case "INFO":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M11.3335 8.66671H12.6668V10H11.3335V8.66671ZM11.3335 11.3334H12.6668V15.3334H11.3335V11.3334ZM12.0002 5.33337C8.32016 5.33337 5.3335 8.32004 5.3335 12C5.3335 15.68 8.32016 18.6667 12.0002 18.6667C15.6802 18.6667 18.6668 15.68 18.6668 12C18.6668 8.32004 15.6802 5.33337 12.0002 5.33337ZM12.0002 17.3334C9.06016 17.3334 6.66683 14.94 6.66683 12C6.66683 9.06004 9.06016 6.66671 12.0002 6.66671C14.9402 6.66671 17.3335 9.06004 17.3335 12C17.3335 14.94 14.9402 17.3334 12.0002 17.3334Z",
            fill: "currentColor"
          }
        )
      );
    case "MEGAPHONE":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M16.7498 10.7917V12.2084H19.5832V10.7917H16.7498ZM15.3332 15.4738C16.0132 15.9767 16.8986 16.6425 17.5998 17.1667C17.8832 16.7913 18.1665 16.4088 18.4498 16.0334C17.7486 15.5092 16.8632 14.8434 16.1832 14.3334C15.8998 14.7159 15.6165 15.0984 15.3332 15.4738ZM18.4498 6.96671C18.1665 6.59129 17.8832 6.20879 17.5998 5.83337C16.8986 6.35754 16.0132 7.02337 15.3332 7.53337C15.6165 7.90879 15.8998 8.29129 16.1832 8.66671C16.8632 8.15671 17.7486 7.49796 18.4498 6.96671ZM6.83317 9.37504C6.054 9.37504 5.4165 10.0125 5.4165 10.7917V12.2084C5.4165 12.9875 6.054 13.625 6.83317 13.625H7.5415V16.4584H8.95817V13.625H9.6665L13.2082 15.75V7.25004L9.6665 9.37504H6.83317ZM14.979 11.5C14.979 10.558 14.5682 9.70796 13.9165 9.12712V13.8659C14.5682 13.2921 14.979 12.4421 14.979 11.5Z",
            fill: "currentColor"
          }
        )
      );
    case "PERCENT":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "circle",
          {
            cx: "8",
            cy: "9",
            r: "1.75",
            stroke: "currentColor",
            strokeWidth: "1.5"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "circle",
          {
            cx: "16",
            cy: "14",
            r: "1.75",
            stroke: "currentColor",
            strokeWidth: "1.5"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "line",
          {
            x1: "9.22548",
            y1: "16.4982",
            x2: "14.8394",
            y2: "6.77452",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round"
          }
        )
      );
    case "STAR":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M12 5L13.5716 9.83688H18.6574L14.5429 12.8262L16.1145 17.6631L12 14.6738L7.8855 17.6631L9.4571 12.8262L5.3426 9.83688H10.4284L12 5Z",
            fill: "currentColor"
          }
        )
      );
    case "SWAP":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M7.32357 17.9999L4.50007 14.7999M4.50007 14.7999L7.32357 11.5999M4.50007 14.7999H12.5",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M16.6764 12.4L19.4999 9.20003M19.4999 9.20003L16.6764 6.00004M19.4999 9.20003H11.5",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      );
    case "UP_ARROW":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M8 10.5293L12 6.99995M12 6.99995L16 10.5293M12 6.99995V16.9999",
            stroke: "currentColor",
            strokeWidth: "1.5",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      );
    case "URGENT":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18ZM11.25 9.25C11.25 8.83579 11.5858 8.5 12 8.5C12.4142 8.5 12.75 8.83579 12.75 9.25V13.25C12.75 13.6642 12.4142 14 12 14C11.5858 14 11.25 13.6642 11.25 13.25V9.25ZM11.25 15.25C11.25 14.8358 11.5858 14.5 12 14.5C12.4142 14.5 12.75 14.8358 12.75 15.25C12.75 15.6642 12.4142 16 12 16C11.5858 16 11.25 15.6642 11.25 15.25Z",
            fill: "currentColor"
          }
        )
      );
    case "WATCH":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M11.9998 7C8.6665 7 5.81984 9.07333 4.6665 12C5.81984 14.9267 8.6665 17 11.9998 17C15.3332 17 18.1798 14.9267 19.3332 12C18.1798 9.07333 15.3332 7 11.9998 7ZM11.9998 15.3333C10.1598 15.3333 8.6665 13.84 8.6665 12C8.6665 10.16 10.1598 8.66667 11.9998 8.66667C13.8398 8.66667 15.3332 10.16 15.3332 12C15.3332 13.84 13.8398 15.3333 11.9998 15.3333ZM11.9998 10C10.8932 10 9.99984 10.8933 9.99984 12C9.99984 13.1067 10.8932 14 11.9998 14C13.1065 14 13.9998 13.1067 13.9998 12C13.9998 10.8933 13.1065 10 11.9998 10Z",
            fill: "currentColor"
          }
        )
      );
    case "empty-box":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "33",
          height: "21",
          viewBox: "0 0 33 21",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M0 12.375C0 11.6156 0.615609 11 1.375 11C2.13439 11 2.75 11.6156 2.75 12.375V16.5C2.75 17.2594 3.36561 17.875 4.125 17.875H6.875H28.875C29.6344 17.875 30.25 17.2594 30.25 16.5V12.375C30.25 11.6156 30.8656 11 31.625 11C32.3844 11 33 11.6156 33 12.375V17.875C33 19.3938 31.7688 20.625 30.25 20.625H2.75C1.23122 20.625 0 19.3938 0 17.875V12.375Z",
            fill: "#B6B8D5"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M16.5 6.875C15.9305 6.875 15.4687 6.41329 15.4687 5.84375L15.4687 1.03125C15.4687 0.461706 15.9305 -2.01818e-08 16.5 -4.50772e-08C17.0695 -6.99726e-08 17.5312 0.461706 17.5312 1.03125L17.5312 5.84375C17.5312 6.41329 17.0695 6.875 16.5 6.875Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M11.653 8.39704C12.0191 7.96074 11.9622 7.31028 11.5259 6.94418L7.83935 3.85077C7.40305 3.48467 6.75258 3.54158 6.38649 3.97787C6.02039 4.41417 6.0773 5.06464 6.5136 5.43073L10.2002 8.52415C10.6365 8.89024 11.2869 8.83333 11.653 8.39704Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M21.2879 8.39704C20.9218 7.96074 20.9787 7.31028 21.415 6.94418L25.1016 3.85077C25.5379 3.48467 26.1883 3.54158 26.5544 3.97787C26.9205 4.41417 26.8636 5.06464 26.4273 5.43073L22.7407 8.52415C22.3044 8.89024 21.654 8.83333 21.2879 8.39704Z",
            fill: "currentColor"
          }
        )
      );
    case "round-bell":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "12",
          height: "12",
          viewBox: "0 0 12 12",
          fill: "none",
          ...props
        },
        /* @__PURE__ */ React10.createElement("g", { clipPath: "url(#clip0_1955_18391)" }, /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M3.71191 10.5C3.90574 10.9457 4.22553 11.3251 4.63199 11.5916C5.03845 11.858 5.51389 12 5.99991 12C6.48594 12 6.96138 11.858 7.36784 11.5916C7.7743 11.3251 8.09408 10.9457 8.28791 10.5H3.71191Z",
            fill: "currentColor"
          }
        ), /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M11.1959 6.27459L10.3279 3.41309C10.0499 2.41226 9.4456 1.53297 8.61084 0.914838C7.77608 0.296701 6.75874 -0.0248542 5.72036 0.001226C4.68198 0.0273062 3.68207 0.399527 2.87939 1.05879C2.07671 1.71805 1.51727 2.62657 1.2899 3.64009L0.615904 6.40859C0.526321 6.77646 0.521467 7.15988 0.601709 7.52991C0.681951 7.89994 0.845193 8.2469 1.07912 8.54463C1.31304 8.84235 1.61153 9.08305 1.95206 9.24857C2.2926 9.41408 2.66628 9.50008 3.0449 9.50009H8.8034C9.19377 9.5001 9.57872 9.4087 9.92741 9.2332C10.2761 9.0577 10.5789 8.80299 10.8114 8.48946C11.044 8.17593 11.1999 7.81229 11.2667 7.42768C11.3334 7.04306 11.3092 6.64816 11.1959 6.27459Z",
            fill: "currentColor"
          }
        )),
        /* @__PURE__ */ React10.createElement("defs", null, /* @__PURE__ */ React10.createElement("clipPath", { id: "clip0_1955_18391" }, /* @__PURE__ */ React10.createElement("rect", { width: "12", height: "12", fill: "white" })))
      );
    case "chevron-right":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "24",
          height: "24",
          viewBox: "0 0 24 24",
          fill: "none",
          ...props
        },
        /* @__PURE__ */ React10.createElement("g", { clipPath: "url(#clip0_1949_17209)" }, /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M8.58984 7.41L13.1698 12L8.58984 16.59L9.99984 18L15.9998 12L9.99984 6L8.58984 7.41Z",
            fill: "currentColor"
          }
        )),
        /* @__PURE__ */ React10.createElement("defs", null, /* @__PURE__ */ React10.createElement("clipPath", { id: "clip0_1949_17209" }, /* @__PURE__ */ React10.createElement(
          "rect",
          {
            width: "24",
            height: "24",
            fill: "white",
            transform: "matrix(0 -1 1 0 0 24)"
          }
        )))
      );
    case "bell-circle":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          width: "21",
          height: "21",
          viewBox: "0 0 21 21",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        },
        /* @__PURE__ */ React10.createElement(
          "circle",
          {
            cx: "10.5",
            cy: "10.5",
            r: "9.5",
            fill: "white",
            stroke: "currentColor",
            strokeWidth: "2"
          }
        ),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M10.4901 16.1875C11.1212 16.1875 11.6376 15.6712 11.6376 15.0401H9.3427C9.3427 15.6712 9.85331 16.1875 10.4901 16.1875ZM13.9324 12.7452V9.8766C13.9324 8.11529 12.9915 6.64083 11.3507 6.25071V5.86058C11.3507 5.38439 10.9663 5 10.4901 5C10.0139 5 9.62956 5.38439 9.62956 5.86058V6.25071C7.98299 6.64083 7.04783 8.10955 7.04783 9.8766V12.7452L5.90039 13.8926V14.4663H15.0799V13.8926L13.9324 12.7452Z",
            fill: "#262949"
          }
        )
      );
    case "check-circle":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "11",
          height: "11",
          viewBox: "0 0 11 11",
          fill: "none",
          ...props
        },
        /* @__PURE__ */ React10.createElement("circle", { cx: "5.45455", cy: "5.45455", r: "5.45455", fill: "currentColor" }),
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M7.87876 4.24243L4.84846 7.27273L3.03027 6.06061",
            strokeWidth: "1.21212",
            strokeLinecap: "round"
          }
        )
      );
    case "bell-thin":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "10",
          height: "12",
          viewBox: "0 0 10 12",
          fill: "none",
          ...props
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M4.58974 11.1875C5.22083 11.1875 5.73718 10.6712 5.73718 10.0401H3.44231C3.44231 10.6712 3.95292 11.1875 4.58974 11.1875ZM8.03205 7.74519V4.8766C8.03205 3.11529 7.09115 1.64083 5.45032 1.25071V0.860577C5.45032 0.384391 5.06593 0 4.58974 0C4.11356 0 3.72917 0.384391 3.72917 0.860577V1.25071C2.0826 1.64083 1.14744 3.10955 1.14744 4.8766V7.74519L0 8.89263V9.46635H9.17949V8.89263L8.03205 7.74519Z",
            fill: "currentColor"
          }
        )
      );
    case "triangle-down":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "7",
          height: "7",
          viewBox: "0 0 7 7",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            d: "M0.849609 0L3.82316 3.54167L6.79671 0H0.849609Z",
            fill: "currentColor"
          }
        )
      );
    case "close":
      return /* @__PURE__ */ React10.createElement(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: "16",
          height: "16",
          viewBox: "0 0 16 16",
          fill: "none"
        },
        /* @__PURE__ */ React10.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M4.08894 4.61125C3.69842 5.00178 3.69842 5.63494 4.08894 6.02547L6.5643 8.50083L4.08977 10.9754C3.69924 11.3659 3.69924 11.999 4.08977 12.3896C4.48029 12.7801 5.11346 12.7801 5.50398 12.3896L7.97852 9.91504L10.4529 12.3894C10.8434 12.78 11.4766 12.78 11.8671 12.3894C12.2576 11.9989 12.2576 11.3657 11.8671 10.9752L9.39273 8.50083L11.8679 6.02561C12.2585 5.63509 12.2585 5.00192 11.8679 4.6114C11.4774 4.22087 10.8443 4.22087 10.4537 4.6114L7.97852 7.08661L5.50316 4.61125C5.11263 4.22073 4.47947 4.22073 4.08894 4.61125Z",
            fill: "currentColor"
          }
        )
      );
    default:
      return null;
  }
};

// lib/hooks/useConnect.tsx
import React11 from "react";
var useConnect = (afterConnected, isTopicsSubscriptionNotRequired) => {
  const {
    login,
    frontendClientStatus,
    loginViaHardwareWallet,
    isLoading: isLoadingLogin,
    error: loginError
  } = useNotifiFrontendClientContext();
  const {
    renewTargetGroup,
    targetDocument: { targetGroupId },
    isLoading: isLoadingCreateTargetGroup
  } = useNotifiTargetContext();
  const {
    updateFtuStage,
    ftuStage,
    isLoading: isLoadingUpdateUserSetting
  } = useNotifiUserSettingContext();
  const [useHardwareWalletLogin, setUseHardwareWalletLogin] = React11.useState(false);
  const { fusionEventTopics, cardConfig } = useNotifiTenantConfigContext();
  const { subscribeAlertsDefault, isLoading: isLoadingSubscribingTopics } = useNotifiTopicContext();
  const [isLoading, setIsLoading] = React11.useState(false);
  React11.useEffect(() => {
    if (isLoadingLogin) {
      setIsLoading(true);
      return;
    }
    if (!isLoading && frontendClientStatus.isAuthenticated) {
      setIsLoading(
        [
          isLoadingCreateTargetGroup,
          isLoadingUpdateUserSetting,
          isLoadingSubscribingTopics
        ].some((loading) => loading)
      );
      return;
    }
    if (loginError) {
      setIsLoading(false);
    }
  }, [
    isLoadingLogin,
    isLoadingCreateTargetGroup,
    isLoadingUpdateUserSetting,
    isLoadingSubscribingTopics
  ]);
  const connect = async () => {
    try {
      let frontendClient = void 0;
      if (useHardwareWalletLogin) {
        frontendClient = await loginViaHardwareWallet();
      } else {
        frontendClient = await login();
      }
      if (!frontendClient) return;
      const isDefaultTargetExist = await validateDefaultTargetGroup(frontendClient);
      if (!isDefaultTargetExist) {
        await renewTargetGroup();
      }
    } catch (error) {
      console.error(error);
      return;
    }
  };
  React11.useEffect(() => {
    if (isLoadingUpdateUserSetting) return;
    const subscribeAndUpdateFtuStage = async () => {
      if (!targetGroupId || !frontendClientStatus.isAuthenticated) return;
      const topicsToSubscribe = fusionEventTopics.filter((topic) => {
        const metadata = getFusionEventMetadata(topic);
        const isStackableTopic = metadata?.uiConfigOverride?.isSubscriptionValueInputable ?? false;
        return !topic.uiConfig.optOutAtSignup && !isStackableTopic;
      });
      switch (ftuStage) {
        case 3 /* Destination */:
        case 2 /* Alerts */:
          afterConnected("ftu");
          break;
        case 1 /* Done */:
          afterConnected("Inbox");
          break;
        default:
          if (!isTopicsSubscriptionNotRequired) {
            await subscribeAlertsDefault(topicsToSubscribe, targetGroupId);
          }
          if (cardConfig?.isContactInfoRequired) {
            await updateFtuStage(3 /* Destination */);
          } else {
            await updateFtuStage(2 /* Alerts */);
          }
          break;
      }
    };
    subscribeAndUpdateFtuStage();
  }, [
    targetGroupId,
    ftuStage,
    frontendClientStatus,
    isLoadingUpdateUserSetting
  ]);
  return {
    connect,
    isLoading,
    useHardwareWalletLogin,
    setUseHardwareWalletLogin
  };
};
var validateDefaultTargetGroup = async (frontendClient) => {
  const targetGroup = (await frontendClient?.fetchFusionData())?.targetGroup?.find((group) => group?.name === "Default");
  return !!targetGroup;
};

// lib/components/LoadingAnimation.tsx
import clsx from "clsx";
import React12 from "react";
var LoadingAnimation = (props) => {
  return /* @__PURE__ */ React12.createElement(
    "div",
    {
      style: props,
      className: clsx(
        "notifi-spinner-container",
        "notifi-spinner",
        props.classNames?.spinner
      )
    }
  );
};

// lib/components/PoweredByNotifi.tsx
import clsx2 from "clsx";
import React14 from "react";

// lib/assets/logos.tsx
import React13 from "react";
var Logo = (props) => {
  switch (props.type) {
    case "notifi":
      return /* @__PURE__ */ React13.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "10",
          height: "9",
          viewBox: "0 0 10 9",
          fill: "none"
        },
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M7.92661 5.02546C7.68505 5.09424 7.43002 5.13107 7.16639 5.13107C5.63641 5.13107 4.39611 3.89077 4.39611 2.36079C4.39611 2.08776 4.43561 1.82397 4.50919 1.5748H1.63872C1.19751 1.5748 0.839844 1.93247 0.839844 2.37367V7.86269C0.839844 8.3039 1.19751 8.66156 1.63872 8.66156H7.12774C7.56894 8.66156 7.92661 8.3039 7.92661 7.86269V5.02546Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            d: "M9.50142 1.96852C9.50142 3.05571 8.62007 3.93706 7.53288 3.93706C6.44568 3.93706 5.56433 3.05571 5.56433 1.96852C5.56433 0.881317 6.44568 -3.05176e-05 7.53288 -3.05176e-05C8.62007 -3.05176e-05 9.50142 0.881317 9.50142 1.96852Z",
            fill: "url(#paint0_linear_4504_6143)"
          }
        ),
        /* @__PURE__ */ React13.createElement("defs", null, /* @__PURE__ */ React13.createElement(
          "linearGradient",
          {
            id: "paint0_linear_4504_6143",
            x1: "8.72167",
            y1: "0.306756",
            x2: "6.82982",
            y2: "3.93706",
            gradientUnits: "userSpaceOnUse"
          },
          /* @__PURE__ */ React13.createElement("stop", { stopColor: "#FE7970" }),
          /* @__PURE__ */ React13.createElement("stop", { offset: "1", stopColor: "#FEB776" })
        ))
      );
    case "notifi-text":
      return /* @__PURE__ */ React13.createElement(
        "svg",
        {
          ...props,
          xmlns: "http://www.w3.org/2000/svg",
          width: "26",
          height: "9",
          viewBox: "0 0 26 9",
          fill: "none"
        },
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            d: "M4.90313 5.61341V8.60201H6.14424V5.61341C6.14424 3.77656 5.19107 2.57516 3.68187 2.57516C2.94713 2.57516 2.29183 3.01203 2.07339 3.6177V2.64466H0.832275V8.60201H2.07339V5.62334C2.07339 4.57087 2.6592 3.81627 3.46344 3.81627C4.33718 3.81627 4.90313 4.52123 4.90313 5.61341Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            d: "M6.97375 5.61341C6.97375 7.34104 8.25458 8.66158 9.90278 8.66158C11.551 8.66158 12.8219 7.34104 12.8219 5.61341C12.8219 3.8957 11.551 2.57516 9.90278 2.57516C8.25458 2.57516 6.97375 3.8957 6.97375 5.61341ZM9.90278 3.81627C10.856 3.81627 11.5808 4.59073 11.5808 5.61341C11.5808 6.64601 10.856 7.42047 9.90278 7.42047C8.94961 7.42047 8.21487 6.64601 8.21487 5.61341C8.21487 4.59073 8.94961 3.81627 9.90278 3.81627Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            d: "M14.5834 1.38368L14.2855 2.64466H13.4019V3.81627H14.2855V6.55665C14.2855 8.0162 14.8813 8.60201 16.3011 8.60201H16.8174V7.37082H16.4203C15.8047 7.37082 15.5267 7.10274 15.5267 6.48715V3.81627H16.8174V2.64466H15.5267V1.38368H14.5834Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            d: "M18.9217 8.60201V2.64466H17.6806V8.60201H18.9217Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            d: "M25.2799 8.60201V2.64466H21.8644V2.20779C21.8644 1.5922 22.1424 1.31418 22.758 1.31418H23.1551V0.092926H22.6388C21.219 0.092926 20.6233 0.668808 20.6233 2.13829V2.64466H19.7495V3.81627H20.6233V8.60201H21.8644V3.81627H24.0388V8.60201H25.2799Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ React13.createElement(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18.1574 -3.05176e-05C17.8067 -3.05176e-05 17.6805 0.192456 17.6805 0.466205V1.46136H18.6536C18.9915 1.46136 19.1419 1.34306 19.1419 1.00943V0.466205C19.1419 0.15396 18.9359 -3.05176e-05 18.6194 -3.05176e-05H18.1574Z",
            fill: "currentColor"
          }
        )
      );
    default:
      return null;
  }
};

// lib/components/PoweredByNotifi.tsx
var PoweredByNotifi = (props) => {
  return /* @__PURE__ */ React14.createElement(
    "div",
    {
      className: clsx2("powered-by-notifi", props.classNames?.container),
      onClick: () => window.open("https://notifi.network", "_blank")
    },
    /* @__PURE__ */ React14.createElement(
      "div",
      {
        className: clsx2(
          "notifi-powered-by-notifi-content",
          props.classNames?.content
        )
      },
      props.copy ?? defaultCopy.poweredByNotifi
    ),
    /* @__PURE__ */ React14.createElement(
      Logo,
      {
        type: "notifi",
        className: clsx2("powered-by-notifi-logo", props.classNames?.notifiLogo)
      }
    ),
    /* @__PURE__ */ React14.createElement(
      Logo,
      {
        type: "notifi-text",
        className: clsx2("powered-by-notifi-text", props.classNames?.notifiText)
      }
    )
  );
};

// lib/components/Toggle.tsx
import clsx3 from "clsx";
import React15 from "react";
var Toggle = ({
  classNames,
  disabled,
  checked,
  setChecked
}) => {
  return /* @__PURE__ */ React15.createElement("label", { className: clsx3("notifi-toggle-container", classNames?.container) }, /* @__PURE__ */ React15.createElement(
    "input",
    {
      className: clsx3("notifi-toggle-input", classNames?.input),
      disabled,
      type: "checkbox",
      checked,
      onChange: (e) => {
        setChecked(e.target.checked);
      }
    }
  ), /* @__PURE__ */ React15.createElement(
    "span",
    {
      className: clsx3("notifi-toggle-slider", classNames?.slider, {
        "notifi-toggle-slider--disabled": disabled
      })
    }
  ));
};

// lib/components/Connect.tsx
var Connect = (props) => {
  const { walletWithSignParams } = useNotifiFrontendClientContext();
  const { fusionEventTopics } = useNotifiTenantConfigContext();
  const { globalCtas } = useGlobalStateContext();
  const {
    connect,
    isLoading,
    useHardwareWalletLogin,
    setUseHardwareWalletLogin
  } = useConnect(
    (cardModalView) => props.setCardModalView(cardModalView),
    props.loginWithoutSubscription
  );
  const topicLists = React16.useMemo(() => {
    const topicGroupNames = [];
    const topicNames = [];
    fusionEventTopics.forEach((topic, id) => {
      if (topic.uiConfig.topicGroupName) {
        if (topicGroupNames.map((name) => name.value).includes(topic.uiConfig.topicGroupName))
          return;
        topicGroupNames.push({
          index: topic.uiConfig.index ?? id,
          value: topic.uiConfig.topicGroupName
        });
        return;
      }
      topicNames.push({
        index: topic.uiConfig.index ?? id,
        value: topic.uiConfig.name
      });
    });
    return [...topicNames, ...topicGroupNames].sort(
      (a, b) => a.index - b.index
    );
  }, [fusionEventTopics]);
  return /* @__PURE__ */ React16.createElement("div", { className: clsx4("notifi-connect", props.classNames?.container) }, /* @__PURE__ */ React16.createElement("div", { className: clsx4("notifi-connect-title", props.classNames?.title) }, props.copy?.title ?? defaultCopy.connect.title, props.navHeaderRightCta && globalCtas?.[props.navHeaderRightCta.globalCtaType] ? /* @__PURE__ */ React16.createElement(
    "div",
    {
      className: "notifi-connect-right-cta",
      onClick: () => globalCtas[props.navHeaderRightCta.globalCtaType]()
    },
    /* @__PURE__ */ React16.createElement(Icon, { type: props.navHeaderRightCta?.icon })
  ) : null), /* @__PURE__ */ React16.createElement(
    "div",
    {
      className: clsx4(
        "notifi-connect-description",
        props.classNames?.description
      )
    },
    props.copy?.description ?? defaultCopy.connect.description
  ), /* @__PURE__ */ React16.createElement("div", { className: clsx4("notifi-connect-main", props.classNames?.main) }, /* @__PURE__ */ React16.createElement(
    "div",
    {
      "data-cy": "notifi-connect-alert-list-container",
      className: clsx4(
        "notifi-connect-alert-list-container",
        props.classNames?.alertsContainer
      )
    },
    topicLists.map((topic) => {
      return /* @__PURE__ */ React16.createElement(
        "div",
        {
          className: clsx4(
            "notifi-connect-alert-list-alert",
            props.classNames?.alert
          ),
          key: topic.value
        },
        /* @__PURE__ */ React16.createElement(
          Icon,
          {
            type: props.iconType ?? "check",
            className: clsx4(
              "notifi-connect-alert-icon",
              props.classNames?.icon
            )
          }
        ),
        topic.value
      );
    })
  )), walletWithSignParams.walletBlockchain === "SOLANA" ? (
    /* NOTE: Only Solana requires special handling for hardware wallet login (see detail in NotifiFrontendClientContext.tsx) */
    // TODO: Crete a separate component SolanaHardwareWalletToggle.tsx
    /* @__PURE__ */ React16.createElement(
      "div",
      {
        className: clsx4(
          "notifi-connect-hardware-wallet",
          props.classNames?.hardwareWallet
        )
      },
      /* @__PURE__ */ React16.createElement(
        "div",
        {
          className: clsx4(
            "notifi-connect-hardware-wallet-label",
            props.classNames?.hardwareWalletLabel
          )
        },
        props.copy?.hardwareWalletLabel ?? defaultCopy.connect.hardwareWalletLabel
      ),
      /* @__PURE__ */ React16.createElement(
        Toggle,
        {
          setChecked: () => setUseHardwareWalletLogin((prev) => !prev),
          checked: useHardwareWalletLogin,
          disabled: isLoading
        }
      )
    )
  ) : null, /* @__PURE__ */ React16.createElement(
    "button",
    {
      "data-cy": "notifi-connect-button",
      className: clsx4(
        "btn",
        "notifi-connect-button",
        props.classNames?.button
      ),
      disabled: isLoading,
      onClick: connect
    },
    isLoading ? /* @__PURE__ */ React16.createElement(
      LoadingAnimation,
      {
        type: "spinner",
        classNames: { spinner: "notifi-connect-button-spinner" }
      }
    ) : null,
    /* @__PURE__ */ React16.createElement(
      "div",
      {
        className: clsx4("notifi-connect-button-text", isLoading && "hidden")
      },
      props.copy?.buttonText ? props.copy.buttonText : defaultCopy.connect.buttonText
    )
  ), props.copy?.footerContent && props.copy.footerContent.length > 0 ? /* @__PURE__ */ React16.createElement(
    "div",
    {
      className: clsx4(
        "notifi-connect-footer-content",
        props.classNames?.footerContent
      )
    },
    props.copy.footerContent.map((content, index) => {
      switch (content.type) {
        case "plain-text":
          return /* @__PURE__ */ React16.createElement("span", { key: index }, content.text);
        case "hyperlink":
          return /* @__PURE__ */ React16.createElement(
            "a",
            {
              key: index,
              href: content.url,
              target: "_blank",
              rel: "noopener noreferrer"
            },
            content.text
          );
      }
    })
  ) : null, /* @__PURE__ */ React16.createElement("div", { className: clsx4("notifi-connect-footer", props.classNames?.footer) }, /* @__PURE__ */ React16.createElement(PoweredByNotifi, { classNames: props.classNames?.PoweredByNotifi })));
};

// lib/components/ErrorView.tsx
import clsx6 from "clsx";
import React18 from "react";

// lib/components/NavHeader.tsx
import clsx5 from "clsx";
import React17 from "react";
var NavHeader = (props) => {
  const { globalCtas } = useGlobalStateContext();
  return /* @__PURE__ */ React17.createElement("div", { className: "notifi-nav-header" }, /* @__PURE__ */ React17.createElement(
    "div",
    {
      className: "notifi-nav-header-left-cta",
      onClick: () => props.leftCta?.action()
    },
    props.leftCta?.icon ? /* @__PURE__ */ React17.createElement(Icon, { type: props.leftCta?.icon }) : null
  ), /* @__PURE__ */ React17.createElement(
    "div",
    {
      className: clsx5(
        "notifi-nav-header-title",
        props.children ? "" : "isEmpty"
      )
    },
    /* @__PURE__ */ React17.createElement("div", null, props.children)
  ), props.rightCta && globalCtas?.[props.rightCta.globalCtaType] ? /* @__PURE__ */ React17.createElement(
    "div",
    {
      className: "notifi-nav-header-right-cta",
      onClick: () => globalCtas[props.rightCta.globalCtaType]()
    },
    /* @__PURE__ */ React17.createElement(Icon, { type: props.rightCta?.icon })
  ) : null);
};

// lib/components/ErrorView.tsx
var ErrorView = (props) => {
  return /* @__PURE__ */ React18.createElement("div", { className: clsx6("notifi-error-global", props.classNames?.container) }, /* @__PURE__ */ React18.createElement(NavHeader, { leftCta: props.cta, rightCta: props.navHeaderRightCta }, props.copy?.header ?? defaultCopy.errorGlobal.header), /* @__PURE__ */ React18.createElement(
    "div",
    {
      className: clsx6(
        "notifi-error-global-error-title",
        props.classNames?.title
      )
    },
    props.title ?? defaultCopy.errorGlobal.title
  ), /* @__PURE__ */ React18.createElement(
    "div",
    {
      className: clsx6(
        "notifi-error-global-error-main",
        props.classNames?.main
      )
    },
    /* @__PURE__ */ React18.createElement(
      "div",
      {
        className: clsx6(
          "notifi-error-global-error-detail",
          props.classNames?.detail
        )
      },
      props.detail ?? defaultCopy.errorGlobal.detail
    )
  ), /* @__PURE__ */ React18.createElement("div", { className: clsx6("notifi-error-footer", props.classNames?.footer) }, /* @__PURE__ */ React18.createElement(PoweredByNotifi, { classNames: props.classNames?.PoweredByNotifi })));
};

// lib/components/Expiry.tsx
import React19 from "react";
var Expiry = (props) => {
  return /* @__PURE__ */ React19.createElement(
    Connect,
    {
      setCardModalView: props.setCardModalView,
      loginWithoutSubscription: true,
      navHeaderRightCta: props.navHeaderRightCta
    }
  );
};

// lib/components/Ftu.tsx
import clsx23 from "clsx";
import React40 from "react";

// lib/components/FtuAlertEdit.tsx
import clsx14 from "clsx";
import React30 from "react";

// lib/components/TopicList.tsx
import clsx13 from "clsx";
import React29 from "react";

// lib/components/TopicRow.tsx
import clsx8 from "clsx";
import React23 from "react";

// lib/hooks/useComponentPosition.tsx
import React20 from "react";
var useComponentPosition = (componentRef, parentComponentRelativeClassName) => {
  const [componentPosition, setComponentPosition] = React20.useState("top-left" /* TopLeft */);
  React20.useEffect(() => {
    const targetListContainer = document.getElementsByClassName(
      parentComponentRelativeClassName
    )?.[0];
    if (!targetListContainer) return;
    const handleScroll = () => {
      if (!componentRef.current) return;
      const tooltipIconRect = componentRef.current.getBoundingClientRect();
      const targetListContainerRect = targetListContainer.getBoundingClientRect();
      const tooltipIconFromTop = tooltipIconRect.top - targetListContainerRect?.top;
      const tooltipIconFromLeft = tooltipIconRect.left - targetListContainerRect?.left;
      const contianerMiddlePositionX = targetListContainerRect?.width / 2;
      const containerMiddlePositionY = targetListContainerRect?.height / 2;
      setComponentPosition(() => {
        if (tooltipIconFromTop > containerMiddlePositionY) {
          return tooltipIconFromLeft > contianerMiddlePositionX ? "bottom-right" /* BottomRight */ : "bottom-left" /* BottomLeft */;
        }
        return tooltipIconFromLeft > contianerMiddlePositionX ? "top-right" /* TopRight */ : "top-left" /* TopLeft */;
      });
    };
    handleScroll();
    targetListContainer.addEventListener("scroll", handleScroll);
    return () => targetListContainer.removeEventListener("scroll", handleScroll);
  }, [componentRef.current, parentComponentRelativeClassName]);
  return {
    componentPosition
  };
};

// lib/components/TopicOptions.tsx
import clsx7 from "clsx";
import React22 from "react";

// lib/hooks/useUserInputParmToFilterOption.tsx
import React21 from "react";
var useUserInputParmToFilterOption = (alertName, userInputParam) => {
  const { getAlertFilterOptions, subscribeAlertsWithFilterOptions } = useNotifiTopicContext();
  const {
    targetDocument: { targetGroupId }
  } = useNotifiTargetContext();
  const alertFilterOptions = getAlertFilterOptions(alertName);
  const filterName = Object.keys(
    alertFilterOptions?.input ?? []
  )[0];
  const userInputOptions = alertFilterOptions?.input[filterName];
  const subscribedValue = convertOptionValue(
    userInputOptions?.[userInputParam.name] ?? "",
    userInputParam.kind,
    "BackendToFrontend" /* BtoF */
  ).toString();
  const [customInput, setCustomInput] = React21.useState("");
  React21.useEffect(() => {
    if (!!subscribedValue && !userInputParam.options.map((option) => option.toString()).includes(subscribedValue.toString())) {
      setCustomInput(subscribedValue);
      return;
    }
    setCustomInput("");
  }, [subscribedValue]);
  const uiType = userInputParam.uiType;
  const prefixAndSuffix = userInputParam.prefixAndSuffix ?? derivePrefixAndSuffixFromValueType(userInputParam.kind);
  const renewFilterOptions = async (option, topics) => {
    if (!alertFilterOptions || !targetGroupId) return;
    const updatedAlertFilterOptiopns = getUpdatedAlertFilterOptions(
      filterName,
      alertFilterOptions,
      userInputParam.name,
      convertOptionValue(option, userInputParam.kind)
    );
    await subscribeAlertsWithFilterOptions(
      topics.map((topic) => {
        return {
          topic,
          filterOptions: updatedAlertFilterOptiopns
        };
      }),
      targetGroupId
    );
  };
  return {
    customInput,
    prefixAndSuffix,
    renewFilterOptions,
    subscribedValue: subscribedValue.toString(),
    uiType,
    setCustomInput
  };
};

// lib/components/TopicOptions.tsx
var TopicOptions = (props) => {
  const isTopicGroup = isTopicGroupOptions(props);
  const benchmarkTopic = isTopicGroup ? props.topics[0] : props.topic;
  if (!benchmarkTopic.fusionEventDescriptor.id) return null;
  const {
    customInput,
    prefixAndSuffix,
    renewFilterOptions,
    subscribedValue,
    uiType,
    setCustomInput
  } = useUserInputParmToFilterOption(
    benchmarkTopic.fusionEventDescriptor.id,
    props.userInputParam
  );
  const { isLoading: isLoadingTopic } = useNotifiTopicContext();
  const [valueToBeSubscribed, setValueToBeSubscribed] = React22.useState(
    subscribedValue || props.userInputParam.defaultValue.toString()
  );
  const options = props.userInputParam.options.map(
    (option) => option.toString()
  );
  const selectedOption = valueToBeSubscribed ? valueToBeSubscribed : subscribedValue;
  const selectOrInputValue = async (value) => {
    if (isLoadingTopic) return;
    setValueToBeSubscribed(value.toString());
    value = value.toString();
    if (props.preventDefault) {
      if (options.includes(value)) {
        setCustomInput("");
      }
      return props.preventDefault(props.userInputParam.name, value);
    }
    await renewFilterOptions(
      value,
      isTopicGroup ? props.topics : [props.topic]
    );
    setValueToBeSubscribed("");
  };
  return /* @__PURE__ */ React22.createElement("div", { className: clsx7("notifi-topic-options", props.classNames?.container) }, props.userInputParam.description ? /* @__PURE__ */ React22.createElement(
    "div",
    {
      className: clsx7(
        "notifi-topic-options-description",
        props.classNames?.discription
      )
    },
    props.userInputParam.description
  ) : null, /* @__PURE__ */ React22.createElement(
    "div",
    {
      className: clsx7("notifi-topic-options-items", props.classNames?.items)
    },
    options.map((option, id) => /* @__PURE__ */ React22.createElement(
      "div",
      {
        key: id,
        className: clsx7(
          "notifi-topic-options-item",
          props.classNames?.item,
          option === selectedOption ? "selected" : "",
          uiType === "button" ? "button" : "radio"
        ),
        onClick: () => {
          if (isLoadingTopic) return;
          if (customInput !== "") setCustomInput("");
          selectOrInputValue(option);
        }
      },
      /* @__PURE__ */ React22.createElement("div", null, "prefix" in prefixAndSuffix && prefixAndSuffix.prefix, option.charAt(0).toUpperCase() + option.slice(1), "suffix" in prefixAndSuffix && prefixAndSuffix.suffix)
    )),
    props.userInputParam.allowCustomInput ? /* @__PURE__ */ React22.createElement(
      "div",
      {
        className: clsx7(
          "notifi-topic-option-custom-input-container",
          customInput ? "selected" : ""
        )
      },
      /* @__PURE__ */ React22.createElement("div", { className: "notifi-topic-option-custom-input-prefix" }, prefixAndSuffix.prefix),
      /* @__PURE__ */ React22.createElement(
        "input",
        {
          disabled: isLoadingTopic,
          onChange: (evt) => {
            setCustomInput((prev) => {
              if (evt.target.value === "") return "";
              if (isUserInputValid(
                props.userInputParam.kind,
                evt.target.value,
                props.userInputParam.customInputConstraints
              )) {
                return evt.target.value;
              }
              return prev;
            });
          },
          onBlur: (evt) => {
            if (evt.target.value === "") {
              return options.includes(selectedOption) ? setCustomInput("") : setCustomInput(selectedOption);
            }
            if (options.includes(evt.target.value) && selectedOption === evt.target.value)
              return setCustomInput("");
            const { value } = validateCustomInputRange(
              evt.target.value,
              props.userInputParam.customInputConstraints
            );
            if (options.includes(value.toString())) {
              setCustomInput("");
            } else {
              setCustomInput(value.toString());
            }
            selectOrInputValue(value);
          },
          className: clsx7(
            "notifi-topic-options-custom-input",
            props.classNames?.inputField
          ),
          placeholder: props.copy?.customInputPlaceholder ?? defaultCopy.topicOptions.customInputPlaceholder,
          value: customInput
        }
      ),
      /* @__PURE__ */ React22.createElement("div", { className: "notifi-topic-option-custom-input-suffix" }, prefixAndSuffix.suffix)
    ) : null
  ));
};
var isTopicGroupOptions = (props) => {
  return "topics" in props;
};
var isUserInputValid = (type, userInputValue, customInputConstraint) => {
  if (userInputValue === "") return true;
  const leadingZeroRegex = /^0\d+/;
  if (type === "percentage" || type === "price" || type === "float") {
    const floatRegex1 = /^\d+(\.)?$/;
    const floatRegex2 = /^\d+(\.\d+)?$/;
    const floatWithMaxDecimalPlacesRegex = new RegExp(
      `^\\d+(\\.\\d{0,${customInputConstraint?.maxDecimalPlaces ?? 18}})?$`
      // If not provided, default to 18 decimal places (EVM bigint limit)
    );
    return !leadingZeroRegex.test(userInputValue.toString()) && floatWithMaxDecimalPlacesRegex.test(userInputValue.toString()) && (floatRegex1.test(userInputValue.toString()) || floatRegex2.test(userInputValue.toString()));
  }
  if (type === "integer") {
    return !leadingZeroRegex.test(userInputValue.toString()) && /^\d+$/.test(userInputValue.toString());
  }
  return true;
};
var validateCustomInputRange = (input, customInputConstraint) => {
  const upperBound = customInputConstraint?.upperBound;
  const lowerBound = customInputConstraint?.lowerBound;
  if (input === "" || isNaN(Number(input)))
    throw new Error("validateCustomInputRange - Input cannot be empty");
  if (isNaN(Number(upperBound)) || isNaN(Number(lowerBound)) || !upperBound || !lowerBound)
    return { status: "valid", value: Number(input) };
  if (Number(input) > Number(upperBound))
    return { status: "aboveUpperBound", value: upperBound };
  if (Number(input) < Number(lowerBound))
    return { status: "belowLowerBound", value: lowerBound };
  return { status: "valid", value: Number(input) };
};

// lib/components/TopicRow.tsx
var TopicRow = (props) => {
  const parentComponent = props.parentComponent ?? "ftu";
  const tooltipRef = React23.useRef(null);
  const isTopicGroup = isTopicGroupRow(props);
  const {
    isAlertSubscribed,
    isLoading: isLoadingTopic,
    subscribeAlertsDefault,
    unsubscribeAlerts
  } = useNotifiTopicContext();
  const {
    targetDocument: { targetGroupId }
  } = useNotifiTargetContext();
  const benchmarkTopic = isTopicGroup ? props.topics[0] : props.topic;
  const fusionEventTypeId = benchmarkTopic.fusionEventDescriptor.id;
  if (!fusionEventTypeId) return null;
  const [isOptimisticRendering, setIsOptimisticRendering] = React23.useState(false);
  const isSubscribed = isOptimisticRendering ? !isAlertSubscribed(fusionEventTypeId) : isAlertSubscribed(fusionEventTypeId);
  if (isTopicGroup && !isTopicGroupValid(props.topics)) return null;
  const userInputParams = getUserInputParams(benchmarkTopic);
  const title = isTopicGroup ? props.topicGroupName : getFusionEventMetadata(benchmarkTopic)?.uiConfigOverride?.topicDisplayName || // 1. Show topic displayname in fusionEventMetadata
  benchmarkTopic.uiConfig.displayNameOverride || // 2. Fall back to cardConfig'displayNameOverride  (May deprecated sooner or later)
  benchmarkTopic.uiConfig.name;
  const toggleTopic = async (topics) => {
    if (!targetGroupId || topics.length === 0) return;
    setIsOptimisticRendering(true);
    if (!isAlertSubscribed(fusionEventTypeId)) {
      await subscribeAlertsDefault(topics, targetGroupId);
      setIsOptimisticRendering(false);
      return;
    }
    const topicsToBeUnsubscribed = topics.filter(
      (topic) => topic.fusionEventDescriptor.id ? isAlertSubscribed(topic.fusionEventDescriptor.id) : false
    );
    const alertIds = topicsToBeUnsubscribed.map(
      (topic) => topic.fusionEventDescriptor.id
    );
    await unsubscribeAlerts(alertIds);
    setIsOptimisticRendering(false);
  };
  const { componentPosition: tooltipPosition } = useComponentPosition(
    tooltipRef,
    parentComponent === "inbox" ? "notifi-inbox-config-topic-main" : "notifi-ftu-alert-edit-main"
  );
  return /* @__PURE__ */ React23.createElement(
    "div",
    {
      className: clsx8("notifi-topic-row", props.classNames?.baseRowContainer)
    },
    /* @__PURE__ */ React23.createElement("div", { className: clsx8("notifi-topic-row-base", props.classNames?.content) }, /* @__PURE__ */ React23.createElement(
      "div",
      {
        className: clsx8(
          "notifi-topic-row-content",
          props.classNames?.content
        )
      },
      /* @__PURE__ */ React23.createElement("div", null, title),
      benchmarkTopic.uiConfig.tooltipContent ? /* @__PURE__ */ React23.createElement(
        "div",
        {
          ref: tooltipRef,
          className: clsx8(
            "notifi-topic-list-tooltip-container",
            props.classNames?.tooltipContainer
          )
        },
        /* @__PURE__ */ React23.createElement(
          Icon,
          {
            type: "info",
            className: clsx8(
              "notifi-topic-list-tooltip-icon",
              props.classNames?.tooltipIcon
            )
          }
        ),
        /* @__PURE__ */ React23.createElement(
          "div",
          {
            className: clsx8(
              "notifi-topic-list-tooltip-content",
              props.classNames?.tooltipContent,
              tooltipPosition
            )
          },
          benchmarkTopic.uiConfig.tooltipContent
        )
      ) : null
    ), /* @__PURE__ */ React23.createElement(
      Toggle,
      {
        checked: isSubscribed,
        disabled: isLoadingTopic,
        setChecked: async () => {
          toggleTopic(isTopicGroup ? props.topics : [props.topic]);
        }
      }
    )),
    userInputParams.length > 0 && isSubscribed ? /* @__PURE__ */ React23.createElement(
      "div",
      {
        className: clsx8(
          "notifi-topic-row-user-inputs-row-container ",
          props.classNames?.userInputsRowContainer
        )
      },
      isTopicGroup ? userInputParams.map((userInput, id) => {
        return /* @__PURE__ */ React23.createElement(
          TopicOptions,
          {
            key: id,
            userInputParam: userInput,
            topics: props.topics
          }
        );
      }) : null,
      !isTopicGroup ? userInputParams.map((userInput, id) => {
        return /* @__PURE__ */ React23.createElement(
          TopicOptions,
          {
            key: id,
            userInputParam: userInput,
            topic: benchmarkTopic
          }
        );
      }) : null
    ) : null
  );
};
var isTopicGroupRow = (props) => {
  return "topics" in props && "topicGroupName" in props;
};

// lib/components/TopicStackRow.tsx
import clsx12 from "clsx";
import React28 from "react";

// lib/components/TopicStack.tsx
import clsx9 from "clsx";
import React24 from "react";
var TopicStack = (props) => {
  const { unsubscribeAlerts, getAlertFilterOptions } = useNotifiTopicContext();
  const { getFusionTopic } = useNotifiTenantConfigContext();
  const benchmarkAlert = props.topicStackAlerts[0];
  const userInputOptions = React24.useMemo(() => {
    const input = getAlertFilterOptions(benchmarkAlert.alertName)?.input;
    if (!input) return;
    return Object.values(input)[0];
  }, [getAlertFilterOptions]);
  const { isLoading } = useNotifiTopicContext();
  const resolved = resolveAlertName(benchmarkAlert.alertName);
  const fusionTopic = getFusionTopic(resolved.fusionEventTypeId);
  if (!fusionTopic) return null;
  const fusionEventMetadata = getFusionEventMetadata(fusionTopic);
  const alertFilter = fusionEventMetadata?.filters.find(isAlertFilter);
  if (!alertFilter) {
    return null;
  }
  const userInputParmValueMetadataMap = new Map(
    alertFilter.userInputParams.map((param) => [
      param.name,
      { valueType: param.kind, prefixAndSuffix: param.prefixAndSuffix ?? null }
    ])
  );
  const userInputOptionValuesToBeDisplayed = userInputOptions && isAboveOrBelowThresholdUserInputOptions(userInputOptions) ? sortAboveOrBelowThresholdUserInputOptionValue(
    userInputOptions,
    userInputParmValueMetadataMap
  ) : [];
  return /* @__PURE__ */ React24.createElement("div", { className: clsx9("notifi-topic-stack", props.classNames?.container) }, /* @__PURE__ */ React24.createElement(
    "div",
    {
      className: clsx9(
        "notifi-topic-stack-content",
        props.classNames?.contentContainer
      )
    },
    /* @__PURE__ */ React24.createElement(
      "div",
      {
        className: clsx9(
          "notifi-topic-stack-content-title",
          props.classNames?.subtitle
        )
      },
      benchmarkAlert.subscriptionValueInfo.label
    ),
    /* @__PURE__ */ React24.createElement(
      "div",
      {
        className: clsx9(
          "notifi-topic-stack-content-subtitle",
          props.classNames?.subtitle
        )
      },
      userInputOptionValuesToBeDisplayed.map((option, id) => /* @__PURE__ */ React24.createElement("div", { key: id }, option))
    )
  ), /* @__PURE__ */ React24.createElement(
    "div",
    {
      className: clsx9(
        "notifi-topic-stack-cta",
        props.classNames?.cta,
        isLoading ? "disabled" : ""
      ),
      onClick: async () => {
        if (isLoading) return;
        const alerts = props.topicStackAlerts.map((alert) => alert.alertName);
        if (alerts.length === 0) return;
        await unsubscribeAlerts(alerts);
      }
    },
    /* @__PURE__ */ React24.createElement(Icon, { type: "bin" })
  ));
};
var isAboveOrBelowThresholdUserInputOptions = (userInputOptions) => {
  return !!userInputOptions.threshold && !!userInputOptions.thresholdDirection;
};
var sortAboveOrBelowThresholdUserInputOptionValue = (filterOptions, userInputParmValueMetadataMap) => {
  const sortedFilterOptions = [];
  if (filterOptions.thresholdDirection) {
    const thresholdDirection = filterOptions.thresholdDirection[0].toUpperCase() + filterOptions.thresholdDirection.slice(1);
    sortedFilterOptions.push(thresholdDirection);
  }
  if (filterOptions.threshold) {
    const inputParamMetadata = userInputParmValueMetadataMap.get("threshold");
    if (inputParamMetadata) {
      const prefix = inputParamMetadata.prefixAndSuffix ? inputParamMetadata.prefixAndSuffix.prefix : derivePrefixAndSuffixFromValueType(inputParamMetadata.valueType).prefix;
      const suffix = inputParamMetadata.prefixAndSuffix ? inputParamMetadata.prefixAndSuffix.suffix : derivePrefixAndSuffixFromValueType(inputParamMetadata.valueType).suffix;
      const thresholdValue = convertOptionValue(
        filterOptions.threshold,
        inputParamMetadata.valueType,
        "BackendToFrontend" /* BtoF */
      );
      sortedFilterOptions.push(`${prefix} ${thresholdValue} ${suffix}`);
    }
  }
  return sortedFilterOptions;
};

// lib/components/TopicStackRowInput.tsx
import clsx11 from "clsx";
import React27 from "react";

// lib/hooks/useTopicStackRowInput.tsx
import React25 from "react";
var useTopicStackRowInput = (topics, userInputParams, filterName, onSave) => {
  const benchmarkTopic = topics[0];
  const [subscriptionValue, setSubscriptionValue] = React25.useState(null);
  const [filterOptionsToBeSubscribed, setFilterOptionsToBeSubscribed] = React25.useState(null);
  const { subscribeAlertsWithFilterOptions, getTopicStackAlerts } = useNotifiTopicContext();
  React25.useEffect(() => {
    if (userInputParams && filterName) {
      const input = {
        [filterName]: {}
      };
      userInputParams.forEach((userInputParam) => {
        input[filterName][userInputParam.name] = convertOptionValue(
          userInputParam.defaultValue,
          userInputParam.kind
        );
      });
      setFilterOptionsToBeSubscribed({
        version: 1,
        input
      });
    }
  }, []);
  const isUserInputParamsValid = React25.useMemo(() => {
    if (filterOptionsToBeSubscribed && filterName) {
      filterOptionsToBeSubscribed.input[filterName];
      const isAllFieldsInputted = userInputParams.every((userInputParam) => {
        const input = filterOptionsToBeSubscribed.input;
        return !!input[filterName][userInputParam.name] && input[filterName][userInputParam.name] !== "";
      });
      return isAllFieldsInputted ? true : false;
    }
    return false;
  }, [filterOptionsToBeSubscribed]);
  const subscribedAlerts = getTopicStackAlerts(
    benchmarkTopic.fusionEventDescriptor.id ?? ""
  );
  const isInvalid = subscribedAlerts?.some((alert) => {
    const alertMetadata = resolveAlertName(alert.alertName);
    if (!isStackTopicAlertMetadata(alertMetadata)) return false;
    const isSubscriptionValueMatched = alertMetadata.subscriptionValue === subscriptionValue?.value;
    const isFilterOptionMatched = isEqual(
      alert.filterOptions,
      filterOptionsToBeSubscribed
    );
    return isFilterOptionMatched && isSubscriptionValueMatched;
  });
  const {
    targetDocument: { targetGroupId }
  } = useNotifiTargetContext();
  const isTopicReadyToSubscribe = !!(filterOptionsToBeSubscribed && subscriptionValue && targetGroupId && isUserInputParamsValid && !isInvalid);
  const subscribeTopic = async () => {
    if (!isTopicReadyToSubscribe || !benchmarkTopic.fusionEventDescriptor.id)
      return;
    const topicWithFilterOptionsList = topics.map((topic) => {
      return !topic.fusionEventDescriptor.id ? null : {
        topic,
        filterOptions: filterOptionsToBeSubscribed,
        customAlertName: composeTopicStackAlertName(
          topic.fusionEventDescriptor.id,
          subscriptionValue.value,
          subscriptionValue.label
        ),
        subscriptionValue: subscriptionValue.value
      };
    }).filter(
      (topicWithFilterOptions) => topicWithFilterOptions !== null
    );
    await subscribeAlertsWithFilterOptions(
      topicWithFilterOptionsList,
      targetGroupId
    );
    setSubscriptionValue(null);
    onSave && onSave();
  };
  return {
    subscriptionValue,
    setSubscriptionValue,
    filterOptionsToBeSubscribed,
    setFilterOptionsToBeSubscribed,
    isUserInputParamsValid,
    subscribeTopic,
    isTopicReadyToSubscribe
  };
};
var isStackTopicAlertMetadata = (metadata) => {
  return "subscriptionValue" in metadata && "subscriptionLabel" in metadata;
};

// lib/components/SubscriptionValueInput.tsx
import {
  resolveObjectArrayRef as resolveObjectArrayRef2
} from "@notifi-network/notifi-frontend-client";
import clsx10 from "clsx";
import React26 from "react";
var SubscriptionValueInput = (props) => {
  const { inputs } = useNotifiTenantConfigContext();
  const subscriptionValueOrRef = props.subscriptionValueRef;
  const [isDropdownOpen, setIsDropdownOpen] = React26.useState(false);
  const subscriptionValueOptions = resolveObjectArrayRef2(
    "subscriptionValueOptions",
    subscriptionValueOrRef,
    inputs
  );
  return /* @__PURE__ */ React26.createElement(
    "div",
    {
      className: clsx10("subscription-value-input", props.className?.container)
    },
    subscriptionValueOptions.length > 0 ? /* @__PURE__ */ React26.createElement(
      "div",
      {
        className: clsx10(
          "subscription-value-input-dropdown",
          props.className?.dropdownContainer
        )
      },
      /* @__PURE__ */ React26.createElement(
        "div",
        {
          className: clsx10(
            "subscription-value-input-dropdown-value",
            props.className?.dropdownValue
          ),
          onClick: () => setIsDropdownOpen((prev) => !prev)
        },
        /* @__PURE__ */ React26.createElement(
          "div",
          {
            className: clsx10(
              "subscription-value-input-dropdown-value-content",
              props.className?.dropdownValueContent,
              !props.subscriptionValue ? "unselected" : ""
            )
          },
          props.subscriptionValue ? props.subscriptionValue.label : props.copy?.dropdownPlaceholder ?? defaultCopy.subscriptionValueInput.dropdownPlaceholder
        ),
        /* @__PURE__ */ React26.createElement(
          "div",
          {
            className: clsx10(
              "subscription-value-input-dropdown-icon",
              props.className?.dropdownIcon
            )
          },
          /* @__PURE__ */ React26.createElement(Icon, { type: "triangle-down" })
        )
      ),
      isDropdownOpen && /* @__PURE__ */ React26.createElement(
        "div",
        {
          className: clsx10(
            "subscription-value-input-dropdown-list",
            props.className?.dropdownList
          )
        },
        subscriptionValueOptions.map((option) => /* @__PURE__ */ React26.createElement(
          "div",
          {
            onClick: () => {
              props.setSubscriptionValue(option);
              setIsDropdownOpen(false);
            },
            key: option.label
          },
          option.label
        ))
      )
    ) : null,
    subscriptionValueOptions.length === 0 ? /* @__PURE__ */ React26.createElement("div", null, /* @__PURE__ */ React26.createElement(
      "input",
      {
        type: "text",
        value: props.subscriptionValue?.value,
        onChange: (e) => props.setSubscriptionValue({
          value: e.target.value,
          label: ""
        })
      }
    )) : null
  );
};

// lib/components/TopicStackRowInput.tsx
var TopicStackRowInput = (props) => {
  const isTopicGroup = isTopicStackRowInput(props);
  const benchmarkTopic = isTopicGroup ? props.topics[0] : props.topic;
  const fusionEventTypeId = benchmarkTopic.fusionEventDescriptor.id;
  if (!fusionEventTypeId) return null;
  if (isTopicGroup && !isTopicGroupValid(props.topics)) return null;
  const subscriptionValueOrRef = getFusionEventMetadata(benchmarkTopic)?.uiConfigOverride?.subscriptionValueOrRef;
  if (!subscriptionValueOrRef) {
    console.error(
      "ERROR - unable to render TopicStackRowInput. Ensure fusionEventMetadata includes subscriptionValueOrRef"
    );
    return null;
  }
  const { isLoading: isLoadingTopic } = useNotifiTopicContext();
  const filterName = getFusionEventMetadata(benchmarkTopic)?.filters.find(isAlertFilter)?.name;
  const userInputParams = getUserInputParams(benchmarkTopic);
  const {
    subscriptionValue,
    setSubscriptionValue,
    filterOptionsToBeSubscribed,
    setFilterOptionsToBeSubscribed,
    subscribeTopic,
    isTopicReadyToSubscribe
  } = useTopicStackRowInput(
    isTopicGroup ? props.topics : [benchmarkTopic],
    userInputParams,
    filterName,
    props.onSave
  );
  return /* @__PURE__ */ React27.createElement(
    "div",
    {
      className: clsx11(
        "notifi-topic-stack-row-input",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React27.createElement(
      SubscriptionValueInput,
      {
        subscriptionValueRef: subscriptionValueOrRef,
        subscriptionValue,
        setSubscriptionValue
      }
    ),
    /* @__PURE__ */ React27.createElement("div", { className: clsx11("notifi-topic-row-user-inputs-row-container") }, userInputParams.map((userInputParm, id) => {
      return /* @__PURE__ */ React27.createElement(
        TopicOptions,
        {
          key: id,
          userInputParam: userInputParm,
          topic: benchmarkTopic,
          preventDefault: (userInputParmName, selected) => {
            if (!filterOptionsToBeSubscribed || !filterName) return;
            const updatedAlertFilterOptiopns = getUpdatedAlertFilterOptions(
              filterName,
              filterOptionsToBeSubscribed,
              userInputParmName,
              convertOptionValue(selected, userInputParm.kind)
            );
            setFilterOptionsToBeSubscribed(updatedAlertFilterOptiopns);
          }
        }
      );
    }), /* @__PURE__ */ React27.createElement(
      "div",
      {
        className: clsx11(
          "notifi-topic-stack-row-input-button",
          props.classNames?.button,
          !isTopicReadyToSubscribe && "disabled"
        ),
        onClick: subscribeTopic
      },
      isLoadingTopic ? /* @__PURE__ */ React27.createElement(
        LoadingAnimation,
        {
          type: "spinner",
          classNames: {
            spinner: "notifi-topic-stack-row-input-button-spinner"
          }
        }
      ) : null,
      /* @__PURE__ */ React27.createElement(
        "div",
        {
          className: clsx11(
            "notifi-topic-stack-row-input-button-text",
            isLoadingTopic && "hidden"
          )
        },
        props.copy?.buttonContent ?? defaultCopy.topicStackRowInput.buttonContent
      )
    ))
  );
};
var isTopicStackRowInput = (props) => {
  return "topics" in props;
};

// lib/components/TopicStackRow.tsx
var TopicStackRow = (props) => {
  const parentComponent = props.parentComponent ?? "ftu";
  const tooltipRef = React28.useRef(null);
  const { getTopicStackAlerts } = useNotifiTopicContext();
  const isTopicGroup = isTopicGroupRow2(props);
  const benchmarkTopic = isTopicGroup ? props.topics[0] : props.topic;
  const fusionEventTypeId = benchmarkTopic.fusionEventDescriptor.id;
  if (!fusionEventTypeId) return null;
  if (isTopicGroup && !isTopicGroupValid(props.topics)) return null;
  const alertsStack = React28.useMemo(() => {
    const topics = isTopicGroup ? props.topics : [benchmarkTopic];
    const existingAlerts = topics.flatMap(
      (topic) => getTopicStackAlerts(topic.fusionEventDescriptor.id)
      // Must has id (BE enforced)
    );
    const uniqueCriteria = /* @__PURE__ */ new Map();
    existingAlerts.forEach((alert) => {
      const criteriaKey = JSON.stringify({
        filterOptions: alert.filterOptions,
        subscriptionValueInfo: alert.subscriptionValueInfo
      });
      if (!uniqueCriteria.has(criteriaKey)) {
        uniqueCriteria.set(criteriaKey, alert);
      }
    });
    const alertsList = Array.from(uniqueCriteria.values()).map(
      (uniqueAlert) => existingAlerts.filter(
        (alert) => isEqual(alert.filterOptions, uniqueAlert.filterOptions) && isEqual(
          alert.subscriptionValueInfo,
          uniqueAlert.subscriptionValueInfo
        )
      )
    );
    return alertsList;
  }, [getTopicStackAlerts, props]);
  const title = isTopicGroup ? props.topicGroupName : getFusionEventMetadata(benchmarkTopic)?.uiConfigOverride?.topicDisplayName || // 1. Show topic displayname in fusionEventMetadata
  benchmarkTopic.uiConfig.displayNameOverride || // 2. Fall back to cardConfig'displayNameOverride  (May deprecated sooner or later)
  benchmarkTopic.uiConfig.name;
  const topicStackAlerts = getTopicStackAlerts(fusionEventTypeId);
  const [isTopicStackRowInputVisible, setIsTopicStackRowInputVisible] = React28.useState(topicStackAlerts.length === 0 ? true : false);
  const { componentPosition: tooltipPosition } = useComponentPosition(
    tooltipRef,
    parentComponent === "inbox" ? "notifi-inbox-config-topic-main" : "notifi-ftu-alert-edit-main"
  );
  return /* @__PURE__ */ React28.createElement(
    "div",
    {
      className: clsx12("notifi-topic-stack-row", props.classNames?.container)
    },
    /* @__PURE__ */ React28.createElement(
      "div",
      {
        className: clsx12(
          "notifi-topic-stack-row-header",
          props.classNames?.header
        )
      },
      /* @__PURE__ */ React28.createElement(
        "div",
        {
          className: clsx12(
            "notifi-topic-stack-header-title",
            props.classNames?.headerTitle
          )
        },
        /* @__PURE__ */ React28.createElement("div", null, title),
        benchmarkTopic.uiConfig.tooltipContent ? /* @__PURE__ */ React28.createElement(
          "div",
          {
            ref: tooltipRef,
            className: clsx12(
              "notifi-topic-list-tooltip-container",
              props.classNames?.tooltipContainer
            )
          },
          /* @__PURE__ */ React28.createElement(
            Icon,
            {
              type: "info",
              className: clsx12(
                "notifi-topic-list-tooltip-icon",
                props.classNames?.icon
              )
            }
          ),
          /* @__PURE__ */ React28.createElement(
            "div",
            {
              className: clsx12(
                "notifi-topic-list-tooltip-content",
                props.classNames?.tooltipContent,
                tooltipPosition
              )
            },
            benchmarkTopic.uiConfig.tooltipContent
          )
        ) : null
      )
    ),
    alertsStack.length > 0 ? /* @__PURE__ */ React28.createElement("div", { className: clsx12("notifi-topic-stacks") }, alertsStack.map((alerts, id) => {
      return /* @__PURE__ */ React28.createElement(TopicStack, { key: id, topicStackAlerts: alerts });
    })) : null,
    isTopicStackRowInputVisible || topicStackAlerts.length === 0 ? /* @__PURE__ */ React28.createElement(React28.Fragment, null, isTopicGroup ? /* @__PURE__ */ React28.createElement(
      TopicStackRowInput,
      {
        topics: props.topics,
        onSave: () => setIsTopicStackRowInputVisible(false),
        classNames: props.classNames?.TopicStackRowInput
      }
    ) : null, !isTopicGroup ? /* @__PURE__ */ React28.createElement(
      TopicStackRowInput,
      {
        topic: props.topic,
        onSave: () => setIsTopicStackRowInputVisible(false),
        classNames: props.classNames?.TopicStackRowInput
      }
    ) : null) : null,
    isTopicStackRowInputVisible || topicStackAlerts.length === 0 ? null : /* @__PURE__ */ React28.createElement(
      "div",
      {
        onClick: () => {
          setIsTopicStackRowInputVisible(true);
        },
        className: clsx12("notifi-topic-stack-row-cta", props.classNames?.cta)
      },
      props.copy?.cta ?? defaultCopy.topicStackRow.cta
    )
  );
};
var isTopicGroupRow2 = (props) => {
  return "topics" in props && "topicGroupName" in props;
};

// lib/components/TopicList.tsx
var TopicList = (props) => {
  const parentComponent = props.parentComponent ?? "ftu";
  const { fusionEventTopics } = useNotifiTenantConfigContext();
  const topicRows = React29.useMemo(() => {
    const fusionEventTopicGroups = {};
    const fusionEventStandaloneTopics = [];
    fusionEventTopics.forEach((topic, index) => {
      if (topic.uiConfig.topicGroupName) {
        if (!fusionEventTopicGroups[topic.uiConfig.topicGroupName]) {
          fusionEventTopicGroups[topic.uiConfig.topicGroupName] = {
            index,
            topicGroupName: topic.uiConfig.topicGroupName,
            topics: [topic]
          };
        } else {
          fusionEventTopicGroups[topic.uiConfig.topicGroupName].topics.push(
            topic
          );
        }
        return;
      }
      fusionEventStandaloneTopics.push({ topic, index });
    });
    return [
      ...Object.values(fusionEventTopicGroups),
      ...fusionEventStandaloneTopics
    ].sort((a, b) => a.index - b.index);
  }, [fusionEventTopics]);
  return /* @__PURE__ */ React29.createElement("div", { className: clsx13("notifi-topic-list", props.classNames?.container) }, topicRows.map((rowMetadata, id) => {
    if (isTopicStandaloneMetadata(rowMetadata)) {
      const isSubscriptionValueInputable = getFusionEventMetadata(
        rowMetadata.topic
      )?.uiConfigOverride?.isSubscriptionValueInputable;
      if (isSubscriptionValueInputable) {
        return /* @__PURE__ */ React29.createElement(
          TopicStackRow,
          {
            key: id,
            ...rowMetadata,
            classNames: props.classNames?.TopicRow,
            parentComponent
          }
        );
      }
      return /* @__PURE__ */ React29.createElement(
        TopicRow,
        {
          ...rowMetadata,
          key: id,
          classNames: props.classNames?.TopicRow,
          parentComponent
        }
      );
    }
    if (isTopicGroupMetadata(rowMetadata)) {
      const isSubscriptionValueInputable = getFusionEventMetadata(
        rowMetadata.topics[0]
      )?.uiConfigOverride?.isSubscriptionValueInputable;
      if (isSubscriptionValueInputable) {
        return /* @__PURE__ */ React29.createElement(
          TopicStackRow,
          {
            key: id,
            ...rowMetadata,
            classNames: props.classNames?.TopicRow,
            parentComponent
          }
        );
      }
      return /* @__PURE__ */ React29.createElement(
        TopicRow,
        {
          key: id,
          ...rowMetadata,
          classNames: props.classNames?.TopicStackRow,
          parentComponent
        }
      );
    }
  }));
};
var isTopicStandaloneMetadata = (topicRowMetadata) => {
  return "topic" in topicRowMetadata;
};
var isTopicGroupMetadata = (topicRowMetadata) => {
  return "topicGroupName" in topicRowMetadata && "topics" in topicRowMetadata;
};

// lib/components/FtuAlertEdit.tsx
var FtuAlertEdit = (props) => {
  const { updateFtuStage } = useNotifiUserSettingContext();
  const [isLoading, setIsLoading] = React30.useState(false);
  const onClick = async () => {
    setIsLoading(true);
    await updateFtuStage(1 /* Done */);
    setIsLoading(false);
    props.onClickNext();
  };
  return /* @__PURE__ */ React30.createElement("div", { className: clsx14("notifi-ftu-alert-edit", props.classNames?.container) }, /* @__PURE__ */ React30.createElement(
    NavHeader,
    {
      leftCta: props.onClickBack ? {
        icon: "arrow-back",
        action: () => props.onClickBack()
      } : void 0,
      rightCta: props.navHeaderRightCta
    },
    props.copy?.headerTitle ?? defaultCopy.ftuAlertEdit.headerTitle
  ), /* @__PURE__ */ React30.createElement(
    "div",
    {
      className: clsx14("notifi-ftu-alert-edit-main", props.classNames?.main)
    },
    /* @__PURE__ */ React30.createElement(TopicList, { parentComponent: "ftu" })
  ), /* @__PURE__ */ React30.createElement(
    "div",
    {
      className: clsx14(
        "notifi-ftu-button-container",
        props.classNames?.buttonContainer
      )
    },
    /* @__PURE__ */ React30.createElement(
      "button",
      {
        "data-cy": "notifi-ftu-alert-edit-button",
        className: clsx14(
          "btn",
          "notifi-ftu-alert-edit-button",
          props.classNames?.button
        ),
        disabled: isLoading,
        onClick
      },
      isLoading ? /* @__PURE__ */ React30.createElement(
        LoadingAnimation,
        {
          type: "spinner",
          classNames: { spinner: "notifi-ftu-alert-edit-button-spinner" }
        }
      ) : null,
      /* @__PURE__ */ React30.createElement(
        "div",
        {
          className: clsx14(
            "notifi-ftu-alert-edit-button-text",
            isLoading && "hidden"
          )
        },
        props.copy?.buttonText ?? defaultCopy.ftuAlertEdit.buttonText
      )
    )
  ));
};

// lib/components/FtuTargetEdit.tsx
import clsx18 from "clsx";
import React35 from "react";

// lib/hooks/useIsTargetInputValid.tsx
import { objectKeys as objectKeys4 } from "@notifi-network/notifi-frontend-client";
import React31 from "react";
var useIsTargetInputValid = () => {
  const { cardConfig } = useNotifiTenantConfigContext();
  const {
    targetDocument: { targetInputs }
  } = useNotifiTargetContext();
  const isInputValid = React31.useMemo(() => {
    const isInputValid2 = !objectKeys4(targetInputs).map((key) => {
      if (typeof targetInputs[key] !== "boolean") {
        const targetInput = targetInputs[key];
        if (targetInput.error) {
          return false;
        }
      }
      return true;
    }).includes(false);
    if (cardConfig?.isContactInfoRequired) {
      return isInputValid2 && getAvailableTargetInputCount(targetInputs) > 0;
    }
    return isInputValid2;
  }, [targetInputs]);
  return isInputValid;
};

// lib/components/TargetInputs.tsx
import clsx17 from "clsx";
import React34 from "react";

// lib/components/TargetInputField.tsx
import clsx15 from "clsx";
import React32, { useMemo as useMemo2 } from "react";
var TargetInputField = (props) => {
  const {
    targetDocument: { targetInputs, targetData },
    updateTargetInputs
  } = useNotifiTargetContext();
  const [isInputFocused, setIsInputFocused] = React32.useState(false);
  const [isShowingInvalidWarning, setIsShowingInvalidWarning] = React32.useState(false);
  React32.useEffect(() => {
    updateTargetInputs(props.targetType, {
      value: targetData?.[props.targetType] ?? ""
    });
    setIsShowingInvalidWarning(false);
  }, []);
  const targetToBeSaved = useMemo2(() => {
    switch (props.targetType) {
      case "email":
        return targetInputs.email;
      case "phoneNumber":
        return targetInputs.phoneNumber;
      case "telegram":
        return targetInputs.telegram;
    }
  }, [props.targetType, targetInputs]);
  const inputPlaceholder = props.copy?.placeholder ?? props.targetType === "email" ? defaultCopy.inputFields.email : props.targetType === "phoneNumber" ? defaultCopy.inputFields.phoneNumber : defaultCopy.inputFields.telegram;
  const isTargetValid = (targetInput) => {
    if (targetInput === "" || !props.validateRegex) {
      return true;
    }
    if (props.validateRegex.test(targetInput)) {
      return true;
    } else {
      return false;
    }
  };
  return /* @__PURE__ */ React32.createElement(
    "div",
    {
      "data-cy": `notifi-target-input-${props.targetType}`,
      className: clsx15("notifi-target-input-field", props.classNames?.container)
    },
    props.copy?.label ? /* @__PURE__ */ React32.createElement(
      "label",
      {
        className: clsx15(
          "notifi-target-input-field-label",
          props.classNames?.label
        )
      },
      props.copy.label
    ) : null,
    /* @__PURE__ */ React32.createElement(
      "div",
      {
        className: clsx15(
          "notifi-target-input-field-input-container",
          props.classNames?.container,
          isInputFocused ? "focused" : "",
          // NOTE: Now we only show error for invalid email input
          isShowingInvalidWarning ? "warning" : ""
        )
      },
      /* @__PURE__ */ React32.createElement(
        Icon,
        {
          type: props.iconType,
          className: clsx15(
            "notifi-target-input-field-icon",
            props.classNames?.icon
          )
        }
      ),
      /* @__PURE__ */ React32.createElement(
        "input",
        {
          type: props.targetType === "email" ? "email" : props.targetType === "phoneNumber" ? "tel" : "text",
          className: clsx15(
            "notifi-target-input-field-input",
            props.classNames?.input
          ),
          value: targetToBeSaved.value,
          onChange: (evt) => {
            const targetInput = evt.target.value;
            if (isTargetValid(targetInput)) {
              updateTargetInputs(props.targetType, {
                value: targetInput,
                error: ""
              });
              return;
            }
            updateTargetInputs(props.targetType, {
              value: targetInput,
              error: defaultCopy.targetInputField.inValidErrorMessage(
                props.targetType
              )
            });
          },
          onFocus: () => {
            setIsInputFocused(true);
            setIsShowingInvalidWarning(false);
            props.onFocus?.(props.targetType);
          },
          onBlur: () => {
            setIsInputFocused(false);
            props.onBlur?.(props.targetType);
            if (targetToBeSaved.error) {
              if (props.targetType === "email") {
                setIsShowingInvalidWarning(true);
              }
            }
          },
          placeholder: inputPlaceholder,
          disabled: props.disabled
        }
      )
    )
  );
};

// lib/components/TargetInputToggle.tsx
import clsx16 from "clsx";
import React33, { useMemo as useMemo3 } from "react";
var TargetInputToggle = (props) => {
  const {
    targetDocument: { targetInputs, targetData },
    updateTargetInputs
  } = useNotifiTargetContext();
  const useTarget = useMemo3(() => {
    switch (props.targetType) {
      case "slack":
        return targetInputs.slack;
      case "discord":
        return targetInputs.discord;
      case "wallet":
        return targetInputs.wallet;
    }
  }, [targetInputs]);
  React33.useEffect(() => {
    switch (props.targetType) {
      case "discord":
        updateTargetInputs("discord", targetData?.discord.useDiscord);
        break;
      case "slack":
        updateTargetInputs("slack", targetData?.slack.useSlack);
        break;
      case "wallet":
        updateTargetInputs("wallet", targetData?.wallet.useWallet);
        break;
    }
  }, []);
  const title = props.targetType === "slack" ? props.copy?.title?.slack ?? defaultCopy.inputToggles.slack : props.targetType === "discord" ? props.copy?.title?.discord ?? defaultCopy.inputToggles.discord : props.targetType === "wallet" ? props.copy?.title?.wallet ?? defaultCopy.inputToggles.wallet : "Unknown target type";
  const unavailable = props.targetType === "slack" ? props.copy?.unavailable?.slack ?? defaultCopy.inputToggles.slackUnavailable : props.targetType === "discord" ? props.copy?.unavailable?.discord ?? defaultCopy.inputToggles.discordUnavailable : props.targetType === "wallet" ? props.copy?.unavailable?.wallet ?? defaultCopy.inputToggles.walletUnavailable : "Unknown target type";
  const onClick = useMemo3(() => {
    switch (props.targetType) {
      case "slack":
        return () => updateTargetInputs("slack", !targetInputs.slack);
      case "discord":
        return () => updateTargetInputs("discord", !targetInputs.discord);
      case "wallet":
        return () => updateTargetInputs("wallet", !targetInputs.wallet);
    }
  }, [targetInputs]);
  const targetIsAvailable = targetData[props.targetType].isAvailable;
  return /* @__PURE__ */ React33.createElement(
    "div",
    {
      "data-cy": `notifi-target-input-${props.targetType}`,
      className: clsx16(
        "notifi-target-input-toggle-container",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React33.createElement(
      "div",
      {
        className: clsx16(
          "notifi-target-input-toggle-label",
          props.classNames?.label
        )
      },
      title
    ),
    targetIsAvailable ? /* @__PURE__ */ React33.createElement(
      Toggle,
      {
        classNames: props.classNames?.toggle,
        disabled: props.disabled,
        checked: useTarget,
        setChecked: onClick
      }
    ) : /* @__PURE__ */ React33.createElement(
      "div",
      {
        className: clsx16(
          "notifi-target-input-toggle-unavailable",
          props.classNames?.unavailable
        )
      },
      unavailable
    )
  );
};

// lib/components/TargetInputs.tsx
var TargetInputs = (props) => {
  const { cardConfig } = useNotifiTenantConfigContext();
  const { isLoading } = useNotifiTargetContext();
  return /* @__PURE__ */ React34.createElement("div", { className: clsx17("notifi-target-inputs", props.classNames?.container) }, cardConfig?.contactInfo.email?.active ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(
    TargetInputField,
    {
      copy: props.copy?.targetInputFields?.email,
      classNames: props.classNames?.targetInputField?.email,
      targetType: "email",
      iconType: props.copy?.iconType?.email ?? "email",
      validateRegex: getTargetValidateRegex("email"),
      onFocus: props.formTargetsOnFocus,
      onBlur: props.formTargetsOnBlur
    }
  ), props.copy?.inputSeparators?.email ? /* @__PURE__ */ React34.createElement(
    "div",
    {
      className: clsx17(
        "notifi-input-separator",
        props.classNames?.inputSeparators?.email
      )
    },
    /* @__PURE__ */ React34.createElement("div", null, props.copy?.inputSeparators?.email)
  ) : null) : null, cardConfig?.contactInfo.sms?.active ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(
    TargetInputField,
    {
      copy: props.copy?.targetInputFields?.phoneNumber,
      classNames: props.classNames?.targetInputField?.phoneNumber,
      targetType: "phoneNumber",
      iconType: props.copy?.iconType?.phoneNumber ?? "sms",
      onFocus: props.formTargetsOnFocus,
      onBlur: props.formTargetsOnBlur
    }
  ), props.copy?.inputSeparators?.sms ? /* @__PURE__ */ React34.createElement(
    "div",
    {
      className: clsx17(
        "notifi-input-separator",
        props.classNames?.inputSeparators?.sms
      )
    },
    /* @__PURE__ */ React34.createElement("div", null, props.copy?.inputSeparators?.sms)
  ) : null) : null, cardConfig?.contactInfo.telegram?.active ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(
    TargetInputField,
    {
      copy: props.copy?.targetInputFields?.telegram,
      classNames: props.classNames?.targetInputField?.telegram,
      targetType: "telegram",
      iconType: props.copy?.iconType?.telegram ?? "telegram",
      validateRegex: getTargetValidateRegex("telegram"),
      onFocus: props.formTargetsOnFocus,
      onBlur: props.formTargetsOnBlur
    }
  ), props.copy?.inputSeparators?.telegram ? /* @__PURE__ */ React34.createElement(
    "div",
    {
      className: clsx17(
        "notifi-input-separator",
        props.classNames?.inputSeparators?.telegram
      )
    },
    /* @__PURE__ */ React34.createElement("div", null, props.copy?.inputSeparators?.telegram)
  ) : null) : null, cardConfig?.contactInfo.discord?.active ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(
    TargetInputToggle,
    {
      targetType: "discord",
      disabled: isLoading,
      copy: props.copy?.TargetInputToggle,
      classNames: props.classNames?.TargetInputToggle
    }
  ), props.copy?.inputSeparators?.discord ? /* @__PURE__ */ React34.createElement(
    "div",
    {
      className: clsx17(
        "notifi-input-separator",
        props.classNames?.inputSeparators?.discord
      )
    },
    /* @__PURE__ */ React34.createElement("div", null, props.copy?.inputSeparators?.discord)
  ) : null) : null, cardConfig?.contactInfo.slack?.active ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(
    TargetInputToggle,
    {
      targetType: "slack",
      disabled: isLoading,
      copy: props.copy?.TargetInputToggle,
      classNames: props.classNames?.TargetInputToggle
    }
  ), props.copy?.inputSeparators?.slack ? /* @__PURE__ */ React34.createElement(
    "div",
    {
      className: clsx17(
        "notifi-input-separator",
        props.classNames?.inputSeparators?.slack
      )
    },
    /* @__PURE__ */ React34.createElement("div", null, props.copy?.inputSeparators?.slack)
  ) : null) : null, cardConfig?.contactInfo.wallet?.active ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(
    TargetInputToggle,
    {
      targetType: "wallet",
      disabled: isLoading,
      copy: props.copy?.TargetInputToggle,
      classNames: props.classNames?.TargetInputToggle
    }
  ), props.copy?.inputSeparators?.wallet ? /* @__PURE__ */ React34.createElement(
    "div",
    {
      className: clsx17(
        "notifi-input-separator",
        props.classNames?.inputSeparators?.wallet
      )
    },
    /* @__PURE__ */ React34.createElement("div", null, props.copy?.inputSeparators?.wallet)
  ) : null) : null);
};

// lib/components/FtuTargetEdit.tsx
var FtuTargetEdit = (props) => {
  const [isLoading, setIsLoading] = React35.useState(false);
  const {
    renewTargetGroup,
    targetDocument: { targetInputs }
  } = useNotifiTargetContext();
  const [isShowingInvalidEmailWarning, setIsShowingInvalidEmailWarning] = React35.useState(false);
  const onClick = async () => {
    setIsLoading(true);
    await renewTargetGroup();
    props.setFtuView("list" /* TargetList */);
    setIsLoading(false);
  };
  const isInputValid = useIsTargetInputValid();
  return /* @__PURE__ */ React35.createElement(
    "div",
    {
      className: clsx18("notifi-ftu-target-edit", props.classNames?.container)
    },
    /* @__PURE__ */ React35.createElement(NavHeader, { rightCta: props.navHeaderRightCta }, props.copy?.headerTitle ?? defaultCopy.ftuTargetEdit.headerTitle),
    /* @__PURE__ */ React35.createElement(
      "div",
      {
        className: clsx18(
          "notifi-ftu-target-edit-description",
          props.classNames?.TargetInputs
        )
      },
      props.copy?.description ?? defaultCopy.ftuTargetEdit.description
    ),
    /* @__PURE__ */ React35.createElement(
      "div",
      {
        className: clsx18(
          "notifi-ftu-target-edit-invalid-email-warning",
          props.classNames?.invalidEmailWarning
        )
      },
      isShowingInvalidEmailWarning ? targetInputs.email.error : null
    ),
    /* @__PURE__ */ React35.createElement(
      "div",
      {
        className: clsx18("notifi-ftu-target-edit-main", props.classNames?.main)
      },
      /* @__PURE__ */ React35.createElement(
        TargetInputs,
        {
          copy: props.copy?.TargetInputs,
          formTargetsOnFocus: (target) => {
            if (target === "email") setIsShowingInvalidEmailWarning(false);
          },
          formTargetsOnBlur: (target) => {
            if (target === "email") setIsShowingInvalidEmailWarning(true);
          }
        }
      )
    ),
    /* @__PURE__ */ React35.createElement(
      "div",
      {
        className: clsx18(
          "notifi-ftu-button-container",
          props.classNames?.buttonContainer
        )
      },
      /* @__PURE__ */ React35.createElement(
        "button",
        {
          "data-cy": "notifi-ftu-target-edit-button",
          className: clsx18(
            "btn",
            "notifi-ftu-target-edit-button",
            props.classNames?.button
          ),
          disabled: !isInputValid || isLoading,
          onClick
        },
        isLoading ? /* @__PURE__ */ React35.createElement(
          LoadingAnimation,
          {
            type: "spinner",
            classNames: { spinner: "notifi-ftu-target-edit-button-spinner" }
          }
        ) : null,
        /* @__PURE__ */ React35.createElement(
          "div",
          {
            className: clsx18(
              "notifi-ftu-target-edit-button-text",
              isLoading && "hidden"
            )
          },
          props.copy?.buttonText ?? defaultCopy.ftuTargetEdit.buttonText
        )
      )
    )
  );
};

// lib/components/FtuTargetList.tsx
import clsx22 from "clsx";
import React39 from "react";

// lib/components/TargetList.tsx
import { objectKeys as objectKeys5 } from "@notifi-network/notifi-frontend-client";
import clsx21 from "clsx";
import React38 from "react";

// lib/components/TargetListItem.tsx
import clsx20 from "clsx";
import React37 from "react";

// lib/components/TargetCta.tsx
import clsx19 from "clsx";
import React36 from "react";
var TargetCta = (props) => {
  const [isPostCtaShown, setIsPostCtaShown] = React36.useState(false);
  const [isLoading, setIsLoading] = React36.useState(false);
  React36.useEffect(() => {
    if (isLoadingAnimationPostCta(props.postCta)) {
      if (isLoading) {
        return setIsPostCtaShown(true);
      }
      return setIsPostCtaShown(false);
    }
  }, [isLoading]);
  return /* @__PURE__ */ React36.createElement("div", { className: clsx19("notifi-target-cta", props.classNames?.container) }, isCtaInfo(props.targetInfoPrompt) ? /* @__PURE__ */ React36.createElement(
    "div",
    {
      className: clsx19(
        props.type === "button" && "notifi-target-cta-button",
        props.type === "button" && props.classNames?.actionRequired?.button,
        props.type === "link" && "notifi-target-cta-link",
        props.type === "link" && props.classNames?.actionRequired?.link,
        isPostCtaShown && "disabled"
      ),
      onClick: async () => {
        if (isPostCtaShown) return;
        if (!isCtaInfo(props.targetInfoPrompt)) return;
        setIsLoading(true);
        await props.targetInfoPrompt.onClick();
        setIsLoading(false);
        if (!isLoadingAnimationPostCta(props.postCta)) {
          setIsPostCtaShown(true);
          setTimeout(
            () => setIsPostCtaShown(false),
            props.postCta.durationInMs
          );
        }
      }
    },
    isPostCtaShown ? isLoadingAnimationPostCta(props.postCta) ? /* @__PURE__ */ React36.createElement(
      LoadingAnimation,
      {
        type: props.postCta.animationType,
        classNames: { spinner: "notifi-target-cta-loading-spinner" }
      }
    ) : props.postCta.text : props.targetInfoPrompt.message
  ) : null, !isCtaInfo(props.targetInfoPrompt) ? /* @__PURE__ */ React36.createElement(
    "div",
    {
      className: clsx19(
        "notifi-target-cta-action-not-required",
        props.classNames?.actionNotRequired
      )
    },
    /* @__PURE__ */ React36.createElement(Icon, { type: "check" }),
    props.targetInfoPrompt.message
  ) : null);
};
var isLoadingAnimationPostCta = (postCta) => {
  return postCta.type === "loading-animation";
};

// lib/components/TargetListItem.tsx
var TargetListItem = (props) => {
  const tooltipRef = React37.useRef(null);
  const {
    targetDocument: { targetData, targetInputs },
    renewTargetGroup,
    updateTargetInputs,
    isChangingTargets
  } = useNotifiTargetContext();
  const { cardConfig } = useNotifiTenantConfigContext();
  const isItemRemoved = React37.useRef(false);
  React37.useEffect(() => {
    if (!isItemRemoved.current) return;
    const hasChange = !Object.values(isChangingTargets).every(
      (hasChange2) => !hasChange2
    );
    hasChange && renewTargetGroup();
    isItemRemoved.current = false;
  }, [isChangingTargets]);
  const isRemoveButtonAvailable = (targetInfoPrompt) => {
    if (cardConfig?.isContactInfoRequired) {
      return getAvailableTargetInputCount(targetInputs) > 1 && isTargetVerified(targetInfoPrompt) && props.parentComponent !== "ftu";
    }
    return isTargetVerified(targetInfoPrompt) && props.parentComponent !== "ftu";
  };
  if (!targetData[props.target] || !props.targetInfo.infoPrompt) return null;
  const { componentPosition: tooltipIconPosition } = useComponentPosition(
    tooltipRef,
    props.parentComponent === "inbox" ? "notifi-inbox-config-target-list-main" : "notifi-ftu-target-list-main"
  );
  if (isFormTarget(props.target))
    return /* @__PURE__ */ React37.createElement(
      "div",
      {
        className: clsx20(
          "notifi-target-list-item",
          props.classNames?.targetListItem,
          // NOTE: only used when we want to adopt different style for verified items
          isTargetVerified(props.targetInfo.infoPrompt) && props.classNames?.targetListVerifiedItem
        )
      },
      /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-item-target",
            props.classNames?.targetListItemTarget
          )
        },
        /* @__PURE__ */ React37.createElement(
          Icon,
          {
            type: props.iconType,
            className: clsx20("notifi-target-list-icon", props.classNames?.icon)
          }
        ),
        /* @__PURE__ */ React37.createElement("label", null, props.label)
      ),
      props.message?.beforeVerify && isTargetCta(props.targetInfo.infoPrompt) ? /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-target-verify-message",
            props.classNames?.verifyMessage
          )
        },
        props.message.beforeVerify
      ) : null,
      /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-item-target-id",
            props.classNames?.targetId
          )
        },
        targetData[props.target]
      ),
      /* @__PURE__ */ React37.createElement(
        TargetCta,
        {
          type: props.targetCtaType,
          targetInfoPrompt: props.targetInfo.infoPrompt,
          classNames: props.classNames?.TargetCta,
          postCta: props.postCta
        }
      ),
      isRemoveButtonAvailable(props.targetInfo.infoPrompt) ? /* @__PURE__ */ React37.createElement(
        TargetListItemAction,
        {
          action: async () => {
            isItemRemoved.current = true;
            updateTargetInputs(props.target, { value: "" });
          },
          classNames: { removeCta: props.classNames?.removeCta }
        }
      ) : null
    );
  if (isToggleTarget(props.target)) {
    const toggleTargetData = targetData[props.target];
    return /* @__PURE__ */ React37.createElement(
      "div",
      {
        className: clsx20(
          "notifi-target-list-item",
          props.classNames?.targetListItem,
          // NOTE: only used when we want to adopt different style for verified items
          isTargetVerified(props.targetInfo.infoPrompt) && props.classNames?.targetListVerifiedItem
        )
      },
      /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-item-target",
            props.classNames?.targetListItemTarget
          )
        },
        /* @__PURE__ */ React37.createElement(
          Icon,
          {
            type: props.iconType,
            className: clsx20("notifi-target-list-icon", props.classNames?.icon)
          }
        ),
        /* @__PURE__ */ React37.createElement("label", null, props.label)
      ),
      !isTargetVerified(props.targetInfo.infoPrompt) || props.parentComponent === "ftu" || props.target === "wallet" ? null : /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-item-target-id",
            props.classNames?.targetId
          )
        },
        toggleTargetData?.data && "username" in toggleTargetData.data && `@${toggleTargetData.data.username}`
      ),
      props.message?.beforeVerify && isTargetCta(props.targetInfo.infoPrompt) ? /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-target-verify-message",
            props.classNames?.verifyMessage
          )
        },
        props.message.beforeVerify,
        /* @__PURE__ */ React37.createElement("div", { className: "notifi-target-list-item-tooltip", ref: tooltipRef }, /* @__PURE__ */ React37.createElement(
          Icon,
          {
            className: clsx20(
              "notifi-target-list-item-tooltip-icon",
              props.classNames?.tooltipIcon
            ),
            type: "info"
          }
        ), /* @__PURE__ */ React37.createElement(
          "div",
          {
            className: clsx20(
              "notifi-target-list-item-tooltip-content",
              props.classNames?.tooltipContent,
              props.parentComponent === "inbox" ? "inbox" : "",
              tooltipIconPosition
            )
          },
          props.message.beforeVerifyTooltip,
          " ",
          props.message.beforeVerifyTooltipEndingLink ? /* @__PURE__ */ React37.createElement(
            "a",
            {
              href: props.message.beforeVerifyTooltipEndingLink.url,
              target: "_blank",
              rel: "noopener noreferrer"
            },
            props.message.beforeVerifyTooltipEndingLink.text
          ) : null
        ))
      ) : null,
      props.message?.afterVerify && isTargetVerified(props.targetInfo.infoPrompt) ? /* @__PURE__ */ React37.createElement(
        "div",
        {
          className: clsx20(
            "notifi-target-list-target-confirmed-message",
            props.classNames?.verifyMessage,
            props.parentComponent === "inbox" ? "inbox" : ""
          )
        },
        props.message.afterVerify,
        /* @__PURE__ */ React37.createElement("div", { className: "notifi-target-list-item-tooltip", ref: tooltipRef }, /* @__PURE__ */ React37.createElement(
          Icon,
          {
            className: clsx20(
              "notifi-target-list-item-tooltip-icon",
              props.classNames?.tooltipIcon
            ),
            type: "info"
          }
        ), /* @__PURE__ */ React37.createElement(
          "div",
          {
            className: clsx20(
              "notifi-target-list-item-tooltip-content",
              props.classNames?.tooltipContent,
              props.parentComponent === "inbox" ? "inbox" : "",
              tooltipIconPosition
            )
          },
          props.message.afterVerifyTooltip,
          " ",
          props.message.afterVerifyTooltipEndingLink ? /* @__PURE__ */ React37.createElement(
            "a",
            {
              href: props.message.afterVerifyTooltipEndingLink.url,
              target: "_blank",
              rel: "noopener noreferrer"
            },
            props.message.afterVerifyTooltipEndingLink.text
          ) : null
        ))
      ) : null,
      /* @__PURE__ */ React37.createElement(
        TargetCta,
        {
          type: props.targetCtaType,
          targetInfoPrompt: props.targetInfo.infoPrompt,
          classNames: props.classNames?.TargetCta,
          postCta: props.postCta
        }
      ),
      isRemoveButtonAvailable(props.targetInfo.infoPrompt) ? /* @__PURE__ */ React37.createElement(
        TargetListItemAction,
        {
          action: async () => {
            isItemRemoved.current = true;
            updateTargetInputs(props.target, false);
          },
          classNames: { removeCta: props.classNames?.removeCta }
        }
      ) : null
    );
  }
  return null;
};
var TargetListItemAction = (props) => {
  return /* @__PURE__ */ React37.createElement(
    "div",
    {
      className: clsx20(
        "notifi-target-list-item-remove",
        props.classNames?.removeCta
      ),
      onClick: props.action
    },
    props.actionText ?? "Remove"
  );
};

// lib/components/TargetList.tsx
var TargetList = (props) => {
  const parentComponent = props.parentComponent ?? "ftu";
  const {
    targetDocument: { targetInfoPrompts, targetData }
  } = useNotifiTargetContext();
  const targetListItemArgsList = React38.useMemo(() => {
    const order = [
      "email",
      "phoneNumber",
      "telegram",
      "discord",
      "slack",
      "wallet"
    ];
    const verifiedTargets = objectKeys5(targetData).filter((target) => {
      const targetInfo = targetInfoPrompts[target];
      return targetInfo ? isTargetVerified(targetInfo.infoPrompt) : false;
    }).sort((a, b) => {
      return order.indexOf(a) - order.indexOf(b);
    });
    const unverifiedTargets = objectKeys5(targetData).filter((target) => !verifiedTargets.includes(target)).sort((a, b) => {
      return order.indexOf(a) - order.indexOf(b);
    });
    return [...unverifiedTargets, ...verifiedTargets].map((target) => {
      const targetInfo = targetInfoPrompts[target];
      if (!targetInfo || !targetData[target]) return null;
      const targetListItemArgs = {
        target,
        targetInfo
      };
      switch (target) {
        case "email":
          targetListItemArgs.iconType = "email";
          targetListItemArgs.label = props.copy?.email ?? defaultCopy.targetList.email;
          targetListItemArgs.targetCtaType = "link";
          targetListItemArgs.message = {
            beforeVerify: props.copy?.emailVerifyMessage ?? defaultCopy.targetList.emailVerifyMessage
          };
          targetListItemArgs.postCta = {
            type: "text",
            text: props.copy?.postCtaEmail ?? defaultCopy.targetList.postCtaEmail,
            durationInMs: props.copy?.postCtaEmailDurationInMs ?? defaultCopy.targetList.postCtaEmailDurationInMs
          };
          break;
        case "phoneNumber":
          targetListItemArgs.iconType = "sms";
          targetListItemArgs.label = props.copy?.sms ?? defaultCopy.targetList.phoneNumber;
          targetListItemArgs.targetCtaType = "button";
          targetListItemArgs.postCta = {
            //NOTE: SMS target will never go through a post CTA
            type: "text",
            text: "",
            durationInMs: 0
          };
          break;
        case "telegram":
          targetListItemArgs.iconType = "telegram";
          targetListItemArgs.label = props.copy?.telegram ?? defaultCopy.targetList.telegram;
          targetListItemArgs.targetCtaType = "button";
          targetListItemArgs.postCta = {
            type: "text",
            text: props.copy?.postCtaTelegram ?? defaultCopy.targetList.postCtaTelegram,
            durationInMs: 5e3
          };
          break;
        case "discord":
          if (!targetData[target].useDiscord) return null;
          targetListItemArgs.iconType = "discord";
          targetListItemArgs.label = defaultCopy.targetList.discord;
          targetListItemArgs.targetCtaType = "button";
          targetListItemArgs.message = {
            afterVerify: props.copy?.discordVerifiedMessage ?? defaultCopy.targetList.discordVerifiedMessage,
            afterVerifyTooltip: props.copy?.discordVerifiedPromptTooltip ?? defaultCopy.targetList.discordVerifiedPromptTooltip
          };
          targetListItemArgs.postCta = {
            type: "text",
            text: props.copy?.postCtaDiscord ?? defaultCopy.targetList.postCtaDiscord,
            durationInMs: props.copy?.postCtaDiscordDurationInMs ?? defaultCopy.targetList.postCtaDiscordDurationInMs
          };
          break;
        case "slack":
          if (!targetData[target].useSlack) return null;
          targetListItemArgs.iconType = "slack";
          targetListItemArgs.label = defaultCopy.targetList.slack;
          targetListItemArgs.targetCtaType = "button";
          targetListItemArgs.postCta = {
            type: "text",
            text: props.copy?.postCtaSlack ?? defaultCopy.targetList.postCtaSlack,
            durationInMs: props.copy?.postCtaSlackDurationInMs ?? defaultCopy.targetList.postCtaSlackDurationInMs
          };
          break;
        case "wallet":
          if (!targetData[target].useWallet) return null;
          targetListItemArgs.iconType = "connect";
          targetListItemArgs.label = defaultCopy.targetList.wallet;
          targetListItemArgs.targetCtaType = "button";
          targetListItemArgs.message = {
            beforeVerify: defaultCopy.targetList.walletVerifyMessage,
            beforeVerifyTooltip: defaultCopy.targetList.walletVerifyTooltip,
            afterVerify: defaultCopy.targetList.walletVerifiedMessage,
            afterVerifyTooltip: defaultCopy.targetList.walletVerifiedTooltip,
            afterVerifyTooltipEndingLink: defaultCopy.targetList.walletVerifiedTooltipLink
          };
          targetListItemArgs.postCta = {
            type: "loading-animation",
            animationType: "spinner"
          };
          break;
      }
      return targetListItemArgs;
    });
  }, [targetData, targetInfoPrompts]);
  return /* @__PURE__ */ React38.createElement("div", { className: clsx21("notifi-target-list", props.classNames?.container) }, targetListItemArgsList.map((targetListItemArgs) => {
    if (!targetListItemArgs) return null;
    return /* @__PURE__ */ React38.createElement(
      TargetListItem,
      {
        key: targetListItemArgs.target,
        ...targetListItemArgs,
        parentComponent,
        classNames: props.classNames?.TargetListItem
      }
    );
  }));
};

// lib/components/FtuTargetList.tsx
var FtuTargetList = (props) => {
  const [isLoading, setIsLoading] = React39.useState(false);
  const { updateFtuStage } = useNotifiUserSettingContext();
  const onClick = async () => {
    setIsLoading(true);
    await updateFtuStage(2 /* Alerts */);
    props.onClickNext();
    setIsLoading(false);
  };
  return /* @__PURE__ */ React39.createElement(
    "div",
    {
      className: clsx22("notifi-ftu-target-list", props.classNames?.container)
    },
    /* @__PURE__ */ React39.createElement(
      NavHeader,
      {
        leftCta: {
          icon: "arrow-back",
          action: () => props.onClickBack()
        },
        rightCta: props.navHeaderRightCta
      },
      props.copy?.headerTitle ?? defaultCopy.ftuTargetList.headerTitle
    ),
    /* @__PURE__ */ React39.createElement(
      "div",
      {
        className: clsx22("notifi-ftu-target-list-main", props.classNames?.main)
      },
      /* @__PURE__ */ React39.createElement(TargetList, null)
    ),
    /* @__PURE__ */ React39.createElement(
      "div",
      {
        className: clsx22(
          "notifi-ftu-button-container",
          props.classNames?.buttonContainer
        )
      },
      /* @__PURE__ */ React39.createElement(
        "button",
        {
          "data-cy": "notifi-ftu-target-list-button",
          className: clsx22(
            "btn",
            "notifi-ftu-target-list-button",
            props.classNames?.button
          ),
          disabled: isLoading,
          onClick
        },
        isLoading ? /* @__PURE__ */ React39.createElement(
          LoadingAnimation,
          {
            type: "spinner",
            classNames: { spinner: "notifi-ftu-target-list-button-spinner" }
          }
        ) : null,
        /* @__PURE__ */ React39.createElement(
          "div",
          {
            className: clsx22(
              "notifi-ftu-target-list-button-text",
              isLoading && "hidden"
            )
          },
          props.copy?.buttonText ?? defaultCopy.ftuTargetList.buttonText
        )
      )
    )
  );
};

// lib/components/Ftu.tsx
var Ftu = (props) => {
  const { ftuStage } = useNotifiUserSettingContext();
  const { cardConfig } = useNotifiTenantConfigContext();
  const {
    targetDocument: { targetData },
    isLoading: isLoadingTarget
  } = useNotifiTargetContext();
  const { updateFtuStage } = useNotifiUserSettingContext();
  const [ftuView, setFtuView] = React40.useState(null);
  const { frontendClientStatus } = useNotifiFrontendClientContext();
  const isInitialLoaded = React40.useRef(false);
  React40.useEffect(() => {
    if (!frontendClientStatus.isAuthenticated || isLoadingTarget || isInitialLoaded.current)
      return;
    isInitialLoaded.current = true;
    if (ftuStage === 3 /* Destination */ && !hasTarget(targetData)) {
      setFtuView("edit" /* TargetEdit */);
      return;
    }
    if (ftuStage === 3 /* Destination */ && hasTarget(targetData)) {
      setFtuView("list" /* TargetList */);
      return;
    }
    setFtuView("alertEdit" /* AlertEdit */);
    return;
  }, [isLoadingTarget]);
  if (!ftuView) return null;
  return /* @__PURE__ */ React40.createElement("div", { className: clsx23("notifi-ftu", props.classNames?.container) }, /* @__PURE__ */ React40.createElement("div", { className: clsx23("notifi-ftu-views", props.classNames?.ftuViews) }, ftuView === "edit" /* TargetEdit */ ? /* @__PURE__ */ React40.createElement(
    FtuTargetEdit,
    {
      setFtuView,
      copy: props.copy?.FtuTargetEdit,
      classNames: props.classNames?.FtuTargetEdit,
      navHeaderRightCta: props.navHeaderRightCta
    }
  ) : null, ftuView === "list" /* TargetList */ ? /* @__PURE__ */ React40.createElement(
    FtuTargetList,
    {
      copy: props.copy?.FtuTargetList,
      classNames: props.classNames?.FtuTargetList,
      onClickNext: () => setFtuView("alertEdit" /* AlertEdit */),
      onClickBack: () => setFtuView("edit" /* TargetEdit */),
      navHeaderRightCta: props.navHeaderRightCta
    }
  ) : null, ftuView === "alertEdit" /* AlertEdit */ ? /* @__PURE__ */ React40.createElement(
    FtuAlertEdit,
    {
      copy: props.copy?.FtuAlertEdit,
      classNames: props.classNames?.FtuAlertEdit,
      onClickNext: () => {
        setFtuView(null);
        props.onComplete();
      },
      onClickBack: cardConfig?.isContactInfoRequired ? () => {
        if (cardConfig?.isContactInfoRequired) {
          updateFtuStage(3 /* Destination */);
          setFtuView("list" /* TargetList */);
          return;
        }
      } : void 0,
      navHeaderRightCta: props.navHeaderRightCta
    }
  ) : null), /* @__PURE__ */ React40.createElement("div", { className: clsx23("notifi-ftu-footer", props.classNames?.footer) }, /* @__PURE__ */ React40.createElement(PoweredByNotifi, { classNames: props.classNames?.PoweredByNotifi })));
};

// lib/components/Inbox.tsx
import clsx33 from "clsx";
import React50 from "react";

// lib/components/InboxConfigTargetEdit.tsx
import clsx24 from "clsx";
import React41 from "react";
var InboxConfigTargetEdit = (props) => {
  const {
    targetDocument: { targetData, targetInputs },
    renewTargetGroup,
    isChangingTargets
  } = useNotifiTargetContext();
  const [isLoading, setIsLoading] = React41.useState(false);
  const isUpdated = React41.useRef(false);
  const isTargetInputChanged = Object.values(isChangingTargets).includes(true);
  const [isShowingInvalidEmailWarning, setIsShowingInvalidEmailWarning] = React41.useState(false);
  React41.useEffect(() => {
    if (!isUpdated.current) return;
    setIsLoading;
    if (hasTarget(targetData)) {
      return props.setInboxView("inbox-config-target-list" /* InboxConfigTargetList */);
    }
    props.setInboxView("inbox-config-topic" /* InboxConfigTopic */);
    isUpdated.current = false;
  }, [targetData]);
  const isInputValid = useIsTargetInputValid();
  const onClick = async () => {
    setIsLoading(true);
    await renewTargetGroup();
    isUpdated.current = true;
  };
  return /* @__PURE__ */ React41.createElement(
    "div",
    {
      className: clsx24(
        "notifi-inbox-config-target-edit",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React41.createElement(
      NavHeader,
      {
        leftCta: {
          icon: "arrow-back",
          action: () => props.setInboxView("inbox-config-target-list" /* InboxConfigTargetList */)
        },
        rightCta: props.navHeaderRightCta
      },
      props.copy?.header ?? defaultCopy.inboxConfigTargetEdit.header
    ),
    /* @__PURE__ */ React41.createElement(
      "div",
      {
        className: clsx24(
          "notifi-inboxConfig-target-edit-description",
          props.classNames?.TargetInputs
        )
      },
      props.copy?.description ?? defaultCopy.ftuTargetEdit.description
    ),
    /* @__PURE__ */ React41.createElement(
      "div",
      {
        className: clsx24(
          "notifi-inbox-config-target-edit-invalid-email-warning",
          props.classNames?.invalidEmailWarning
        )
      },
      isShowingInvalidEmailWarning ? targetInputs.email.error : null
    ),
    /* @__PURE__ */ React41.createElement(
      "div",
      {
        className: clsx24(
          "notifi-inbox-config-target-edit-main",
          props.classNames?.main
        )
      },
      /* @__PURE__ */ React41.createElement(
        TargetInputs,
        {
          classNames: props.classNames?.TargetInputs,
          copy: props.copy?.TargetInputs,
          formTargetsOnFocus: (target) => {
            if (target === "email") setIsShowingInvalidEmailWarning(false);
          },
          formTargetsOnBlur: (target) => {
            if (target === "email") setIsShowingInvalidEmailWarning(true);
          }
        }
      )
    ),
    /* @__PURE__ */ React41.createElement(
      "button",
      {
        className: clsx24(
          "btn",
          "notifi-inbox-config-target-edit-button",
          props.classNames?.button
        ),
        disabled: isLoading || !isTargetInputChanged || !isInputValid,
        onClick
      },
      isLoading ? /* @__PURE__ */ React41.createElement(
        LoadingAnimation,
        {
          type: "spinner",
          classNames: {
            spinner: "notifi-inbox-config-target-edit-button-spinner"
          }
        }
      ) : null,
      /* @__PURE__ */ React41.createElement(
        "div",
        {
          className: clsx24(
            "notifi-inbox-config-target-edit-button-text",
            isLoading && "hidden"
          )
        },
        hasTarget(targetData) ? props.copy?.buttonTextHasTarget ?? defaultCopy.inboxConfigTargetEdit.buttonTextHasTarget : props.copy?.buttonTextNoTarget ?? defaultCopy.inboxConfigTargetEdit.buttonTextNoTarget
      )
    )
  );
};

// lib/components/InboxConfigTargetList.tsx
import clsx25 from "clsx";
import React42 from "react";
var InboxConfigTargetList = (props) => {
  const {
    isLoading,
    targetDocument: { targetData }
  } = useNotifiTargetContext();
  if (!hasTarget(targetData)) {
    props.setInboxView("inbox-config-topic" /* InboxConfigTopic */);
  }
  return /* @__PURE__ */ React42.createElement(
    "div",
    {
      className: clsx25(
        "notifi-inbox-config-target-list",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React42.createElement(
      NavHeader,
      {
        leftCta: {
          icon: "arrow-back",
          action: () => props.setInboxView("inbox-config-topic" /* InboxConfigTopic */)
        },
        rightCta: props.navHeaderRightCta
      },
      props.copy?.header ?? defaultCopy.inboxConfigTargetList.header
    ),
    /* @__PURE__ */ React42.createElement(
      "div",
      {
        className: clsx25(
          "notifi-inbox-config-target-list-main",
          props.classNames?.main
        )
      },
      /* @__PURE__ */ React42.createElement(
        TargetList,
        {
          classNames: {
            TargetListItem: {
              targetListVerifiedItem: "notifi-inbox-config-target-list-item"
            }
          },
          parentComponent: "inbox"
        }
      )
    ),
    /* @__PURE__ */ React42.createElement(
      "button",
      {
        "data-cy": "notifi-inbox-config-target-list-button",
        className: clsx25(
          "btn",
          "notifi-inbox-config-target-list-button",
          props.classNames?.button
        ),
        disabled: isLoading,
        onClick: () => props.setInboxView("inbox-config-target-edit" /* InboxConfigTargetEdit */)
      },
      /* @__PURE__ */ React42.createElement("div", { className: clsx25("notifi-inbox-config-target-list-button-text") }, props.copy?.buttonText ?? defaultCopy.inboxConfigTargetList.buttonText)
    )
  );
};

// lib/components/InboxConfigTopic.tsx
import clsx27 from "clsx";
import React44 from "react";

// lib/components/TargetStateBanner.tsx
import { objectKeys as objectKeys6 } from "@notifi-network/notifi-frontend-client";
import clsx26 from "clsx";
import React43 from "react";
var TargetStateBanner = (props) => {
  const parentComponent = props.parentComponent ?? "config";
  const {
    unVerifiedTargets,
    targetDocument: { targetData },
    isLoading: isLoadingTarget
  } = useNotifiTargetContext();
  const { cardConfig } = useNotifiTenantConfigContext();
  if (!cardConfig || isLoadingTarget) return null;
  const activeTargets = objectKeys6(cardConfig.contactInfo).map((target) => {
    if (!cardConfig.contactInfo[target]?.active) return null;
    switch (target) {
      case "sms":
        return "SMS";
      case "email":
        return target;
      default:
        return target.charAt(0).toUpperCase() + target.slice(1);
    }
  }).filter((target) => target !== null);
  return /* @__PURE__ */ React43.createElement(
    "div",
    {
      className: clsx26(
        "notifi-target-state-banner",
        props.classNames?.container
      )
    },
    !hasTarget(targetData) && parentComponent === "history" ? /* @__PURE__ */ React43.createElement(
      "div",
      {
        className: clsx26(
          "notifi-target-state-banner-signup",
          props.classNames?.signupBanner
        )
      },
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-signup-content",
            props.classNames?.signupBannerContent
          )
        },
        /* @__PURE__ */ React43.createElement(
          "div",
          {
            className: clsx26(
              "notifi-target-state-banner-signup-icon",
              props.classNames?.signupBannerIcon
            )
          },
          /* @__PURE__ */ React43.createElement(Icon, { type: "bell-thin" })
        ),
        /* @__PURE__ */ React43.createElement(
          "div",
          {
            className: clsx26(
              "notifi-target-state-banner-signup-text",
              props.classNames?.signupBannerText
            )
          },
          props.copy?.signup?.text ?? defaultCopy.targetStateBanner.Signup.textInHistory,
          " ",
          activeTargets.length > 1 ? `${activeTargets.slice(0, -1).join(", ")}, or ${activeTargets.slice(-1)}` : activeTargets.slice(-1)
        )
      ),
      /* @__PURE__ */ React43.createElement(
        "button",
        {
          className: clsx26(
            "btn",
            "notifi-target-state-banner-signup-cta",
            props.classNames?.signupBannerCta
          ),
          onClick: props.onClickCta
        },
        props.copy?.signup?.cta ?? defaultCopy.targetStateBanner.Signup.ctaInHistory
      )
    ) : null,
    parentComponent === "config" ? /* @__PURE__ */ React43.createElement(
      "div",
      {
        className: clsx26(
          "notifi-target-state-banner-verify",
          props.classNames?.verifyBanner
        ),
        onClick: props.onClickCta
      },
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-verify-icon",
            props.classNames?.verifyBannerIcon
          )
        },
        /* @__PURE__ */ React43.createElement(Icon, { type: "round-bell" })
      ),
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-verify-content",
            props.classNames?.verifyBannerContent
          )
        },
        /* @__PURE__ */ React43.createElement(
          "div",
          {
            className: clsx26(
              "notifi-target-state-banner-verify-title",
              props.classNames?.verifyBannerTitle
            )
          },
          hasTarget(targetData) ? props.copy?.verify?.title ?? defaultCopy.targetStateBanner.verify.title : props.copy?.signup?.textInConfig ?? defaultCopy.targetStateBanner.Signup.textInConfig
        ),
        unVerifiedTargets.length > 0 && /* @__PURE__ */ React43.createElement(
          "div",
          {
            className: clsx26(
              "notifi-target-state-banner-verify-description",
              props.classNames?.verifyBannerDescription
            )
          },
          props.copy?.verify?.description ?? defaultCopy.targetStateBanner.verify.description
        )
      ),
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-verify-cta",
            props.classNames?.verifyBannerCta
          )
        },
        /* @__PURE__ */ React43.createElement(Icon, { type: "chevron-right" })
      )
    ) : null,
    hasTarget(targetData) && parentComponent === "history" ? /* @__PURE__ */ React43.createElement(
      "div",
      {
        className: clsx26(
          "notifi-target-state-banner-verify",
          parentComponent === "history" && "history",
          props.classNames?.verifyBanner
        ),
        onClick: props.onClickCta
      },
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-verify-icon",
            parentComponent === "history" && "history",
            props.classNames?.verifyBannerIcon
          )
        },
        /* @__PURE__ */ React43.createElement(Icon, { type: "check-circle" })
      ),
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-verify-content",
            props.classNames?.verifyBannerContent
          )
        },
        /* @__PURE__ */ React43.createElement(
          "div",
          {
            className: clsx26(
              "notifi-target-state-banner-verify-title",
              parentComponent === "history" && "history",
              props.classNames?.verifyBannerTitle
            )
          },
          `${defaultCopy.targetStateBanner.verifyInHistory.title} ${formatUnverifiedTargets(unVerifiedTargets)}`
        )
      ),
      /* @__PURE__ */ React43.createElement(
        "div",
        {
          className: clsx26(
            "notifi-target-state-banner-verify-cta",
            parentComponent === "history" && "history",
            props.classNames?.verifyBannerCta
          )
        },
        defaultCopy.targetStateBanner.verifyInHistory.ctaText
      )
    ) : null
  );
};
var formatUnverifiedTargets = (unVerifiedTargets) => {
  const captializedTargets = unVerifiedTargets.map((target) => {
    if (target === "email") return target;
    return target.charAt(0).toUpperCase() + target.slice(1);
  });
  return captializedTargets.length > 1 ? captializedTargets.length === 2 ? `${captializedTargets[0]} and ${captializedTargets[1]}` : `${captializedTargets.slice(0, -1).join(", ")}, and ${captializedTargets.slice(-1)}` : captializedTargets.slice(-1);
};

// lib/components/InboxConfigTopic.tsx
var InboxConfigTopic = (props) => {
  const {
    targetDocument: { targetData }
  } = useNotifiTargetContext();
  return /* @__PURE__ */ React44.createElement(
    "div",
    {
      className: clsx27("notifi-inbox-config-topic", props.classNames?.container)
    },
    /* @__PURE__ */ React44.createElement(NavHeader, { rightCta: props.navHeaderRightCta }, props.copy?.header ?? defaultCopy.inboxConfigTopic.header),
    /* @__PURE__ */ React44.createElement(
      "div",
      {
        className: clsx27(
          "notifi-inbox-config-topic-main",
          props.classNames?.main
        )
      },
      /* @__PURE__ */ React44.createElement(
        "div",
        {
          className: clsx27(
            "notifi-inbox-config-topic-banner",
            props.classNames?.banner
          )
        },
        /* @__PURE__ */ React44.createElement(
          TargetStateBanner,
          {
            classNames: props.classNames?.TargetStateBanner,
            onClickCta: () => {
              if (!hasTarget(targetData)) {
                return props.setInboxView("inbox-config-target-edit" /* InboxConfigTargetEdit */);
              }
              props.setInboxView("inbox-config-target-list" /* InboxConfigTargetList */);
            }
          }
        )
      ),
      /* @__PURE__ */ React44.createElement(
        "div",
        {
          className: clsx27(
            "notifi-inbox-config-topic-title",
            props.classNames?.title
          )
        },
        props.copy?.title ?? defaultCopy.inboxConfigTopic.title
      ),
      /* @__PURE__ */ React44.createElement("div", null, /* @__PURE__ */ React44.createElement(TopicList, { parentComponent: "inbox" }))
    )
  );
};

// lib/components/InboxHistory.tsx
import clsx31 from "clsx";
import React48 from "react";

// lib/components/HistoryDetail.tsx
import clsx28 from "clsx";
import { format, parseISO } from "date-fns";
import DOMPurify from "dompurify";
import React45 from "react";
var HistoryDetail = (props) => {
  const historyItem = props.selectedHistoryItem;
  if (!historyItem) {
    return null;
  }
  const sanitizedMessage = React45.useMemo(
    () => DOMPurify.sanitize(historyItem.message),
    [historyItem.message]
  );
  return /* @__PURE__ */ React45.createElement(
    "div",
    {
      "data-cy": "notifi-history-detail",
      className: clsx28(
        "notifi-history-detail",
        props.isHidden && "hidden",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React45.createElement(
      NavHeader,
      {
        leftCta: {
          icon: "arrow-back",
          action: () => props.setSelectedHistoryItem(null)
        },
        rightCta: props.navHeaderRightCta
      },
      props.copy?.headerTitle ?? defaultCopy.inboxHistoryDetail.headerTitle
    ),
    /* @__PURE__ */ React45.createElement(
      "div",
      {
        className: clsx28("notifi-history-detail-main", props.classNames?.main)
      },
      /* @__PURE__ */ React45.createElement(
        "div",
        {
          className: clsx28(
            "notifi-history-detail-subject",
            props.classNames?.subject
          )
        },
        historyItem.topic
      ),
      /* @__PURE__ */ React45.createElement(
        "div",
        {
          className: clsx28(
            "notifi-history-detail-timestamp",
            props.classNames?.timestamp
          )
        },
        formatTimestampInHistoryDetail(historyItem.timestamp)
      ),
      /* @__PURE__ */ React45.createElement(
        "div",
        {
          className: clsx28(
            "notifi-history-detail-message",
            props.classNames?.message
          ),
          dangerouslySetInnerHTML: { __html: replaceLinks(sanitizedMessage) }
        }
      )
    )
  );
};
var formatTimestampInHistoryDetail = (date) => {
  try {
    return format(parseISO(date), "PPPp");
  } catch {
    return "-";
  }
};
var replaceLinks = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const links = doc.getElementsByTagName("a");
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    link.setAttribute("target", "_blank");
  }
  return doc.documentElement.innerHTML;
};

// lib/components/HistoryList.tsx
import clsx30 from "clsx";
import React47 from "react";

// lib/components/HistoryRow.tsx
import clsx29 from "clsx";
import { format as format2, isToday, isWithinInterval, subDays } from "date-fns";
import React46 from "react";
var HistoryRow = (props) => {
  const icon = props.historyItem.icon;
  const { markAsRead } = useNotifiHistoryContext();
  return /* @__PURE__ */ React46.createElement(
    "div",
    {
      className: clsx29(
        "notifi-history-row",
        props.className?.container,
        props.historyItem.read ? "read" : ""
      ),
      onClick: () => {
        if (!props.historyItem.read) {
          markAsRead([props.historyItem.id]);
        }
        props.onClick?.();
      }
    },
    /* @__PURE__ */ React46.createElement(
      "div",
      {
        className: clsx29(
          "notifi-history-row-unread-indicator",
          props.historyItem.read && "read",
          props.className?.unreadIndicator
        )
      }
    ),
    /* @__PURE__ */ React46.createElement("div", { className: clsx29("notifi-history-row-icon", props.className?.icon) }, /* @__PURE__ */ React46.createElement(Icon, { type: icon })),
    /* @__PURE__ */ React46.createElement(
      "div",
      {
        className: clsx29("notifi-history-row-content", props.className?.content)
      },
      /* @__PURE__ */ React46.createElement(
        "div",
        {
          className: clsx29("notifi-history-row-title", props.className?.title)
        },
        /* @__PURE__ */ React46.createElement(
          "div",
          {
            className: clsx29(
              "notifi-history-row-title-text",
              props.className?.titleText
            )
          },
          props.historyItem.topic ?? defaultCopy.historyRow.legacyTopic
        ),
        /* @__PURE__ */ React46.createElement(
          "div",
          {
            className: clsx29(
              "notifi-history-row-timestamp",
              props.className?.timestamp
            )
          },
          formatTimestampInHistoryRow(props.historyItem.timestamp)
        )
      ),
      /* @__PURE__ */ React46.createElement(
        "div",
        {
          className: clsx29(
            "notifi-history-row-subject",
            props.className?.subject
          )
        },
        props.historyItem.subject
      )
    )
  );
};
var formatTimestampInHistoryRow = (timestamp) => {
  const dateObject = new Date(timestamp);
  const now = /* @__PURE__ */ new Date();
  const sevenDaysAgo = subDays(now, 7);
  return format2(
    new Date(timestamp),
    isToday(dateObject) ? "hh:mm b" : isWithinInterval(dateObject, { start: sevenDaysAgo, end: now }) ? "eee" : "MMM d"
  ).replace("noon", "PM").replace("midnight", "AM");
};

// lib/components/HistoryList.tsx
var HistoryList = (props) => {
  const {
    historyItems,
    hasNextPage,
    getHistoryItems,
    isLoading: isLoadingHistoryItems
  } = useNotifiHistoryContext();
  const { cardConfig } = useNotifiTenantConfigContext();
  const [isLoadingMoreItems, setIsLoadingMoreItems] = React47.useState(false);
  const mainRef = React47.useRef(null);
  React47.useEffect(() => {
    const scrollDetectedAction = async () => {
      const scrollTop = mainRef.current?.scrollTop ?? 0;
      const scrollHeight = mainRef.current?.scrollHeight ?? 0;
      const offsetHeight = mainRef.current?.offsetHeight ?? 0;
      const edgeBuffer = 10;
      if (scrollTop > scrollHeight - offsetHeight - edgeBuffer) {
        if (isLoadingHistoryItems || !hasNextPage) return;
        setIsLoadingMoreItems(true);
        await getHistoryItems();
        setIsLoadingMoreItems(false);
      }
    };
    mainRef.current?.addEventListener("scroll", scrollDetectedAction);
    return () => mainRef.current?.removeEventListener("scroll", scrollDetectedAction);
  }, [hasNextPage, isLoadingHistoryItems]);
  const {
    targetDocument: { targetData },
    unVerifiedTargets
  } = useNotifiTargetContext();
  const isShowingTargetStateBanner = unVerifiedTargets.length > 0 || !hasTarget(targetData) && !cardConfig?.isContactInfoRequired;
  return /* @__PURE__ */ React47.createElement(
    "div",
    {
      "data-cy": "notifi-history-list",
      className: clsx30(
        "notifi-history-list",
        props.isHidden && "hidden",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React47.createElement(NavHeader, { rightCta: props.navHeaderRightCta }, props.copy?.headerTitle ?? defaultCopy.inboxHistoryList.headerTitle),
    isShowingTargetStateBanner ? /* @__PURE__ */ React47.createElement(
      "div",
      {
        className: clsx30(
          "notifi-history-list-target-state-banner-container",
          props.classNames?.TargetStateBannerContainer
        )
      },
      /* @__PURE__ */ React47.createElement(
        TargetStateBanner,
        {
          classNames: props.classNames?.TargetStateBanner,
          onClickCta: () => {
            if (!hasTarget(targetData)) {
              return props.setInboxView("inbox-config-target-edit" /* InboxConfigTargetEdit */);
            }
            props.setInboxView("inbox-config-target-list" /* InboxConfigTargetList */);
          },
          parentComponent: "history"
        }
      )
    ) : null,
    /* @__PURE__ */ React47.createElement(
      "div",
      {
        ref: mainRef,
        className: clsx30(
          "notifi-history-list-main",
          props.classNames?.main,
          isShowingTargetStateBanner ? "w-banner" : ""
        )
      },
      historyItems.map((item, id) => /* @__PURE__ */ React47.createElement(
        HistoryRow,
        {
          key: id,
          historyItem: item,
          onClick: () => props.setSelectedHistoryItem(item)
        }
      )),
      isLoadingMoreItems ? /* @__PURE__ */ React47.createElement(
        "div",
        {
          className: clsx30(
            "notifi-history-list-loading-more",
            props.classNames?.loadingMore,
            !hasNextPage && "hidden"
          )
        },
        /* @__PURE__ */ React47.createElement(
          LoadingAnimation,
          {
            type: "spinner",
            classNames: {
              spinner: "notifi-history-list-loading-more-spinner"
            }
          }
        )
      ) : null,
      !isLoadingHistoryItems && historyItems.length === 0 ? /* @__PURE__ */ React47.createElement(
        "div",
        {
          className: clsx30(
            "notifi-history-list-empty",
            props.classNames?.inboxEmpty
          )
        },
        /* @__PURE__ */ React47.createElement(
          "div",
          {
            className: clsx30(
              "notifi-history-list-empty-icon",
              props.classNames?.emptyIcon
            )
          },
          /* @__PURE__ */ React47.createElement(Icon, { type: props.emptyIcon ?? "empty-box" })
        ),
        /* @__PURE__ */ React47.createElement(
          "div",
          {
            className: clsx30(
              "notifi-history-list-empty-title",
              props.classNames?.emptyTitle
            )
          },
          props.copy?.emptyTitle ?? defaultCopy.historyList.inboxEmptyTitle
        ),
        /* @__PURE__ */ React47.createElement(
          "div",
          {
            className: clsx30(
              "notifi-history-list-empty-description",
              props.classNames?.emptyDescription
            )
          },
          props.copy?.emptyDescription ?? defaultCopy.historyList.inboxEmptyDescription
        )
      ) : null
    )
  );
};

// lib/components/InboxHistory.tsx
var InboxHistory = (props) => {
  const [selectedHistoryItem, setSelectedHistoryItem] = React48.useState(null);
  return /* @__PURE__ */ React48.createElement(
    "div",
    {
      className: clsx31(
        "notifi-inbox-history",
        props.isHidden && "hidden",
        props.classNames?.container
      )
    },
    /* @__PURE__ */ React48.createElement(
      HistoryList,
      {
        ...{ setSelectedHistoryItem },
        isHidden: !!selectedHistoryItem,
        setInboxView: props.setInboxView,
        navHeaderRightCta: props.navHeaderRightCta
      }
    ),
    /* @__PURE__ */ React48.createElement(
      HistoryDetail,
      {
        ...{ selectedHistoryItem, setSelectedHistoryItem },
        isHidden: !selectedHistoryItem,
        navHeaderRightCta: props.navHeaderRightCta
      }
    )
  );
};

// lib/components/InboxNavTabs.tsx
import clsx32 from "clsx";
import React49 from "react";
var InboxNavTabs = (props) => {
  const bellActive = props.inboxView === "inbox-history" /* InboxHistory */;
  const configActive = props.inboxView === "inbox-config-topic" /* InboxConfigTopic */ || props.inboxView === "inbox-config-target-list" /* InboxConfigTargetList */ || props.inboxView === "inbox-config-target-edit" /* InboxConfigTargetEdit */;
  return /* @__PURE__ */ React49.createElement(
    "div",
    {
      "data-cy": "notifi-inbox-nav-tabs",
      className: clsx32("notifi-inbox-nav-tabs", props.classNames?.container)
    },
    /* @__PURE__ */ React49.createElement("div", { onClick: () => props.setInboxView("inbox-history" /* InboxHistory */) }, /* @__PURE__ */ React49.createElement(Icon, { type: bellActive ? "bell-fill" : "bell" })),
    /* @__PURE__ */ React49.createElement("div", { onClick: () => props.setInboxView("inbox-config-topic" /* InboxConfigTopic */) }, /* @__PURE__ */ React49.createElement(Icon, { type: configActive ? "gear-fill" : "gear" }))
  );
};

// lib/components/Inbox.tsx
var Inbox = (props) => {
  const [inboxView, setInboxView] = React50.useState(
    "inbox-history" /* InboxHistory */
  );
  return /* @__PURE__ */ React50.createElement("div", { className: clsx33("notifi-inbox", props.classNames?.container) }, /* @__PURE__ */ React50.createElement("div", { className: clsx33("notifi-inbox-views", props.classNames?.inboxViews) }, /* @__PURE__ */ React50.createElement(
    InboxHistory,
    {
      isHidden: inboxView !== "inbox-history" /* InboxHistory */,
      classNames: props.classNames?.InboxHistory,
      setInboxView,
      navHeaderRightCta: props.navHeaderRightCta
    }
  ), inboxView === "inbox-config-topic" /* InboxConfigTopic */ ? /* @__PURE__ */ React50.createElement(
    InboxConfigTopic,
    {
      ...{ setInboxView },
      classNames: props.classNames?.InboxConfigTopic,
      copy: props.copy?.InboxConfigTopic,
      navHeaderRightCta: props.navHeaderRightCta
    }
  ) : null, inboxView === "inbox-config-target-list" /* InboxConfigTargetList */ ? /* @__PURE__ */ React50.createElement(
    InboxConfigTargetList,
    {
      ...{ setInboxView },
      classNames: props.classNames?.InboxConfigTargetList,
      copy: props.copy?.InboxConfigTargetList,
      navHeaderRightCta: props.navHeaderRightCta
    }
  ) : null, inboxView === "inbox-config-target-edit" /* InboxConfigTargetEdit */ ? /* @__PURE__ */ React50.createElement(
    InboxConfigTargetEdit,
    {
      ...{ setInboxView },
      classNames: props.classNames?.InboxConfigTargetEdit,
      copy: props.copy?.InboxConfigTargetEdit,
      navHeaderRightCta: props.navHeaderRightCta
    }
  ) : null), /* @__PURE__ */ React50.createElement("div", { className: clsx33("notifi-inbox-footer", props.classNames?.footer) }, /* @__PURE__ */ React50.createElement(
    InboxNavTabs,
    {
      setInboxView,
      inboxView,
      classNames: props.classNames?.InboxNavTabs
    }
  )));
};

// lib/components/NotifiCardModal.tsx
var navHeaderRightCta = {
  globalCtaType: "onClose",
  icon: "close"
};
var NotifiCardModal = (props) => {
  const { frontendClientStatus, error: clientError } = useNotifiFrontendClientContext();
  const {
    ftuStage,
    error: userSettingError,
    isLoading: isLoadingFtu
  } = useNotifiUserSettingContext();
  const [CardModalView, setCardModalView] = useState7(
    null
  );
  const { setGlobalError, globalError, setGlobalCtas } = useGlobalStateContext();
  const { error: historyError } = useNotifiHistoryContext();
  const { error: tenantError } = useNotifiTenantConfigContext();
  const { error: topicError } = useNotifiTopicContext();
  const { error: targetError } = useNotifiTargetContext();
  useEffect6(() => {
    if (props?.globalCtas) {
      setGlobalCtas({ ...props.globalCtas });
    }
  }, [props?.globalCtas]);
  useEffect6(() => {
    if (!frontendClientStatus.isInitialized) return;
    if (frontendClientStatus.isExpired) {
      setCardModalView("expiry");
      return;
    }
    if (!frontendClientStatus.isAuthenticated) {
      return setCardModalView("connect");
    }
    if (frontendClientStatus.isAuthenticated && !isLoadingFtu) {
      if (ftuStage !== 1 /* Done */) {
        setCardModalView("ftu");
        return;
      }
      setCardModalView("Inbox");
      return;
    }
  }, [ftuStage]);
  useEffect6(() => {
    if (clientError || userSettingError || historyError || tenantError || topicError || targetError) {
      setGlobalError({
        error: new Error(
          `In 
              ${clientError?.message ? ` ClientContext: 
 ${clientError.message}` : ""} 
              ${userSettingError?.message ? ` UserSettingContext: 
 ${userSettingError.message}` : ""}
              ${historyError?.message ? ` HistoryContext: 
 ${historyError.message}` : ""}
              ${tenantError?.message ? ` TenantConfigContext: 
 ${tenantError.message}` : ""}
              ${topicError?.message ? ` TopicContext: 
 ${topicError.message}` : ""}
              ${targetError?.message ? ` TargetContext: 
 ${targetError.message}` : ""}
          `
        )
      });
      return;
    }
  }, [
    userSettingError,
    isLoadingFtu,
    clientError,
    historyError,
    tenantError,
    targetError
  ]);
  if (globalError) {
    return /* @__PURE__ */ React51.createElement(
      "div",
      {
        className: clsx34(
          props.darkMode ? "notifi-theme-dark" : "notifi-theme-light",
          "notifi-card-modal",
          props.classNames?.container
        )
      },
      /* @__PURE__ */ React51.createElement(
        ErrorView,
        {
          detail: globalError.error.message,
          cta: {
            icon: "arrow-back",
            action: () => setGlobalError(null)
          },
          navHeaderRightCta
        }
      )
    );
  }
  return /* @__PURE__ */ React51.createElement(
    "div",
    {
      className: clsx34(
        props.darkMode ? "notifi-theme-dark" : "notifi-theme-light",
        "notifi-card-modal",
        props.classNames?.container
      )
    },
    CardModalView === "connect" ? /* @__PURE__ */ React51.createElement(
      Connect,
      {
        setCardModalView,
        copy: props.copy?.Connect,
        classNames: props.classNames?.Connect,
        navHeaderRightCta
      }
    ) : null,
    CardModalView === "expiry" ? /* @__PURE__ */ React51.createElement(
      Expiry,
      {
        setCardModalView,
        navHeaderRightCta
      }
    ) : null,
    CardModalView === "ftu" ? /* @__PURE__ */ React51.createElement(
      Ftu,
      {
        classNames: props.classNames?.Ftu,
        copy: props.copy?.Ftu,
        onComplete: () => setCardModalView("Inbox"),
        navHeaderRightCta
      }
    ) : null,
    CardModalView === "Inbox" ? /* @__PURE__ */ React51.createElement(
      Inbox,
      {
        classNames: props.classNames?.Inbox,
        copy: props.copy?.Inbox,
        navHeaderRightCta
      }
    ) : null
  );
};
export {
  ConvertOptionDirection,
  FtuStage,
  NotifiCardModal,
  NotifiContextProvider,
  NotifiFrontendClientContext,
  NotifiFrontendClientContextProvider,
  NotifiHistoryContextProvider,
  NotifiTargetContextProvider,
  NotifiTenantConfigContextProvider,
  NotifiTopicContextProvider,
  NotifiUserSettingContextProvider,
  categorizeTopics,
  composeTopicStackAlertName,
  convertOptionValue,
  defaultCopy,
  derivePrefixAndSuffixFromValueType,
  formatTelegramForSubscription,
  getAvailableTargetInputCount,
  getFusionEventMetadata,
  getFusionFilter,
  getTargetValidateRegex,
  getUiConfigOverride,
  getUpdatedAlertFilterOptions,
  getUserInputParams,
  getWalletTargetSignMessage,
  hasTarget,
  isAlertFilter,
  isAlertMetadataForTopicStack,
  isCtaInfo,
  isEqual,
  isFormTarget,
  isFusionEventMetadata,
  isFusionFilterOptions,
  isTargetCta,
  isTargetVerified,
  isToggleTarget,
  isTopicGroupValid,
  loginViaSolanaHardwareWallet,
  reformatSignatureForWalletTarget,
  resolveAlertName,
  resolveTopicStackAlertName,
  useNotifiFrontendClientContext,
  useNotifiHistoryContext,
  useNotifiTargetContext,
  useNotifiTenantConfigContext,
  useNotifiTopicContext,
  useNotifiUserSettingContext,
  validateEventDetails
};
//# sourceMappingURL=index.mjs.map